import * as i0 from '@angular/core';
import { InjectionToken, Pipe, EventEmitter, forwardRef, Component, Inject, Input, ViewChild, Output, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatLegacyAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { Subject, BehaviorSubject, combineLatest, fromEvent } from 'rxjs';
import { debounceTime, takeUntil, startWith } from 'rxjs/operators';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@angular/material/input';
import { MatInputModule } from '@angular/material/input';
import * as i4 from '@angular/material/form-field';
import { MatFormFieldModule } from '@angular/material/form-field';
import * as i5 from '@angular/material/autocomplete';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import * as i6 from '@angular/material/core';
import * as i1$1 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import * as i8 from '@angular/material/progress-bar';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import * as i2$1 from '@angular/platform-browser';

const MatSelectCountryLangToken = new InjectionToken('MatSelectCountryLangToken');

class JoinStringsPipe {
    transform(value, separator) {
        return value.filter(Boolean).join(separator ? separator : ' - ');
    }
}
JoinStringsPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.1", ngImport: i0, type: JoinStringsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
JoinStringsPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.0.1", ngImport: i0, type: JoinStringsPipe, name: "joinStrings" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.1", ngImport: i0, type: JoinStringsPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'joinStrings' }]
        }] });

/**
 * @author Anthony Nahas
 * @since 11.19
 * @version 2.1.0
 */
class MatSelectCountryComponent {
    constructor(i18n, cdRef) {
        this.i18n = i18n;
        this.cdRef = cdRef;
        this.appearance = "outline";
        this.countries = [];
        this.placeHolder = "Select country";
        this.showCallingCode = false;
        this.excludedCountries = [];
        this.name = "country";
        this.error = "";
        this.cleareable = false;
        this.formControl = undefined;
        // tslint:disable-next-line: no-output-on-prefix
        this.onCountrySelected = new EventEmitter();
        this.debounceTime = 300;
        this.filterString = "";
        this.modelChanged = new Subject();
        this.countries$ = new BehaviorSubject([]);
        this.excludedCountries$ = new BehaviorSubject([]);
        this.value$ = new BehaviorSubject(null);
        this.unsubscribe$ = new Subject();
        this.propagateChange = (_) => { };
    }
    get value() {
        return this._value;
    }
    set value(value) {
        // setting a value on a reactive form (formControlName) doesn't trigger ngOnChanges but it does call this setter
        this.value$.next(value);
    }
    ngOnInit() {
        combineLatest([this.countries$, this.value$, this.excludedCountries$])
            .pipe(
        // fixing the glitch on combineLatest https://blog.strongbrew.io/combine-latest-glitch/
        debounceTime(0), takeUntil(this.unsubscribe$))
            .subscribe(([countries, value, excludedCountries]) => {
            this._populateCountries(countries, excludedCountries);
            if (value) {
                this._setValue(value);
            }
        });
        if (!this.countries.length) {
            this._loadCountriesFromDb();
        }
        this.modelChanged
            .pipe(startWith(""), debounceTime(this.debounceTime), takeUntil(this.unsubscribe$))
            .subscribe((value) => {
            this.filterString = value;
            this._filter(value);
        });
    }
    ngOnChanges(changes) {
        if (changes.countries?.currentValue) {
            this.countries$.next(changes.countries.currentValue);
        }
        if (changes.excludedCountries?.currentValue) {
            this.excludedCountries$.next(changes.excludedCountries.currentValue);
        }
        if (changes.language?.currentValue &&
            changes.language.currentValue !== changes.language.previousValue) {
            console.log("Change on language detected", changes.language);
            let lastValue = this._value;
            this.filterString = "";
            this.inputChanged("");
            this._setValue(null);
            this.onCountrySelected.emit(null);
            this._loadCountriesFromDb(lastValue?.alpha2Code);
        }
    }
    onBlur() {
        if (this.nullable &&
            !this.inputElement.value &&
            this.statesAutocompleteRef.panel) {
            this._setValue(null);
            this.onCountrySelected.emit(null);
        }
    }
    onOptionsSelected($event) {
        const value = this.countries.find((country) => country.name === $event.option.value);
        this._setValue(value);
        this.onCountrySelected.emit(value);
    }
    writeValue(obj) {
        if (obj) {
            this.value = obj;
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    registerOnTouched(fn) {
        // throw new Error('Method not implemented.');
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    autocompleteScroll() {
        if (this.itemsLoadSize) {
            setTimeout(() => {
                if (this.statesAutocompleteRef &&
                    this.autocompleteTrigger &&
                    this.statesAutocompleteRef.panel) {
                    fromEvent(this.statesAutocompleteRef.panel.nativeElement, "scroll")
                        .pipe(takeUntil(this.autocompleteTrigger.panelClosingActions))
                        .subscribe(() => {
                        const scrollTop = this.statesAutocompleteRef.panel.nativeElement.scrollTop;
                        const scrollHeight = this.statesAutocompleteRef.panel.nativeElement.scrollHeight;
                        const elementHeight = this.statesAutocompleteRef.panel.nativeElement.clientHeight;
                        const atBottom = scrollHeight === scrollTop + elementHeight;
                        if (atBottom) {
                            // fetch more data if not filtered
                            if (this.filterString === "") {
                                const fromIndex = this.filteredOptions.length;
                                const toIndex = +this.filteredOptions.length + +this.itemsLoadSize;
                                this.filteredOptions = [
                                    ...this.filteredOptions,
                                    ...this.countries.slice(fromIndex, toIndex),
                                ];
                            }
                        }
                    });
                }
            });
        }
    }
    inputChanged(value) {
        this.modelChanged.next(value);
    }
    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
    clear() {
        this.filterString = "";
        this.inputChanged("");
        this._setValue(null);
        this.onCountrySelected.emit(null);
    }
    _loadCountriesFromDb(alpha2Code) {
        this.loadingDB = true;
        this._importLang()
            .then((res) => {
            this.countries$.next(res);
            this._setValue(res.find((el) => el.alpha2Code == alpha2Code));
        })
            .catch((err) => console.error("Error: " + err))
            .finally(() => (this.loadingDB = false));
    }
    _populateCountries(countries, excludedCountries) {
        const excludeCountries = excludedCountries.map((c) => c.alpha2Code);
        this.countries = countries.filter((c) => !excludeCountries.includes(c.alpha2Code));
    }
    _setValue(value) {
        if (value && (!value.name || value.name === "Unknown")) {
            // lookup name based on alpha2 values could be extended to lookup on other values too
            const matchingCountry = this.countries.find((c) => c.alpha2Code === value.alpha2Code);
            if (!!matchingCountry) {
                value = matchingCountry;
            }
        }
        this._value = value?.name ? value : null;
        this.propagateChange(this._value);
    }
    _importLang() {
        const lang = this.language || this.i18n;
        switch (lang) {
            case "br":
                return Promise.resolve().then(function () { return br; })
                    .then((result) => result.COUNTRIES_DB_BR)
                    .then((y) => y);
            case "by":
                return Promise.resolve().then(function () { return by; })
                    .then((result) => result.COUNTRIES_DB_BY)
                    .then((y) => y);
            case "de":
                return Promise.resolve().then(function () { return de; })
                    .then((result) => result.COUNTRIES_DB_DE)
                    .then((y) => y);
            case "es":
                return Promise.resolve().then(function () { return es; })
                    .then((result) => result.COUNTRIES_DB_ES)
                    .then((y) => y);
            case "fr":
                return Promise.resolve().then(function () { return fr; })
                    .then((result) => result.COUNTRIES_DB_FR)
                    .then((y) => y);
            case "hr":
                return Promise.resolve().then(function () { return hr; })
                    .then((result) => result.COUNTRIES_DB_HR)
                    .then((y) => y);
            case "hu":
                return Promise.resolve().then(function () { return hu; })
                    .then((result) => result.COUNTRIES_DB_HU)
                    .then((y) => y);
            case "it":
                return Promise.resolve().then(function () { return it; })
                    .then((result) => result.COUNTRIES_DB_IT)
                    .then((y) => y);
            case "nl":
                return Promise.resolve().then(function () { return nl; })
                    .then((result) => result.COUNTRIES_DB_NL)
                    .then((y) => y);
            case "pt":
                return Promise.resolve().then(function () { return pt; })
                    .then((result) => result.COUNTRIES_DB_PT)
                    .then((y) => y);
            case "ru":
                return Promise.resolve().then(function () { return ru; })
                    .then((result) => result.COUNTRIES_DB_RU)
                    .then((y) => y);
            case "ua":
                return Promise.resolve().then(function () { return ua; })
                    .then((result) => result.COUNTRIES_DB_UA)
                    .then((y) => y);
            case "gl":
                return Promise.resolve().then(function () { return gl; })
                    .then((result) => result.COUNTRIES_DB_GL)
                    .then((y) => y);
            case "eu":
                return Promise.resolve().then(function () { return eu; })
                    .then((result) => result.COUNTRIES_DB_EU)
                    .then((y) => y);
            case "ca":
                return Promise.resolve().then(function () { return ca; })
                    .then((result) => result.COUNTRIES_DB_CA)
                    .then((y) => y);
            default:
                return Promise.resolve().then(function () { return en; })
                    .then((result) => result.COUNTRIES_DB)
                    .then((y) => y);
        }
    }
    _filter(value) {
        const filterValue = value.toLowerCase();
        // if not filtered, fetch reduced array
        if (this.itemsLoadSize && filterValue === "") {
            this.filteredOptions = this.countries.slice(0, this.itemsLoadSize);
        }
        else {
            this.filteredOptions = this.countries.filter((option) => option.name.toLowerCase().includes(filterValue) ||
                option.alpha2Code.toLowerCase().includes(filterValue) ||
                option.alpha3Code.toLowerCase().includes(filterValue));
        }
        // options in the UI are not updated when this component is used within a host component that uses OnPush
        this.cdRef.markForCheck();
    }
}
MatSelectCountryComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.1", ngImport: i0, type: MatSelectCountryComponent, deps: [{ token: forwardRef(() => MatSelectCountryLangToken) }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
MatSelectCountryComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.1", type: MatSelectCountryComponent, selector: "mat-select-country", inputs: { appearance: "appearance", countries: "countries", label: "label", placeHolder: "placeHolder", required: "required", disabled: "disabled", nullable: "nullable", readonly: "readonly", tabIndex: "tabIndex", class: "class", itemsLoadSize: "itemsLoadSize", loading: "loading", showCallingCode: "showCallingCode", excludedCountries: "excludedCountries", autocomplete: "autocomplete", language: "language", name: "name", error: "error", cleareable: "cleareable", formControl: "formControl", value: "value" }, outputs: { onCountrySelected: "onCountrySelected" }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MatSelectCountryComponent),
            multi: true,
        },
    ], viewQueries: [{ propertyName: "statesAutocompleteRef", first: true, predicate: ["countryAutocomplete"], descendants: true }, { propertyName: "autocompleteTrigger", first: true, predicate: MatLegacyAutocompleteTrigger, descendants: true }, { propertyName: "inputElement", first: true, predicate: ["inputElement"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<mat-form-field [appearance]=\"appearance\">\n  <mat-label *ngIf=\"label\">{{ label }}</mat-label>\n  <mat-icon\n    *ngIf=\"this.value\"\n    [svgIcon]=\"this.value?.alpha2Code?.toLowerCase()\"\n    class=\"mr-12 s-20 secondary-text\"\n    matSuffix\n  ></mat-icon>\n  <input\n    *ngIf=\"!!formControl\"\n    (blur)=\"onBlur()\"\n    (input)=\"inputChanged($event?.target?.value)\"\n    [class]=\"class\"\n    [matAutocomplete]=\"this.countryAutocomplete\"\n    [placeholder]=\"this.placeHolder\"\n    [readonly]=\"this.readonly\"\n    [tabIndex]=\"tabIndex\"\n    [value]=\"formControl.value?.name\"\n    [formControl]=\"formControl\"\n    [autocomplete]=\"this.autocomplete\"\n    [name]=\"this.name\"\n    aria-label=\"country\"\n    matInput\n    type=\"text\"\n    #inputElement\n  />\n  <input\n    *ngIf=\"!formControl\"\n    (blur)=\"onBlur()\"\n    (input)=\"inputChanged($event?.target?.value)\"\n    [class]=\"class\"\n    [matAutocomplete]=\"this.countryAutocomplete\"\n    [placeholder]=\"this.placeHolder\"\n    [readonly]=\"this.readonly\"\n    [tabIndex]=\"tabIndex\"\n    [value]=\"\n      showCallingCode\n        ? this.value\n          ? this.value?.name + ' (' + this.value?.callingCode + ')'\n          : ''\n        : this.value?.name\n    \"\n    [required]=\"this.required\"\n    [disabled]=\"this.disabled || this.loadingDB\"\n    [autocomplete]=\"this.autocomplete\"\n    [name]=\"this.name\"\n    aria-label=\"country\"\n    matInput\n    type=\"text\"\n    #inputElement\n  />\n  <mat-progress-bar\n    *ngIf=\"this.loadingDB || this.loading\"\n    mode=\"buffer\"\n  ></mat-progress-bar>\n  <mat-autocomplete\n    #countryAutocomplete=\"matAutocomplete\"\n    (opened)=\"autocompleteScroll()\"\n    (optionSelected)=\"onOptionsSelected($event)\"\n  >\n    <mat-option *ngFor=\"let country of filteredOptions\" [value]=\"country?.name\">\n      <div class=\"d-flex\">\n        <mat-icon [svgIcon]=\"country?.alpha2Code?.toLowerCase()\"></mat-icon>\n        <small\n          class=\"country-list-item\"\n          [title]=\"[country?.name, country?.alpha3Code] | joinStrings\"\n          *ngIf=\"!showCallingCode\"\n          >{{ [country?.name, country?.alpha3Code] | joinStrings }}</small\n        >\n        <small\n          class=\"country-list-item\"\n          [title]=\"\n            [country?.name, '(' + country?.callingCode + ')']\n              | joinStrings : ' '\n          \"\n          *ngIf=\"showCallingCode\"\n          >{{\n            [country?.name, \"(\" + country?.callingCode + \")\"]\n              | joinStrings : \" \"\n          }}</small\n        >\n      </div>\n    </mat-option>\n  </mat-autocomplete>\n  <mat-icon\n    class=\"cursor-pointer pos-rel\"\n    matSuffix\n    *ngIf=\"cleareable && !!value\"\n    (click)=\"clear()\"\n    >cancel</mat-icon\n  >\n  <mat-error *ngIf=\"formControl && formControl.invalid\">{{ error }}</mat-error>\n</mat-form-field>\n", styles: [".cursor-pointer{cursor:pointer}.pos-rel{position:relative}.country-list-item{float:right;margin-left:.5rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.d-flex{display:flex;width:100%}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "directive", type: i3.MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["disabled", "id", "placeholder", "name", "required", "type", "errorStateMatcher", "aria-describedby", "value", "readonly"], exportAs: ["matInput"] }, { kind: "component", type: i4.MatFormField, selector: "mat-form-field", inputs: ["hideRequiredMarker", "color", "floatLabel", "appearance", "subscriptSizing", "hintLabel"], exportAs: ["matFormField"] }, { kind: "directive", type: i4.MatLabel, selector: "mat-label" }, { kind: "directive", type: i4.MatError, selector: "mat-error, [matError]", inputs: ["id"] }, { kind: "directive", type: i4.MatSuffix, selector: "[matSuffix], [matIconSuffix], [matTextSuffix]" }, { kind: "component", type: i5.MatAutocomplete, selector: "mat-autocomplete", inputs: ["disableRipple"], exportAs: ["matAutocomplete"] }, { kind: "component", type: i6.MatOption, selector: "mat-option", exportAs: ["matOption"] }, { kind: "directive", type: i5.MatAutocompleteTrigger, selector: "input[matAutocomplete], textarea[matAutocomplete]", exportAs: ["matAutocompleteTrigger"] }, { kind: "component", type: i1$1.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { kind: "component", type: i8.MatProgressBar, selector: "mat-progress-bar", inputs: ["color", "value", "bufferValue", "mode"], outputs: ["animationEnd"], exportAs: ["matProgressBar"] }, { kind: "pipe", type: JoinStringsPipe, name: "joinStrings" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.1", ngImport: i0, type: MatSelectCountryComponent, decorators: [{
            type: Component,
            args: [{ selector: "mat-select-country", providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => MatSelectCountryComponent),
                            multi: true,
                        },
                    ], template: "<mat-form-field [appearance]=\"appearance\">\n  <mat-label *ngIf=\"label\">{{ label }}</mat-label>\n  <mat-icon\n    *ngIf=\"this.value\"\n    [svgIcon]=\"this.value?.alpha2Code?.toLowerCase()\"\n    class=\"mr-12 s-20 secondary-text\"\n    matSuffix\n  ></mat-icon>\n  <input\n    *ngIf=\"!!formControl\"\n    (blur)=\"onBlur()\"\n    (input)=\"inputChanged($event?.target?.value)\"\n    [class]=\"class\"\n    [matAutocomplete]=\"this.countryAutocomplete\"\n    [placeholder]=\"this.placeHolder\"\n    [readonly]=\"this.readonly\"\n    [tabIndex]=\"tabIndex\"\n    [value]=\"formControl.value?.name\"\n    [formControl]=\"formControl\"\n    [autocomplete]=\"this.autocomplete\"\n    [name]=\"this.name\"\n    aria-label=\"country\"\n    matInput\n    type=\"text\"\n    #inputElement\n  />\n  <input\n    *ngIf=\"!formControl\"\n    (blur)=\"onBlur()\"\n    (input)=\"inputChanged($event?.target?.value)\"\n    [class]=\"class\"\n    [matAutocomplete]=\"this.countryAutocomplete\"\n    [placeholder]=\"this.placeHolder\"\n    [readonly]=\"this.readonly\"\n    [tabIndex]=\"tabIndex\"\n    [value]=\"\n      showCallingCode\n        ? this.value\n          ? this.value?.name + ' (' + this.value?.callingCode + ')'\n          : ''\n        : this.value?.name\n    \"\n    [required]=\"this.required\"\n    [disabled]=\"this.disabled || this.loadingDB\"\n    [autocomplete]=\"this.autocomplete\"\n    [name]=\"this.name\"\n    aria-label=\"country\"\n    matInput\n    type=\"text\"\n    #inputElement\n  />\n  <mat-progress-bar\n    *ngIf=\"this.loadingDB || this.loading\"\n    mode=\"buffer\"\n  ></mat-progress-bar>\n  <mat-autocomplete\n    #countryAutocomplete=\"matAutocomplete\"\n    (opened)=\"autocompleteScroll()\"\n    (optionSelected)=\"onOptionsSelected($event)\"\n  >\n    <mat-option *ngFor=\"let country of filteredOptions\" [value]=\"country?.name\">\n      <div class=\"d-flex\">\n        <mat-icon [svgIcon]=\"country?.alpha2Code?.toLowerCase()\"></mat-icon>\n        <small\n          class=\"country-list-item\"\n          [title]=\"[country?.name, country?.alpha3Code] | joinStrings\"\n          *ngIf=\"!showCallingCode\"\n          >{{ [country?.name, country?.alpha3Code] | joinStrings }}</small\n        >\n        <small\n          class=\"country-list-item\"\n          [title]=\"\n            [country?.name, '(' + country?.callingCode + ')']\n              | joinStrings : ' '\n          \"\n          *ngIf=\"showCallingCode\"\n          >{{\n            [country?.name, \"(\" + country?.callingCode + \")\"]\n              | joinStrings : \" \"\n          }}</small\n        >\n      </div>\n    </mat-option>\n  </mat-autocomplete>\n  <mat-icon\n    class=\"cursor-pointer pos-rel\"\n    matSuffix\n    *ngIf=\"cleareable && !!value\"\n    (click)=\"clear()\"\n    >cancel</mat-icon\n  >\n  <mat-error *ngIf=\"formControl && formControl.invalid\">{{ error }}</mat-error>\n</mat-form-field>\n", styles: [".cursor-pointer{cursor:pointer}.pos-rel{position:relative}.country-list-item{float:right;margin-left:.5rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.d-flex{display:flex;width:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => MatSelectCountryLangToken)]
                }] }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { appearance: [{
                type: Input
            }], countries: [{
                type: Input
            }], label: [{
                type: Input
            }], placeHolder: [{
                type: Input
            }], required: [{
                type: Input
            }], disabled: [{
                type: Input
            }], nullable: [{
                type: Input
            }], readonly: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], class: [{
                type: Input
            }], itemsLoadSize: [{
                type: Input
            }], loading: [{
                type: Input
            }], showCallingCode: [{
                type: Input
            }], excludedCountries: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], language: [{
                type: Input
            }], name: [{
                type: Input
            }], error: [{
                type: Input
            }], cleareable: [{
                type: Input
            }], formControl: [{
                type: Input
            }], statesAutocompleteRef: [{
                type: ViewChild,
                args: ["countryAutocomplete"]
            }], autocompleteTrigger: [{
                type: ViewChild,
                args: [MatLegacyAutocompleteTrigger]
            }], inputElement: [{
                type: ViewChild,
                args: ["inputElement"]
            }], onCountrySelected: [{
                type: Output
            }], value: [{
                type: Input
            }] } });

const COUNTRIES_DB_BR = [
    {
        name: 'Afeganistão',
        alpha2Code: 'AF',
        alpha3Code: 'AFG',
        numericCode: '004',
        callingCode: '+93'
    },
    {
        name: 'Ilhas de Aland',
        alpha2Code: 'AX',
        alpha3Code: 'ALA',
        numericCode: '248',
        callingCode: '+358'
    },
    {
        name: 'Albânia',
        alpha2Code: 'AL',
        alpha3Code: 'ALB',
        numericCode: '008',
        callingCode: '+355'
    },
    {
        name: 'Argélia',
        alpha2Code: 'DZ',
        alpha3Code: 'DZA',
        numericCode: '012',
        callingCode: '+213'
    },
    {
        name: 'Samoa Americana',
        alpha2Code: 'AS',
        alpha3Code: 'ASM',
        numericCode: '016',
        callingCode: '+1684'
    },
    {
        name: 'Andorra',
        alpha2Code: 'AD',
        alpha3Code: 'AND',
        numericCode: '020',
        callingCode: '+376'
    },
    {
        name: 'Angola',
        alpha2Code: 'AO',
        alpha3Code: 'AGO',
        numericCode: '024',
        callingCode: '+244'
    },
    {
        name: 'Anguila',
        alpha2Code: 'AI',
        alpha3Code: 'AIA',
        numericCode: '660',
        callingCode: '+1264'
    },
    {
        name: 'Antártida',
        alpha2Code: 'AQ',
        alpha3Code: 'ATA',
        numericCode: '010',
        callingCode: '+672'
    },
    {
        name: 'Antígua e Barbuda',
        alpha2Code: 'AG',
        alpha3Code: 'ATG',
        numericCode: '028',
        callingCode: '+1268'
    },
    {
        name: 'Argentina',
        alpha2Code: 'AR',
        alpha3Code: 'ARG',
        numericCode: '032',
        callingCode: '+54'
    },
    {
        name: 'Armênia',
        alpha2Code: 'AM',
        alpha3Code: 'ARM',
        numericCode: '051',
        callingCode: '+374'
    },
    {
        name: 'Aruba',
        alpha2Code: 'AW',
        alpha3Code: 'ABW',
        numericCode: '533',
        callingCode: '+297'
    },
    {
        name: 'Austrália',
        alpha2Code: 'AU',
        alpha3Code: 'AUS',
        numericCode: '036',
        callingCode: '+61'
    },
    {
        name: 'áustria',
        alpha2Code: 'AT',
        alpha3Code: 'AUT',
        numericCode: '040',
        callingCode: '+43'
    },
    {
        name: 'Azerbaijão',
        alpha2Code: 'AZ',
        alpha3Code: 'AZE',
        numericCode: '031',
        callingCode: '+994'
    },
    {
        name: 'Bahamas',
        alpha2Code: 'BS',
        alpha3Code: 'BHS',
        numericCode: '044',
        callingCode: '+1242'
    },
    {
        name: 'Bahrein',
        alpha2Code: 'BH',
        alpha3Code: 'BHR',
        numericCode: '048',
        callingCode: '+973'
    },
    {
        name: 'Bangladesh',
        alpha2Code: 'BD',
        alpha3Code: 'BGD',
        numericCode: '050',
        callingCode: '+880'
    },
    {
        name: 'Barbados',
        alpha2Code: 'BB',
        alpha3Code: 'BRB',
        numericCode: '052',
        callingCode: '+1246'
    },
    {
        name: 'Bielorrússia',
        alpha2Code: 'BY',
        alpha3Code: 'BLR',
        numericCode: '112',
        callingCode: '+375'
    },
    {
        name: 'Bélgica',
        alpha2Code: 'BE',
        alpha3Code: 'BEL',
        numericCode: '056',
        callingCode: '+32'
    },
    {
        name: 'Belize',
        alpha2Code: 'BZ',
        alpha3Code: 'BLZ',
        numericCode: '084',
        callingCode: '+501'
    },
    {
        name: 'Benin',
        alpha2Code: 'BJ',
        alpha3Code: 'BEN',
        numericCode: '204',
        callingCode: '+229'
    },
    {
        name: 'Bermudas',
        alpha2Code: 'BM',
        alpha3Code: 'BMU',
        numericCode: '060',
        callingCode: '+1441'
    },
    {
        name: 'Butão',
        alpha2Code: 'BT',
        alpha3Code: 'BTN',
        numericCode: '064',
        callingCode: '+975'
    },
    {
        name: 'Bolívia',
        alpha2Code: 'BO',
        alpha3Code: 'BOL',
        numericCode: '068',
        callingCode: '+591'
    },
    {
        name: 'Bonaire',
        alpha2Code: 'BQ',
        alpha3Code: 'BES',
        numericCode: '535',
        callingCode: '+5997'
    },
    {
        name: 'Bósnia e Herzegovina',
        alpha2Code: 'BA',
        alpha3Code: 'BIH',
        numericCode: '070',
        callingCode: '+387'
    },
    {
        name: 'Botsuana',
        alpha2Code: 'BW',
        alpha3Code: 'BWA',
        numericCode: '072',
        callingCode: '+267'
    },
    {
        name: 'Ilha Bouvet',
        alpha2Code: 'BV',
        alpha3Code: 'BVT',
        numericCode: '074',
        callingCode: '+'
    },
    {
        name: 'Brasil',
        alpha2Code: 'BR',
        alpha3Code: 'BRA',
        numericCode: '076',
        callingCode: '+55'
    },
    {
        name: 'Território Britânico do Oceano íÍdico',
        alpha2Code: 'IO',
        alpha3Code: 'IOT',
        numericCode: '086',
        callingCode: '+246'
    },
    {
        name: 'Ilhas Menores Distantes dos Estados Unidos',
        alpha2Code: 'UM',
        alpha3Code: 'UMI',
        numericCode: '581',
        callingCode: '+'
    },
    {
        name: 'Ilhas Virgens Britânicas',
        alpha2Code: 'VG',
        alpha3Code: 'VGB',
        numericCode: '092',
        callingCode: '+1284'
    },
    {
        name: 'Ilhas Virgens Americanas',
        alpha2Code: 'VI',
        alpha3Code: 'VIR',
        numericCode: '850',
        callingCode: '+1 340'
    },
    {
        name: 'Brunei',
        alpha2Code: 'BN',
        alpha3Code: 'BRN',
        numericCode: '096',
        callingCode: '+673'
    },
    {
        name: 'Bulgária',
        alpha2Code: 'BG',
        alpha3Code: 'BGR',
        numericCode: '100',
        callingCode: '+359'
    },
    {
        name: 'Burkina Faso',
        alpha2Code: 'BF',
        alpha3Code: 'BFA',
        numericCode: '854',
        callingCode: '+226'
    },
    {
        name: 'Burundi',
        alpha2Code: 'BI',
        alpha3Code: 'BDI',
        numericCode: '108',
        callingCode: '+257'
    },
    {
        name: 'Camboja',
        alpha2Code: 'KH',
        alpha3Code: 'KHM',
        numericCode: '116',
        callingCode: '+855'
    },
    {
        name: 'Camarões',
        alpha2Code: 'CM',
        alpha3Code: 'CMR',
        numericCode: '120',
        callingCode: '+237'
    },
    {
        name: 'Canadá',
        alpha2Code: 'CA',
        alpha3Code: 'CAN',
        numericCode: '124',
        callingCode: '+1'
    },
    {
        name: 'Cabo Verde',
        alpha2Code: 'CV',
        alpha3Code: 'CPV',
        numericCode: '132',
        callingCode: '+238'
    },
    {
        name: 'Ilhas Cayman',
        alpha2Code: 'KY',
        alpha3Code: 'CYM',
        numericCode: '136',
        callingCode: '+1345'
    },
    {
        name: 'República Centro-Africana',
        alpha2Code: 'CF',
        alpha3Code: 'CAF',
        numericCode: '140',
        callingCode: '+236'
    },
    {
        name: 'Chade',
        alpha2Code: 'TD',
        alpha3Code: 'TCD',
        numericCode: '148',
        callingCode: '+235'
    },
    {
        name: 'Chile',
        alpha2Code: 'CL',
        alpha3Code: 'CHL',
        numericCode: '152',
        callingCode: '+56'
    },
    {
        name: 'China',
        alpha2Code: 'CN',
        alpha3Code: 'CHN',
        numericCode: '156',
        callingCode: '+86'
    },
    {
        name: 'Ilha Christmas',
        alpha2Code: 'CX',
        alpha3Code: 'CXR',
        numericCode: '162',
        callingCode: '+61'
    },
    {
        name: 'Ilhas Cocos',
        alpha2Code: 'CC',
        alpha3Code: 'CCK',
        numericCode: '166',
        callingCode: '+61'
    },
    {
        name: 'Colômbia',
        alpha2Code: 'CO',
        alpha3Code: 'COL',
        numericCode: '170',
        callingCode: '+57'
    },
    {
        name: 'Comores',
        alpha2Code: 'KM',
        alpha3Code: 'COM',
        numericCode: '174',
        callingCode: '+269'
    },
    {
        name: 'Congo',
        alpha2Code: 'CG',
        alpha3Code: 'COG',
        numericCode: '178',
        callingCode: '+242'
    },
    {
        name: 'RD Congo',
        alpha2Code: 'CD',
        alpha3Code: 'COD',
        numericCode: '180',
        callingCode: '+243'
    },
    {
        name: 'Ilhas Cook',
        alpha2Code: 'CK',
        alpha3Code: 'COK',
        numericCode: '184',
        callingCode: '+682'
    },
    {
        name: 'Costa Rica',
        alpha2Code: 'CR',
        alpha3Code: 'CRI',
        numericCode: '188',
        callingCode: '+506'
    },
    {
        name: 'Croácia',
        alpha2Code: 'HR',
        alpha3Code: 'HRV',
        numericCode: '191',
        callingCode: '+385'
    },
    {
        name: 'Cuba',
        alpha2Code: 'CU',
        alpha3Code: 'CUB',
        numericCode: '192',
        callingCode: '+53'
    },
    {
        name: 'Curaçao',
        alpha2Code: 'CW',
        alpha3Code: 'CUW',
        numericCode: '531',
        callingCode: '+599'
    },
    {
        name: 'Chipre',
        alpha2Code: 'CY',
        alpha3Code: 'CYP',
        numericCode: '196',
        callingCode: '+357'
    },
    {
        name: 'República Tcheca',
        alpha2Code: 'CZ',
        alpha3Code: 'CZE',
        numericCode: '203',
        callingCode: '+420'
    },
    {
        name: 'Dinamarca',
        alpha2Code: 'DK',
        alpha3Code: 'DNK',
        numericCode: '208',
        callingCode: '+45'
    },
    {
        name: 'Djibuti',
        alpha2Code: 'DJ',
        alpha3Code: 'DJI',
        numericCode: '262',
        callingCode: '+253'
    },
    {
        name: 'Dominica',
        alpha2Code: 'DM',
        alpha3Code: 'DMA',
        numericCode: '212',
        callingCode: '+1767'
    },
    {
        name: 'República Dominicana',
        alpha2Code: 'DO',
        alpha3Code: 'DOM',
        numericCode: '214',
        callingCode: '+1809'
    },
    {
        name: 'Equador',
        alpha2Code: 'EC',
        alpha3Code: 'ECU',
        numericCode: '218',
        callingCode: '+593'
    },
    {
        name: 'Egito',
        alpha2Code: 'EG',
        alpha3Code: 'EGY',
        numericCode: '818',
        callingCode: '+20'
    },
    {
        name: 'El Salvador',
        alpha2Code: 'SV',
        alpha3Code: 'SLV',
        numericCode: '222',
        callingCode: '+503'
    },
    {
        name: 'Guiné Equatorial',
        alpha2Code: 'GQ',
        alpha3Code: 'GNQ',
        numericCode: '226',
        callingCode: '+240'
    },
    {
        name: 'Eritreia',
        alpha2Code: 'ER',
        alpha3Code: 'ERI',
        numericCode: '232',
        callingCode: '+291'
    },
    {
        name: 'Estônia',
        alpha2Code: 'EE',
        alpha3Code: 'EST',
        numericCode: '233',
        callingCode: '+372'
    },
    {
        name: 'Etiópia',
        alpha2Code: 'ET',
        alpha3Code: 'ETH',
        numericCode: '231',
        callingCode: '+251'
    },
    {
        name: 'Ilhas Malvinas',
        alpha2Code: 'FK',
        alpha3Code: 'FLK',
        numericCode: '238',
        callingCode: '+500'
    },
    {
        name: 'Ilhas Faroé',
        alpha2Code: 'FO',
        alpha3Code: 'FRO',
        numericCode: '234',
        callingCode: '+298'
    },
    {
        name: 'Fiji',
        alpha2Code: 'FJ',
        alpha3Code: 'FJI',
        numericCode: '242',
        callingCode: '+679'
    },
    {
        name: 'Finlândia',
        alpha2Code: 'FI',
        alpha3Code: 'FIN',
        numericCode: '246',
        callingCode: '+358'
    },
    {
        name: 'França',
        alpha2Code: 'FR',
        alpha3Code: 'FRA',
        numericCode: '250',
        callingCode: '+33'
    },
    {
        name: 'Guiana Francesa',
        alpha2Code: 'GF',
        alpha3Code: 'GUF',
        numericCode: '254',
        callingCode: '+594'
    },
    {
        name: 'Polinésia Francesa',
        alpha2Code: 'PF',
        alpha3Code: 'PYF',
        numericCode: '258',
        callingCode: '+689'
    },
    {
        name: 'Terras Austrais e Antárticas Francesas',
        alpha2Code: 'TF',
        alpha3Code: 'ATF',
        numericCode: '260',
        callingCode: '+'
    },
    {
        name: 'Gabão',
        alpha2Code: 'GA',
        alpha3Code: 'GAB',
        numericCode: '266',
        callingCode: '+241'
    },
    {
        name: 'Gâmbia',
        alpha2Code: 'GM',
        alpha3Code: 'GMB',
        numericCode: '270',
        callingCode: '+220'
    },
    {
        name: 'Geórgia',
        alpha2Code: 'GE',
        alpha3Code: 'GEO',
        numericCode: '268',
        callingCode: '+995'
    },
    {
        name: 'Alemanha',
        alpha2Code: 'DE',
        alpha3Code: 'DEU',
        numericCode: '276',
        callingCode: '+49'
    },
    {
        name: 'Gana',
        alpha2Code: 'GH',
        alpha3Code: 'GHA',
        numericCode: '288',
        callingCode: '+233'
    },
    {
        name: 'Gibraltar',
        alpha2Code: 'GI',
        alpha3Code: 'GIB',
        numericCode: '292',
        callingCode: '+350'
    },
    {
        name: 'Grécia',
        alpha2Code: 'GR',
        alpha3Code: 'GRC',
        numericCode: '300',
        callingCode: '+30'
    },
    {
        name: 'Groelândia',
        alpha2Code: 'GL',
        alpha3Code: 'GRL',
        numericCode: '304',
        callingCode: '+299'
    },
    {
        name: 'Granada',
        alpha2Code: 'GD',
        alpha3Code: 'GRD',
        numericCode: '308',
        callingCode: '+1473'
    },
    {
        name: 'Guadalupe',
        alpha2Code: 'GP',
        alpha3Code: 'GLP',
        numericCode: '312',
        callingCode: '+590'
    },
    {
        name: 'Guam',
        alpha2Code: 'GU',
        alpha3Code: 'GUM',
        numericCode: '316',
        callingCode: '+1671'
    },
    {
        name: 'Guatemala',
        alpha2Code: 'GT',
        alpha3Code: 'GTM',
        numericCode: '320',
        callingCode: '+502'
    },
    {
        name: 'Guernsey',
        alpha2Code: 'GG',
        alpha3Code: 'GGY',
        numericCode: '831',
        callingCode: '+44'
    },
    {
        name: 'Guiné',
        alpha2Code: 'GN',
        alpha3Code: 'GIN',
        numericCode: '324',
        callingCode: '+224'
    },
    {
        name: 'Guiné-Bissau',
        alpha2Code: 'GW',
        alpha3Code: 'GNB',
        numericCode: '624',
        callingCode: '+245'
    },
    {
        name: 'Guiana',
        alpha2Code: 'GY',
        alpha3Code: 'GUY',
        numericCode: '328',
        callingCode: '+592'
    },
    {
        name: 'Haiti',
        alpha2Code: 'HT',
        alpha3Code: 'HTI',
        numericCode: '332',
        callingCode: '+509'
    },
    {
        name: 'Ilha Heard e Ilhas McDonald',
        alpha2Code: 'HM',
        alpha3Code: 'HMD',
        numericCode: '334',
        callingCode: '+'
    },
    {
        name: 'Vaticano',
        alpha2Code: 'VA',
        alpha3Code: 'VAT',
        numericCode: '336',
        callingCode: '+379'
    },
    {
        name: 'Honduras',
        alpha2Code: 'HN',
        alpha3Code: 'HND',
        numericCode: '340',
        callingCode: '+504'
    },
    {
        name: 'Hong Kong',
        alpha2Code: 'HK',
        alpha3Code: 'HKG',
        numericCode: '344',
        callingCode: '+852'
    },
    {
        name: 'Hungria',
        alpha2Code: 'HU',
        alpha3Code: 'HUN',
        numericCode: '348',
        callingCode: '+36'
    },
    {
        name: 'Islândia',
        alpha2Code: 'IS',
        alpha3Code: 'ISL',
        numericCode: '352',
        callingCode: '+354'
    },
    {
        name: 'Índia',
        alpha2Code: 'IN',
        alpha3Code: 'IND',
        numericCode: '356',
        callingCode: '+91'
    },
    {
        name: 'Indonésia',
        alpha2Code: 'ID',
        alpha3Code: 'IDN',
        numericCode: '360',
        callingCode: '+62'
    },
    {
        name: 'Costa do Marfim',
        alpha2Code: 'CI',
        alpha3Code: 'CIV',
        numericCode: '384',
        callingCode: '+225'
    },
    {
        name: 'Irã',
        alpha2Code: 'IR',
        alpha3Code: 'IRN',
        numericCode: '364',
        callingCode: '+98'
    },
    {
        name: 'Iraque',
        alpha2Code: 'IQ',
        alpha3Code: 'IRQ',
        numericCode: '368',
        callingCode: '+964'
    },
    {
        name: 'Irlanda',
        alpha2Code: 'IE',
        alpha3Code: 'IRL',
        numericCode: '372',
        callingCode: '+353'
    },
    {
        name: 'Ilha de Man',
        alpha2Code: 'IM',
        alpha3Code: 'IMN',
        numericCode: '833',
        callingCode: '+44'
    },
    {
        name: 'Israel',
        alpha2Code: 'IL',
        alpha3Code: 'ISR',
        numericCode: '376',
        callingCode: '+972'
    },
    {
        name: 'Itália',
        alpha2Code: 'IT',
        alpha3Code: 'ITA',
        numericCode: '380',
        callingCode: '+39'
    },
    {
        name: 'Jamaica',
        alpha2Code: 'JM',
        alpha3Code: 'JAM',
        numericCode: '388',
        callingCode: '+1876'
    },
    {
        name: 'Japão',
        alpha2Code: 'JP',
        alpha3Code: 'JPN',
        numericCode: '392',
        callingCode: '+81'
    },
    {
        name: 'Jersey',
        alpha2Code: 'JE',
        alpha3Code: 'JEY',
        numericCode: '832',
        callingCode: '+44'
    },
    {
        name: 'Jordânia',
        alpha2Code: 'JO',
        alpha3Code: 'JOR',
        numericCode: '400',
        callingCode: '+962'
    },
    {
        name: 'Cazaquistão',
        alpha2Code: 'KZ',
        alpha3Code: 'KAZ',
        numericCode: '398',
        callingCode: '+7'
    },
    {
        name: 'Quênia',
        alpha2Code: 'KE',
        alpha3Code: 'KEN',
        numericCode: '404',
        callingCode: '+254'
    },
    {
        name: 'Kiribati',
        alpha2Code: 'KI',
        alpha3Code: 'KIR',
        numericCode: '296',
        callingCode: '+686'
    },
    {
        name: 'Kuwait',
        alpha2Code: 'KW',
        alpha3Code: 'KWT',
        numericCode: '414',
        callingCode: '+965'
    },
    {
        name: 'Quirguistão',
        alpha2Code: 'KG',
        alpha3Code: 'KGZ',
        numericCode: '417',
        callingCode: '+996'
    },
    {
        name: 'Laos',
        alpha2Code: 'LA',
        alpha3Code: 'LAO',
        numericCode: '418',
        callingCode: '+856'
    },
    {
        name: 'Letônia',
        alpha2Code: 'LV',
        alpha3Code: 'LVA',
        numericCode: '428',
        callingCode: '+371'
    },
    {
        name: 'Líbano',
        alpha2Code: 'LB',
        alpha3Code: 'LBN',
        numericCode: '422',
        callingCode: '+961'
    },
    {
        name: 'Lesoto',
        alpha2Code: 'LS',
        alpha3Code: 'LSO',
        numericCode: '426',
        callingCode: '+266'
    },
    {
        name: 'Libéria',
        alpha2Code: 'LR',
        alpha3Code: 'LBR',
        numericCode: '430',
        callingCode: '+231'
    },
    {
        name: 'Líbia',
        alpha2Code: 'LY',
        alpha3Code: 'LBY',
        numericCode: '434',
        callingCode: '+218'
    },
    {
        name: 'Liechtenstein',
        alpha2Code: 'LI',
        alpha3Code: 'LIE',
        numericCode: '438',
        callingCode: '+423'
    },
    {
        name: 'Lituânia',
        alpha2Code: 'LT',
        alpha3Code: 'LTU',
        numericCode: '440',
        callingCode: '+370'
    },
    {
        name: 'Luxemburgo',
        alpha2Code: 'LU',
        alpha3Code: 'LUX',
        numericCode: '442',
        callingCode: '+352'
    },
    {
        name: 'Macau',
        alpha2Code: 'MO',
        alpha3Code: 'MAC',
        numericCode: '446',
        callingCode: '+853'
    },
    {
        name: 'Macedônia',
        alpha2Code: 'MK',
        alpha3Code: 'MKD',
        numericCode: '807',
        callingCode: '+389'
    },
    {
        name: 'Madagascar',
        alpha2Code: 'MG',
        alpha3Code: 'MDG',
        numericCode: '450',
        callingCode: '+261'
    },
    {
        name: 'Malawi',
        alpha2Code: 'MW',
        alpha3Code: 'MWI',
        numericCode: '454',
        callingCode: '+265'
    },
    {
        name: 'Malásia',
        alpha2Code: 'MY',
        alpha3Code: 'MYS',
        numericCode: '458',
        callingCode: '+60'
    },
    {
        name: 'Maldivas',
        alpha2Code: 'MV',
        alpha3Code: 'MDV',
        numericCode: '462',
        callingCode: '+960'
    },
    {
        name: 'Mali',
        alpha2Code: 'ML',
        alpha3Code: 'MLI',
        numericCode: '466',
        callingCode: '+223'
    },
    {
        name: 'Malta',
        alpha2Code: 'MT',
        alpha3Code: 'MLT',
        numericCode: '470',
        callingCode: '+356'
    },
    {
        name: 'Ilhas Marshall',
        alpha2Code: 'MH',
        alpha3Code: 'MHL',
        numericCode: '584',
        callingCode: '+692'
    },
    {
        name: 'Martinica',
        alpha2Code: 'MQ',
        alpha3Code: 'MTQ',
        numericCode: '474',
        callingCode: '+596'
    },
    {
        name: 'Mauritânia',
        alpha2Code: 'MR',
        alpha3Code: 'MRT',
        numericCode: '478',
        callingCode: '+222'
    },
    {
        name: 'Maurício',
        alpha2Code: 'MU',
        alpha3Code: 'MUS',
        numericCode: '480',
        callingCode: '+230'
    },
    {
        name: 'Mayotte',
        alpha2Code: 'YT',
        alpha3Code: 'MYT',
        numericCode: '175',
        callingCode: '+262'
    },
    {
        name: 'México',
        alpha2Code: 'MX',
        alpha3Code: 'MEX',
        numericCode: '484',
        callingCode: '+52'
    },
    {
        name: 'Micronésia',
        alpha2Code: 'FM',
        alpha3Code: 'FSM',
        numericCode: '583',
        callingCode: '+691'
    },
    {
        name: 'Moldávia',
        alpha2Code: 'MD',
        alpha3Code: 'MDA',
        numericCode: '498',
        callingCode: '+373'
    },
    {
        name: 'Mônaco',
        alpha2Code: 'MC',
        alpha3Code: 'MCO',
        numericCode: '492',
        callingCode: '+377'
    },
    {
        name: 'Mongólia',
        alpha2Code: 'MN',
        alpha3Code: 'MNG',
        numericCode: '496',
        callingCode: '+976'
    },
    {
        name: 'Montenegro',
        alpha2Code: 'ME',
        alpha3Code: 'MNE',
        numericCode: '499',
        callingCode: '+382'
    },
    {
        name: 'Montserrat',
        alpha2Code: 'MS',
        alpha3Code: 'MSR',
        numericCode: '500',
        callingCode: '+1664'
    },
    {
        name: 'Marrocos',
        alpha2Code: 'MA',
        alpha3Code: 'MAR',
        numericCode: '504',
        callingCode: '+212'
    },
    {
        name: 'Moçambique',
        alpha2Code: 'MZ',
        alpha3Code: 'MOZ',
        numericCode: '508',
        callingCode: '+258'
    },
    {
        name: 'Myanmar',
        alpha2Code: 'MM',
        alpha3Code: 'MMR',
        numericCode: '104',
        callingCode: '+95'
    },
    {
        name: 'Namíbia',
        alpha2Code: 'NA',
        alpha3Code: 'NAM',
        numericCode: '516',
        callingCode: '+264'
    },
    {
        name: 'Nauru',
        alpha2Code: 'NR',
        alpha3Code: 'NRU',
        numericCode: '520',
        callingCode: '+674'
    },
    {
        name: 'Nepal',
        alpha2Code: 'NP',
        alpha3Code: 'NPL',
        numericCode: '524',
        callingCode: '+977'
    },
    {
        name: 'Holanda',
        alpha2Code: 'NL',
        alpha3Code: 'NLD',
        numericCode: '528',
        callingCode: '+31'
    },
    {
        name: 'Nova Caledônia',
        alpha2Code: 'NC',
        alpha3Code: 'NCL',
        numericCode: '540',
        callingCode: '+687'
    },
    {
        name: 'Nova Zelândia',
        alpha2Code: 'NZ',
        alpha3Code: 'NZL',
        numericCode: '554',
        callingCode: '+64'
    },
    {
        name: 'Nicarágua',
        alpha2Code: 'NI',
        alpha3Code: 'NIC',
        numericCode: '558',
        callingCode: '+505'
    },
    {
        name: 'Níger',
        alpha2Code: 'NE',
        alpha3Code: 'NER',
        numericCode: '562',
        callingCode: '+227'
    },
    {
        name: 'Nigéria',
        alpha2Code: 'NG',
        alpha3Code: 'NGA',
        numericCode: '566',
        callingCode: '+234'
    },
    {
        name: 'Niue',
        alpha2Code: 'NU',
        alpha3Code: 'NIU',
        numericCode: '570',
        callingCode: '+683'
    },
    {
        name: 'Ilha Norfolk',
        alpha2Code: 'NF',
        alpha3Code: 'NFK',
        numericCode: '574',
        callingCode: '+672'
    },
    {
        name: 'Coreia do Norte',
        alpha2Code: 'KP',
        alpha3Code: 'PRK',
        numericCode: '408',
        callingCode: '+850'
    },
    {
        name: 'Ilhas Marianas',
        alpha2Code: 'MP',
        alpha3Code: 'MNP',
        numericCode: '580',
        callingCode: '+1670'
    },
    {
        name: 'Noruega',
        alpha2Code: 'NO',
        alpha3Code: 'NOR',
        numericCode: '578',
        callingCode: '+47'
    },
    {
        name: 'Omã',
        alpha2Code: 'OM',
        alpha3Code: 'OMN',
        numericCode: '512',
        callingCode: '+968'
    },
    {
        name: 'Paquistão',
        alpha2Code: 'PK',
        alpha3Code: 'PAK',
        numericCode: '586',
        callingCode: '+92'
    },
    {
        name: 'Palau',
        alpha2Code: 'PW',
        alpha3Code: 'PLW',
        numericCode: '585',
        callingCode: '+680'
    },
    {
        name: 'Palestina',
        alpha2Code: 'PS',
        alpha3Code: 'PSE',
        numericCode: '275',
        callingCode: '+970'
    },
    {
        name: 'Panamá',
        alpha2Code: 'PA',
        alpha3Code: 'PAN',
        numericCode: '591',
        callingCode: '+507'
    },
    {
        name: 'Papua Nova Guiné',
        alpha2Code: 'PG',
        alpha3Code: 'PNG',
        numericCode: '598',
        callingCode: '+675'
    },
    {
        name: 'Paraguai',
        alpha2Code: 'PY',
        alpha3Code: 'PRY',
        numericCode: '600',
        callingCode: '+595'
    },
    {
        name: 'Peru',
        alpha2Code: 'PE',
        alpha3Code: 'PER',
        numericCode: '604',
        callingCode: '+51'
    },
    {
        name: 'Filipinas',
        alpha2Code: 'PH',
        alpha3Code: 'PHL',
        numericCode: '608',
        callingCode: '+63'
    },
    {
        name: 'Ilhas Pitcairn',
        alpha2Code: 'PN',
        alpha3Code: 'PCN',
        numericCode: '612',
        callingCode: '+64'
    },
    {
        name: 'Polônia',
        alpha2Code: 'PL',
        alpha3Code: 'POL',
        numericCode: '616',
        callingCode: '+48'
    },
    {
        name: 'Portugal',
        alpha2Code: 'PT',
        alpha3Code: 'PRT',
        numericCode: '620',
        callingCode: '+351'
    },
    {
        name: 'Porto Rico',
        alpha2Code: 'PR',
        alpha3Code: 'PRI',
        numericCode: '630',
        callingCode: '+1'
    },
    {
        name: 'Catar',
        alpha2Code: 'QA',
        alpha3Code: 'QAT',
        numericCode: '634',
        callingCode: '+974'
    },
    {
        name: 'Kosovo',
        alpha2Code: 'XK',
        alpha3Code: 'KOS',
        numericCode: null,
        callingCode: '+383'
    },
    {
        name: 'Reunião',
        alpha2Code: 'RE',
        alpha3Code: 'REU',
        numericCode: '638',
        callingCode: '+262'
    },
    {
        name: 'Romênia',
        alpha2Code: 'RO',
        alpha3Code: 'ROU',
        numericCode: '642',
        callingCode: '+40'
    },
    {
        name: 'Rússia',
        alpha2Code: 'RU',
        alpha3Code: 'RUS',
        numericCode: '643',
        callingCode: '+7'
    },
    {
        name: 'Ruanda',
        alpha2Code: 'RW',
        alpha3Code: 'RWA',
        numericCode: '646',
        callingCode: '+250'
    },
    {
        name: 'São Bartolomeu',
        alpha2Code: 'BL',
        alpha3Code: 'BLM',
        numericCode: '652',
        callingCode: '+590'
    },
    {
        name: 'Santa Helena',
        alpha2Code: 'SH',
        alpha3Code: 'SHN',
        numericCode: '654',
        callingCode: '+290'
    },
    {
        name: 'São Cristóvão e Neves',
        alpha2Code: 'KN',
        alpha3Code: 'KNA',
        numericCode: '659',
        callingCode: '+1869'
    },
    {
        name: 'Santa Lúcia',
        alpha2Code: 'LC',
        alpha3Code: 'LCA',
        numericCode: '662',
        callingCode: '+1758'
    },
    {
        name: 'Saint Martin',
        alpha2Code: 'MF',
        alpha3Code: 'MAF',
        numericCode: '663',
        callingCode: '+590'
    },
    {
        name: 'Saint-Pierre e Miquelon',
        alpha2Code: 'PM',
        alpha3Code: 'SPM',
        numericCode: '666',
        callingCode: '+508'
    },
    {
        name: 'São Vicente e Granadinas',
        alpha2Code: 'VC',
        alpha3Code: 'VCT',
        numericCode: '670',
        callingCode: '+1784'
    },
    {
        name: 'Samoa',
        alpha2Code: 'WS',
        alpha3Code: 'WSM',
        numericCode: '882',
        callingCode: '+685'
    },
    {
        name: 'San Marino',
        alpha2Code: 'SM',
        alpha3Code: 'SMR',
        numericCode: '674',
        callingCode: '+378'
    },
    {
        name: 'São Tomé e Príncipe',
        alpha2Code: 'ST',
        alpha3Code: 'STP',
        numericCode: '678',
        callingCode: '+239'
    },
    {
        name: 'Arábia Saudita',
        alpha2Code: 'SA',
        alpha3Code: 'SAU',
        numericCode: '682',
        callingCode: '+966'
    },
    {
        name: 'Senegal',
        alpha2Code: 'SN',
        alpha3Code: 'SEN',
        numericCode: '686',
        callingCode: '+221'
    },
    {
        name: 'Sérvia',
        alpha2Code: 'RS',
        alpha3Code: 'SRB',
        numericCode: '688',
        callingCode: '+381'
    },
    {
        name: 'Seicheles',
        alpha2Code: 'SC',
        alpha3Code: 'SYC',
        numericCode: '690',
        callingCode: '+248'
    },
    {
        name: 'Serra Leoa',
        alpha2Code: 'SL',
        alpha3Code: 'SLE',
        numericCode: '694',
        callingCode: '+232'
    },
    {
        name: 'Singapura',
        alpha2Code: 'SG',
        alpha3Code: 'SGP',
        numericCode: '702',
        callingCode: '+65'
    },
    {
        name: 'Sint Maarten',
        alpha2Code: 'SX',
        alpha3Code: 'SXM',
        numericCode: '534',
        callingCode: '+1721'
    },
    {
        name: 'Eslováquia',
        alpha2Code: 'SK',
        alpha3Code: 'SVK',
        numericCode: '703',
        callingCode: '+421'
    },
    {
        name: 'Eslovênia',
        alpha2Code: 'SI',
        alpha3Code: 'SVN',
        numericCode: '705',
        callingCode: '+386'
    },
    {
        name: 'Ilhas Salomão',
        alpha2Code: 'SB',
        alpha3Code: 'SLB',
        numericCode: '090',
        callingCode: '+677'
    },
    {
        name: 'Somália',
        alpha2Code: 'SO',
        alpha3Code: 'SOM',
        numericCode: '706',
        callingCode: '+252'
    },
    {
        name: 'República Sul-Africana',
        alpha2Code: 'ZA',
        alpha3Code: 'ZAF',
        numericCode: '710',
        callingCode: '+27'
    },
    {
        name: 'Ilhas Geórgias do Sul e Sandwich do Sul',
        alpha2Code: 'GS',
        alpha3Code: 'SGS',
        numericCode: '239',
        callingCode: '+500'
    },
    {
        name: 'Coreia do Sul',
        alpha2Code: 'KR',
        alpha3Code: 'KOR',
        numericCode: '410',
        callingCode: '+82'
    },
    {
        name: 'Sudão do Sul',
        alpha2Code: 'SS',
        alpha3Code: 'SSD',
        numericCode: '728',
        callingCode: '+211'
    },
    {
        name: 'Espanha',
        alpha2Code: 'ES',
        alpha3Code: 'ESP',
        numericCode: '724',
        callingCode: '+34'
    },
    {
        name: 'Sri Lanka',
        alpha2Code: 'LK',
        alpha3Code: 'LKA',
        numericCode: '144',
        callingCode: '+94'
    },
    {
        name: 'Sudão',
        alpha2Code: 'SD',
        alpha3Code: 'SDN',
        numericCode: '729',
        callingCode: '+249'
    },
    {
        name: 'Suriname',
        alpha2Code: 'SR',
        alpha3Code: 'SUR',
        numericCode: '740',
        callingCode: '+597'
    },
    {
        name: 'Svalbard',
        alpha2Code: 'SJ',
        alpha3Code: 'SJM',
        numericCode: '744',
        callingCode: '+4779'
    },
    {
        name: 'Suazilândia',
        alpha2Code: 'SZ',
        alpha3Code: 'SWZ',
        numericCode: '748',
        callingCode: '+268'
    },
    {
        name: 'Suécia',
        alpha2Code: 'SE',
        alpha3Code: 'SWE',
        numericCode: '752',
        callingCode: '+46'
    },
    {
        name: 'Suíça',
        alpha2Code: 'CH',
        alpha3Code: 'CHE',
        numericCode: '756',
        callingCode: '+41'
    },
    {
        name: 'Síria',
        alpha2Code: 'SY',
        alpha3Code: 'SYR',
        numericCode: '760',
        callingCode: '+963'
    },
    {
        name: 'Taiwan, China',
        alpha2Code: 'TW',
        alpha3Code: 'TWN',
        numericCode: '158',
        callingCode: '+886'
    },
    {
        name: 'Tajiquistão',
        alpha2Code: 'TJ',
        alpha3Code: 'TJK',
        numericCode: '762',
        callingCode: '+992'
    },
    {
        name: 'Tanzânia',
        alpha2Code: 'TZ',
        alpha3Code: 'TZA',
        numericCode: '834',
        callingCode: '+255'
    },
    {
        name: 'Tailândia',
        alpha2Code: 'TH',
        alpha3Code: 'THA',
        numericCode: '764',
        callingCode: '+66'
    },
    {
        name: 'Timor Leste',
        alpha2Code: 'TL',
        alpha3Code: 'TLS',
        numericCode: '626',
        callingCode: '+670'
    },
    {
        name: 'Togo',
        alpha2Code: 'TG',
        alpha3Code: 'TGO',
        numericCode: '768',
        callingCode: '+228'
    },
    {
        name: 'Tokelau',
        alpha2Code: 'TK',
        alpha3Code: 'TKL',
        numericCode: '772',
        callingCode: '+690'
    },
    {
        name: 'Tonga',
        alpha2Code: 'TO',
        alpha3Code: 'TON',
        numericCode: '776',
        callingCode: '+676'
    },
    {
        name: 'Trinidad e Tobago',
        alpha2Code: 'TT',
        alpha3Code: 'TTO',
        numericCode: '780',
        callingCode: '+1868'
    },
    {
        name: 'Tunísia',
        alpha2Code: 'TN',
        alpha3Code: 'TUN',
        numericCode: '788',
        callingCode: '+216'
    },
    {
        name: 'Turquia',
        alpha2Code: 'TR',
        alpha3Code: 'TUR',
        numericCode: '792',
        callingCode: '+90'
    },
    {
        name: 'Turcomenistão',
        alpha2Code: 'TM',
        alpha3Code: 'TKM',
        numericCode: '795',
        callingCode: '+993'
    },
    {
        name: 'Ilhas Turcas e Caicos',
        alpha2Code: 'TC',
        alpha3Code: 'TCA',
        numericCode: '796',
        callingCode: '+1649'
    },
    {
        name: 'Tuvalu',
        alpha2Code: 'TV',
        alpha3Code: 'TUV',
        numericCode: '798',
        callingCode: '+688'
    },
    {
        name: 'Uganda',
        alpha2Code: 'UG',
        alpha3Code: 'UGA',
        numericCode: '800',
        callingCode: '+256'
    },
    {
        name: 'Ucrânia',
        alpha2Code: 'UA',
        alpha3Code: 'UKR',
        numericCode: '804',
        callingCode: '+380'
    },
    {
        name: 'Emirados árabes Unidos',
        alpha2Code: 'AE',
        alpha3Code: 'ARE',
        numericCode: '784',
        callingCode: '+971'
    },
    {
        name: 'Reino Unido',
        alpha2Code: 'GB',
        alpha3Code: 'GBR',
        numericCode: '826',
        callingCode: '+44'
    },
    {
        name: 'Estados Unidos',
        alpha2Code: 'US',
        alpha3Code: 'USA',
        numericCode: '840',
        callingCode: '+1'
    },
    {
        name: 'Uruguai',
        alpha2Code: 'UY',
        alpha3Code: 'URY',
        numericCode: '858',
        callingCode: '+598'
    },
    {
        name: 'Uzbequistão',
        alpha2Code: 'UZ',
        alpha3Code: 'UZB',
        numericCode: '860',
        callingCode: '+998'
    },
    {
        name: 'Vanuatu',
        alpha2Code: 'VU',
        alpha3Code: 'VUT',
        numericCode: '548',
        callingCode: '+678'
    },
    {
        name: 'Venezuela',
        alpha2Code: 'VE',
        alpha3Code: 'VEN',
        numericCode: '862',
        callingCode: '+58'
    },
    {
        name: 'Vietnã',
        alpha2Code: 'VN',
        alpha3Code: 'VNM',
        numericCode: '704',
        callingCode: '+84'
    },
    {
        name: 'Wallis e Futuna',
        alpha2Code: 'WF',
        alpha3Code: 'WLF',
        numericCode: '876',
        callingCode: '+681'
    },
    {
        name: 'Saara Ocidental',
        alpha2Code: 'EH',
        alpha3Code: 'ESH',
        numericCode: '732',
        callingCode: '+212'
    },
    {
        name: 'Iêmen',
        alpha2Code: 'YE',
        alpha3Code: 'YEM',
        numericCode: '887',
        callingCode: '+967'
    },
    {
        name: 'Zâmbia',
        alpha2Code: 'ZM',
        alpha3Code: 'ZMB',
        numericCode: '894',
        callingCode: '+260'
    },
    {
        name: 'Zimbabwe',
        alpha2Code: 'ZW',
        alpha3Code: 'ZWE',
        numericCode: '716',
        callingCode: '+263'
    }
];

var br = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB_BR: COUNTRIES_DB_BR
});

const COUNTRIES_DB_BY = [
    {
        name: 'Афганістан',
        alpha2Code: 'AF',
        alpha3Code: 'AFG',
        numericCode: '004',
        callingCode: '+93'
    },
    {
        name: 'Аландскія астравы',
        alpha2Code: 'AX',
        alpha3Code: 'ALA',
        numericCode: '248',
        callingCode: '+358'
    },
    {
        name: 'Албанія',
        alpha2Code: 'AL',
        alpha3Code: 'ALB',
        numericCode: '008',
        callingCode: '+355'
    },
    {
        name: 'Алжыр',
        alpha2Code: 'DZ',
        alpha3Code: 'DZA',
        numericCode: '012',
        callingCode: '+213'
    },
    {
        name: 'Амерыканскае Самоа',
        alpha2Code: 'AS',
        alpha3Code: 'ASM',
        numericCode: '016',
        callingCode: '+1684'
    },
    {
        name: 'Андора',
        alpha2Code: 'AD',
        alpha3Code: 'AND',
        numericCode: '020',
        callingCode: '+376'
    },
    {
        name: 'Ангола',
        alpha2Code: 'AO',
        alpha3Code: 'AGO',
        numericCode: '024',
        callingCode: '+244'
    },
    {
        name: 'Ангілья',
        alpha2Code: 'AI',
        alpha3Code: 'AIA',
        numericCode: '660',
        callingCode: '+1264'
    },
    {
        name: 'Антарктыда',
        alpha2Code: 'AQ',
        alpha3Code: 'ATA',
        numericCode: '010',
        callingCode: '+672'
    },
    {
        name: 'Антыгуа і Барбуда',
        alpha2Code: 'AG',
        alpha3Code: 'ATG',
        numericCode: '028',
        callingCode: '+1268'
    },
    {
        name: 'Аргенціна',
        alpha2Code: 'AR',
        alpha3Code: 'ARG',
        numericCode: '032',
        callingCode: '+54'
    },
    {
        name: 'Арменія',
        alpha2Code: 'AM',
        alpha3Code: 'ARM',
        numericCode: '051',
        callingCode: '+374'
    },
    {
        name: 'Аруба',
        alpha2Code: 'AW',
        alpha3Code: 'ABW',
        numericCode: '533',
        callingCode: '+297'
    },
    {
        name: 'Аўстралія',
        alpha2Code: 'AU',
        alpha3Code: 'AUS',
        numericCode: '036',
        callingCode: '+61'
    },
    {
        name: 'Аўстрыя',
        alpha2Code: 'AT',
        alpha3Code: 'AUT',
        numericCode: '040',
        callingCode: '+43'
    },
    {
        name: 'Азербайджан',
        alpha2Code: 'AZ',
        alpha3Code: 'AZE',
        numericCode: '031',
        callingCode: '+994'
    },
    {
        name: 'Багамы',
        alpha2Code: 'BS',
        alpha3Code: 'BHS',
        numericCode: '044',
        callingCode: '+1242'
    },
    {
        name: 'Бахрэйн',
        alpha2Code: 'BH',
        alpha3Code: 'BHR',
        numericCode: '048',
        callingCode: '+973'
    },
    {
        name: 'Бангладэш',
        alpha2Code: 'BD',
        alpha3Code: 'BGD',
        numericCode: '050',
        callingCode: '+880'
    },
    {
        name: 'Барбадас',
        alpha2Code: 'BB',
        alpha3Code: 'BRB',
        numericCode: '052',
        callingCode: '+1246'
    },
    {
        name: 'Беларусь',
        alpha2Code: 'BY',
        alpha3Code: 'BLR',
        numericCode: '112',
        callingCode: '+375'
    },
    {
        name: 'Бельгія',
        alpha2Code: 'BE',
        alpha3Code: 'BEL',
        numericCode: '056',
        callingCode: '+32'
    },
    {
        name: 'Беліз',
        alpha2Code: 'BZ',
        alpha3Code: 'BLZ',
        numericCode: '084',
        callingCode: '+501'
    },
    {
        name: 'Бенін',
        alpha2Code: 'BJ',
        alpha3Code: 'BEN',
        numericCode: '204',
        callingCode: '+229'
    },
    {
        name: 'Бермуды',
        alpha2Code: 'BM',
        alpha3Code: 'BMU',
        numericCode: '060',
        callingCode: '+1441'
    },
    {
        name: 'Бутан',
        alpha2Code: 'BT',
        alpha3Code: 'BTN',
        numericCode: '064',
        callingCode: '+975'
    },
    {
        name: 'Балівія',
        alpha2Code: 'BO',
        alpha3Code: 'BOL',
        numericCode: '068',
        callingCode: '+591'
    },
    {
        name: 'Карыбскія Нідэрланды',
        alpha2Code: 'BQ',
        alpha3Code: 'BES',
        numericCode: '535',
        callingCode: '+5997'
    },
    {
        name: 'Боснія і Герцагавіна',
        alpha2Code: 'BA',
        alpha3Code: 'BIH',
        numericCode: '070',
        callingCode: '+387'
    },
    {
        name: 'Батсвана',
        alpha2Code: 'BW',
        alpha3Code: 'BWA',
        numericCode: '072',
        callingCode: '+267'
    },
    {
        name: 'Востраў Бувэ',
        alpha2Code: 'BV',
        alpha3Code: 'BVT',
        numericCode: '074',
        callingCode: '+'
    },
    {
        name: 'Бразілія',
        alpha2Code: 'BR',
        alpha3Code: 'BRA',
        numericCode: '076',
        callingCode: '+55'
    },
    {
        name: 'Брытанская тэрыторыя ў Індыйскім акіяне',
        alpha2Code: 'IO',
        alpha3Code: 'IOT',
        numericCode: '086',
        callingCode: '+246'
    },
    {
        name: 'Вонкавыя малыя астравы ЗША',
        alpha2Code: 'UM',
        alpha3Code: 'UMI',
        numericCode: '581',
        callingCode: '+'
    },
    {
        name: 'Брытанскія Віргінскія астравы',
        alpha2Code: 'VG',
        alpha3Code: 'VGB',
        numericCode: '092',
        callingCode: '+1284'
    },
    {
        name: 'Амерыканскія Віргінскія астравы',
        alpha2Code: 'VI',
        alpha3Code: 'VIR',
        numericCode: '850',
        callingCode: '+1 340'
    },
    {
        name: 'Бруней',
        alpha2Code: 'BN',
        alpha3Code: 'BRN',
        numericCode: '096',
        callingCode: '+673'
    },
    {
        name: 'Балгарыя',
        alpha2Code: 'BG',
        alpha3Code: 'BGR',
        numericCode: '100',
        callingCode: '+359'
    },
    {
        name: 'Буркіна-Фасо',
        alpha2Code: 'BF',
        alpha3Code: 'BFA',
        numericCode: '854',
        callingCode: '+226'
    },
    {
        name: 'Бурундзі',
        alpha2Code: 'BI',
        alpha3Code: 'BDI',
        numericCode: '108',
        callingCode: '+257'
    },
    {
        name: 'Камбоджа',
        alpha2Code: 'KH',
        alpha3Code: 'KHM',
        numericCode: '116',
        callingCode: '+855'
    },
    {
        name: 'Камерун',
        alpha2Code: 'CM',
        alpha3Code: 'CMR',
        numericCode: '120',
        callingCode: '+237'
    },
    {
        name: 'Канада',
        alpha2Code: 'CA',
        alpha3Code: 'CAN',
        numericCode: '124',
        callingCode: '+1'
    },
    {
        name: 'Каба-Вердэ',
        alpha2Code: 'CV',
        alpha3Code: 'CPV',
        numericCode: '132',
        callingCode: '+238'
    },
    {
        name: 'Кайманавы астравы',
        alpha2Code: 'KY',
        alpha3Code: 'CYM',
        numericCode: '136',
        callingCode: '+1345'
    },
    {
        name: 'ЦАР',
        alpha2Code: 'CF',
        alpha3Code: 'CAF',
        numericCode: '140',
        callingCode: '+236'
    },
    {
        name: 'Чад',
        alpha2Code: 'TD',
        alpha3Code: 'TCD',
        numericCode: '148',
        callingCode: '+235'
    },
    {
        name: 'Чылі',
        alpha2Code: 'CL',
        alpha3Code: 'CHL',
        numericCode: '152',
        callingCode: '+56'
    },
    {
        name: 'Кітай',
        alpha2Code: 'CN',
        alpha3Code: 'CHN',
        numericCode: '156',
        callingCode: '+86'
    },
    {
        name: 'Востраў Ражства',
        alpha2Code: 'CX',
        alpha3Code: 'CXR',
        numericCode: '162',
        callingCode: '+61'
    },
    {
        name: 'Какосавыя астравы',
        alpha2Code: 'CC',
        alpha3Code: 'CCK',
        numericCode: '166',
        callingCode: '+61'
    },
    {
        name: 'Калумбія',
        alpha2Code: 'CO',
        alpha3Code: 'COL',
        numericCode: '170',
        callingCode: '+57'
    },
    {
        name: 'Каморскія астравы',
        alpha2Code: 'KM',
        alpha3Code: 'COM',
        numericCode: '174',
        callingCode: '+269'
    },
    {
        name: 'Рэспубліка Конга',
        alpha2Code: 'CG',
        alpha3Code: 'COG',
        numericCode: '178',
        callingCode: '+242'
    },
    {
        name: 'ДР Конга',
        alpha2Code: 'CD',
        alpha3Code: 'COD',
        numericCode: '180',
        callingCode: '+243'
    },
    {
        name: 'Астравы Кука',
        alpha2Code: 'CK',
        alpha3Code: 'COK',
        numericCode: '184',
        callingCode: '+682'
    },
    {
        name: 'Коста-Рыка',
        alpha2Code: 'CR',
        alpha3Code: 'CRI',
        numericCode: '188',
        callingCode: '+506'
    },
    {
        name: 'Харватыя',
        alpha2Code: 'HR',
        alpha3Code: 'HRV',
        numericCode: '191',
        callingCode: '+385'
    },
    {
        name: 'Куба',
        alpha2Code: 'CU',
        alpha3Code: 'CUB',
        numericCode: '192',
        callingCode: '+53'
    },
    {
        name: 'Кюрасаа',
        alpha2Code: 'CW',
        alpha3Code: 'CUW',
        numericCode: '531',
        callingCode: '+599'
    },
    {
        name: 'Кіпр',
        alpha2Code: 'CY',
        alpha3Code: 'CYP',
        numericCode: '196',
        callingCode: '+357'
    },
    {
        name: 'Чэхія',
        alpha2Code: 'CZ',
        alpha3Code: 'CZE',
        numericCode: '203',
        callingCode: '+420'
    },
    {
        name: 'Данія',
        alpha2Code: 'DK',
        alpha3Code: 'DNK',
        numericCode: '208',
        callingCode: '+45'
    },
    {
        name: 'Джыбуці',
        alpha2Code: 'DJ',
        alpha3Code: 'DJI',
        numericCode: '262',
        callingCode: '+253'
    },
    {
        name: 'Дамініка',
        alpha2Code: 'DM',
        alpha3Code: 'DMA',
        numericCode: '212',
        callingCode: '+1767'
    },
    {
        name: 'Дамініканская Рэспубліка',
        alpha2Code: 'DO',
        alpha3Code: 'DOM',
        numericCode: '214',
        callingCode: '+1809'
    },
    {
        name: 'Эквадор',
        alpha2Code: 'EC',
        alpha3Code: 'ECU',
        numericCode: '218',
        callingCode: '+593'
    },
    {
        name: 'Егіпет',
        alpha2Code: 'EG',
        alpha3Code: 'EGY',
        numericCode: '818',
        callingCode: '+20'
    },
    {
        name: 'Сальвадор',
        alpha2Code: 'SV',
        alpha3Code: 'SLV',
        numericCode: '222',
        callingCode: '+503'
    },
    {
        name: 'Экватарыяльная Гвінея',
        alpha2Code: 'GQ',
        alpha3Code: 'GNQ',
        numericCode: '226',
        callingCode: '+240'
    },
    {
        name: 'Эрытрэя',
        alpha2Code: 'ER',
        alpha3Code: 'ERI',
        numericCode: '232',
        callingCode: '+291'
    },
    {
        name: 'Эстонія',
        alpha2Code: 'EE',
        alpha3Code: 'EST',
        numericCode: '233',
        callingCode: '+372'
    },
    {
        name: 'Эфіопія',
        alpha2Code: 'ET',
        alpha3Code: 'ETH',
        numericCode: '231',
        callingCode: '+251'
    },
    {
        name: 'Фалклендскія астравы',
        alpha2Code: 'FK',
        alpha3Code: 'FLK',
        numericCode: '238',
        callingCode: '+500'
    },
    {
        name: 'Фарэрскія астравы',
        alpha2Code: 'FO',
        alpha3Code: 'FRO',
        numericCode: '234',
        callingCode: '+298'
    },
    {
        name: 'Фіджы',
        alpha2Code: 'FJ',
        alpha3Code: 'FJI',
        numericCode: '242',
        callingCode: '+679'
    },
    {
        name: 'Фінляндыя',
        alpha2Code: 'FI',
        alpha3Code: 'FIN',
        numericCode: '246',
        callingCode: '+358'
    },
    {
        name: 'Францыя',
        alpha2Code: 'FR',
        alpha3Code: 'FRA',
        numericCode: '250',
        callingCode: '+33'
    },
    {
        name: 'Гвіяна',
        alpha2Code: 'GF',
        alpha3Code: 'GUF',
        numericCode: '254',
        callingCode: '+594'
    },
    {
        name: 'Французская Палінезія',
        alpha2Code: 'PF',
        alpha3Code: 'PYF',
        numericCode: '258',
        callingCode: '+689'
    },
    {
        name: 'Французскія Паўднёвыя і Антарктычныя Тэрыторыі',
        alpha2Code: 'TF',
        alpha3Code: 'ATF',
        numericCode: '260',
        callingCode: '+'
    },
    {
        name: 'Габон',
        alpha2Code: 'GA',
        alpha3Code: 'GAB',
        numericCode: '266',
        callingCode: '+241'
    },
    {
        name: 'Гамбія',
        alpha2Code: 'GM',
        alpha3Code: 'GMB',
        numericCode: '270',
        callingCode: '+220'
    },
    {
        name: 'Грузія',
        alpha2Code: 'GE',
        alpha3Code: 'GEO',
        numericCode: '268',
        callingCode: '+995'
    },
    {
        name: 'Германія',
        alpha2Code: 'DE',
        alpha3Code: 'DEU',
        numericCode: '276',
        callingCode: '+49'
    },
    {
        name: 'Гана',
        alpha2Code: 'GH',
        alpha3Code: 'GHA',
        numericCode: '288',
        callingCode: '+233'
    },
    {
        name: 'Гібралтар',
        alpha2Code: 'GI',
        alpha3Code: 'GIB',
        numericCode: '292',
        callingCode: '+350'
    },
    {
        name: 'Грэцыя',
        alpha2Code: 'GR',
        alpha3Code: 'GRC',
        numericCode: '300',
        callingCode: '+30'
    },
    {
        name: 'Грэнландыя',
        alpha2Code: 'GL',
        alpha3Code: 'GRL',
        numericCode: '304',
        callingCode: '+299'
    },
    {
        name: 'Грэнада',
        alpha2Code: 'GD',
        alpha3Code: 'GRD',
        numericCode: '308',
        callingCode: '+1473'
    },
    {
        name: 'Гвадэлупа',
        alpha2Code: 'GP',
        alpha3Code: 'GLP',
        numericCode: '312',
        callingCode: '+590'
    },
    {
        name: 'Гуам',
        alpha2Code: 'GU',
        alpha3Code: 'GUM',
        numericCode: '316',
        callingCode: '+1671'
    },
    {
        name: 'Гватэмала',
        alpha2Code: 'GT',
        alpha3Code: 'GTM',
        numericCode: '320',
        callingCode: '+502'
    },
    {
        name: 'Гернсі',
        alpha2Code: 'GG',
        alpha3Code: 'GGY',
        numericCode: '831',
        callingCode: '+44'
    },
    {
        name: 'Гвінея',
        alpha2Code: 'GN',
        alpha3Code: 'GIN',
        numericCode: '324',
        callingCode: '+224'
    },
    {
        name: 'Гвінея-Бісау',
        alpha2Code: 'GW',
        alpha3Code: 'GNB',
        numericCode: '624',
        callingCode: '+245'
    },
    {
        name: 'Гаяна',
        alpha2Code: 'GY',
        alpha3Code: 'GUY',
        numericCode: '328',
        callingCode: '+592'
    },
    {
        name: 'Гаіці',
        alpha2Code: 'HT',
        alpha3Code: 'HTI',
        numericCode: '332',
        callingCode: '+509'
    },
    {
        name: 'Херд і Макдональд',
        alpha2Code: 'HM',
        alpha3Code: 'HMD',
        numericCode: '334',
        callingCode: '+'
    },
    {
        name: 'Ватыкан',
        alpha2Code: 'VA',
        alpha3Code: 'VAT',
        numericCode: '336',
        callingCode: '+379'
    },
    {
        name: 'Гандурас',
        alpha2Code: 'HN',
        alpha3Code: 'HND',
        numericCode: '340',
        callingCode: '+504'
    },
    {
        name: 'Ганконг',
        alpha2Code: 'HK',
        alpha3Code: 'HKG',
        numericCode: '344',
        callingCode: '+852'
    },
    {
        name: 'Венгрыя',
        alpha2Code: 'HU',
        alpha3Code: 'HUN',
        numericCode: '348',
        callingCode: '+36'
    },
    {
        name: 'Ісландыя',
        alpha2Code: 'IS',
        alpha3Code: 'ISL',
        numericCode: '352',
        callingCode: '+354'
    },
    {
        name: 'Індыя',
        alpha2Code: 'IN',
        alpha3Code: 'IND',
        numericCode: '356',
        callingCode: '+91'
    },
    {
        name: 'Інданезія',
        alpha2Code: 'ID',
        alpha3Code: 'IDN',
        numericCode: '360',
        callingCode: '+62'
    },
    {
        name: 'Кот-д’Івуар',
        alpha2Code: 'CI',
        alpha3Code: 'CIV',
        numericCode: '384',
        callingCode: '+225'
    },
    {
        name: 'Іран',
        alpha2Code: 'IR',
        alpha3Code: 'IRN',
        numericCode: '364',
        callingCode: '+98'
    },
    {
        name: 'Ірак',
        alpha2Code: 'IQ',
        alpha3Code: 'IRQ',
        numericCode: '368',
        callingCode: '+964'
    },
    {
        name: 'Ірландыя',
        alpha2Code: 'IE',
        alpha3Code: 'IRL',
        numericCode: '372',
        callingCode: '+353'
    },
    {
        name: 'Востраў Мэн',
        alpha2Code: 'IM',
        alpha3Code: 'IMN',
        numericCode: '833',
        callingCode: '+44'
    },
    {
        name: 'Ізраіль',
        alpha2Code: 'IL',
        alpha3Code: 'ISR',
        numericCode: '376',
        callingCode: '+972'
    },
    {
        name: 'Італія',
        alpha2Code: 'IT',
        alpha3Code: 'ITA',
        numericCode: '380',
        callingCode: '+39'
    },
    {
        name: 'Ямайка',
        alpha2Code: 'JM',
        alpha3Code: 'JAM',
        numericCode: '388',
        callingCode: '+1876'
    },
    {
        name: 'Японія',
        alpha2Code: 'JP',
        alpha3Code: 'JPN',
        numericCode: '392',
        callingCode: '+81'
    },
    {
        name: 'Джэрсі',
        alpha2Code: 'JE',
        alpha3Code: 'JEY',
        numericCode: '832',
        callingCode: '+44'
    },
    {
        name: 'Іарданія',
        alpha2Code: 'JO',
        alpha3Code: 'JOR',
        numericCode: '400',
        callingCode: '+962'
    },
    {
        name: 'Казахстан',
        alpha2Code: 'KZ',
        alpha3Code: 'KAZ',
        numericCode: '398',
        callingCode: '+7'
    },
    {
        name: 'Кенія',
        alpha2Code: 'KE',
        alpha3Code: 'KEN',
        numericCode: '404',
        callingCode: '+254'
    },
    {
        name: 'Кірыбаці',
        alpha2Code: 'KI',
        alpha3Code: 'KIR',
        numericCode: '296',
        callingCode: '+686'
    },
    {
        name: 'Кувейт',
        alpha2Code: 'KW',
        alpha3Code: 'KWT',
        numericCode: '414',
        callingCode: '+965'
    },
    {
        name: 'Кыргызстан',
        alpha2Code: 'KG',
        alpha3Code: 'KGZ',
        numericCode: '417',
        callingCode: '+996'
    },
    {
        name: 'Лаос',
        alpha2Code: 'LA',
        alpha3Code: 'LAO',
        numericCode: '418',
        callingCode: '+856'
    },
    {
        name: 'Латвія',
        alpha2Code: 'LV',
        alpha3Code: 'LVA',
        numericCode: '428',
        callingCode: '+371'
    },
    {
        name: 'Ліван',
        alpha2Code: 'LB',
        alpha3Code: 'LBN',
        numericCode: '422',
        callingCode: '+961'
    },
    {
        name: 'Лесота',
        alpha2Code: 'LS',
        alpha3Code: 'LSO',
        numericCode: '426',
        callingCode: '+266'
    },
    {
        name: 'Ліберыя',
        alpha2Code: 'LR',
        alpha3Code: 'LBR',
        numericCode: '430',
        callingCode: '+231'
    },
    {
        name: 'Лівія',
        alpha2Code: 'LY',
        alpha3Code: 'LBY',
        numericCode: '434',
        callingCode: '+218'
    },
    {
        name: 'Ліхтэнштэйн',
        alpha2Code: 'LI',
        alpha3Code: 'LIE',
        numericCode: '438',
        callingCode: '+423'
    },
    {
        name: 'Літва',
        alpha2Code: 'LT',
        alpha3Code: 'LTU',
        numericCode: '440',
        callingCode: '+370'
    },
    {
        name: 'Люксембург',
        alpha2Code: 'LU',
        alpha3Code: 'LUX',
        numericCode: '442',
        callingCode: '+352'
    },
    {
        name: 'Макаа',
        alpha2Code: 'MO',
        alpha3Code: 'MAC',
        numericCode: '446',
        callingCode: '+853'
    },
    {
        name: 'Паўночная Македонія',
        alpha2Code: 'MK',
        alpha3Code: 'MKD',
        numericCode: '807',
        callingCode: '+389'
    },
    {
        name: 'Мадагаскар',
        alpha2Code: 'MG',
        alpha3Code: 'MDG',
        numericCode: '450',
        callingCode: '+261'
    },
    {
        name: 'Малаві',
        alpha2Code: 'MW',
        alpha3Code: 'MWI',
        numericCode: '454',
        callingCode: '+265'
    },
    {
        name: 'Малайзія',
        alpha2Code: 'MY',
        alpha3Code: 'MYS',
        numericCode: '458',
        callingCode: '+60'
    },
    {
        name: 'Мальдывы',
        alpha2Code: 'MV',
        alpha3Code: 'MDV',
        numericCode: '462',
        callingCode: '+960'
    },
    {
        name: 'Малі',
        alpha2Code: 'ML',
        alpha3Code: 'MLI',
        numericCode: '466',
        callingCode: '+223'
    },
    {
        name: 'Мальта',
        alpha2Code: 'MT',
        alpha3Code: 'MLT',
        numericCode: '470',
        callingCode: '+356'
    },
    {
        name: 'Маршалавы Астравы',
        alpha2Code: 'MH',
        alpha3Code: 'MHL',
        numericCode: '584',
        callingCode: '+692'
    },
    {
        name: 'Марцініка',
        alpha2Code: 'MQ',
        alpha3Code: 'MTQ',
        numericCode: '474',
        callingCode: '+596'
    },
    {
        name: 'Маўрытанія',
        alpha2Code: 'MR',
        alpha3Code: 'MRT',
        numericCode: '478',
        callingCode: '+222'
    },
    {
        name: 'Маўрыкій',
        alpha2Code: 'MU',
        alpha3Code: 'MUS',
        numericCode: '480',
        callingCode: '+230'
    },
    {
        name: 'Маёта',
        alpha2Code: 'YT',
        alpha3Code: 'MYT',
        numericCode: '175',
        callingCode: '+262'
    },
    {
        name: 'Мексіка',
        alpha2Code: 'MX',
        alpha3Code: 'MEX',
        numericCode: '484',
        callingCode: '+52'
    },
    {
        name: 'Мікранезія',
        alpha2Code: 'FM',
        alpha3Code: 'FSM',
        numericCode: '583',
        callingCode: '+691'
    },
    {
        name: 'Малдова',
        alpha2Code: 'MD',
        alpha3Code: 'MDA',
        numericCode: '498',
        callingCode: '+373'
    },
    {
        name: 'Манака',
        alpha2Code: 'MC',
        alpha3Code: 'MCO',
        numericCode: '492',
        callingCode: '+377'
    },
    {
        name: 'Манголія',
        alpha2Code: 'MN',
        alpha3Code: 'MNG',
        numericCode: '496',
        callingCode: '+976'
    },
    {
        name: 'Чарнагорыя',
        alpha2Code: 'ME',
        alpha3Code: 'MNE',
        numericCode: '499',
        callingCode: '+382'
    },
    {
        name: 'Мантсерат',
        alpha2Code: 'MS',
        alpha3Code: 'MSR',
        numericCode: '500',
        callingCode: '+1664'
    },
    {
        name: 'Марока',
        alpha2Code: 'MA',
        alpha3Code: 'MAR',
        numericCode: '504',
        callingCode: '+212'
    },
    {
        name: 'Мазамбік',
        alpha2Code: 'MZ',
        alpha3Code: 'MOZ',
        numericCode: '508',
        callingCode: '+258'
    },
    {
        name: 'М’янма',
        alpha2Code: 'MM',
        alpha3Code: 'MMR',
        numericCode: '104',
        callingCode: '+95'
    },
    {
        name: 'Намібія',
        alpha2Code: 'NA',
        alpha3Code: 'NAM',
        numericCode: '516',
        callingCode: '+264'
    },
    {
        name: 'Науру',
        alpha2Code: 'NR',
        alpha3Code: 'NRU',
        numericCode: '520',
        callingCode: '+674'
    },
    {
        name: 'Непал',
        alpha2Code: 'NP',
        alpha3Code: 'NPL',
        numericCode: '524',
        callingCode: '+977'
    },
    {
        name: 'Нідэрланды',
        alpha2Code: 'NL',
        alpha3Code: 'NLD',
        numericCode: '528',
        callingCode: '+31'
    },
    {
        name: 'Новая Каледонія',
        alpha2Code: 'NC',
        alpha3Code: 'NCL',
        numericCode: '540',
        callingCode: '+687'
    },
    {
        name: 'Новая Зеландыя',
        alpha2Code: 'NZ',
        alpha3Code: 'NZL',
        numericCode: '554',
        callingCode: '+64'
    },
    {
        name: 'Нікарагуа',
        alpha2Code: 'NI',
        alpha3Code: 'NIC',
        numericCode: '558',
        callingCode: '+505'
    },
    {
        name: 'Нігер',
        alpha2Code: 'NE',
        alpha3Code: 'NER',
        numericCode: '562',
        callingCode: '+227'
    },
    {
        name: 'Нігерыя',
        alpha2Code: 'NG',
        alpha3Code: 'NGA',
        numericCode: '566',
        callingCode: '+234'
    },
    {
        name: 'Ніуэ',
        alpha2Code: 'NU',
        alpha3Code: 'NIU',
        numericCode: '570',
        callingCode: '+683'
    },
    {
        name: 'Востраў Норфалк',
        alpha2Code: 'NF',
        alpha3Code: 'NFK',
        numericCode: '574',
        callingCode: '+672'
    },
    {
        name: 'Паўночная Карэя',
        alpha2Code: 'KP',
        alpha3Code: 'PRK',
        numericCode: '408',
        callingCode: '+850'
    },
    {
        name: 'Паўночныя Марыянскія астравы',
        alpha2Code: 'MP',
        alpha3Code: 'MNP',
        numericCode: '580',
        callingCode: '+1670'
    },
    {
        name: 'Нарвегія',
        alpha2Code: 'NO',
        alpha3Code: 'NOR',
        numericCode: '578',
        callingCode: '+47'
    },
    {
        name: 'Аман',
        alpha2Code: 'OM',
        alpha3Code: 'OMN',
        numericCode: '512',
        callingCode: '+968'
    },
    {
        name: 'Пакістан',
        alpha2Code: 'PK',
        alpha3Code: 'PAK',
        numericCode: '586',
        callingCode: '+92'
    },
    {
        name: 'Палау',
        alpha2Code: 'PW',
        alpha3Code: 'PLW',
        numericCode: '585',
        callingCode: '+680'
    },
    {
        name: 'Палесціна',
        alpha2Code: 'PS',
        alpha3Code: 'PSE',
        numericCode: '275',
        callingCode: '+970'
    },
    {
        name: 'Панама',
        alpha2Code: 'PA',
        alpha3Code: 'PAN',
        numericCode: '591',
        callingCode: '+507'
    },
    {
        name: 'Папуа — Новая Гвінея',
        alpha2Code: 'PG',
        alpha3Code: 'PNG',
        numericCode: '598',
        callingCode: '+675'
    },
    {
        name: 'Парагвай',
        alpha2Code: 'PY',
        alpha3Code: 'PRY',
        numericCode: '600',
        callingCode: '+595'
    },
    {
        name: 'Перу',
        alpha2Code: 'PE',
        alpha3Code: 'PER',
        numericCode: '604',
        callingCode: '+51'
    },
    {
        name: 'Філіпіны',
        alpha2Code: 'PH',
        alpha3Code: 'PHL',
        numericCode: '608',
        callingCode: '+63'
    },
    {
        name: 'Астравы Піткэрн',
        alpha2Code: 'PN',
        alpha3Code: 'PCN',
        numericCode: '612',
        callingCode: '+64'
    },
    {
        name: 'Польшча',
        alpha2Code: 'PL',
        alpha3Code: 'POL',
        numericCode: '616',
        callingCode: '+48'
    },
    {
        name: 'Партугалія',
        alpha2Code: 'PT',
        alpha3Code: 'PRT',
        numericCode: '620',
        callingCode: '+351'
    },
    {
        name: 'Пуэрта-Рыка',
        alpha2Code: 'PR',
        alpha3Code: 'PRI',
        numericCode: '630',
        callingCode: '+1'
    },
    {
        name: 'Катар',
        alpha2Code: 'QA',
        alpha3Code: 'QAT',
        numericCode: '634',
        callingCode: '+974'
    },
    {
        name: 'Рэспубліка Косава',
        alpha2Code: 'XK',
        alpha3Code: 'KOS',
        numericCode: null,
        callingCode: '+383'
    },
    {
        name: 'Рэюньён',
        alpha2Code: 'RE',
        alpha3Code: 'REU',
        numericCode: '638',
        callingCode: '+262'
    },
    {
        name: 'Румынія',
        alpha2Code: 'RO',
        alpha3Code: 'ROU',
        numericCode: '642',
        callingCode: '+40'
    },
    {
        name: 'Расія',
        alpha2Code: 'RU',
        alpha3Code: 'RUS',
        numericCode: '643',
        callingCode: '+7'
    },
    {
        name: 'Руанда',
        alpha2Code: 'RW',
        alpha3Code: 'RWA',
        numericCode: '646',
        callingCode: '+250'
    },
    {
        name: 'Сен-Бартэльмі',
        alpha2Code: 'BL',
        alpha3Code: 'BLM',
        numericCode: '652',
        callingCode: '+590'
    },
    {
        name: 'Астравы Святой Алены, Узнясення і Трыстан-да-Кунья',
        alpha2Code: 'SH',
        alpha3Code: 'SHN',
        numericCode: '654',
        callingCode: '+290'
    },
    {
        name: 'Сент-Кітс і Невіс',
        alpha2Code: 'KN',
        alpha3Code: 'KNA',
        numericCode: '659',
        callingCode: '+1869'
    },
    {
        name: 'Сент-Люсія',
        alpha2Code: 'LC',
        alpha3Code: 'LCA',
        numericCode: '662',
        callingCode: '+1758'
    },
    {
        name: 'Сен-Мартэн',
        alpha2Code: 'MF',
        alpha3Code: 'MAF',
        numericCode: '663',
        callingCode: '+590'
    },
    {
        name: 'Сен-П\'ер і Мікелон',
        alpha2Code: 'PM',
        alpha3Code: 'SPM',
        numericCode: '666',
        callingCode: '+508'
    },
    {
        name: 'Сент-Вінсент і Грэнадзіны',
        alpha2Code: 'VC',
        alpha3Code: 'VCT',
        numericCode: '670',
        callingCode: '+1784'
    },
    {
        name: 'Самоа',
        alpha2Code: 'WS',
        alpha3Code: 'WSM',
        numericCode: '882',
        callingCode: '+685'
    },
    {
        name: 'Сан-Марына',
        alpha2Code: 'SM',
        alpha3Code: 'SMR',
        numericCode: '674',
        callingCode: '+378'
    },
    {
        name: 'Сан-Тамэ і Прынсіпі',
        alpha2Code: 'ST',
        alpha3Code: 'STP',
        numericCode: '678',
        callingCode: '+239'
    },
    {
        name: 'Саудаўская Аравія',
        alpha2Code: 'SA',
        alpha3Code: 'SAU',
        numericCode: '682',
        callingCode: '+966'
    },
    {
        name: 'Сенегал',
        alpha2Code: 'SN',
        alpha3Code: 'SEN',
        numericCode: '686',
        callingCode: '+221'
    },
    {
        name: 'Сербія',
        alpha2Code: 'RS',
        alpha3Code: 'SRB',
        numericCode: '688',
        callingCode: '+381'
    },
    {
        name: 'Сейшэльскія Астравы',
        alpha2Code: 'SC',
        alpha3Code: 'SYC',
        numericCode: '690',
        callingCode: '+248'
    },
    {
        name: 'Сьера-Леонэ',
        alpha2Code: 'SL',
        alpha3Code: 'SLE',
        numericCode: '694',
        callingCode: '+232'
    },
    {
        name: 'Сінгапур',
        alpha2Code: 'SG',
        alpha3Code: 'SGP',
        numericCode: '702',
        callingCode: '+65'
    },
    {
        name: 'Сінт-Мартэн',
        alpha2Code: 'SX',
        alpha3Code: 'SXM',
        numericCode: '534',
        callingCode: '+1721'
    },
    {
        name: 'Славакія',
        alpha2Code: 'SK',
        alpha3Code: 'SVK',
        numericCode: '703',
        callingCode: '+421'
    },
    {
        name: 'Славенія',
        alpha2Code: 'SI',
        alpha3Code: 'SVN',
        numericCode: '705',
        callingCode: '+386'
    },
    {
        name: 'Саламонавы Астравы',
        alpha2Code: 'SB',
        alpha3Code: 'SLB',
        numericCode: '090',
        callingCode: '+677'
    },
    {
        name: 'Самалі',
        alpha2Code: 'SO',
        alpha3Code: 'SOM',
        numericCode: '706',
        callingCode: '+252'
    },
    {
        name: 'ПАР',
        alpha2Code: 'ZA',
        alpha3Code: 'ZAF',
        numericCode: '710',
        callingCode: '+27'
    },
    {
        name: 'Паўднёвая Джорджыя і Паўднёвыя Сандвічавы астравы',
        alpha2Code: 'GS',
        alpha3Code: 'SGS',
        numericCode: '239',
        callingCode: '+500'
    },
    {
        name: 'Рэспубліка Карэя',
        alpha2Code: 'KR',
        alpha3Code: 'KOR',
        numericCode: '410',
        callingCode: '+82'
    },
    {
        name: 'Паўднёвы Судан',
        alpha2Code: 'SS',
        alpha3Code: 'SSD',
        numericCode: '728',
        callingCode: '+211'
    },
    {
        name: 'Іспанія',
        alpha2Code: 'ES',
        alpha3Code: 'ESP',
        numericCode: '724',
        callingCode: '+34'
    },
    {
        name: 'Шры-Ланка',
        alpha2Code: 'LK',
        alpha3Code: 'LKA',
        numericCode: '144',
        callingCode: '+94'
    },
    {
        name: 'Судан',
        alpha2Code: 'SD',
        alpha3Code: 'SDN',
        numericCode: '729',
        callingCode: '+249'
    },
    {
        name: 'Сурынам',
        alpha2Code: 'SR',
        alpha3Code: 'SUR',
        numericCode: '740',
        callingCode: '+597'
    },
    {
        name: 'Шпіцберген і Ян-Маен',
        alpha2Code: 'SJ',
        alpha3Code: 'SJM',
        numericCode: '744',
        callingCode: '+4779'
    },
    {
        name: 'Свазіленд',
        alpha2Code: 'SZ',
        alpha3Code: 'SWZ',
        numericCode: '748',
        callingCode: '+268'
    },
    {
        name: 'Швецыя',
        alpha2Code: 'SE',
        alpha3Code: 'SWE',
        numericCode: '752',
        callingCode: '+46'
    },
    {
        name: 'Швейцарыя',
        alpha2Code: 'CH',
        alpha3Code: 'CHE',
        numericCode: '756',
        callingCode: '+41'
    },
    {
        name: 'Сірыя',
        alpha2Code: 'SY',
        alpha3Code: 'SYR',
        numericCode: '760',
        callingCode: '+963'
    },
    {
        name: 'Кітайская Рэспубліка',
        alpha2Code: 'TW',
        alpha3Code: 'TWN',
        numericCode: '158',
        callingCode: '+886'
    },
    {
        name: 'Таджыкістан',
        alpha2Code: 'TJ',
        alpha3Code: 'TJK',
        numericCode: '762',
        callingCode: '+992'
    },
    {
        name: 'Танзанія',
        alpha2Code: 'TZ',
        alpha3Code: 'TZA',
        numericCode: '834',
        callingCode: '+255'
    },
    {
        name: 'Тайланд',
        alpha2Code: 'TH',
        alpha3Code: 'THA',
        numericCode: '764',
        callingCode: '+66'
    },
    {
        name: 'Усходні Тымор',
        alpha2Code: 'TL',
        alpha3Code: 'TLS',
        numericCode: '626',
        callingCode: '+670'
    },
    {
        name: 'Тога',
        alpha2Code: 'TG',
        alpha3Code: 'TGO',
        numericCode: '768',
        callingCode: '+228'
    },
    {
        name: 'Такелау',
        alpha2Code: 'TK',
        alpha3Code: 'TKL',
        numericCode: '772',
        callingCode: '+690'
    },
    {
        name: 'Тонга',
        alpha2Code: 'TO',
        alpha3Code: 'TON',
        numericCode: '776',
        callingCode: '+676'
    },
    {
        name: 'Трынідад і Табага',
        alpha2Code: 'TT',
        alpha3Code: 'TTO',
        numericCode: '780',
        callingCode: '+1868'
    },
    {
        name: 'Туніс',
        alpha2Code: 'TN',
        alpha3Code: 'TUN',
        numericCode: '788',
        callingCode: '+216'
    },
    {
        name: 'Турцыя',
        alpha2Code: 'TR',
        alpha3Code: 'TUR',
        numericCode: '792',
        callingCode: '+90'
    },
    {
        name: 'Туркменістан',
        alpha2Code: 'TM',
        alpha3Code: 'TKM',
        numericCode: '795',
        callingCode: '+993'
    },
    {
        name: 'Цёркс і Кайкас',
        alpha2Code: 'TC',
        alpha3Code: 'TCA',
        numericCode: '796',
        callingCode: '+1649'
    },
    {
        name: 'Тувалу',
        alpha2Code: 'TV',
        alpha3Code: 'TUV',
        numericCode: '798',
        callingCode: '+688'
    },
    {
        name: 'Уганда',
        alpha2Code: 'UG',
        alpha3Code: 'UGA',
        numericCode: '800',
        callingCode: '+256'
    },
    {
        name: 'Украіна',
        alpha2Code: 'UA',
        alpha3Code: 'UKR',
        numericCode: '804',
        callingCode: '+380'
    },
    {
        name: 'Аб’яднаныя Арабскія Эміраты',
        alpha2Code: 'AE',
        alpha3Code: 'ARE',
        numericCode: '784',
        callingCode: '+971'
    },
    {
        name: 'Вялікабрытанія',
        alpha2Code: 'GB',
        alpha3Code: 'GBR',
        numericCode: '826',
        callingCode: '+44'
    },
    {
        name: 'ЗША',
        alpha2Code: 'US',
        alpha3Code: 'USA',
        numericCode: '840',
        callingCode: '+1'
    },
    {
        name: 'Уругвай',
        alpha2Code: 'UY',
        alpha3Code: 'URY',
        numericCode: '858',
        callingCode: '+598'
    },
    {
        name: 'Узбекістан',
        alpha2Code: 'UZ',
        alpha3Code: 'UZB',
        numericCode: '860',
        callingCode: '+998'
    },
    {
        name: 'Вануату',
        alpha2Code: 'VU',
        alpha3Code: 'VUT',
        numericCode: '548',
        callingCode: '+678'
    },
    {
        name: 'Венесуэла',
        alpha2Code: 'VE',
        alpha3Code: 'VEN',
        numericCode: '862',
        callingCode: '+58'
    },
    {
        name: 'В’етнам',
        alpha2Code: 'VN',
        alpha3Code: 'VNM',
        numericCode: '704',
        callingCode: '+84'
    },
    {
        name: 'Уоліс і Футуна',
        alpha2Code: 'WF',
        alpha3Code: 'WLF',
        numericCode: '876',
        callingCode: '+681'
    },
    {
        name: 'Заходняя Сахара',
        alpha2Code: 'EH',
        alpha3Code: 'ESH',
        numericCode: '732',
        callingCode: '+212'
    },
    {
        name: 'Емен',
        alpha2Code: 'YE',
        alpha3Code: 'YEM',
        numericCode: '887',
        callingCode: '+967'
    },
    {
        name: 'Замбія',
        alpha2Code: 'ZM',
        alpha3Code: 'ZMB',
        numericCode: '894',
        callingCode: '+260'
    },
    {
        name: 'Зімбабвэ',
        alpha2Code: 'ZW',
        alpha3Code: 'ZWE',
        numericCode: '716',
        callingCode: '+263'
    }
];

var by = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB_BY: COUNTRIES_DB_BY
});

const COUNTRIES_DB_DE = [
    {
        name: 'Afghanistan',
        alpha2Code: 'AF',
        alpha3Code: 'AFG',
        numericCode: '004',
        callingCode: '+93'
    },
    {
        name: 'Åland',
        alpha2Code: 'AX',
        alpha3Code: 'ALA',
        numericCode: '248',
        callingCode: '+358'
    },
    {
        name: 'Albanien',
        alpha2Code: 'AL',
        alpha3Code: 'ALB',
        numericCode: '008',
        callingCode: '+355'
    },
    {
        name: 'Algerien',
        alpha2Code: 'DZ',
        alpha3Code: 'DZA',
        numericCode: '012',
        callingCode: '+213'
    },
    {
        name: 'Amerikanisch-Samoa',
        alpha2Code: 'AS',
        alpha3Code: 'ASM',
        numericCode: '016',
        callingCode: '+1684'
    },
    {
        name: 'Andorra',
        alpha2Code: 'AD',
        alpha3Code: 'AND',
        numericCode: '020',
        callingCode: '+376'
    },
    {
        name: 'Angola',
        alpha2Code: 'AO',
        alpha3Code: 'AGO',
        numericCode: '024',
        callingCode: '+244'
    },
    {
        name: 'Anguilla',
        alpha2Code: 'AI',
        alpha3Code: 'AIA',
        numericCode: '660',
        callingCode: '+1264'
    },
    {
        name: 'Antarktika',
        alpha2Code: 'AQ',
        alpha3Code: 'ATA',
        numericCode: '010',
        callingCode: '+672'
    },
    {
        name: 'Antigua und Barbuda',
        alpha2Code: 'AG',
        alpha3Code: 'ATG',
        numericCode: '028',
        callingCode: '+1268'
    },
    {
        name: 'Argentinien',
        alpha2Code: 'AR',
        alpha3Code: 'ARG',
        numericCode: '032',
        callingCode: '+54'
    },
    {
        name: 'Armenien',
        alpha2Code: 'AM',
        alpha3Code: 'ARM',
        numericCode: '051',
        callingCode: '+374'
    },
    {
        name: 'Aruba',
        alpha2Code: 'AW',
        alpha3Code: 'ABW',
        numericCode: '533',
        callingCode: '+297'
    },
    {
        name: 'Australien',
        alpha2Code: 'AU',
        alpha3Code: 'AUS',
        numericCode: '036',
        callingCode: '+61'
    },
    {
        name: 'Österreich',
        alpha2Code: 'AT',
        alpha3Code: 'AUT',
        numericCode: '040',
        callingCode: '+43'
    },
    {
        name: 'Aserbaidschan',
        alpha2Code: 'AZ',
        alpha3Code: 'AZE',
        numericCode: '031',
        callingCode: '+994'
    },
    {
        name: 'Bahamas',
        alpha2Code: 'BS',
        alpha3Code: 'BHS',
        numericCode: '044',
        callingCode: '+1242'
    },
    {
        name: 'Bahrain',
        alpha2Code: 'BH',
        alpha3Code: 'BHR',
        numericCode: '048',
        callingCode: '+973'
    },
    {
        name: 'Bangladesch',
        alpha2Code: 'BD',
        alpha3Code: 'BGD',
        numericCode: '050',
        callingCode: '+880'
    },
    {
        name: 'Barbados',
        alpha2Code: 'BB',
        alpha3Code: 'BRB',
        numericCode: '052',
        callingCode: '+1246'
    },
    {
        name: 'Weißrussland',
        alpha2Code: 'BY',
        alpha3Code: 'BLR',
        numericCode: '112',
        callingCode: '+375'
    },
    {
        name: 'Belgien',
        alpha2Code: 'BE',
        alpha3Code: 'BEL',
        numericCode: '056',
        callingCode: '+32'
    },
    {
        name: 'Belize',
        alpha2Code: 'BZ',
        alpha3Code: 'BLZ',
        numericCode: '084',
        callingCode: '+501'
    },
    {
        name: 'Benin',
        alpha2Code: 'BJ',
        alpha3Code: 'BEN',
        numericCode: '204',
        callingCode: '+229'
    },
    {
        name: 'Bermuda',
        alpha2Code: 'BM',
        alpha3Code: 'BMU',
        numericCode: '060',
        callingCode: '+1441'
    },
    {
        name: 'Bhutan',
        alpha2Code: 'BT',
        alpha3Code: 'BTN',
        numericCode: '064',
        callingCode: '+975'
    },
    {
        name: 'Bolivien',
        alpha2Code: 'BO',
        alpha3Code: 'BOL',
        numericCode: '068',
        callingCode: '+591'
    },
    {
        name: 'Bonaire, Sint Eustatius und Saba',
        alpha2Code: 'BQ',
        alpha3Code: 'BES',
        numericCode: '535',
        callingCode: '+5997'
    },
    {
        name: 'Bosnien und Herzegowina',
        alpha2Code: 'BA',
        alpha3Code: 'BIH',
        numericCode: '070',
        callingCode: '+387'
    },
    {
        name: 'Botswana',
        alpha2Code: 'BW',
        alpha3Code: 'BWA',
        numericCode: '072',
        callingCode: '+267'
    },
    {
        name: 'Bouvetinsel',
        alpha2Code: 'BV',
        alpha3Code: 'BVT',
        numericCode: '074',
        callingCode: '+'
    },
    {
        name: 'Brasilien',
        alpha2Code: 'BR',
        alpha3Code: 'BRA',
        numericCode: '076',
        callingCode: '+55'
    },
    {
        name: 'Britisches Territorium im Indischen Ozean',
        alpha2Code: 'IO',
        alpha3Code: 'IOT',
        numericCode: '086',
        callingCode: '+246'
    },
    {
        name: 'Kleinere Inselbesitzungen der Vereinigten Staaten',
        alpha2Code: 'UM',
        alpha3Code: 'UMI',
        numericCode: '581',
        callingCode: '+'
    },
    {
        name: 'Britische Jungferninseln',
        alpha2Code: 'VG',
        alpha3Code: 'VGB',
        numericCode: '092',
        callingCode: '+1284'
    },
    {
        name: 'Amerikanische Jungferninseln',
        alpha2Code: 'VI',
        alpha3Code: 'VIR',
        numericCode: '850',
        callingCode: '+1 340'
    },
    {
        name: 'Brunei',
        alpha2Code: 'BN',
        alpha3Code: 'BRN',
        numericCode: '096',
        callingCode: '+673'
    },
    {
        name: 'Bulgarien',
        alpha2Code: 'BG',
        alpha3Code: 'BGR',
        numericCode: '100',
        callingCode: '+359'
    },
    {
        name: 'Burkina Faso',
        alpha2Code: 'BF',
        alpha3Code: 'BFA',
        numericCode: '854',
        callingCode: '+226'
    },
    {
        name: 'Burundi',
        alpha2Code: 'BI',
        alpha3Code: 'BDI',
        numericCode: '108',
        callingCode: '+257'
    },
    {
        name: 'Kambodscha',
        alpha2Code: 'KH',
        alpha3Code: 'KHM',
        numericCode: '116',
        callingCode: '+855'
    },
    {
        name: 'Kamerun',
        alpha2Code: 'CM',
        alpha3Code: 'CMR',
        numericCode: '120',
        callingCode: '+237'
    },
    {
        name: 'Kanada',
        alpha2Code: 'CA',
        alpha3Code: 'CAN',
        numericCode: '124',
        callingCode: '+1'
    },
    {
        name: 'Kap Verde',
        alpha2Code: 'CV',
        alpha3Code: 'CPV',
        numericCode: '132',
        callingCode: '+238'
    },
    {
        name: 'Kaimaninseln',
        alpha2Code: 'KY',
        alpha3Code: 'CYM',
        numericCode: '136',
        callingCode: '+1345'
    },
    {
        name: 'Zentralafrikanische Republik',
        alpha2Code: 'CF',
        alpha3Code: 'CAF',
        numericCode: '140',
        callingCode: '+236'
    },
    {
        name: 'Tschad',
        alpha2Code: 'TD',
        alpha3Code: 'TCD',
        numericCode: '148',
        callingCode: '+235'
    },
    {
        name: 'Chile',
        alpha2Code: 'CL',
        alpha3Code: 'CHL',
        numericCode: '152',
        callingCode: '+56'
    },
    {
        name: 'China',
        alpha2Code: 'CN',
        alpha3Code: 'CHN',
        numericCode: '156',
        callingCode: '+86'
    },
    {
        name: 'Weihnachtsinsel',
        alpha2Code: 'CX',
        alpha3Code: 'CXR',
        numericCode: '162',
        callingCode: '+61'
    },
    {
        name: 'Kokosinseln',
        alpha2Code: 'CC',
        alpha3Code: 'CCK',
        numericCode: '166',
        callingCode: '+61'
    },
    {
        name: 'Kolumbien',
        alpha2Code: 'CO',
        alpha3Code: 'COL',
        numericCode: '170',
        callingCode: '+57'
    },
    {
        name: 'Union der Komoren',
        alpha2Code: 'KM',
        alpha3Code: 'COM',
        numericCode: '174',
        callingCode: '+269'
    },
    {
        name: 'Kongo',
        alpha2Code: 'CG',
        alpha3Code: 'COG',
        numericCode: '178',
        callingCode: '+242'
    },
    {
        name: 'Kongo (Dem. Rep.)',
        alpha2Code: 'CD',
        alpha3Code: 'COD',
        numericCode: '180',
        callingCode: '+243'
    },
    {
        name: 'Cookinseln',
        alpha2Code: 'CK',
        alpha3Code: 'COK',
        numericCode: '184',
        callingCode: '+682'
    },
    {
        name: 'Costa Rica',
        alpha2Code: 'CR',
        alpha3Code: 'CRI',
        numericCode: '188',
        callingCode: '+506'
    },
    {
        name: 'Kroatien',
        alpha2Code: 'HR',
        alpha3Code: 'HRV',
        numericCode: '191',
        callingCode: '+385'
    },
    {
        name: 'Kuba',
        alpha2Code: 'CU',
        alpha3Code: 'CUB',
        numericCode: '192',
        callingCode: '+53'
    },
    {
        name: 'Curaçao',
        alpha2Code: 'CW',
        alpha3Code: 'CUW',
        numericCode: '531',
        callingCode: '+599'
    },
    {
        name: 'Zypern',
        alpha2Code: 'CY',
        alpha3Code: 'CYP',
        numericCode: '196',
        callingCode: '+357'
    },
    {
        name: 'Tschechische Republik',
        alpha2Code: 'CZ',
        alpha3Code: 'CZE',
        numericCode: '203',
        callingCode: '+420'
    },
    {
        name: 'Dänemark',
        alpha2Code: 'DK',
        alpha3Code: 'DNK',
        numericCode: '208',
        callingCode: '+45'
    },
    {
        name: 'Dschibuti',
        alpha2Code: 'DJ',
        alpha3Code: 'DJI',
        numericCode: '262',
        callingCode: '+253'
    },
    {
        name: 'Dominica',
        alpha2Code: 'DM',
        alpha3Code: 'DMA',
        numericCode: '212',
        callingCode: '+1767'
    },
    {
        name: 'Dominikanische Republik',
        alpha2Code: 'DO',
        alpha3Code: 'DOM',
        numericCode: '214',
        callingCode: '+1809'
    },
    {
        name: 'Ecuador',
        alpha2Code: 'EC',
        alpha3Code: 'ECU',
        numericCode: '218',
        callingCode: '+593'
    },
    {
        name: 'Ägypten',
        alpha2Code: 'EG',
        alpha3Code: 'EGY',
        numericCode: '818',
        callingCode: '+20'
    },
    {
        name: 'El Salvador',
        alpha2Code: 'SV',
        alpha3Code: 'SLV',
        numericCode: '222',
        callingCode: '+503'
    },
    {
        name: 'Äquatorial-Guinea',
        alpha2Code: 'GQ',
        alpha3Code: 'GNQ',
        numericCode: '226',
        callingCode: '+240'
    },
    {
        name: 'Eritrea',
        alpha2Code: 'ER',
        alpha3Code: 'ERI',
        numericCode: '232',
        callingCode: '+291'
    },
    {
        name: 'Estland',
        alpha2Code: 'EE',
        alpha3Code: 'EST',
        numericCode: '233',
        callingCode: '+372'
    },
    {
        name: 'Äthiopien',
        alpha2Code: 'ET',
        alpha3Code: 'ETH',
        numericCode: '231',
        callingCode: '+251'
    },
    {
        name: 'Falklandinseln',
        alpha2Code: 'FK',
        alpha3Code: 'FLK',
        numericCode: '238',
        callingCode: '+500'
    },
    {
        name: 'Färöer-Inseln',
        alpha2Code: 'FO',
        alpha3Code: 'FRO',
        numericCode: '234',
        callingCode: '+298'
    },
    {
        name: 'Fidschi',
        alpha2Code: 'FJ',
        alpha3Code: 'FJI',
        numericCode: '242',
        callingCode: '+679'
    },
    {
        name: 'Finnland',
        alpha2Code: 'FI',
        alpha3Code: 'FIN',
        numericCode: '246',
        callingCode: '+358'
    },
    {
        name: 'Frankreich',
        alpha2Code: 'FR',
        alpha3Code: 'FRA',
        numericCode: '250',
        callingCode: '+33'
    },
    {
        name: 'Französisch Guyana',
        alpha2Code: 'GF',
        alpha3Code: 'GUF',
        numericCode: '254',
        callingCode: '+594'
    },
    {
        name: 'Französisch-Polynesien',
        alpha2Code: 'PF',
        alpha3Code: 'PYF',
        numericCode: '258',
        callingCode: '+689'
    },
    {
        name: 'Französische Süd- und Antarktisgebiete',
        alpha2Code: 'TF',
        alpha3Code: 'ATF',
        numericCode: '260',
        callingCode: '+'
    },
    {
        name: 'Gabun',
        alpha2Code: 'GA',
        alpha3Code: 'GAB',
        numericCode: '266',
        callingCode: '+241'
    },
    {
        name: 'Gambia',
        alpha2Code: 'GM',
        alpha3Code: 'GMB',
        numericCode: '270',
        callingCode: '+220'
    },
    {
        name: 'Georgien',
        alpha2Code: 'GE',
        alpha3Code: 'GEO',
        numericCode: '268',
        callingCode: '+995'
    },
    {
        name: 'Deutschland',
        alpha2Code: 'DE',
        alpha3Code: 'DEU',
        numericCode: '276',
        callingCode: '+49'
    },
    {
        name: 'Ghana',
        alpha2Code: 'GH',
        alpha3Code: 'GHA',
        numericCode: '288',
        callingCode: '+233'
    },
    {
        name: 'Gibraltar',
        alpha2Code: 'GI',
        alpha3Code: 'GIB',
        numericCode: '292',
        callingCode: '+350'
    },
    {
        name: 'Griechenland',
        alpha2Code: 'GR',
        alpha3Code: 'GRC',
        numericCode: '300',
        callingCode: '+30'
    },
    {
        name: 'Grönland',
        alpha2Code: 'GL',
        alpha3Code: 'GRL',
        numericCode: '304',
        callingCode: '+299'
    },
    {
        name: 'Grenada',
        alpha2Code: 'GD',
        alpha3Code: 'GRD',
        numericCode: '308',
        callingCode: '+1473'
    },
    {
        name: 'Guadeloupe',
        alpha2Code: 'GP',
        alpha3Code: 'GLP',
        numericCode: '312',
        callingCode: '+590'
    },
    {
        name: 'Guam',
        alpha2Code: 'GU',
        alpha3Code: 'GUM',
        numericCode: '316',
        callingCode: '+1671'
    },
    {
        name: 'Guatemala',
        alpha2Code: 'GT',
        alpha3Code: 'GTM',
        numericCode: '320',
        callingCode: '+502'
    },
    {
        name: 'Guernsey',
        alpha2Code: 'GG',
        alpha3Code: 'GGY',
        numericCode: '831',
        callingCode: '+44'
    },
    {
        name: 'Guinea',
        alpha2Code: 'GN',
        alpha3Code: 'GIN',
        numericCode: '324',
        callingCode: '+224'
    },
    {
        name: 'Guinea-Bissau',
        alpha2Code: 'GW',
        alpha3Code: 'GNB',
        numericCode: '624',
        callingCode: '+245'
    },
    {
        name: 'Guyana',
        alpha2Code: 'GY',
        alpha3Code: 'GUY',
        numericCode: '328',
        callingCode: '+592'
    },
    {
        name: 'Haiti',
        alpha2Code: 'HT',
        alpha3Code: 'HTI',
        numericCode: '332',
        callingCode: '+509'
    },
    {
        name: 'Heard und die McDonaldinseln',
        alpha2Code: 'HM',
        alpha3Code: 'HMD',
        numericCode: '334',
        callingCode: '+'
    },
    {
        name: 'Heiliger Stuhl',
        alpha2Code: 'VA',
        alpha3Code: 'VAT',
        numericCode: '336',
        callingCode: '+379'
    },
    {
        name: 'Honduras',
        alpha2Code: 'HN',
        alpha3Code: 'HND',
        numericCode: '340',
        callingCode: '+504'
    },
    {
        name: 'Hong Kong',
        alpha2Code: 'HK',
        alpha3Code: 'HKG',
        numericCode: '344',
        callingCode: '+852'
    },
    {
        name: 'Ungarn',
        alpha2Code: 'HU',
        alpha3Code: 'HUN',
        numericCode: '348',
        callingCode: '+36'
    },
    {
        name: 'Island',
        alpha2Code: 'IS',
        alpha3Code: 'ISL',
        numericCode: '352',
        callingCode: '+354'
    },
    {
        name: 'Indien',
        alpha2Code: 'IN',
        alpha3Code: 'IND',
        numericCode: '356',
        callingCode: '+91'
    },
    {
        name: 'Indonesien',
        alpha2Code: 'ID',
        alpha3Code: 'IDN',
        numericCode: '360',
        callingCode: '+62'
    },
    {
        name: 'Elfenbeinküste',
        alpha2Code: 'CI',
        alpha3Code: 'CIV',
        numericCode: '384',
        callingCode: '+225'
    },
    {
        name: 'Iran',
        alpha2Code: 'IR',
        alpha3Code: 'IRN',
        numericCode: '364',
        callingCode: '+98'
    },
    {
        name: 'Irak',
        alpha2Code: 'IQ',
        alpha3Code: 'IRQ',
        numericCode: '368',
        callingCode: '+964'
    },
    {
        name: 'Irland',
        alpha2Code: 'IE',
        alpha3Code: 'IRL',
        numericCode: '372',
        callingCode: '+353'
    },
    {
        name: 'Insel Man',
        alpha2Code: 'IM',
        alpha3Code: 'IMN',
        numericCode: '833',
        callingCode: '+44'
    },
    {
        name: 'Israel',
        alpha2Code: 'IL',
        alpha3Code: 'ISR',
        numericCode: '376',
        callingCode: '+972'
    },
    {
        name: 'Italien',
        alpha2Code: 'IT',
        alpha3Code: 'ITA',
        numericCode: '380',
        callingCode: '+39'
    },
    {
        name: 'Jamaika',
        alpha2Code: 'JM',
        alpha3Code: 'JAM',
        numericCode: '388',
        callingCode: '+1876'
    },
    {
        name: 'Japan',
        alpha2Code: 'JP',
        alpha3Code: 'JPN',
        numericCode: '392',
        callingCode: '+81'
    },
    {
        name: 'Jersey',
        alpha2Code: 'JE',
        alpha3Code: 'JEY',
        numericCode: '832',
        callingCode: '+44'
    },
    {
        name: 'Jordanien',
        alpha2Code: 'JO',
        alpha3Code: 'JOR',
        numericCode: '400',
        callingCode: '+962'
    },
    {
        name: 'Kasachstan',
        alpha2Code: 'KZ',
        alpha3Code: 'KAZ',
        numericCode: '398',
        callingCode: '+7'
    },
    {
        name: 'Kenia',
        alpha2Code: 'KE',
        alpha3Code: 'KEN',
        numericCode: '404',
        callingCode: '+254'
    },
    {
        name: 'Kiribati',
        alpha2Code: 'KI',
        alpha3Code: 'KIR',
        numericCode: '296',
        callingCode: '+686'
    },
    {
        name: 'Kuwait',
        alpha2Code: 'KW',
        alpha3Code: 'KWT',
        numericCode: '414',
        callingCode: '+965'
    },
    {
        name: 'Kirgisistan',
        alpha2Code: 'KG',
        alpha3Code: 'KGZ',
        numericCode: '417',
        callingCode: '+996'
    },
    {
        name: 'Laos',
        alpha2Code: 'LA',
        alpha3Code: 'LAO',
        numericCode: '418',
        callingCode: '+856'
    },
    {
        name: 'Lettland',
        alpha2Code: 'LV',
        alpha3Code: 'LVA',
        numericCode: '428',
        callingCode: '+371'
    },
    {
        name: 'Libanon',
        alpha2Code: 'LB',
        alpha3Code: 'LBN',
        numericCode: '422',
        callingCode: '+961'
    },
    {
        name: 'Lesotho',
        alpha2Code: 'LS',
        alpha3Code: 'LSO',
        numericCode: '426',
        callingCode: '+266'
    },
    {
        name: 'Liberia',
        alpha2Code: 'LR',
        alpha3Code: 'LBR',
        numericCode: '430',
        callingCode: '+231'
    },
    {
        name: 'Libyen',
        alpha2Code: 'LY',
        alpha3Code: 'LBY',
        numericCode: '434',
        callingCode: '+218'
    },
    {
        name: 'Liechtenstein',
        alpha2Code: 'LI',
        alpha3Code: 'LIE',
        numericCode: '438',
        callingCode: '+423'
    },
    {
        name: 'Litauen',
        alpha2Code: 'LT',
        alpha3Code: 'LTU',
        numericCode: '440',
        callingCode: '+370'
    },
    {
        name: 'Luxemburg',
        alpha2Code: 'LU',
        alpha3Code: 'LUX',
        numericCode: '442',
        callingCode: '+352'
    },
    {
        name: 'Macao',
        alpha2Code: 'MO',
        alpha3Code: 'MAC',
        numericCode: '446',
        callingCode: '+853'
    },
    {
        name: 'Mazedonien',
        alpha2Code: 'MK',
        alpha3Code: 'MKD',
        numericCode: '807',
        callingCode: '+389'
    },
    {
        name: 'Madagaskar',
        alpha2Code: 'MG',
        alpha3Code: 'MDG',
        numericCode: '450',
        callingCode: '+261'
    },
    {
        name: 'Malawi',
        alpha2Code: 'MW',
        alpha3Code: 'MWI',
        numericCode: '454',
        callingCode: '+265'
    },
    {
        name: 'Malaysia',
        alpha2Code: 'MY',
        alpha3Code: 'MYS',
        numericCode: '458',
        callingCode: '+60'
    },
    {
        name: 'Malediven',
        alpha2Code: 'MV',
        alpha3Code: 'MDV',
        numericCode: '462',
        callingCode: '+960'
    },
    {
        name: 'Mali',
        alpha2Code: 'ML',
        alpha3Code: 'MLI',
        numericCode: '466',
        callingCode: '+223'
    },
    {
        name: 'Malta',
        alpha2Code: 'MT',
        alpha3Code: 'MLT',
        numericCode: '470',
        callingCode: '+356'
    },
    {
        name: 'Marshallinseln',
        alpha2Code: 'MH',
        alpha3Code: 'MHL',
        numericCode: '584',
        callingCode: '+692'
    },
    {
        name: 'Martinique',
        alpha2Code: 'MQ',
        alpha3Code: 'MTQ',
        numericCode: '474',
        callingCode: '+596'
    },
    {
        name: 'Mauretanien',
        alpha2Code: 'MR',
        alpha3Code: 'MRT',
        numericCode: '478',
        callingCode: '+222'
    },
    {
        name: 'Mauritius',
        alpha2Code: 'MU',
        alpha3Code: 'MUS',
        numericCode: '480',
        callingCode: '+230'
    },
    {
        name: 'Mayotte',
        alpha2Code: 'YT',
        alpha3Code: 'MYT',
        numericCode: '175',
        callingCode: '+262'
    },
    {
        name: 'Mexiko',
        alpha2Code: 'MX',
        alpha3Code: 'MEX',
        numericCode: '484',
        callingCode: '+52'
    },
    {
        name: 'Mikronesien',
        alpha2Code: 'FM',
        alpha3Code: 'FSM',
        numericCode: '583',
        callingCode: '+691'
    },
    {
        name: 'Moldawie',
        alpha2Code: 'MD',
        alpha3Code: 'MDA',
        numericCode: '498',
        callingCode: '+373'
    },
    {
        name: 'Monaco',
        alpha2Code: 'MC',
        alpha3Code: 'MCO',
        numericCode: '492',
        callingCode: '+377'
    },
    {
        name: 'Mongolei',
        alpha2Code: 'MN',
        alpha3Code: 'MNG',
        numericCode: '496',
        callingCode: '+976'
    },
    {
        name: 'Montenegro',
        alpha2Code: 'ME',
        alpha3Code: 'MNE',
        numericCode: '499',
        callingCode: '+382'
    },
    {
        name: 'Montserrat',
        alpha2Code: 'MS',
        alpha3Code: 'MSR',
        numericCode: '500',
        callingCode: '+1664'
    },
    {
        name: 'Marokko',
        alpha2Code: 'MA',
        alpha3Code: 'MAR',
        numericCode: '504',
        callingCode: '+212'
    },
    {
        name: 'Mosambik',
        alpha2Code: 'MZ',
        alpha3Code: 'MOZ',
        numericCode: '508',
        callingCode: '+258'
    },
    {
        name: 'Myanmar',
        alpha2Code: 'MM',
        alpha3Code: 'MMR',
        numericCode: '104',
        callingCode: '+95'
    },
    {
        name: 'Namibia',
        alpha2Code: 'NA',
        alpha3Code: 'NAM',
        numericCode: '516',
        callingCode: '+264'
    },
    {
        name: 'Nauru',
        alpha2Code: 'NR',
        alpha3Code: 'NRU',
        numericCode: '520',
        callingCode: '+674'
    },
    {
        name: 'Népal',
        alpha2Code: 'NP',
        alpha3Code: 'NPL',
        numericCode: '524',
        callingCode: '+977'
    },
    {
        name: 'Niederlande',
        alpha2Code: 'NL',
        alpha3Code: 'NLD',
        numericCode: '528',
        callingCode: '+31'
    },
    {
        name: 'Neukaledonien',
        alpha2Code: 'NC',
        alpha3Code: 'NCL',
        numericCode: '540',
        callingCode: '+687'
    },
    {
        name: 'Neuseeland',
        alpha2Code: 'NZ',
        alpha3Code: 'NZL',
        numericCode: '554',
        callingCode: '+64'
    },
    {
        name: 'Nicaragua',
        alpha2Code: 'NI',
        alpha3Code: 'NIC',
        numericCode: '558',
        callingCode: '+505'
    },
    {
        name: 'Niger',
        alpha2Code: 'NE',
        alpha3Code: 'NER',
        numericCode: '562',
        callingCode: '+227'
    },
    {
        name: 'Nigeria',
        alpha2Code: 'NG',
        alpha3Code: 'NGA',
        numericCode: '566',
        callingCode: '+234'
    },
    {
        name: 'Niue',
        alpha2Code: 'NU',
        alpha3Code: 'NIU',
        numericCode: '570',
        callingCode: '+683'
    },
    {
        name: 'Norfolkinsel',
        alpha2Code: 'NF',
        alpha3Code: 'NFK',
        numericCode: '574',
        callingCode: '+672'
    },
    {
        name: 'Nordkorea',
        alpha2Code: 'KP',
        alpha3Code: 'PRK',
        numericCode: '408',
        callingCode: '+850'
    },
    {
        name: 'Nördliche Marianen',
        alpha2Code: 'MP',
        alpha3Code: 'MNP',
        numericCode: '580',
        callingCode: '+1670'
    },
    {
        name: 'Norwegen',
        alpha2Code: 'NO',
        alpha3Code: 'NOR',
        numericCode: '578',
        callingCode: '+47'
    },
    {
        name: 'Oman',
        alpha2Code: 'OM',
        alpha3Code: 'OMN',
        numericCode: '512',
        callingCode: '+968'
    },
    {
        name: 'Pakistan',
        alpha2Code: 'PK',
        alpha3Code: 'PAK',
        numericCode: '586',
        callingCode: '+92'
    },
    {
        name: 'Palau',
        alpha2Code: 'PW',
        alpha3Code: 'PLW',
        numericCode: '585',
        callingCode: '+680'
    },
    {
        name: 'Palästina',
        alpha2Code: 'PS',
        alpha3Code: 'PSE',
        numericCode: '275',
        callingCode: '+970'
    },
    {
        name: 'Panama',
        alpha2Code: 'PA',
        alpha3Code: 'PAN',
        numericCode: '591',
        callingCode: '+507'
    },
    {
        name: 'Papua-Neuguinea',
        alpha2Code: 'PG',
        alpha3Code: 'PNG',
        numericCode: '598',
        callingCode: '+675'
    },
    {
        name: 'Paraguay',
        alpha2Code: 'PY',
        alpha3Code: 'PRY',
        numericCode: '600',
        callingCode: '+595'
    },
    {
        name: 'Peru',
        alpha2Code: 'PE',
        alpha3Code: 'PER',
        numericCode: '604',
        callingCode: '+51'
    },
    {
        name: 'Philippinen',
        alpha2Code: 'PH',
        alpha3Code: 'PHL',
        numericCode: '608',
        callingCode: '+63'
    },
    {
        name: 'Pitcairn',
        alpha2Code: 'PN',
        alpha3Code: 'PCN',
        numericCode: '612',
        callingCode: '+64'
    },
    {
        name: 'Polen',
        alpha2Code: 'PL',
        alpha3Code: 'POL',
        numericCode: '616',
        callingCode: '+48'
    },
    {
        name: 'Portugal',
        alpha2Code: 'PT',
        alpha3Code: 'PRT',
        numericCode: '620',
        callingCode: '+351'
    },
    {
        name: 'Puerto Rico',
        alpha2Code: 'PR',
        alpha3Code: 'PRI',
        numericCode: '630',
        callingCode: '+1'
    },
    {
        name: 'Katar',
        alpha2Code: 'QA',
        alpha3Code: 'QAT',
        numericCode: '634',
        callingCode: '+974'
    },
    {
        name: 'Republik Kosovo',
        alpha2Code: 'XK',
        alpha3Code: 'KOS',
        numericCode: null,
        callingCode: '+383'
    },
    {
        name: 'Réunion',
        alpha2Code: 'RE',
        alpha3Code: 'REU',
        numericCode: '638',
        callingCode: '+262'
    },
    {
        name: 'Rumänien',
        alpha2Code: 'RO',
        alpha3Code: 'ROU',
        numericCode: '642',
        callingCode: '+40'
    },
    {
        name: 'Russland',
        alpha2Code: 'RU',
        alpha3Code: 'RUS',
        numericCode: '643',
        callingCode: '+7'
    },
    {
        name: 'Ruanda',
        alpha2Code: 'RW',
        alpha3Code: 'RWA',
        numericCode: '646',
        callingCode: '+250'
    },
    {
        name: 'Saint-Barthélemy',
        alpha2Code: 'BL',
        alpha3Code: 'BLM',
        numericCode: '652',
        callingCode: '+590'
    },
    {
        name: 'Sankt Helena',
        alpha2Code: 'SH',
        alpha3Code: 'SHN',
        numericCode: '654',
        callingCode: '+290'
    },
    {
        name: 'St. Kitts und Nevis',
        alpha2Code: 'KN',
        alpha3Code: 'KNA',
        numericCode: '659',
        callingCode: '+1869'
    },
    {
        name: 'Saint Lucia',
        alpha2Code: 'LC',
        alpha3Code: 'LCA',
        numericCode: '662',
        callingCode: '+1758'
    },
    {
        name: 'Saint Martin',
        alpha2Code: 'MF',
        alpha3Code: 'MAF',
        numericCode: '663',
        callingCode: '+590'
    },
    {
        name: 'Saint-Pierre und Miquelon',
        alpha2Code: 'PM',
        alpha3Code: 'SPM',
        numericCode: '666',
        callingCode: '+508'
    },
    {
        name: 'Saint Vincent und die Grenadinen',
        alpha2Code: 'VC',
        alpha3Code: 'VCT',
        numericCode: '670',
        callingCode: '+1784'
    },
    {
        name: 'Samoa',
        alpha2Code: 'WS',
        alpha3Code: 'WSM',
        numericCode: '882',
        callingCode: '+685'
    },
    {
        name: 'San Marino',
        alpha2Code: 'SM',
        alpha3Code: 'SMR',
        numericCode: '674',
        callingCode: '+378'
    },
    {
        name: 'São Tomé und Príncipe',
        alpha2Code: 'ST',
        alpha3Code: 'STP',
        numericCode: '678',
        callingCode: '+239'
    },
    {
        name: 'Saudi-Arabien',
        alpha2Code: 'SA',
        alpha3Code: 'SAU',
        numericCode: '682',
        callingCode: '+966'
    },
    {
        name: 'Senegal',
        alpha2Code: 'SN',
        alpha3Code: 'SEN',
        numericCode: '686',
        callingCode: '+221'
    },
    {
        name: 'Serbien',
        alpha2Code: 'RS',
        alpha3Code: 'SRB',
        numericCode: '688',
        callingCode: '+381'
    },
    {
        name: 'Seychellen',
        alpha2Code: 'SC',
        alpha3Code: 'SYC',
        numericCode: '690',
        callingCode: '+248'
    },
    {
        name: 'Sierra Leone',
        alpha2Code: 'SL',
        alpha3Code: 'SLE',
        numericCode: '694',
        callingCode: '+232'
    },
    {
        name: 'Singapur',
        alpha2Code: 'SG',
        alpha3Code: 'SGP',
        numericCode: '702',
        callingCode: '+65'
    },
    {
        name: 'Sint Maarten (niederl. Teil)',
        alpha2Code: 'SX',
        alpha3Code: 'SXM',
        numericCode: '534',
        callingCode: '+1721'
    },
    {
        name: 'Slowakei',
        alpha2Code: 'SK',
        alpha3Code: 'SVK',
        numericCode: '703',
        callingCode: '+421'
    },
    {
        name: 'Slowenien',
        alpha2Code: 'SI',
        alpha3Code: 'SVN',
        numericCode: '705',
        callingCode: '+386'
    },
    {
        name: 'Salomonen',
        alpha2Code: 'SB',
        alpha3Code: 'SLB',
        numericCode: '090',
        callingCode: '+677'
    },
    {
        name: 'Somalia',
        alpha2Code: 'SO',
        alpha3Code: 'SOM',
        numericCode: '706',
        callingCode: '+252'
    },
    {
        name: 'Republik Südafrika',
        alpha2Code: 'ZA',
        alpha3Code: 'ZAF',
        numericCode: '710',
        callingCode: '+27'
    },
    {
        name: 'Südgeorgien und die Südlichen Sandwichinseln',
        alpha2Code: 'GS',
        alpha3Code: 'SGS',
        numericCode: '239',
        callingCode: '+500'
    },
    {
        name: 'Südkorea',
        alpha2Code: 'KR',
        alpha3Code: 'KOR',
        numericCode: '410',
        callingCode: '+82'
    },
    {
        name: 'Südsudan',
        alpha2Code: 'SS',
        alpha3Code: 'SSD',
        numericCode: '728',
        callingCode: '+211'
    },
    {
        name: 'Spanien',
        alpha2Code: 'ES',
        alpha3Code: 'ESP',
        numericCode: '724',
        callingCode: '+34'
    },
    {
        name: 'Sri Lanka',
        alpha2Code: 'LK',
        alpha3Code: 'LKA',
        numericCode: '144',
        callingCode: '+94'
    },
    {
        name: 'Sudan',
        alpha2Code: 'SD',
        alpha3Code: 'SDN',
        numericCode: '729',
        callingCode: '+249'
    },
    {
        name: 'Suriname',
        alpha2Code: 'SR',
        alpha3Code: 'SUR',
        numericCode: '740',
        callingCode: '+597'
    },
    {
        name: 'Svalbard und Jan Mayen',
        alpha2Code: 'SJ',
        alpha3Code: 'SJM',
        numericCode: '744',
        callingCode: '+4779'
    },
    {
        name: 'Swasiland',
        alpha2Code: 'SZ',
        alpha3Code: 'SWZ',
        numericCode: '748',
        callingCode: '+268'
    },
    {
        name: 'Schweden',
        alpha2Code: 'SE',
        alpha3Code: 'SWE',
        numericCode: '752',
        callingCode: '+46'
    },
    {
        name: 'Schweiz',
        alpha2Code: 'CH',
        alpha3Code: 'CHE',
        numericCode: '756',
        callingCode: '+41'
    },
    {
        name: 'Syrien',
        alpha2Code: 'SY',
        alpha3Code: 'SYR',
        numericCode: '760',
        callingCode: '+963'
    },
    {
        name: 'Taiwan, China',
        alpha2Code: 'TW',
        alpha3Code: 'TWN',
        numericCode: '158',
        callingCode: '+886'
    },
    {
        name: 'Tadschikistan',
        alpha2Code: 'TJ',
        alpha3Code: 'TJK',
        numericCode: '762',
        callingCode: '+992'
    },
    {
        name: 'Tansania',
        alpha2Code: 'TZ',
        alpha3Code: 'TZA',
        numericCode: '834',
        callingCode: '+255'
    },
    {
        name: 'Thailand',
        alpha2Code: 'TH',
        alpha3Code: 'THA',
        numericCode: '764',
        callingCode: '+66'
    },
    {
        name: 'Timor-Leste',
        alpha2Code: 'TL',
        alpha3Code: 'TLS',
        numericCode: '626',
        callingCode: '+670'
    },
    {
        name: 'Togo',
        alpha2Code: 'TG',
        alpha3Code: 'TGO',
        numericCode: '768',
        callingCode: '+228'
    },
    {
        name: 'Tokelau',
        alpha2Code: 'TK',
        alpha3Code: 'TKL',
        numericCode: '772',
        callingCode: '+690'
    },
    {
        name: 'Tonga',
        alpha2Code: 'TO',
        alpha3Code: 'TON',
        numericCode: '776',
        callingCode: '+676'
    },
    {
        name: 'Trinidad und Tobago',
        alpha2Code: 'TT',
        alpha3Code: 'TTO',
        numericCode: '780',
        callingCode: '+1868'
    },
    {
        name: 'Tunesien',
        alpha2Code: 'TN',
        alpha3Code: 'TUN',
        numericCode: '788',
        callingCode: '+216'
    },
    {
        name: 'Türkei',
        alpha2Code: 'TR',
        alpha3Code: 'TUR',
        numericCode: '792',
        callingCode: '+90'
    },
    {
        name: 'Turkmenistan',
        alpha2Code: 'TM',
        alpha3Code: 'TKM',
        numericCode: '795',
        callingCode: '+993'
    },
    {
        name: 'Turks- und Caicosinseln',
        alpha2Code: 'TC',
        alpha3Code: 'TCA',
        numericCode: '796',
        callingCode: '+1649'
    },
    {
        name: 'Tuvalu',
        alpha2Code: 'TV',
        alpha3Code: 'TUV',
        numericCode: '798',
        callingCode: '+688'
    },
    {
        name: 'Uganda',
        alpha2Code: 'UG',
        alpha3Code: 'UGA',
        numericCode: '800',
        callingCode: '+256'
    },
    {
        name: 'Ukraine',
        alpha2Code: 'UA',
        alpha3Code: 'UKR',
        numericCode: '804',
        callingCode: '+380'
    },
    {
        name: 'Vereinigte Arabische Emirate',
        alpha2Code: 'AE',
        alpha3Code: 'ARE',
        numericCode: '784',
        callingCode: '+971'
    },
    {
        name: 'Vereinigtes Königreich',
        alpha2Code: 'GB',
        alpha3Code: 'GBR',
        numericCode: '826',
        callingCode: '+44'
    },
    {
        name: 'Vereinigte Staaten von Amerika',
        alpha2Code: 'US',
        alpha3Code: 'USA',
        numericCode: '840',
        callingCode: '+1'
    },
    {
        name: 'Uruguay',
        alpha2Code: 'UY',
        alpha3Code: 'URY',
        numericCode: '858',
        callingCode: '+598'
    },
    {
        name: 'Usbekistan',
        alpha2Code: 'UZ',
        alpha3Code: 'UZB',
        numericCode: '860',
        callingCode: '+998'
    },
    {
        name: 'Vanuatu',
        alpha2Code: 'VU',
        alpha3Code: 'VUT',
        numericCode: '548',
        callingCode: '+678'
    },
    {
        name: 'Venezuela',
        alpha2Code: 'VE',
        alpha3Code: 'VEN',
        numericCode: '862',
        callingCode: '+58'
    },
    {
        name: 'Vietnam',
        alpha2Code: 'VN',
        alpha3Code: 'VNM',
        numericCode: '704',
        callingCode: '+84'
    },
    {
        name: 'Wallis und Futuna',
        alpha2Code: 'WF',
        alpha3Code: 'WLF',
        numericCode: '876',
        callingCode: '+681'
    },
    {
        name: 'Westsahara',
        alpha2Code: 'EH',
        alpha3Code: 'ESH',
        numericCode: '732',
        callingCode: '+212'
    },
    {
        name: 'Jemen',
        alpha2Code: 'YE',
        alpha3Code: 'YEM',
        numericCode: '887',
        callingCode: '+967'
    },
    {
        name: 'Sambia',
        alpha2Code: 'ZM',
        alpha3Code: 'ZMB',
        numericCode: '894',
        callingCode: '+260'
    },
    {
        name: 'Simbabwe',
        alpha2Code: 'ZW',
        alpha3Code: 'ZWE',
        numericCode: '716',
        callingCode: '+263'
    }
];

var de = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB_DE: COUNTRIES_DB_DE
});

const COUNTRIES_DB = [
    {
        name: 'Afghanistan',
        alpha2Code: 'AF',
        alpha3Code: 'AFG',
        numericCode: '004',
        callingCode: '+93'
    },
    {
        name: 'Åland Islands',
        alpha2Code: 'AX',
        alpha3Code: 'ALA',
        numericCode: '248',
        callingCode: '+358'
    },
    {
        name: 'Albania',
        alpha2Code: 'AL',
        alpha3Code: 'ALB',
        numericCode: '008',
        callingCode: '+355'
    },
    {
        name: 'Algeria',
        alpha2Code: 'DZ',
        alpha3Code: 'DZA',
        numericCode: '012',
        callingCode: '+213'
    },
    {
        name: 'American Samoa',
        alpha2Code: 'AS',
        alpha3Code: 'ASM',
        numericCode: '016',
        callingCode: '+1684'
    },
    {
        name: 'Andorra',
        alpha2Code: 'AD',
        alpha3Code: 'AND',
        numericCode: '020',
        callingCode: '+376'
    },
    {
        name: 'Angola',
        alpha2Code: 'AO',
        alpha3Code: 'AGO',
        numericCode: '024',
        callingCode: '+244'
    },
    {
        name: 'Anguilla',
        alpha2Code: 'AI',
        alpha3Code: 'AIA',
        numericCode: '660',
        callingCode: '+1264'
    },
    {
        name: 'Antarctica',
        alpha2Code: 'AQ',
        alpha3Code: 'ATA',
        numericCode: '010',
        callingCode: '+672'
    },
    {
        name: 'Antigua and Barbuda',
        alpha2Code: 'AG',
        alpha3Code: 'ATG',
        numericCode: '028',
        callingCode: '+1268'
    },
    {
        name: 'Argentina',
        alpha2Code: 'AR',
        alpha3Code: 'ARG',
        numericCode: '032',
        callingCode: '+54'
    },
    {
        name: 'Armenia',
        alpha2Code: 'AM',
        alpha3Code: 'ARM',
        numericCode: '051',
        callingCode: '+374'
    },
    {
        name: 'Aruba',
        alpha2Code: 'AW',
        alpha3Code: 'ABW',
        numericCode: '533',
        callingCode: '+297'
    },
    {
        name: 'Australia',
        alpha2Code: 'AU',
        alpha3Code: 'AUS',
        numericCode: '036',
        callingCode: '+61'
    },
    {
        name: 'Austria',
        alpha2Code: 'AT',
        alpha3Code: 'AUT',
        numericCode: '040',
        callingCode: '+43'
    },
    {
        name: 'Azerbaijan',
        alpha2Code: 'AZ',
        alpha3Code: 'AZE',
        numericCode: '031',
        callingCode: '+994'
    },
    {
        name: 'Bahamas',
        alpha2Code: 'BS',
        alpha3Code: 'BHS',
        numericCode: '044',
        callingCode: '+1242'
    },
    {
        name: 'Bahrain',
        alpha2Code: 'BH',
        alpha3Code: 'BHR',
        numericCode: '048',
        callingCode: '+973'
    },
    {
        name: 'Bangladesh',
        alpha2Code: 'BD',
        alpha3Code: 'BGD',
        numericCode: '050',
        callingCode: '+880'
    },
    {
        name: 'Barbados',
        alpha2Code: 'BB',
        alpha3Code: 'BRB',
        numericCode: '052',
        callingCode: '+1246'
    },
    {
        name: 'Belarus',
        alpha2Code: 'BY',
        alpha3Code: 'BLR',
        numericCode: '112',
        callingCode: '+375'
    },
    {
        name: 'Belgium',
        alpha2Code: 'BE',
        alpha3Code: 'BEL',
        numericCode: '056',
        callingCode: '+32'
    },
    {
        name: 'Belize',
        alpha2Code: 'BZ',
        alpha3Code: 'BLZ',
        numericCode: '084',
        callingCode: '+501'
    },
    {
        name: 'Benin',
        alpha2Code: 'BJ',
        alpha3Code: 'BEN',
        numericCode: '204',
        callingCode: '+229'
    },
    {
        name: 'Bermuda',
        alpha2Code: 'BM',
        alpha3Code: 'BMU',
        numericCode: '060',
        callingCode: '+1441'
    },
    {
        name: 'Bhutan',
        alpha2Code: 'BT',
        alpha3Code: 'BTN',
        numericCode: '064',
        callingCode: '+975'
    },
    {
        name: 'Bolivia (Plurinational State of)',
        alpha2Code: 'BO',
        alpha3Code: 'BOL',
        numericCode: '068',
        callingCode: '+591'
    },
    {
        name: 'Bonaire, Sint Eustatius and Saba',
        alpha2Code: 'BQ',
        alpha3Code: 'BES',
        numericCode: '535',
        callingCode: '+5997'
    },
    {
        name: 'Bosnia and Herzegovina',
        alpha2Code: 'BA',
        alpha3Code: 'BIH',
        numericCode: '070',
        callingCode: '+387'
    },
    {
        name: 'Botswana',
        alpha2Code: 'BW',
        alpha3Code: 'BWA',
        numericCode: '072',
        callingCode: '+267'
    },
    {
        name: 'Bouvet Island',
        alpha2Code: 'BV',
        alpha3Code: 'BVT',
        numericCode: '074',
        callingCode: '+'
    },
    {
        name: 'Brazil',
        alpha2Code: 'BR',
        alpha3Code: 'BRA',
        numericCode: '076',
        callingCode: '+55'
    },
    {
        name: 'British Indian Ocean Territory',
        alpha2Code: 'IO',
        alpha3Code: 'IOT',
        numericCode: '086',
        callingCode: '+246'
    },
    {
        name: 'United States Minor Outlying Islands',
        alpha2Code: 'UM',
        alpha3Code: 'UMI',
        numericCode: '581',
        callingCode: '+'
    },
    {
        name: 'Virgin Islands (British)',
        alpha2Code: 'VG',
        alpha3Code: 'VGB',
        numericCode: '092',
        callingCode: '+1284'
    },
    {
        name: 'Virgin Islands (U.S.)',
        alpha2Code: 'VI',
        alpha3Code: 'VIR',
        numericCode: '850',
        callingCode: '+1 340'
    },
    {
        name: 'Brunei Darussalam',
        alpha2Code: 'BN',
        alpha3Code: 'BRN',
        numericCode: '096',
        callingCode: '+673'
    },
    {
        name: 'Bulgaria',
        alpha2Code: 'BG',
        alpha3Code: 'BGR',
        numericCode: '100',
        callingCode: '+359'
    },
    {
        name: 'Burkina Faso',
        alpha2Code: 'BF',
        alpha3Code: 'BFA',
        numericCode: '854',
        callingCode: '+226'
    },
    {
        name: 'Burundi',
        alpha2Code: 'BI',
        alpha3Code: 'BDI',
        numericCode: '108',
        callingCode: '+257'
    },
    {
        name: 'Cambodia',
        alpha2Code: 'KH',
        alpha3Code: 'KHM',
        numericCode: '116',
        callingCode: '+855'
    },
    {
        name: 'Cameroon',
        alpha2Code: 'CM',
        alpha3Code: 'CMR',
        numericCode: '120',
        callingCode: '+237'
    },
    {
        name: 'Canada',
        alpha2Code: 'CA',
        alpha3Code: 'CAN',
        numericCode: '124',
        callingCode: '+1'
    },
    {
        name: 'Cabo Verde',
        alpha2Code: 'CV',
        alpha3Code: 'CPV',
        numericCode: '132',
        callingCode: '+238'
    },
    {
        name: 'Cayman Islands',
        alpha2Code: 'KY',
        alpha3Code: 'CYM',
        numericCode: '136',
        callingCode: '+1345'
    },
    {
        name: 'Central African Republic',
        alpha2Code: 'CF',
        alpha3Code: 'CAF',
        numericCode: '140',
        callingCode: '+236'
    },
    {
        name: 'Chad',
        alpha2Code: 'TD',
        alpha3Code: 'TCD',
        numericCode: '148',
        callingCode: '+235'
    },
    {
        name: 'Chile',
        alpha2Code: 'CL',
        alpha3Code: 'CHL',
        numericCode: '152',
        callingCode: '+56'
    },
    {
        name: 'China',
        alpha2Code: 'CN',
        alpha3Code: 'CHN',
        numericCode: '156',
        callingCode: '+86'
    },
    {
        name: 'Christmas Island',
        alpha2Code: 'CX',
        alpha3Code: 'CXR',
        numericCode: '162',
        callingCode: '+61'
    },
    {
        name: 'Cocos (Keeling) Islands',
        alpha2Code: 'CC',
        alpha3Code: 'CCK',
        numericCode: '166',
        callingCode: '+61'
    },
    {
        name: 'Colombia',
        alpha2Code: 'CO',
        alpha3Code: 'COL',
        numericCode: '170',
        callingCode: '+57'
    },
    {
        name: 'Comoros',
        alpha2Code: 'KM',
        alpha3Code: 'COM',
        numericCode: '174',
        callingCode: '+269'
    },
    {
        name: 'Congo',
        alpha2Code: 'CG',
        alpha3Code: 'COG',
        numericCode: '178',
        callingCode: '+242'
    },
    {
        name: 'Congo (Democratic Republic of the)',
        alpha2Code: 'CD',
        alpha3Code: 'COD',
        numericCode: '180',
        callingCode: '+243'
    },
    {
        name: 'Cook Islands',
        alpha2Code: 'CK',
        alpha3Code: 'COK',
        numericCode: '184',
        callingCode: '+682'
    },
    {
        name: 'Costa Rica',
        alpha2Code: 'CR',
        alpha3Code: 'CRI',
        numericCode: '188',
        callingCode: '+506'
    },
    {
        name: 'Croatia',
        alpha2Code: 'HR',
        alpha3Code: 'HRV',
        numericCode: '191',
        callingCode: '+385'
    },
    {
        name: 'Cuba',
        alpha2Code: 'CU',
        alpha3Code: 'CUB',
        numericCode: '192',
        callingCode: '+53'
    },
    {
        name: 'Curaçao',
        alpha2Code: 'CW',
        alpha3Code: 'CUW',
        numericCode: '531',
        callingCode: '+599'
    },
    {
        name: 'Cyprus',
        alpha2Code: 'CY',
        alpha3Code: 'CYP',
        numericCode: '196',
        callingCode: '+357'
    },
    {
        name: 'Czech Republic',
        alpha2Code: 'CZ',
        alpha3Code: 'CZE',
        numericCode: '203',
        callingCode: '+420'
    },
    {
        name: 'Denmark',
        alpha2Code: 'DK',
        alpha3Code: 'DNK',
        numericCode: '208',
        callingCode: '+45'
    },
    {
        name: 'Djibouti',
        alpha2Code: 'DJ',
        alpha3Code: 'DJI',
        numericCode: '262',
        callingCode: '+253'
    },
    {
        name: 'Dominica',
        alpha2Code: 'DM',
        alpha3Code: 'DMA',
        numericCode: '212',
        callingCode: '+1767'
    },
    {
        name: 'Dominican Republic',
        alpha2Code: 'DO',
        alpha3Code: 'DOM',
        numericCode: '214',
        callingCode: '+1809'
    },
    {
        name: 'Ecuador',
        alpha2Code: 'EC',
        alpha3Code: 'ECU',
        numericCode: '218',
        callingCode: '+593'
    },
    {
        name: 'Egypt',
        alpha2Code: 'EG',
        alpha3Code: 'EGY',
        numericCode: '818',
        callingCode: '+20'
    },
    {
        name: 'El Salvador',
        alpha2Code: 'SV',
        alpha3Code: 'SLV',
        numericCode: '222',
        callingCode: '+503'
    },
    {
        name: 'Equatorial Guinea',
        alpha2Code: 'GQ',
        alpha3Code: 'GNQ',
        numericCode: '226',
        callingCode: '+240'
    },
    {
        name: 'Eritrea',
        alpha2Code: 'ER',
        alpha3Code: 'ERI',
        numericCode: '232',
        callingCode: '+291'
    },
    {
        name: 'Estonia',
        alpha2Code: 'EE',
        alpha3Code: 'EST',
        numericCode: '233',
        callingCode: '+372'
    },
    {
        name: 'Ethiopia',
        alpha2Code: 'ET',
        alpha3Code: 'ETH',
        numericCode: '231',
        callingCode: '+251'
    },
    {
        name: 'Falkland Islands (Malvinas)',
        alpha2Code: 'FK',
        alpha3Code: 'FLK',
        numericCode: '238',
        callingCode: '+500'
    },
    {
        name: 'Faroe Islands',
        alpha2Code: 'FO',
        alpha3Code: 'FRO',
        numericCode: '234',
        callingCode: '+298'
    },
    {
        name: 'Fiji',
        alpha2Code: 'FJ',
        alpha3Code: 'FJI',
        numericCode: '242',
        callingCode: '+679'
    },
    {
        name: 'Finland',
        alpha2Code: 'FI',
        alpha3Code: 'FIN',
        numericCode: '246',
        callingCode: '+358'
    },
    {
        name: 'France',
        alpha2Code: 'FR',
        alpha3Code: 'FRA',
        numericCode: '250',
        callingCode: '+33'
    },
    {
        name: 'French Guiana',
        alpha2Code: 'GF',
        alpha3Code: 'GUF',
        numericCode: '254',
        callingCode: '+594'
    },
    {
        name: 'French Polynesia',
        alpha2Code: 'PF',
        alpha3Code: 'PYF',
        numericCode: '258',
        callingCode: '+689'
    },
    {
        name: 'French Southern Territories',
        alpha2Code: 'TF',
        alpha3Code: 'ATF',
        numericCode: '260',
        callingCode: '+'
    },
    {
        name: 'Gabon',
        alpha2Code: 'GA',
        alpha3Code: 'GAB',
        numericCode: '266',
        callingCode: '+241'
    },
    {
        name: 'Gambia',
        alpha2Code: 'GM',
        alpha3Code: 'GMB',
        numericCode: '270',
        callingCode: '+220'
    },
    {
        name: 'Georgia',
        alpha2Code: 'GE',
        alpha3Code: 'GEO',
        numericCode: '268',
        callingCode: '+995'
    },
    {
        name: 'Germany',
        alpha2Code: 'DE',
        alpha3Code: 'DEU',
        numericCode: '276',
        callingCode: '+49'
    },
    {
        name: 'Ghana',
        alpha2Code: 'GH',
        alpha3Code: 'GHA',
        numericCode: '288',
        callingCode: '+233'
    },
    {
        name: 'Gibraltar',
        alpha2Code: 'GI',
        alpha3Code: 'GIB',
        numericCode: '292',
        callingCode: '+350'
    },
    {
        name: 'Greece',
        alpha2Code: 'GR',
        alpha3Code: 'GRC',
        numericCode: '300',
        callingCode: '+30'
    },
    {
        name: 'Greenland',
        alpha2Code: 'GL',
        alpha3Code: 'GRL',
        numericCode: '304',
        callingCode: '+299'
    },
    {
        name: 'Grenada',
        alpha2Code: 'GD',
        alpha3Code: 'GRD',
        numericCode: '308',
        callingCode: '+1473'
    },
    {
        name: 'Guadeloupe',
        alpha2Code: 'GP',
        alpha3Code: 'GLP',
        numericCode: '312',
        callingCode: '+590'
    },
    {
        name: 'Guam',
        alpha2Code: 'GU',
        alpha3Code: 'GUM',
        numericCode: '316',
        callingCode: '+1671'
    },
    {
        name: 'Guatemala',
        alpha2Code: 'GT',
        alpha3Code: 'GTM',
        numericCode: '320',
        callingCode: '+502'
    },
    {
        name: 'Guernsey',
        alpha2Code: 'GG',
        alpha3Code: 'GGY',
        numericCode: '831',
        callingCode: '+44'
    },
    {
        name: 'Guinea',
        alpha2Code: 'GN',
        alpha3Code: 'GIN',
        numericCode: '324',
        callingCode: '+224'
    },
    {
        name: 'Guinea-Bissau',
        alpha2Code: 'GW',
        alpha3Code: 'GNB',
        numericCode: '624',
        callingCode: '+245'
    },
    {
        name: 'Guyana',
        alpha2Code: 'GY',
        alpha3Code: 'GUY',
        numericCode: '328',
        callingCode: '+592'
    },
    {
        name: 'Haiti',
        alpha2Code: 'HT',
        alpha3Code: 'HTI',
        numericCode: '332',
        callingCode: '+509'
    },
    {
        name: 'Heard Island and McDonald Islands',
        alpha2Code: 'HM',
        alpha3Code: 'HMD',
        numericCode: '334',
        callingCode: '+'
    },
    {
        name: 'Holy See',
        alpha2Code: 'VA',
        alpha3Code: 'VAT',
        numericCode: '336',
        callingCode: '+379'
    },
    {
        name: 'Honduras',
        alpha2Code: 'HN',
        alpha3Code: 'HND',
        numericCode: '340',
        callingCode: '+504'
    },
    {
        name: 'Hong Kong',
        alpha2Code: 'HK',
        alpha3Code: 'HKG',
        numericCode: '344',
        callingCode: '+852'
    },
    {
        name: 'Hungary',
        alpha2Code: 'HU',
        alpha3Code: 'HUN',
        numericCode: '348',
        callingCode: '+36'
    },
    {
        name: 'Iceland',
        alpha2Code: 'IS',
        alpha3Code: 'ISL',
        numericCode: '352',
        callingCode: '+354'
    },
    {
        name: 'India',
        alpha2Code: 'IN',
        alpha3Code: 'IND',
        numericCode: '356',
        callingCode: '+91'
    },
    {
        name: 'Indonesia',
        alpha2Code: 'ID',
        alpha3Code: 'IDN',
        numericCode: '360',
        callingCode: '+62'
    },
    {
        name: "Côte d'Ivoire",
        alpha2Code: 'CI',
        alpha3Code: 'CIV',
        numericCode: '384',
        callingCode: '+225'
    },
    {
        name: 'Iran (Islamic Republic of)',
        alpha2Code: 'IR',
        alpha3Code: 'IRN',
        numericCode: '364',
        callingCode: '+98'
    },
    {
        name: 'Iraq',
        alpha2Code: 'IQ',
        alpha3Code: 'IRQ',
        numericCode: '368',
        callingCode: '+964'
    },
    {
        name: 'Ireland',
        alpha2Code: 'IE',
        alpha3Code: 'IRL',
        numericCode: '372',
        callingCode: '+353'
    },
    {
        name: 'Isle of Man',
        alpha2Code: 'IM',
        alpha3Code: 'IMN',
        numericCode: '833',
        callingCode: '+44'
    },
    {
        name: 'Israel',
        alpha2Code: 'IL',
        alpha3Code: 'ISR',
        numericCode: '376',
        callingCode: '+972'
    },
    {
        name: 'Italy',
        alpha2Code: 'IT',
        alpha3Code: 'ITA',
        numericCode: '380',
        callingCode: '+39'
    },
    {
        name: 'Jamaica',
        alpha2Code: 'JM',
        alpha3Code: 'JAM',
        numericCode: '388',
        callingCode: '+1876'
    },
    {
        name: 'Japan',
        alpha2Code: 'JP',
        alpha3Code: 'JPN',
        numericCode: '392',
        callingCode: '+81'
    },
    {
        name: 'Jersey',
        alpha2Code: 'JE',
        alpha3Code: 'JEY',
        numericCode: '832',
        callingCode: '+44'
    },
    {
        name: 'Jordan',
        alpha2Code: 'JO',
        alpha3Code: 'JOR',
        numericCode: '400',
        callingCode: '+962'
    },
    {
        name: 'Kazakhstan',
        alpha2Code: 'KZ',
        alpha3Code: 'KAZ',
        numericCode: '398',
        callingCode: '+7'
    },
    {
        name: 'Kenya',
        alpha2Code: 'KE',
        alpha3Code: 'KEN',
        numericCode: '404',
        callingCode: '+254'
    },
    {
        name: 'Kiribati',
        alpha2Code: 'KI',
        alpha3Code: 'KIR',
        numericCode: '296',
        callingCode: '+686'
    },
    {
        name: 'Kuwait',
        alpha2Code: 'KW',
        alpha3Code: 'KWT',
        numericCode: '414',
        callingCode: '+965'
    },
    {
        name: 'Kyrgyzstan',
        alpha2Code: 'KG',
        alpha3Code: 'KGZ',
        numericCode: '417',
        callingCode: '+996'
    },
    {
        name: "Lao People's Democratic Republic",
        alpha2Code: 'LA',
        alpha3Code: 'LAO',
        numericCode: '418',
        callingCode: '+856'
    },
    {
        name: 'Latvia',
        alpha2Code: 'LV',
        alpha3Code: 'LVA',
        numericCode: '428',
        callingCode: '+371'
    },
    {
        name: 'Lebanon',
        alpha2Code: 'LB',
        alpha3Code: 'LBN',
        numericCode: '422',
        callingCode: '+961'
    },
    {
        name: 'Lesotho',
        alpha2Code: 'LS',
        alpha3Code: 'LSO',
        numericCode: '426',
        callingCode: '+266'
    },
    {
        name: 'Liberia',
        alpha2Code: 'LR',
        alpha3Code: 'LBR',
        numericCode: '430',
        callingCode: '+231'
    },
    {
        name: 'Libya',
        alpha2Code: 'LY',
        alpha3Code: 'LBY',
        numericCode: '434',
        callingCode: '+218'
    },
    {
        name: 'Liechtenstein',
        alpha2Code: 'LI',
        alpha3Code: 'LIE',
        numericCode: '438',
        callingCode: '+423'
    },
    {
        name: 'Lithuania',
        alpha2Code: 'LT',
        alpha3Code: 'LTU',
        numericCode: '440',
        callingCode: '+370'
    },
    {
        name: 'Luxembourg',
        alpha2Code: 'LU',
        alpha3Code: 'LUX',
        numericCode: '442',
        callingCode: '+352'
    },
    {
        name: 'Macao',
        alpha2Code: 'MO',
        alpha3Code: 'MAC',
        numericCode: '446',
        callingCode: '+853'
    },
    {
        name: 'Macedonia (the former Yugoslav Republic of)',
        alpha2Code: 'MK',
        alpha3Code: 'MKD',
        numericCode: '807',
        callingCode: '+389'
    },
    {
        name: 'Madagascar',
        alpha2Code: 'MG',
        alpha3Code: 'MDG',
        numericCode: '450',
        callingCode: '+261'
    },
    {
        name: 'Malawi',
        alpha2Code: 'MW',
        alpha3Code: 'MWI',
        numericCode: '454',
        callingCode: '+265'
    },
    {
        name: 'Malaysia',
        alpha2Code: 'MY',
        alpha3Code: 'MYS',
        numericCode: '458',
        callingCode: '+60'
    },
    {
        name: 'Maldives',
        alpha2Code: 'MV',
        alpha3Code: 'MDV',
        numericCode: '462',
        callingCode: '+960'
    },
    {
        name: 'Mali',
        alpha2Code: 'ML',
        alpha3Code: 'MLI',
        numericCode: '466',
        callingCode: '+223'
    },
    {
        name: 'Malta',
        alpha2Code: 'MT',
        alpha3Code: 'MLT',
        numericCode: '470',
        callingCode: '+356'
    },
    {
        name: 'Marshall Islands',
        alpha2Code: 'MH',
        alpha3Code: 'MHL',
        numericCode: '584',
        callingCode: '+692'
    },
    {
        name: 'Martinique',
        alpha2Code: 'MQ',
        alpha3Code: 'MTQ',
        numericCode: '474',
        callingCode: '+596'
    },
    {
        name: 'Mauritania',
        alpha2Code: 'MR',
        alpha3Code: 'MRT',
        numericCode: '478',
        callingCode: '+222'
    },
    {
        name: 'Mauritius',
        alpha2Code: 'MU',
        alpha3Code: 'MUS',
        numericCode: '480',
        callingCode: '+230'
    },
    {
        name: 'Mayotte',
        alpha2Code: 'YT',
        alpha3Code: 'MYT',
        numericCode: '175',
        callingCode: '+262'
    },
    {
        name: 'Mexico',
        alpha2Code: 'MX',
        alpha3Code: 'MEX',
        numericCode: '484',
        callingCode: '+52'
    },
    {
        name: 'Micronesia (Federated States of)',
        alpha2Code: 'FM',
        alpha3Code: 'FSM',
        numericCode: '583',
        callingCode: '+691'
    },
    {
        name: 'Moldova (Republic of)',
        alpha2Code: 'MD',
        alpha3Code: 'MDA',
        numericCode: '498',
        callingCode: '+373'
    },
    {
        name: 'Monaco',
        alpha2Code: 'MC',
        alpha3Code: 'MCO',
        numericCode: '492',
        callingCode: '+377'
    },
    {
        name: 'Mongolia',
        alpha2Code: 'MN',
        alpha3Code: 'MNG',
        numericCode: '496',
        callingCode: '+976'
    },
    {
        name: 'Montenegro',
        alpha2Code: 'ME',
        alpha3Code: 'MNE',
        numericCode: '499',
        callingCode: '+382'
    },
    {
        name: 'Montserrat',
        alpha2Code: 'MS',
        alpha3Code: 'MSR',
        numericCode: '500',
        callingCode: '+1664'
    },
    {
        name: 'Morocco',
        alpha2Code: 'MA',
        alpha3Code: 'MAR',
        numericCode: '504',
        callingCode: '+212'
    },
    {
        name: 'Mozambique',
        alpha2Code: 'MZ',
        alpha3Code: 'MOZ',
        numericCode: '508',
        callingCode: '+258'
    },
    {
        name: 'Myanmar',
        alpha2Code: 'MM',
        alpha3Code: 'MMR',
        numericCode: '104',
        callingCode: '+95'
    },
    {
        name: 'Namibia',
        alpha2Code: 'NA',
        alpha3Code: 'NAM',
        numericCode: '516',
        callingCode: '+264'
    },
    {
        name: 'Nauru',
        alpha2Code: 'NR',
        alpha3Code: 'NRU',
        numericCode: '520',
        callingCode: '+674'
    },
    {
        name: 'Nepal',
        alpha2Code: 'NP',
        alpha3Code: 'NPL',
        numericCode: '524',
        callingCode: '+977'
    },
    {
        name: 'Netherlands',
        alpha2Code: 'NL',
        alpha3Code: 'NLD',
        numericCode: '528',
        callingCode: '+31'
    },
    {
        name: 'New Caledonia',
        alpha2Code: 'NC',
        alpha3Code: 'NCL',
        numericCode: '540',
        callingCode: '+687'
    },
    {
        name: 'New Zealand',
        alpha2Code: 'NZ',
        alpha3Code: 'NZL',
        numericCode: '554',
        callingCode: '+64'
    },
    {
        name: 'Nicaragua',
        alpha2Code: 'NI',
        alpha3Code: 'NIC',
        numericCode: '558',
        callingCode: '+505'
    },
    {
        name: 'Niger',
        alpha2Code: 'NE',
        alpha3Code: 'NER',
        numericCode: '562',
        callingCode: '+227'
    },
    {
        name: 'Nigeria',
        alpha2Code: 'NG',
        alpha3Code: 'NGA',
        numericCode: '566',
        callingCode: '+234'
    },
    {
        name: 'Niue',
        alpha2Code: 'NU',
        alpha3Code: 'NIU',
        numericCode: '570',
        callingCode: '+683'
    },
    {
        name: 'Norfolk Island',
        alpha2Code: 'NF',
        alpha3Code: 'NFK',
        numericCode: '574',
        callingCode: '+672'
    },
    {
        name: "Korea (Democratic People's Republic of)",
        alpha2Code: 'KP',
        alpha3Code: 'PRK',
        numericCode: '408',
        callingCode: '+850'
    },
    {
        name: 'Northern Mariana Islands',
        alpha2Code: 'MP',
        alpha3Code: 'MNP',
        numericCode: '580',
        callingCode: '+1670'
    },
    {
        name: 'Norway',
        alpha2Code: 'NO',
        alpha3Code: 'NOR',
        numericCode: '578',
        callingCode: '+47'
    },
    {
        name: 'Oman',
        alpha2Code: 'OM',
        alpha3Code: 'OMN',
        numericCode: '512',
        callingCode: '+968'
    },
    {
        name: 'Pakistan',
        alpha2Code: 'PK',
        alpha3Code: 'PAK',
        numericCode: '586',
        callingCode: '+92'
    },
    {
        name: 'Palau',
        alpha2Code: 'PW',
        alpha3Code: 'PLW',
        numericCode: '585',
        callingCode: '+680'
    },
    {
        name: 'Palestine, State of',
        alpha2Code: 'PS',
        alpha3Code: 'PSE',
        numericCode: '275',
        callingCode: '+970'
    },
    {
        name: 'Panama',
        alpha2Code: 'PA',
        alpha3Code: 'PAN',
        numericCode: '591',
        callingCode: '+507'
    },
    {
        name: 'Papua New Guinea',
        alpha2Code: 'PG',
        alpha3Code: 'PNG',
        numericCode: '598',
        callingCode: '+675'
    },
    {
        name: 'Paraguay',
        alpha2Code: 'PY',
        alpha3Code: 'PRY',
        numericCode: '600',
        callingCode: '+595'
    },
    {
        name: 'Peru',
        alpha2Code: 'PE',
        alpha3Code: 'PER',
        numericCode: '604',
        callingCode: '+51'
    },
    {
        name: 'Philippines',
        alpha2Code: 'PH',
        alpha3Code: 'PHL',
        numericCode: '608',
        callingCode: '+63'
    },
    {
        name: 'Pitcairn',
        alpha2Code: 'PN',
        alpha3Code: 'PCN',
        numericCode: '612',
        callingCode: '+64'
    },
    {
        name: 'Poland',
        alpha2Code: 'PL',
        alpha3Code: 'POL',
        numericCode: '616',
        callingCode: '+48'
    },
    {
        name: 'Portugal',
        alpha2Code: 'PT',
        alpha3Code: 'PRT',
        numericCode: '620',
        callingCode: '+351'
    },
    {
        name: 'Puerto Rico',
        alpha2Code: 'PR',
        alpha3Code: 'PRI',
        numericCode: '630',
        callingCode: '+1'
    },
    {
        name: 'Qatar',
        alpha2Code: 'QA',
        alpha3Code: 'QAT',
        numericCode: '634',
        callingCode: '+974'
    },
    {
        name: 'Republic of Kosovo',
        alpha2Code: 'XK',
        alpha3Code: 'KOS',
        numericCode: null,
        callingCode: '+383'
    },
    {
        name: 'Réunion',
        alpha2Code: 'RE',
        alpha3Code: 'REU',
        numericCode: '638',
        callingCode: '+262'
    },
    {
        name: 'Romania',
        alpha2Code: 'RO',
        alpha3Code: 'ROU',
        numericCode: '642',
        callingCode: '+40'
    },
    {
        name: 'Russian Federation',
        alpha2Code: 'RU',
        alpha3Code: 'RUS',
        numericCode: '643',
        callingCode: '+7'
    },
    {
        name: 'Rwanda',
        alpha2Code: 'RW',
        alpha3Code: 'RWA',
        numericCode: '646',
        callingCode: '+250'
    },
    {
        name: 'Saint Barthélemy',
        alpha2Code: 'BL',
        alpha3Code: 'BLM',
        numericCode: '652',
        callingCode: '+590'
    },
    {
        name: 'Saint Helena, Ascension and Tristan da Cunha',
        alpha2Code: 'SH',
        alpha3Code: 'SHN',
        numericCode: '654',
        callingCode: '+290'
    },
    {
        name: 'Saint Kitts and Nevis',
        alpha2Code: 'KN',
        alpha3Code: 'KNA',
        numericCode: '659',
        callingCode: '+1869'
    },
    {
        name: 'Saint Lucia',
        alpha2Code: 'LC',
        alpha3Code: 'LCA',
        numericCode: '662',
        callingCode: '+1758'
    },
    {
        name: 'Saint Martin (French part)',
        alpha2Code: 'MF',
        alpha3Code: 'MAF',
        numericCode: '663',
        callingCode: '+590'
    },
    {
        name: 'Saint Pierre and Miquelon',
        alpha2Code: 'PM',
        alpha3Code: 'SPM',
        numericCode: '666',
        callingCode: '+508'
    },
    {
        name: 'Saint Vincent and the Grenadines',
        alpha2Code: 'VC',
        alpha3Code: 'VCT',
        numericCode: '670',
        callingCode: '+1784'
    },
    {
        name: 'Samoa',
        alpha2Code: 'WS',
        alpha3Code: 'WSM',
        numericCode: '882',
        callingCode: '+685'
    },
    {
        name: 'San Marino',
        alpha2Code: 'SM',
        alpha3Code: 'SMR',
        numericCode: '674',
        callingCode: '+378'
    },
    {
        name: 'Sao Tome and Principe',
        alpha2Code: 'ST',
        alpha3Code: 'STP',
        numericCode: '678',
        callingCode: '+239'
    },
    {
        name: 'Saudi Arabia',
        alpha2Code: 'SA',
        alpha3Code: 'SAU',
        numericCode: '682',
        callingCode: '+966'
    },
    {
        name: 'Senegal',
        alpha2Code: 'SN',
        alpha3Code: 'SEN',
        numericCode: '686',
        callingCode: '+221'
    },
    {
        name: 'Serbia',
        alpha2Code: 'RS',
        alpha3Code: 'SRB',
        numericCode: '688',
        callingCode: '+381'
    },
    {
        name: 'Seychelles',
        alpha2Code: 'SC',
        alpha3Code: 'SYC',
        numericCode: '690',
        callingCode: '+248'
    },
    {
        name: 'Sierra Leone',
        alpha2Code: 'SL',
        alpha3Code: 'SLE',
        numericCode: '694',
        callingCode: '+232'
    },
    {
        name: 'Singapore',
        alpha2Code: 'SG',
        alpha3Code: 'SGP',
        numericCode: '702',
        callingCode: '+65'
    },
    {
        name: 'Sint Maarten (Dutch part)',
        alpha2Code: 'SX',
        alpha3Code: 'SXM',
        numericCode: '534',
        callingCode: '+1721'
    },
    {
        name: 'Slovakia',
        alpha2Code: 'SK',
        alpha3Code: 'SVK',
        numericCode: '703',
        callingCode: '+421'
    },
    {
        name: 'Slovenia',
        alpha2Code: 'SI',
        alpha3Code: 'SVN',
        numericCode: '705',
        callingCode: '+386'
    },
    {
        name: 'Solomon Islands',
        alpha2Code: 'SB',
        alpha3Code: 'SLB',
        numericCode: '090',
        callingCode: '+677'
    },
    {
        name: 'Somalia',
        alpha2Code: 'SO',
        alpha3Code: 'SOM',
        numericCode: '706',
        callingCode: '+252'
    },
    {
        name: 'South Africa',
        alpha2Code: 'ZA',
        alpha3Code: 'ZAF',
        numericCode: '710',
        callingCode: '+27'
    },
    {
        name: 'South Georgia and the South Sandwich Islands',
        alpha2Code: 'GS',
        alpha3Code: 'SGS',
        numericCode: '239',
        callingCode: '+500'
    },
    {
        name: 'Korea (Republic of)',
        alpha2Code: 'KR',
        alpha3Code: 'KOR',
        numericCode: '410',
        callingCode: '+82'
    },
    {
        name: 'South Sudan',
        alpha2Code: 'SS',
        alpha3Code: 'SSD',
        numericCode: '728',
        callingCode: '+211'
    },
    {
        name: 'Spain',
        alpha2Code: 'ES',
        alpha3Code: 'ESP',
        numericCode: '724',
        callingCode: '+34'
    },
    {
        name: 'Sri Lanka',
        alpha2Code: 'LK',
        alpha3Code: 'LKA',
        numericCode: '144',
        callingCode: '+94'
    },
    {
        name: 'Sudan',
        alpha2Code: 'SD',
        alpha3Code: 'SDN',
        numericCode: '729',
        callingCode: '+249'
    },
    {
        name: 'Suriname',
        alpha2Code: 'SR',
        alpha3Code: 'SUR',
        numericCode: '740',
        callingCode: '+597'
    },
    {
        name: 'Svalbard and Jan Mayen',
        alpha2Code: 'SJ',
        alpha3Code: 'SJM',
        numericCode: '744',
        callingCode: '+4779'
    },
    {
        name: 'Swaziland',
        alpha2Code: 'SZ',
        alpha3Code: 'SWZ',
        numericCode: '748',
        callingCode: '+268'
    },
    {
        name: 'Sweden',
        alpha2Code: 'SE',
        alpha3Code: 'SWE',
        numericCode: '752',
        callingCode: '+46'
    },
    {
        name: 'Switzerland',
        alpha2Code: 'CH',
        alpha3Code: 'CHE',
        numericCode: '756',
        callingCode: '+41'
    },
    {
        name: 'Syrian Arab Republic',
        alpha2Code: 'SY',
        alpha3Code: 'SYR',
        numericCode: '760',
        callingCode: '+963'
    },
    {
        name: 'Taiwan, China',
        alpha2Code: 'TW',
        alpha3Code: 'TWN',
        numericCode: '158',
        callingCode: '+886'
    },
    {
        name: 'Tajikistan',
        alpha2Code: 'TJ',
        alpha3Code: 'TJK',
        numericCode: '762',
        callingCode: '+992'
    },
    {
        name: 'Tanzania, United Republic of',
        alpha2Code: 'TZ',
        alpha3Code: 'TZA',
        numericCode: '834',
        callingCode: '+255'
    },
    {
        name: 'Thailand',
        alpha2Code: 'TH',
        alpha3Code: 'THA',
        numericCode: '764',
        callingCode: '+66'
    },
    {
        name: 'Timor-Leste',
        alpha2Code: 'TL',
        alpha3Code: 'TLS',
        numericCode: '626',
        callingCode: '+670'
    },
    {
        name: 'Togo',
        alpha2Code: 'TG',
        alpha3Code: 'TGO',
        numericCode: '768',
        callingCode: '+228'
    },
    {
        name: 'Tokelau',
        alpha2Code: 'TK',
        alpha3Code: 'TKL',
        numericCode: '772',
        callingCode: '+690'
    },
    {
        name: 'Tonga',
        alpha2Code: 'TO',
        alpha3Code: 'TON',
        numericCode: '776',
        callingCode: '+676'
    },
    {
        name: 'Trinidad and Tobago',
        alpha2Code: 'TT',
        alpha3Code: 'TTO',
        numericCode: '780',
        callingCode: '+1868'
    },
    {
        name: 'Tunisia',
        alpha2Code: 'TN',
        alpha3Code: 'TUN',
        numericCode: '788',
        callingCode: '+216'
    },
    {
        name: 'Turkey',
        alpha2Code: 'TR',
        alpha3Code: 'TUR',
        numericCode: '792',
        callingCode: '+90'
    },
    {
        name: 'Turkmenistan',
        alpha2Code: 'TM',
        alpha3Code: 'TKM',
        numericCode: '795',
        callingCode: '+993'
    },
    {
        name: 'Turks and Caicos Islands',
        alpha2Code: 'TC',
        alpha3Code: 'TCA',
        numericCode: '796',
        callingCode: '+1649'
    },
    {
        name: 'Tuvalu',
        alpha2Code: 'TV',
        alpha3Code: 'TUV',
        numericCode: '798',
        callingCode: '+688'
    },
    {
        name: 'Uganda',
        alpha2Code: 'UG',
        alpha3Code: 'UGA',
        numericCode: '800',
        callingCode: '+256'
    },
    {
        name: 'Ukraine',
        alpha2Code: 'UA',
        alpha3Code: 'UKR',
        numericCode: '804',
        callingCode: '+380'
    },
    {
        name: 'United Arab Emirates',
        alpha2Code: 'AE',
        alpha3Code: 'ARE',
        numericCode: '784',
        callingCode: '+971'
    },
    {
        name: 'United Kingdom of Great Britain and Northern Ireland',
        alpha2Code: 'GB',
        alpha3Code: 'GBR',
        numericCode: '826',
        callingCode: '+44'
    },
    {
        name: 'United States of America',
        alpha2Code: 'US',
        alpha3Code: 'USA',
        numericCode: '840',
        callingCode: '+1'
    },
    {
        name: 'Uruguay',
        alpha2Code: 'UY',
        alpha3Code: 'URY',
        numericCode: '858',
        callingCode: '+598'
    },
    {
        name: 'Uzbekistan',
        alpha2Code: 'UZ',
        alpha3Code: 'UZB',
        numericCode: '860',
        callingCode: '+998'
    },
    {
        name: 'Vanuatu',
        alpha2Code: 'VU',
        alpha3Code: 'VUT',
        numericCode: '548',
        callingCode: '+678'
    },
    {
        name: 'Venezuela (Bolivarian Republic of)',
        alpha2Code: 'VE',
        alpha3Code: 'VEN',
        numericCode: '862',
        callingCode: '+58'
    },
    {
        name: 'Viet Nam',
        alpha2Code: 'VN',
        alpha3Code: 'VNM',
        numericCode: '704',
        callingCode: '+84'
    },
    {
        name: 'Wallis and Futuna',
        alpha2Code: 'WF',
        alpha3Code: 'WLF',
        numericCode: '876',
        callingCode: '+681'
    },
    {
        name: 'Western Sahara',
        alpha2Code: 'EH',
        alpha3Code: 'ESH',
        numericCode: '732',
        callingCode: '+212'
    },
    {
        name: 'Yemen',
        alpha2Code: 'YE',
        alpha3Code: 'YEM',
        numericCode: '887',
        callingCode: '+967'
    },
    {
        name: 'Zambia',
        alpha2Code: 'ZM',
        alpha3Code: 'ZMB',
        numericCode: '894',
        callingCode: '+260'
    },
    {
        name: 'Zimbabwe',
        alpha2Code: 'ZW',
        alpha3Code: 'ZWE',
        numericCode: '716',
        callingCode: '+263'
    }
];

var en = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB: COUNTRIES_DB
});

const COUNTRIES_DB_ES = [
    {
        name: 'Afganistán',
        alpha2Code: 'AF',
        alpha3Code: 'AFG',
        numericCode: '004',
        callingCode: '+93'
    },
    {
        name: 'Alandia',
        alpha2Code: 'AX',
        alpha3Code: 'ALA',
        numericCode: '248',
        callingCode: '+358'
    },
    {
        name: 'Albania',
        alpha2Code: 'AL',
        alpha3Code: 'ALB',
        numericCode: '008',
        callingCode: '+355'
    },
    {
        name: 'Argelia',
        alpha2Code: 'DZ',
        alpha3Code: 'DZA',
        numericCode: '012',
        callingCode: '+213'
    },
    {
        name: 'Samoa Americana',
        alpha2Code: 'AS',
        alpha3Code: 'ASM',
        numericCode: '016',
        callingCode: '+1684'
    },
    {
        name: 'Andorra',
        alpha2Code: 'AD',
        alpha3Code: 'AND',
        numericCode: '020',
        callingCode: '+376'
    },
    {
        name: 'Angola',
        alpha2Code: 'AO',
        alpha3Code: 'AGO',
        numericCode: '024',
        callingCode: '+244'
    },
    {
        name: 'Anguilla',
        alpha2Code: 'AI',
        alpha3Code: 'AIA',
        numericCode: '660',
        callingCode: '+1264'
    },
    {
        name: 'Antártida',
        alpha2Code: 'AQ',
        alpha3Code: 'ATA',
        numericCode: '010',
        callingCode: '+672'
    },
    {
        name: 'Antigua y Barbuda',
        alpha2Code: 'AG',
        alpha3Code: 'ATG',
        numericCode: '028',
        callingCode: '+1268'
    },
    {
        name: 'Argentina',
        alpha2Code: 'AR',
        alpha3Code: 'ARG',
        numericCode: '032',
        callingCode: '+54'
    },
    {
        name: 'Armenia',
        alpha2Code: 'AM',
        alpha3Code: 'ARM',
        numericCode: '051',
        callingCode: '+374'
    },
    {
        name: 'Aruba',
        alpha2Code: 'AW',
        alpha3Code: 'ABW',
        numericCode: '533',
        callingCode: '+297'
    },
    {
        name: 'Australia',
        alpha2Code: 'AU',
        alpha3Code: 'AUS',
        numericCode: '036',
        callingCode: '+61'
    },
    {
        name: 'Austria',
        alpha2Code: 'AT',
        alpha3Code: 'AUT',
        numericCode: '040',
        callingCode: '+43'
    },
    {
        name: 'Azerbaiyán',
        alpha2Code: 'AZ',
        alpha3Code: 'AZE',
        numericCode: '031',
        callingCode: '+994'
    },
    {
        name: 'Bahamas',
        alpha2Code: 'BS',
        alpha3Code: 'BHS',
        numericCode: '044',
        callingCode: '+1242'
    },
    {
        name: 'Bahrein',
        alpha2Code: 'BH',
        alpha3Code: 'BHR',
        numericCode: '048',
        callingCode: '+973'
    },
    {
        name: 'Bangladesh',
        alpha2Code: 'BD',
        alpha3Code: 'BGD',
        numericCode: '050',
        callingCode: '+880'
    },
    {
        name: 'Barbados',
        alpha2Code: 'BB',
        alpha3Code: 'BRB',
        numericCode: '052',
        callingCode: '+1246'
    },
    {
        name: 'Bielorrusia',
        alpha2Code: 'BY',
        alpha3Code: 'BLR',
        numericCode: '112',
        callingCode: '+375'
    },
    {
        name: 'Bélgica',
        alpha2Code: 'BE',
        alpha3Code: 'BEL',
        numericCode: '056',
        callingCode: '+32'
    },
    {
        name: 'Belice',
        alpha2Code: 'BZ',
        alpha3Code: 'BLZ',
        numericCode: '084',
        callingCode: '+501'
    },
    {
        name: 'Benín',
        alpha2Code: 'BJ',
        alpha3Code: 'BEN',
        numericCode: '204',
        callingCode: '+229'
    },
    {
        name: 'Bermudas',
        alpha2Code: 'BM',
        alpha3Code: 'BMU',
        numericCode: '060',
        callingCode: '+1441'
    },
    {
        name: 'Bután',
        alpha2Code: 'BT',
        alpha3Code: 'BTN',
        numericCode: '064',
        callingCode: '+975'
    },
    {
        name: 'Bolivia',
        alpha2Code: 'BO',
        alpha3Code: 'BOL',
        numericCode: '068',
        callingCode: '+591'
    },
    {
        name: 'Bonaire, Saint-Eustache y Saba',
        alpha2Code: 'BQ',
        alpha3Code: 'BES',
        numericCode: '535',
        callingCode: '+5997'
    },
    {
        name: 'Bosnia y Herzegovina',
        alpha2Code: 'BA',
        alpha3Code: 'BIH',
        numericCode: '070',
        callingCode: '+387'
    },
    {
        name: 'Botswana',
        alpha2Code: 'BW',
        alpha3Code: 'BWA',
        numericCode: '072',
        callingCode: '+267'
    },
    {
        name: 'Isla Bouvet',
        alpha2Code: 'BV',
        alpha3Code: 'BVT',
        numericCode: '074',
        callingCode: '+'
    },
    {
        name: 'Brasil',
        alpha2Code: 'BR',
        alpha3Code: 'BRA',
        numericCode: '076',
        callingCode: '+55'
    },
    {
        name: 'Territorio Británico del Océano Índico',
        alpha2Code: 'IO',
        alpha3Code: 'IOT',
        numericCode: '086',
        callingCode: '+246'
    },
    {
        name: 'Islas Ultramarinas Menores de Estados Unidos',
        alpha2Code: 'UM',
        alpha3Code: 'UMI',
        numericCode: '581',
        callingCode: '+'
    },
    {
        name: 'Islas Vírgenes del Reino Unido',
        alpha2Code: 'VG',
        alpha3Code: 'VGB',
        numericCode: '092',
        callingCode: '+1284'
    },
    {
        name: 'Islas Vírgenes de los Estados Unidos',
        alpha2Code: 'VI',
        alpha3Code: 'VIR',
        numericCode: '850',
        callingCode: '+1 340'
    },
    {
        name: 'Brunei',
        alpha2Code: 'BN',
        alpha3Code: 'BRN',
        numericCode: '096',
        callingCode: '+673'
    },
    {
        name: 'Bulgaria',
        alpha2Code: 'BG',
        alpha3Code: 'BGR',
        numericCode: '100',
        callingCode: '+359'
    },
    {
        name: 'Burkina Faso',
        alpha2Code: 'BF',
        alpha3Code: 'BFA',
        numericCode: '854',
        callingCode: '+226'
    },
    {
        name: 'Burundi',
        alpha2Code: 'BI',
        alpha3Code: 'BDI',
        numericCode: '108',
        callingCode: '+257'
    },
    {
        name: 'Camboya',
        alpha2Code: 'KH',
        alpha3Code: 'KHM',
        numericCode: '116',
        callingCode: '+855'
    },
    {
        name: 'Camerún',
        alpha2Code: 'CM',
        alpha3Code: 'CMR',
        numericCode: '120',
        callingCode: '+237'
    },
    {
        name: 'Canadá',
        alpha2Code: 'CA',
        alpha3Code: 'CAN',
        numericCode: '124',
        callingCode: '+1'
    },
    {
        name: 'Cabo Verde',
        alpha2Code: 'CV',
        alpha3Code: 'CPV',
        numericCode: '132',
        callingCode: '+238'
    },
    {
        name: 'Islas Caimán',
        alpha2Code: 'KY',
        alpha3Code: 'CYM',
        numericCode: '136',
        callingCode: '+1345'
    },
    {
        name: 'República Centroafricana',
        alpha2Code: 'CF',
        alpha3Code: 'CAF',
        numericCode: '140',
        callingCode: '+236'
    },
    {
        name: 'Chad',
        alpha2Code: 'TD',
        alpha3Code: 'TCD',
        numericCode: '148',
        callingCode: '+235'
    },
    {
        name: 'Chile',
        alpha2Code: 'CL',
        alpha3Code: 'CHL',
        numericCode: '152',
        callingCode: '+56'
    },
    {
        name: 'China',
        alpha2Code: 'CN',
        alpha3Code: 'CHN',
        numericCode: '156',
        callingCode: '+86'
    },
    {
        name: 'Isla de Navidad',
        alpha2Code: 'CX',
        alpha3Code: 'CXR',
        numericCode: '162',
        callingCode: '+61'
    },
    {
        name: 'Islas Cocos o Islas Keeling',
        alpha2Code: 'CC',
        alpha3Code: 'CCK',
        numericCode: '166',
        callingCode: '+61'
    },
    {
        name: 'Colombia',
        alpha2Code: 'CO',
        alpha3Code: 'COL',
        numericCode: '170',
        callingCode: '+57'
    },
    {
        name: 'Comoras',
        alpha2Code: 'KM',
        alpha3Code: 'COM',
        numericCode: '174',
        callingCode: '+269'
    },
    {
        name: 'Congo',
        alpha2Code: 'CG',
        alpha3Code: 'COG',
        numericCode: '178',
        callingCode: '+242'
    },
    {
        name: 'Congo (Rep. Dem.)',
        alpha2Code: 'CD',
        alpha3Code: 'COD',
        numericCode: '180',
        callingCode: '+243'
    },
    {
        name: 'Islas Cook',
        alpha2Code: 'CK',
        alpha3Code: 'COK',
        numericCode: '184',
        callingCode: '+682'
    },
    {
        name: 'Costa Rica',
        alpha2Code: 'CR',
        alpha3Code: 'CRI',
        numericCode: '188',
        callingCode: '+506'
    },
    {
        name: 'Croacia',
        alpha2Code: 'HR',
        alpha3Code: 'HRV',
        numericCode: '191',
        callingCode: '+385'
    },
    {
        name: 'Cuba',
        alpha2Code: 'CU',
        alpha3Code: 'CUB',
        numericCode: '192',
        callingCode: '+53'
    },
    {
        name: 'Curaçao',
        alpha2Code: 'CW',
        alpha3Code: 'CUW',
        numericCode: '531',
        callingCode: '+599'
    },
    {
        name: 'Chipre',
        alpha2Code: 'CY',
        alpha3Code: 'CYP',
        numericCode: '196',
        callingCode: '+357'
    },
    {
        name: 'República Checa',
        alpha2Code: 'CZ',
        alpha3Code: 'CZE',
        numericCode: '203',
        callingCode: '+420'
    },
    {
        name: 'Dinamarca',
        alpha2Code: 'DK',
        alpha3Code: 'DNK',
        numericCode: '208',
        callingCode: '+45'
    },
    {
        name: 'Yibuti',
        alpha2Code: 'DJ',
        alpha3Code: 'DJI',
        numericCode: '262',
        callingCode: '+253'
    },
    {
        name: 'Dominica',
        alpha2Code: 'DM',
        alpha3Code: 'DMA',
        numericCode: '212',
        callingCode: '+1767'
    },
    {
        name: 'República Dominicana',
        alpha2Code: 'DO',
        alpha3Code: 'DOM',
        numericCode: '214',
        callingCode: '+1809'
    },
    {
        name: 'Ecuador',
        alpha2Code: 'EC',
        alpha3Code: 'ECU',
        numericCode: '218',
        callingCode: '+593'
    },
    {
        name: 'Egipto',
        alpha2Code: 'EG',
        alpha3Code: 'EGY',
        numericCode: '818',
        callingCode: '+20'
    },
    {
        name: 'El Salvador',
        alpha2Code: 'SV',
        alpha3Code: 'SLV',
        numericCode: '222',
        callingCode: '+503'
    },
    {
        name: 'Guinea Ecuatorial',
        alpha2Code: 'GQ',
        alpha3Code: 'GNQ',
        numericCode: '226',
        callingCode: '+240'
    },
    {
        name: 'Eritrea',
        alpha2Code: 'ER',
        alpha3Code: 'ERI',
        numericCode: '232',
        callingCode: '+291'
    },
    {
        name: 'Estonia',
        alpha2Code: 'EE',
        alpha3Code: 'EST',
        numericCode: '233',
        callingCode: '+372'
    },
    {
        name: 'Etiopía',
        alpha2Code: 'ET',
        alpha3Code: 'ETH',
        numericCode: '231',
        callingCode: '+251'
    },
    {
        name: 'Islas Malvinas',
        alpha2Code: 'FK',
        alpha3Code: 'FLK',
        numericCode: '238',
        callingCode: '+500'
    },
    {
        name: 'Islas Faroe',
        alpha2Code: 'FO',
        alpha3Code: 'FRO',
        numericCode: '234',
        callingCode: '+298'
    },
    {
        name: 'Fiyi',
        alpha2Code: 'FJ',
        alpha3Code: 'FJI',
        numericCode: '242',
        callingCode: '+679'
    },
    {
        name: 'Finlandia',
        alpha2Code: 'FI',
        alpha3Code: 'FIN',
        numericCode: '246',
        callingCode: '+358'
    },
    {
        name: 'Francia',
        alpha2Code: 'FR',
        alpha3Code: 'FRA',
        numericCode: '250',
        callingCode: '+33'
    },
    {
        name: 'Guayana Francesa',
        alpha2Code: 'GF',
        alpha3Code: 'GUF',
        numericCode: '254',
        callingCode: '+594'
    },
    {
        name: 'Polinesia Francesa',
        alpha2Code: 'PF',
        alpha3Code: 'PYF',
        numericCode: '258',
        callingCode: '+689'
    },
    {
        name: 'Tierras Australes y Antárticas Francesas',
        alpha2Code: 'TF',
        alpha3Code: 'ATF',
        numericCode: '260',
        callingCode: '+'
    },
    {
        name: 'Gabón',
        alpha2Code: 'GA',
        alpha3Code: 'GAB',
        numericCode: '266',
        callingCode: '+241'
    },
    {
        name: 'Gambia',
        alpha2Code: 'GM',
        alpha3Code: 'GMB',
        numericCode: '270',
        callingCode: '+220'
    },
    {
        name: 'Georgia',
        alpha2Code: 'GE',
        alpha3Code: 'GEO',
        numericCode: '268',
        callingCode: '+995'
    },
    {
        name: 'Alemania',
        alpha2Code: 'DE',
        alpha3Code: 'DEU',
        numericCode: '276',
        callingCode: '+49'
    },
    {
        name: 'Ghana',
        alpha2Code: 'GH',
        alpha3Code: 'GHA',
        numericCode: '288',
        callingCode: '+233'
    },
    {
        name: 'Gibraltar',
        alpha2Code: 'GI',
        alpha3Code: 'GIB',
        numericCode: '292',
        callingCode: '+350'
    },
    {
        name: 'Grecia',
        alpha2Code: 'GR',
        alpha3Code: 'GRC',
        numericCode: '300',
        callingCode: '+30'
    },
    {
        name: 'Groenlandia',
        alpha2Code: 'GL',
        alpha3Code: 'GRL',
        numericCode: '304',
        callingCode: '+299'
    },
    {
        name: 'Grenada',
        alpha2Code: 'GD',
        alpha3Code: 'GRD',
        numericCode: '308',
        callingCode: '+1473'
    },
    {
        name: 'Guadalupe',
        alpha2Code: 'GP',
        alpha3Code: 'GLP',
        numericCode: '312',
        callingCode: '+590'
    },
    {
        name: 'Guam',
        alpha2Code: 'GU',
        alpha3Code: 'GUM',
        numericCode: '316',
        callingCode: '+1671'
    },
    {
        name: 'Guatemala',
        alpha2Code: 'GT',
        alpha3Code: 'GTM',
        numericCode: '320',
        callingCode: '+502'
    },
    {
        name: 'Guernsey',
        alpha2Code: 'GG',
        alpha3Code: 'GGY',
        numericCode: '831',
        callingCode: '+44'
    },
    {
        name: 'Guinea',
        alpha2Code: 'GN',
        alpha3Code: 'GIN',
        numericCode: '324',
        callingCode: '+224'
    },
    {
        name: 'Guinea-Bisáu',
        alpha2Code: 'GW',
        alpha3Code: 'GNB',
        numericCode: '624',
        callingCode: '+245'
    },
    {
        name: 'Guyana',
        alpha2Code: 'GY',
        alpha3Code: 'GUY',
        numericCode: '328',
        callingCode: '+592'
    },
    {
        name: 'Haiti',
        alpha2Code: 'HT',
        alpha3Code: 'HTI',
        numericCode: '332',
        callingCode: '+509'
    },
    {
        name: 'Islas Heard y McDonald',
        alpha2Code: 'HM',
        alpha3Code: 'HMD',
        numericCode: '334',
        callingCode: '+'
    },
    {
        name: 'Santa Sede',
        alpha2Code: 'VA',
        alpha3Code: 'VAT',
        numericCode: '336',
        callingCode: '+379'
    },
    {
        name: 'Honduras',
        alpha2Code: 'HN',
        alpha3Code: 'HND',
        numericCode: '340',
        callingCode: '+504'
    },
    {
        name: 'Hong Kong',
        alpha2Code: 'HK',
        alpha3Code: 'HKG',
        numericCode: '344',
        callingCode: '+852'
    },
    {
        name: 'Hungría',
        alpha2Code: 'HU',
        alpha3Code: 'HUN',
        numericCode: '348',
        callingCode: '+36'
    },
    {
        name: 'Islandia',
        alpha2Code: 'IS',
        alpha3Code: 'ISL',
        numericCode: '352',
        callingCode: '+354'
    },
    {
        name: 'India',
        alpha2Code: 'IN',
        alpha3Code: 'IND',
        numericCode: '356',
        callingCode: '+91'
    },
    {
        name: 'Indonesia',
        alpha2Code: 'ID',
        alpha3Code: 'IDN',
        numericCode: '360',
        callingCode: '+62'
    },
    {
        name: 'Costa de Marfil',
        alpha2Code: 'CI',
        alpha3Code: 'CIV',
        numericCode: '384',
        callingCode: '+225'
    },
    {
        name: 'Iran',
        alpha2Code: 'IR',
        alpha3Code: 'IRN',
        numericCode: '364',
        callingCode: '+98'
    },
    {
        name: 'Irak',
        alpha2Code: 'IQ',
        alpha3Code: 'IRQ',
        numericCode: '368',
        callingCode: '+964'
    },
    {
        name: 'Irlanda',
        alpha2Code: 'IE',
        alpha3Code: 'IRL',
        numericCode: '372',
        callingCode: '+353'
    },
    {
        name: 'Isla de Man',
        alpha2Code: 'IM',
        alpha3Code: 'IMN',
        numericCode: '833',
        callingCode: '+44'
    },
    {
        name: 'Israel',
        alpha2Code: 'IL',
        alpha3Code: 'ISR',
        numericCode: '376',
        callingCode: '+972'
    },
    {
        name: 'Italia',
        alpha2Code: 'IT',
        alpha3Code: 'ITA',
        numericCode: '380',
        callingCode: '+39'
    },
    {
        name: 'Jamaica',
        alpha2Code: 'JM',
        alpha3Code: 'JAM',
        numericCode: '388',
        callingCode: '+1876'
    },
    {
        name: 'Japón',
        alpha2Code: 'JP',
        alpha3Code: 'JPN',
        numericCode: '392',
        callingCode: '+81'
    },
    {
        name: 'Jersey',
        alpha2Code: 'JE',
        alpha3Code: 'JEY',
        numericCode: '832',
        callingCode: '+44'
    },
    {
        name: 'Jordania',
        alpha2Code: 'JO',
        alpha3Code: 'JOR',
        numericCode: '400',
        callingCode: '+962'
    },
    {
        name: 'Kazajistán',
        alpha2Code: 'KZ',
        alpha3Code: 'KAZ',
        numericCode: '398',
        callingCode: '+7'
    },
    {
        name: 'Kenia',
        alpha2Code: 'KE',
        alpha3Code: 'KEN',
        numericCode: '404',
        callingCode: '+254'
    },
    {
        name: 'Kiribati',
        alpha2Code: 'KI',
        alpha3Code: 'KIR',
        numericCode: '296',
        callingCode: '+686'
    },
    {
        name: 'Kuwait',
        alpha2Code: 'KW',
        alpha3Code: 'KWT',
        numericCode: '414',
        callingCode: '+965'
    },
    {
        name: 'Kirguizistán',
        alpha2Code: 'KG',
        alpha3Code: 'KGZ',
        numericCode: '417',
        callingCode: '+996'
    },
    {
        name: 'Laos',
        alpha2Code: 'LA',
        alpha3Code: 'LAO',
        numericCode: '418',
        callingCode: '+856'
    },
    {
        name: 'Letonia',
        alpha2Code: 'LV',
        alpha3Code: 'LVA',
        numericCode: '428',
        callingCode: '+371'
    },
    {
        name: 'Líbano',
        alpha2Code: 'LB',
        alpha3Code: 'LBN',
        numericCode: '422',
        callingCode: '+961'
    },
    {
        name: 'Lesotho',
        alpha2Code: 'LS',
        alpha3Code: 'LSO',
        numericCode: '426',
        callingCode: '+266'
    },
    {
        name: 'Liberia',
        alpha2Code: 'LR',
        alpha3Code: 'LBR',
        numericCode: '430',
        callingCode: '+231'
    },
    {
        name: 'Libia',
        alpha2Code: 'LY',
        alpha3Code: 'LBY',
        numericCode: '434',
        callingCode: '+218'
    },
    {
        name: 'Liechtenstein',
        alpha2Code: 'LI',
        alpha3Code: 'LIE',
        numericCode: '438',
        callingCode: '+423'
    },
    {
        name: 'Lituania',
        alpha2Code: 'LT',
        alpha3Code: 'LTU',
        numericCode: '440',
        callingCode: '+370'
    },
    {
        name: 'Luxemburgo',
        alpha2Code: 'LU',
        alpha3Code: 'LUX',
        numericCode: '442',
        callingCode: '+352'
    },
    {
        name: 'Macao',
        alpha2Code: 'MO',
        alpha3Code: 'MAC',
        numericCode: '446',
        callingCode: '+853'
    },
    {
        name: 'Macedonia',
        alpha2Code: 'MK',
        alpha3Code: 'MKD',
        numericCode: '807',
        callingCode: '+389'
    },
    {
        name: 'Madagascar',
        alpha2Code: 'MG',
        alpha3Code: 'MDG',
        numericCode: '450',
        callingCode: '+261'
    },
    {
        name: 'Malawi',
        alpha2Code: 'MW',
        alpha3Code: 'MWI',
        numericCode: '454',
        callingCode: '+265'
    },
    {
        name: 'Malasia',
        alpha2Code: 'MY',
        alpha3Code: 'MYS',
        numericCode: '458',
        callingCode: '+60'
    },
    {
        name: 'Maldivas',
        alpha2Code: 'MV',
        alpha3Code: 'MDV',
        numericCode: '462',
        callingCode: '+960'
    },
    {
        name: 'Mali',
        alpha2Code: 'ML',
        alpha3Code: 'MLI',
        numericCode: '466',
        callingCode: '+223'
    },
    {
        name: 'Malta',
        alpha2Code: 'MT',
        alpha3Code: 'MLT',
        numericCode: '470',
        callingCode: '+356'
    },
    {
        name: 'Islas Marshall',
        alpha2Code: 'MH',
        alpha3Code: 'MHL',
        numericCode: '584',
        callingCode: '+692'
    },
    {
        name: 'Martinica',
        alpha2Code: 'MQ',
        alpha3Code: 'MTQ',
        numericCode: '474',
        callingCode: '+596'
    },
    {
        name: 'Mauritania',
        alpha2Code: 'MR',
        alpha3Code: 'MRT',
        numericCode: '478',
        callingCode: '+222'
    },
    {
        name: 'Mauricio',
        alpha2Code: 'MU',
        alpha3Code: 'MUS',
        numericCode: '480',
        callingCode: '+230'
    },
    {
        name: 'Mayotte',
        alpha2Code: 'YT',
        alpha3Code: 'MYT',
        numericCode: '175',
        callingCode: '+262'
    },
    {
        name: 'México',
        alpha2Code: 'MX',
        alpha3Code: 'MEX',
        numericCode: '484',
        callingCode: '+52'
    },
    {
        name: 'Micronesia',
        alpha2Code: 'FM',
        alpha3Code: 'FSM',
        numericCode: '583',
        callingCode: '+691'
    },
    {
        name: 'Moldavia',
        alpha2Code: 'MD',
        alpha3Code: 'MDA',
        numericCode: '498',
        callingCode: '+373'
    },
    {
        name: 'Mónaco',
        alpha2Code: 'MC',
        alpha3Code: 'MCO',
        numericCode: '492',
        callingCode: '+377'
    },
    {
        name: 'Mongolia',
        alpha2Code: 'MN',
        alpha3Code: 'MNG',
        numericCode: '496',
        callingCode: '+976'
    },
    {
        name: 'Montenegro',
        alpha2Code: 'ME',
        alpha3Code: 'MNE',
        numericCode: '499',
        callingCode: '+382'
    },
    {
        name: 'Montserrat',
        alpha2Code: 'MS',
        alpha3Code: 'MSR',
        numericCode: '500',
        callingCode: '+1664'
    },
    {
        name: 'Marruecos',
        alpha2Code: 'MA',
        alpha3Code: 'MAR',
        numericCode: '504',
        callingCode: '+212'
    },
    {
        name: 'Mozambique',
        alpha2Code: 'MZ',
        alpha3Code: 'MOZ',
        numericCode: '508',
        callingCode: '+258'
    },
    {
        name: 'Myanmar',
        alpha2Code: 'MM',
        alpha3Code: 'MMR',
        numericCode: '104',
        callingCode: '+95'
    },
    {
        name: 'Namibia',
        alpha2Code: 'NA',
        alpha3Code: 'NAM',
        numericCode: '516',
        callingCode: '+264'
    },
    {
        name: 'Nauru',
        alpha2Code: 'NR',
        alpha3Code: 'NRU',
        numericCode: '520',
        callingCode: '+674'
    },
    {
        name: 'Nepal',
        alpha2Code: 'NP',
        alpha3Code: 'NPL',
        numericCode: '524',
        callingCode: '+977'
    },
    {
        name: 'Países Bajos',
        alpha2Code: 'NL',
        alpha3Code: 'NLD',
        numericCode: '528',
        callingCode: '+31'
    },
    {
        name: 'Nueva Caledonia',
        alpha2Code: 'NC',
        alpha3Code: 'NCL',
        numericCode: '540',
        callingCode: '+687'
    },
    {
        name: 'Nueva Zelanda',
        alpha2Code: 'NZ',
        alpha3Code: 'NZL',
        numericCode: '554',
        callingCode: '+64'
    },
    {
        name: 'Nicaragua',
        alpha2Code: 'NI',
        alpha3Code: 'NIC',
        numericCode: '558',
        callingCode: '+505'
    },
    {
        name: 'Níger',
        alpha2Code: 'NE',
        alpha3Code: 'NER',
        numericCode: '562',
        callingCode: '+227'
    },
    {
        name: 'Nigeria',
        alpha2Code: 'NG',
        alpha3Code: 'NGA',
        numericCode: '566',
        callingCode: '+234'
    },
    {
        name: 'Niue',
        alpha2Code: 'NU',
        alpha3Code: 'NIU',
        numericCode: '570',
        callingCode: '+683'
    },
    {
        name: 'Isla de Norfolk',
        alpha2Code: 'NF',
        alpha3Code: 'NFK',
        numericCode: '574',
        callingCode: '+672'
    },
    {
        name: 'Corea del Norte',
        alpha2Code: 'KP',
        alpha3Code: 'PRK',
        numericCode: '408',
        callingCode: '+850'
    },
    {
        name: 'Islas Marianas del Norte',
        alpha2Code: 'MP',
        alpha3Code: 'MNP',
        numericCode: '580',
        callingCode: '+1670'
    },
    {
        name: 'Noruega',
        alpha2Code: 'NO',
        alpha3Code: 'NOR',
        numericCode: '578',
        callingCode: '+47'
    },
    {
        name: 'Omán',
        alpha2Code: 'OM',
        alpha3Code: 'OMN',
        numericCode: '512',
        callingCode: '+968'
    },
    {
        name: 'Pakistán',
        alpha2Code: 'PK',
        alpha3Code: 'PAK',
        numericCode: '586',
        callingCode: '+92'
    },
    {
        name: 'Palau',
        alpha2Code: 'PW',
        alpha3Code: 'PLW',
        numericCode: '585',
        callingCode: '+680'
    },
    {
        name: 'Palestina',
        alpha2Code: 'PS',
        alpha3Code: 'PSE',
        numericCode: '275',
        callingCode: '+970'
    },
    {
        name: 'Panamá',
        alpha2Code: 'PA',
        alpha3Code: 'PAN',
        numericCode: '591',
        callingCode: '+507'
    },
    {
        name: 'Papúa Nueva Guinea',
        alpha2Code: 'PG',
        alpha3Code: 'PNG',
        numericCode: '598',
        callingCode: '+675'
    },
    {
        name: 'Paraguay',
        alpha2Code: 'PY',
        alpha3Code: 'PRY',
        numericCode: '600',
        callingCode: '+595'
    },
    {
        name: 'Perú',
        alpha2Code: 'PE',
        alpha3Code: 'PER',
        numericCode: '604',
        callingCode: '+51'
    },
    {
        name: 'Filipinas',
        alpha2Code: 'PH',
        alpha3Code: 'PHL',
        numericCode: '608',
        callingCode: '+63'
    },
    {
        name: 'Islas Pitcairn',
        alpha2Code: 'PN',
        alpha3Code: 'PCN',
        numericCode: '612',
        callingCode: '+64'
    },
    {
        name: 'Polonia',
        alpha2Code: 'PL',
        alpha3Code: 'POL',
        numericCode: '616',
        callingCode: '+48'
    },
    {
        name: 'Portugal',
        alpha2Code: 'PT',
        alpha3Code: 'PRT',
        numericCode: '620',
        callingCode: '+351'
    },
    {
        name: 'Puerto Rico',
        alpha2Code: 'PR',
        alpha3Code: 'PRI',
        numericCode: '630',
        callingCode: '+1'
    },
    {
        name: 'Catar',
        alpha2Code: 'QA',
        alpha3Code: 'QAT',
        numericCode: '634',
        callingCode: '+974'
    },
    {
        name: 'Kosovo',
        alpha2Code: 'XK',
        alpha3Code: 'KOS',
        numericCode: null,
        callingCode: '+383'
    },
    {
        name: 'Reunión',
        alpha2Code: 'RE',
        alpha3Code: 'REU',
        numericCode: '638',
        callingCode: '+262'
    },
    {
        name: 'Rumania',
        alpha2Code: 'RO',
        alpha3Code: 'ROU',
        numericCode: '642',
        callingCode: '+40'
    },
    {
        name: 'Rusia',
        alpha2Code: 'RU',
        alpha3Code: 'RUS',
        numericCode: '643',
        callingCode: '+7'
    },
    {
        name: 'Ruanda',
        alpha2Code: 'RW',
        alpha3Code: 'RWA',
        numericCode: '646',
        callingCode: '+250'
    },
    {
        name: 'San Bartolomé',
        alpha2Code: 'BL',
        alpha3Code: 'BLM',
        numericCode: '652',
        callingCode: '+590'
    },
    {
        name: 'Santa Helena',
        alpha2Code: 'SH',
        alpha3Code: 'SHN',
        numericCode: '654',
        callingCode: '+290'
    },
    {
        name: 'San Cristóbal y Nieves',
        alpha2Code: 'KN',
        alpha3Code: 'KNA',
        numericCode: '659',
        callingCode: '+1869'
    },
    {
        name: 'Santa Lucía',
        alpha2Code: 'LC',
        alpha3Code: 'LCA',
        numericCode: '662',
        callingCode: '+1758'
    },
    {
        name: 'Saint Martin',
        alpha2Code: 'MF',
        alpha3Code: 'MAF',
        numericCode: '663',
        callingCode: '+590'
    },
    {
        name: 'San Pedro y Miquelón',
        alpha2Code: 'PM',
        alpha3Code: 'SPM',
        numericCode: '666',
        callingCode: '+508'
    },
    {
        name: 'San Vicente y Granadinas',
        alpha2Code: 'VC',
        alpha3Code: 'VCT',
        numericCode: '670',
        callingCode: '+1784'
    },
    {
        name: 'Samoa',
        alpha2Code: 'WS',
        alpha3Code: 'WSM',
        numericCode: '882',
        callingCode: '+685'
    },
    {
        name: 'San Marino',
        alpha2Code: 'SM',
        alpha3Code: 'SMR',
        numericCode: '674',
        callingCode: '+378'
    },
    {
        name: 'Santo Tomé y Príncipe',
        alpha2Code: 'ST',
        alpha3Code: 'STP',
        numericCode: '678',
        callingCode: '+239'
    },
    {
        name: 'Arabia Saudí',
        alpha2Code: 'SA',
        alpha3Code: 'SAU',
        numericCode: '682',
        callingCode: '+966'
    },
    {
        name: 'Senegal',
        alpha2Code: 'SN',
        alpha3Code: 'SEN',
        numericCode: '686',
        callingCode: '+221'
    },
    {
        name: 'Serbia',
        alpha2Code: 'RS',
        alpha3Code: 'SRB',
        numericCode: '688',
        callingCode: '+381'
    },
    {
        name: 'Seychelles',
        alpha2Code: 'SC',
        alpha3Code: 'SYC',
        numericCode: '690',
        callingCode: '+248'
    },
    {
        name: 'Sierra Leone',
        alpha2Code: 'SL',
        alpha3Code: 'SLE',
        numericCode: '694',
        callingCode: '+232'
    },
    {
        name: 'Singapur',
        alpha2Code: 'SG',
        alpha3Code: 'SGP',
        numericCode: '702',
        callingCode: '+65'
    },
    {
        name: 'San Martín (parte holandesa)',
        alpha2Code: 'SX',
        alpha3Code: 'SXM',
        numericCode: '534',
        callingCode: '+1721'
    },
    {
        name: 'República Eslovaca',
        alpha2Code: 'SK',
        alpha3Code: 'SVK',
        numericCode: '703',
        callingCode: '+421'
    },
    {
        name: 'Eslovenia',
        alpha2Code: 'SI',
        alpha3Code: 'SVN',
        numericCode: '705',
        callingCode: '+386'
    },
    {
        name: 'Islas Salomón',
        alpha2Code: 'SB',
        alpha3Code: 'SLB',
        numericCode: '090',
        callingCode: '+677'
    },
    {
        name: 'Somalia',
        alpha2Code: 'SO',
        alpha3Code: 'SOM',
        numericCode: '706',
        callingCode: '+252'
    },
    {
        name: 'República de Sudáfrica',
        alpha2Code: 'ZA',
        alpha3Code: 'ZAF',
        numericCode: '710',
        callingCode: '+27'
    },
    {
        name: 'Islas Georgias del Sur y Sandwich del Sur',
        alpha2Code: 'GS',
        alpha3Code: 'SGS',
        numericCode: '239',
        callingCode: '+500'
    },
    {
        name: 'Corea del Sur',
        alpha2Code: 'KR',
        alpha3Code: 'KOR',
        numericCode: '410',
        callingCode: '+82'
    },
    {
        name: 'Sudán del Sur',
        alpha2Code: 'SS',
        alpha3Code: 'SSD',
        numericCode: '728',
        callingCode: '+211'
    },
    {
        name: 'España',
        alpha2Code: 'ES',
        alpha3Code: 'ESP',
        numericCode: '724',
        callingCode: '+34'
    },
    {
        name: 'Sri Lanka',
        alpha2Code: 'LK',
        alpha3Code: 'LKA',
        numericCode: '144',
        callingCode: '+94'
    },
    {
        name: 'Sudán',
        alpha2Code: 'SD',
        alpha3Code: 'SDN',
        numericCode: '729',
        callingCode: '+249'
    },
    {
        name: 'Surinam',
        alpha2Code: 'SR',
        alpha3Code: 'SUR',
        numericCode: '740',
        callingCode: '+597'
    },
    {
        name: 'Islas Svalbard y Jan Mayen',
        alpha2Code: 'SJ',
        alpha3Code: 'SJM',
        numericCode: '744',
        callingCode: '+4779'
    },
    {
        name: 'Suazilandia',
        alpha2Code: 'SZ',
        alpha3Code: 'SWZ',
        numericCode: '748',
        callingCode: '+268'
    },
    {
        name: 'Suecia',
        alpha2Code: 'SE',
        alpha3Code: 'SWE',
        numericCode: '752',
        callingCode: '+46'
    },
    {
        name: 'Suiza',
        alpha2Code: 'CH',
        alpha3Code: 'CHE',
        numericCode: '756',
        callingCode: '+41'
    },
    {
        name: 'Siria',
        alpha2Code: 'SY',
        alpha3Code: 'SYR',
        numericCode: '760',
        callingCode: '+963'
    },
    {
        name: 'Taiwán',
        alpha2Code: 'TW',
        alpha3Code: 'TWN',
        numericCode: '158',
        callingCode: '+886'
    },
    {
        name: 'Tayikistán',
        alpha2Code: 'TJ',
        alpha3Code: 'TJK',
        numericCode: '762',
        callingCode: '+992'
    },
    {
        name: 'Tanzania',
        alpha2Code: 'TZ',
        alpha3Code: 'TZA',
        numericCode: '834',
        callingCode: '+255'
    },
    {
        name: 'Tailandia',
        alpha2Code: 'TH',
        alpha3Code: 'THA',
        numericCode: '764',
        callingCode: '+66'
    },
    {
        name: 'Timor Oriental',
        alpha2Code: 'TL',
        alpha3Code: 'TLS',
        numericCode: '626',
        callingCode: '+670'
    },
    {
        name: 'Togo',
        alpha2Code: 'TG',
        alpha3Code: 'TGO',
        numericCode: '768',
        callingCode: '+228'
    },
    {
        name: 'Islas Tokelau',
        alpha2Code: 'TK',
        alpha3Code: 'TKL',
        numericCode: '772',
        callingCode: '+690'
    },
    {
        name: 'Tonga',
        alpha2Code: 'TO',
        alpha3Code: 'TON',
        numericCode: '776',
        callingCode: '+676'
    },
    {
        name: 'Trinidad y Tobago',
        alpha2Code: 'TT',
        alpha3Code: 'TTO',
        numericCode: '780',
        callingCode: '+1868'
    },
    {
        name: 'Túnez',
        alpha2Code: 'TN',
        alpha3Code: 'TUN',
        numericCode: '788',
        callingCode: '+216'
    },
    {
        name: 'Turquía',
        alpha2Code: 'TR',
        alpha3Code: 'TUR',
        numericCode: '792',
        callingCode: '+90'
    },
    {
        name: 'Turkmenistán',
        alpha2Code: 'TM',
        alpha3Code: 'TKM',
        numericCode: '795',
        callingCode: '+993'
    },
    {
        name: 'Islas Turks y Caicos',
        alpha2Code: 'TC',
        alpha3Code: 'TCA',
        numericCode: '796',
        callingCode: '+1649'
    },
    {
        name: 'Tuvalu',
        alpha2Code: 'TV',
        alpha3Code: 'TUV',
        numericCode: '798',
        callingCode: '+688'
    },
    {
        name: 'Uganda',
        alpha2Code: 'UG',
        alpha3Code: 'UGA',
        numericCode: '800',
        callingCode: '+256'
    },
    {
        name: 'Ucrania',
        alpha2Code: 'UA',
        alpha3Code: 'UKR',
        numericCode: '804',
        callingCode: '+380'
    },
    {
        name: 'Emiratos Árabes Unidos',
        alpha2Code: 'AE',
        alpha3Code: 'ARE',
        numericCode: '784',
        callingCode: '+971'
    },
    {
        name: 'Reino Unido',
        alpha2Code: 'GB',
        alpha3Code: 'GBR',
        numericCode: '826',
        callingCode: '+44'
    },
    {
        name: 'Estados Unidos',
        alpha2Code: 'US',
        alpha3Code: 'USA',
        numericCode: '840',
        callingCode: '+1'
    },
    {
        name: 'Uruguay',
        alpha2Code: 'UY',
        alpha3Code: 'URY',
        numericCode: '858',
        callingCode: '+598'
    },
    {
        name: 'Uzbekistán',
        alpha2Code: 'UZ',
        alpha3Code: 'UZB',
        numericCode: '860',
        callingCode: '+998'
    },
    {
        name: 'Vanuatu',
        alpha2Code: 'VU',
        alpha3Code: 'VUT',
        numericCode: '548',
        callingCode: '+678'
    },
    {
        name: 'Venezuela',
        alpha2Code: 'VE',
        alpha3Code: 'VEN',
        numericCode: '862',
        callingCode: '+58'
    },
    {
        name: 'Vietnam',
        alpha2Code: 'VN',
        alpha3Code: 'VNM',
        numericCode: '704',
        callingCode: '+84'
    },
    {
        name: 'Wallis y Futuna',
        alpha2Code: 'WF',
        alpha3Code: 'WLF',
        numericCode: '876',
        callingCode: '+681'
    },
    {
        name: 'Sahara Occidental',
        alpha2Code: 'EH',
        alpha3Code: 'ESH',
        numericCode: '732',
        callingCode: '+212'
    },
    {
        name: 'Yemen',
        alpha2Code: 'YE',
        alpha3Code: 'YEM',
        numericCode: '887',
        callingCode: '+967'
    },
    {
        name: 'Zambia',
        alpha2Code: 'ZM',
        alpha3Code: 'ZMB',
        numericCode: '894',
        callingCode: '+260'
    },
    {
        name: 'Zimbabue',
        alpha2Code: 'ZW',
        alpha3Code: 'ZWE',
        numericCode: '716',
        callingCode: '+263'
    }
];

var es = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB_ES: COUNTRIES_DB_ES
});

const COUNTRIES_DB_FR = [
    {
        name: 'Afghanistan',
        alpha2Code: 'AF',
        alpha3Code: 'AFG',
        numericCode: '004',
        callingCode: '+93'
    },
    {
        name: 'Åland',
        alpha2Code: 'AX',
        alpha3Code: 'ALA',
        numericCode: '248',
        callingCode: '+358'
    },
    {
        name: 'Albanie',
        alpha2Code: 'AL',
        alpha3Code: 'ALB',
        numericCode: '008',
        callingCode: '+355'
    },
    {
        name: 'Algérie',
        alpha2Code: 'DZ',
        alpha3Code: 'DZA',
        numericCode: '012',
        callingCode: '+213'
    },
    {
        name: 'Samoa américaines',
        alpha2Code: 'AS',
        alpha3Code: 'ASM',
        numericCode: '016',
        callingCode: '+1684'
    },
    {
        name: 'Andorre',
        alpha2Code: 'AD',
        alpha3Code: 'AND',
        numericCode: '020',
        callingCode: '+376'
    },
    {
        name: 'Angola',
        alpha2Code: 'AO',
        alpha3Code: 'AGO',
        numericCode: '024',
        callingCode: '+244'
    },
    {
        name: 'Anguilla',
        alpha2Code: 'AI',
        alpha3Code: 'AIA',
        numericCode: '660',
        callingCode: '+1264'
    },
    {
        name: 'Antarctique',
        alpha2Code: 'AQ',
        alpha3Code: 'ATA',
        numericCode: '010',
        callingCode: '+672'
    },
    {
        name: 'Antigua-et-Barbuda',
        alpha2Code: 'AG',
        alpha3Code: 'ATG',
        numericCode: '028',
        callingCode: '+1268'
    },
    {
        name: 'Argentine',
        alpha2Code: 'AR',
        alpha3Code: 'ARG',
        numericCode: '032',
        callingCode: '+54'
    },
    {
        name: 'Arménie',
        alpha2Code: 'AM',
        alpha3Code: 'ARM',
        numericCode: '051',
        callingCode: '+374'
    },
    {
        name: 'Aruba',
        alpha2Code: 'AW',
        alpha3Code: 'ABW',
        numericCode: '533',
        callingCode: '+297'
    },
    {
        name: 'Australie',
        alpha2Code: 'AU',
        alpha3Code: 'AUS',
        numericCode: '036',
        callingCode: '+61'
    },
    {
        name: 'Autriche',
        alpha2Code: 'AT',
        alpha3Code: 'AUT',
        numericCode: '040',
        callingCode: '+43'
    },
    {
        name: 'Azerbaïdjan',
        alpha2Code: 'AZ',
        alpha3Code: 'AZE',
        numericCode: '031',
        callingCode: '+994'
    },
    {
        name: 'Bahamas',
        alpha2Code: 'BS',
        alpha3Code: 'BHS',
        numericCode: '044',
        callingCode: '+1242'
    },
    {
        name: 'Bahreïn',
        alpha2Code: 'BH',
        alpha3Code: 'BHR',
        numericCode: '048',
        callingCode: '+973'
    },
    {
        name: 'Bangladesh',
        alpha2Code: 'BD',
        alpha3Code: 'BGD',
        numericCode: '050',
        callingCode: '+880'
    },
    {
        name: 'Barbade',
        alpha2Code: 'BB',
        alpha3Code: 'BRB',
        numericCode: '052',
        callingCode: '+1246'
    },
    {
        name: 'Biélorussie',
        alpha2Code: 'BY',
        alpha3Code: 'BLR',
        numericCode: '112',
        callingCode: '+375'
    },
    {
        name: 'Belgique',
        alpha2Code: 'BE',
        alpha3Code: 'BEL',
        numericCode: '056',
        callingCode: '+32'
    },
    {
        name: 'Belize',
        alpha2Code: 'BZ',
        alpha3Code: 'BLZ',
        numericCode: '084',
        callingCode: '+501'
    },
    {
        name: 'Bénin',
        alpha2Code: 'BJ',
        alpha3Code: 'BEN',
        numericCode: '204',
        callingCode: '+229'
    },
    {
        name: 'Bermudes',
        alpha2Code: 'BM',
        alpha3Code: 'BMU',
        numericCode: '060',
        callingCode: '+1441'
    },
    {
        name: 'Bhoutan',
        alpha2Code: 'BT',
        alpha3Code: 'BTN',
        numericCode: '064',
        callingCode: '+975'
    },
    {
        name: 'Bolivie',
        alpha2Code: 'BO',
        alpha3Code: 'BOL',
        numericCode: '068',
        callingCode: '+591'
    },
    {
        name: 'Bonaire, Saint-Eustache et Saba',
        alpha2Code: 'BQ',
        alpha3Code: 'BES',
        numericCode: '535',
        callingCode: '+5997'
    },
    {
        name: 'Bosnie-Herzégovine',
        alpha2Code: 'BA',
        alpha3Code: 'BIH',
        numericCode: '070',
        callingCode: '+387'
    },
    {
        name: 'Botswana',
        alpha2Code: 'BW',
        alpha3Code: 'BWA',
        numericCode: '072',
        callingCode: '+267'
    },
    {
        name: 'Île Bouvet',
        alpha2Code: 'BV',
        alpha3Code: 'BVT',
        numericCode: '074',
        callingCode: '+'
    },
    {
        name: 'Brésil',
        alpha2Code: 'BR',
        alpha3Code: 'BRA',
        numericCode: '076',
        callingCode: '+55'
    },
    {
        name: "Territoire britannique de l'océan Indien",
        alpha2Code: 'IO',
        alpha3Code: 'IOT',
        numericCode: '086',
        callingCode: '+246'
    },
    {
        name: 'Îles mineures éloignées des États-Unis',
        alpha2Code: 'UM',
        alpha3Code: 'UMI',
        numericCode: '581',
        callingCode: '+'
    },
    {
        name: 'Îles Vierges britanniques',
        alpha2Code: 'VG',
        alpha3Code: 'VGB',
        numericCode: '092',
        callingCode: '+1284'
    },
    {
        name: 'Îles Vierges des États-Unis',
        alpha2Code: 'VI',
        alpha3Code: 'VIR',
        numericCode: '850',
        callingCode: '+1 340'
    },
    {
        name: 'Brunei',
        alpha2Code: 'BN',
        alpha3Code: 'BRN',
        numericCode: '096',
        callingCode: '+673'
    },
    {
        name: 'Bulgarie',
        alpha2Code: 'BG',
        alpha3Code: 'BGR',
        numericCode: '100',
        callingCode: '+359'
    },
    {
        name: 'Burkina Faso',
        alpha2Code: 'BF',
        alpha3Code: 'BFA',
        numericCode: '854',
        callingCode: '+226'
    },
    {
        name: 'Burundi',
        alpha2Code: 'BI',
        alpha3Code: 'BDI',
        numericCode: '108',
        callingCode: '+257'
    },
    {
        name: 'Cambodge',
        alpha2Code: 'KH',
        alpha3Code: 'KHM',
        numericCode: '116',
        callingCode: '+855'
    },
    {
        name: 'Cameroun',
        alpha2Code: 'CM',
        alpha3Code: 'CMR',
        numericCode: '120',
        callingCode: '+237'
    },
    {
        name: 'Canada',
        alpha2Code: 'CA',
        alpha3Code: 'CAN',
        numericCode: '124',
        callingCode: '+1'
    },
    {
        name: 'Cap Vert',
        alpha2Code: 'CV',
        alpha3Code: 'CPV',
        numericCode: '132',
        callingCode: '+238'
    },
    {
        name: 'Îles Caïmans',
        alpha2Code: 'KY',
        alpha3Code: 'CYM',
        numericCode: '136',
        callingCode: '+1345'
    },
    {
        name: 'République centrafricaine',
        alpha2Code: 'CF',
        alpha3Code: 'CAF',
        numericCode: '140',
        callingCode: '+236'
    },
    {
        name: 'Tchad',
        alpha2Code: 'TD',
        alpha3Code: 'TCD',
        numericCode: '148',
        callingCode: '+235'
    },
    {
        name: 'Chili',
        alpha2Code: 'CL',
        alpha3Code: 'CHL',
        numericCode: '152',
        callingCode: '+56'
    },
    {
        name: 'Chine',
        alpha2Code: 'CN',
        alpha3Code: 'CHN',
        numericCode: '156',
        callingCode: '+86'
    },
    {
        name: 'Île Christmas',
        alpha2Code: 'CX',
        alpha3Code: 'CXR',
        numericCode: '162',
        callingCode: '+61'
    },
    {
        name: 'Îles Cocos',
        alpha2Code: 'CC',
        alpha3Code: 'CCK',
        numericCode: '166',
        callingCode: '+61'
    },
    {
        name: 'Colombie',
        alpha2Code: 'CO',
        alpha3Code: 'COL',
        numericCode: '170',
        callingCode: '+57'
    },
    {
        name: 'Comores',
        alpha2Code: 'KM',
        alpha3Code: 'COM',
        numericCode: '174',
        callingCode: '+269'
    },
    {
        name: 'Congo',
        alpha2Code: 'CG',
        alpha3Code: 'COG',
        numericCode: '178',
        callingCode: '+242'
    },
    {
        name: 'Congo (Rép. dém.)',
        alpha2Code: 'CD',
        alpha3Code: 'COD',
        numericCode: '180',
        callingCode: '+243'
    },
    {
        name: 'Îles Cook',
        alpha2Code: 'CK',
        alpha3Code: 'COK',
        numericCode: '184',
        callingCode: '+682'
    },
    {
        name: 'Costa Rica',
        alpha2Code: 'CR',
        alpha3Code: 'CRI',
        numericCode: '188',
        callingCode: '+506'
    },
    {
        name: 'Croatie',
        alpha2Code: 'HR',
        alpha3Code: 'HRV',
        numericCode: '191',
        callingCode: '+385'
    },
    {
        name: 'Cuba',
        alpha2Code: 'CU',
        alpha3Code: 'CUB',
        numericCode: '192',
        callingCode: '+53'
    },
    {
        name: 'Curaçao',
        alpha2Code: 'CW',
        alpha3Code: 'CUW',
        numericCode: '531',
        callingCode: '+599'
    },
    {
        name: 'Chypre',
        alpha2Code: 'CY',
        alpha3Code: 'CYP',
        numericCode: '196',
        callingCode: '+357'
    },
    {
        name: 'République tchèque',
        alpha2Code: 'CZ',
        alpha3Code: 'CZE',
        numericCode: '203',
        callingCode: '+420'
    },
    {
        name: 'Danemark',
        alpha2Code: 'DK',
        alpha3Code: 'DNK',
        numericCode: '208',
        callingCode: '+45'
    },
    {
        name: 'Djibouti',
        alpha2Code: 'DJ',
        alpha3Code: 'DJI',
        numericCode: '262',
        callingCode: '+253'
    },
    {
        name: 'Dominique',
        alpha2Code: 'DM',
        alpha3Code: 'DMA',
        numericCode: '212',
        callingCode: '+1767'
    },
    {
        name: 'République dominicaine',
        alpha2Code: 'DO',
        alpha3Code: 'DOM',
        numericCode: '214',
        callingCode: '+1809'
    },
    {
        name: 'Équateur',
        alpha2Code: 'EC',
        alpha3Code: 'ECU',
        numericCode: '218',
        callingCode: '+593'
    },
    {
        name: 'Égypte',
        alpha2Code: 'EG',
        alpha3Code: 'EGY',
        numericCode: '818',
        callingCode: '+20'
    },
    {
        name: 'Salvador',
        alpha2Code: 'SV',
        alpha3Code: 'SLV',
        numericCode: '222',
        callingCode: '+503'
    },
    {
        name: 'Guinée-Équatoriale',
        alpha2Code: 'GQ',
        alpha3Code: 'GNQ',
        numericCode: '226',
        callingCode: '+240'
    },
    {
        name: 'Érythrée',
        alpha2Code: 'ER',
        alpha3Code: 'ERI',
        numericCode: '232',
        callingCode: '+291'
    },
    {
        name: 'Estonie',
        alpha2Code: 'EE',
        alpha3Code: 'EST',
        numericCode: '233',
        callingCode: '+372'
    },
    {
        name: 'Éthiopie',
        alpha2Code: 'ET',
        alpha3Code: 'ETH',
        numericCode: '231',
        callingCode: '+251'
    },
    {
        name: 'Îles Malouines',
        alpha2Code: 'FK',
        alpha3Code: 'FLK',
        numericCode: '238',
        callingCode: '+500'
    },
    {
        name: 'Îles Féroé',
        alpha2Code: 'FO',
        alpha3Code: 'FRO',
        numericCode: '234',
        callingCode: '+298'
    },
    {
        name: 'Fidji',
        alpha2Code: 'FJ',
        alpha3Code: 'FJI',
        numericCode: '242',
        callingCode: '+679'
    },
    {
        name: 'Finlande',
        alpha2Code: 'FI',
        alpha3Code: 'FIN',
        numericCode: '246',
        callingCode: '+358'
    },
    {
        name: 'France',
        alpha2Code: 'FR',
        alpha3Code: 'FRA',
        numericCode: '250',
        callingCode: '+33'
    },
    {
        name: 'Guayane',
        alpha2Code: 'GF',
        alpha3Code: 'GUF',
        numericCode: '254',
        callingCode: '+594'
    },
    {
        name: 'Polynésie française',
        alpha2Code: 'PF',
        alpha3Code: 'PYF',
        numericCode: '258',
        callingCode: '+689'
    },
    {
        name: 'Terres australes et antarctiques françaises',
        alpha2Code: 'TF',
        alpha3Code: 'ATF',
        numericCode: '260',
        callingCode: '+'
    },
    {
        name: 'Gabon',
        alpha2Code: 'GA',
        alpha3Code: 'GAB',
        numericCode: '266',
        callingCode: '+241'
    },
    {
        name: 'Gambie',
        alpha2Code: 'GM',
        alpha3Code: 'GMB',
        numericCode: '270',
        callingCode: '+220'
    },
    {
        name: 'Géorgie',
        alpha2Code: 'GE',
        alpha3Code: 'GEO',
        numericCode: '268',
        callingCode: '+995'
    },
    {
        name: 'Allemagne',
        alpha2Code: 'DE',
        alpha3Code: 'DEU',
        numericCode: '276',
        callingCode: '+49',
    },
    {
        name: 'Ghana',
        alpha2Code: 'GH',
        alpha3Code: 'GHA',
        numericCode: '288',
        callingCode: '+233'
    },
    {
        name: 'Gibraltar',
        alpha2Code: 'GI',
        alpha3Code: 'GIB',
        numericCode: '292',
        callingCode: '+350'
    },
    {
        name: 'Grèce',
        alpha2Code: 'GR',
        alpha3Code: 'GRC',
        numericCode: '300',
        callingCode: '+30'
    },
    {
        name: 'Groenland',
        alpha2Code: 'GL',
        alpha3Code: 'GRL',
        numericCode: '304',
        callingCode: '+299'
    },
    {
        name: 'Grenade',
        alpha2Code: 'GD',
        alpha3Code: 'GRD',
        numericCode: '308',
        callingCode: '+1473'
    },
    {
        name: 'Guadeloupe',
        alpha2Code: 'GP',
        alpha3Code: 'GLP',
        numericCode: '312',
        callingCode: '+590'
    },
    {
        name: 'Guam',
        alpha2Code: 'GU',
        alpha3Code: 'GUM',
        numericCode: '316',
        callingCode: '+1671'
    },
    {
        name: 'Guatemala',
        alpha2Code: 'GT',
        alpha3Code: 'GTM',
        numericCode: '320',
        callingCode: '+502'
    },
    {
        name: 'Guernesey',
        alpha2Code: 'GG',
        alpha3Code: 'GGY',
        numericCode: '831',
        callingCode: '+44'
    },
    {
        name: 'Guinée',
        alpha2Code: 'GN',
        alpha3Code: 'GIN',
        numericCode: '324',
        callingCode: '+224'
    },
    {
        name: 'Guinée-Bissau',
        alpha2Code: 'GW',
        alpha3Code: 'GNB',
        numericCode: '624',
        callingCode: '+245'
    },
    {
        name: 'Guyane',
        alpha2Code: 'GY',
        alpha3Code: 'GUY',
        numericCode: '328',
        callingCode: '+592'
    },
    {
        name: 'Haïti',
        alpha2Code: 'HT',
        alpha3Code: 'HTI',
        numericCode: '332',
        callingCode: '+509'
    },
    {
        name: 'Îles Heard-et-MacDonald',
        alpha2Code: 'HM',
        alpha3Code: 'HMD',
        numericCode: '334',
        callingCode: '+'
    },
    {
        name: 'voir Saint',
        alpha2Code: 'VA',
        alpha3Code: 'VAT',
        numericCode: '336',
        callingCode: '+379'
    },
    {
        name: 'Honduras',
        alpha2Code: 'HN',
        alpha3Code: 'HND',
        numericCode: '340',
        callingCode: '+504'
    },
    {
        name: 'Hong Kong',
        alpha2Code: 'HK',
        alpha3Code: 'HKG',
        numericCode: '344',
        callingCode: '+852'
    },
    {
        name: 'Hongrie',
        alpha2Code: 'HU',
        alpha3Code: 'HUN',
        numericCode: '348',
        callingCode: '+36'
    },
    {
        name: 'Islande',
        alpha2Code: 'IS',
        alpha3Code: 'ISL',
        numericCode: '352',
        callingCode: '+354'
    },
    {
        name: 'Inde',
        alpha2Code: 'IN',
        alpha3Code: 'IND',
        numericCode: '356',
        callingCode: '+91'
    },
    {
        name: 'Indonésie',
        alpha2Code: 'ID',
        alpha3Code: 'IDN',
        numericCode: '360',
        callingCode: '+62'
    },
    {
        name: "Côte d'Ivoire",
        alpha2Code: 'CI',
        alpha3Code: 'CIV',
        numericCode: '384',
        callingCode: '+225'
    },
    {
        name: 'Iran',
        alpha2Code: 'IR',
        alpha3Code: 'IRN',
        numericCode: '364',
        callingCode: '+98'
    },
    {
        name: 'Irak',
        alpha2Code: 'IQ',
        alpha3Code: 'IRQ',
        numericCode: '368',
        callingCode: '+964'
    },
    {
        name: 'Irlande',
        alpha2Code: 'IE',
        alpha3Code: 'IRL',
        numericCode: '372',
        callingCode: '+353'
    },
    {
        name: 'Île de Man',
        alpha2Code: 'IM',
        alpha3Code: 'IMN',
        numericCode: '833',
        callingCode: '+44'
    },
    {
        name: 'Israël',
        alpha2Code: 'IL',
        alpha3Code: 'ISR',
        numericCode: '376',
        callingCode: '+972'
    },
    {
        name: 'Italie',
        alpha2Code: 'IT',
        alpha3Code: 'ITA',
        numericCode: '380',
        callingCode: '+39'
    },
    {
        name: 'Jamaïque',
        alpha2Code: 'JM',
        alpha3Code: 'JAM',
        numericCode: '388',
        callingCode: '+1876'
    },
    {
        name: 'Japon',
        alpha2Code: 'JP',
        alpha3Code: 'JPN',
        numericCode: '392',
        callingCode: '+81'
    },
    {
        name: 'Jersey',
        alpha2Code: 'JE',
        alpha3Code: 'JEY',
        numericCode: '832',
        callingCode: '+44'
    },
    {
        name: 'Jordanie',
        alpha2Code: 'JO',
        alpha3Code: 'JOR',
        numericCode: '400',
        callingCode: '+962'
    },
    {
        name: 'Kazakhstan',
        alpha2Code: 'KZ',
        alpha3Code: 'KAZ',
        numericCode: '398',
        callingCode: '+7'
    },
    {
        name: 'Kenya',
        alpha2Code: 'KE',
        alpha3Code: 'KEN',
        numericCode: '404',
        callingCode: '+254'
    },
    {
        name: 'Kiribati',
        alpha2Code: 'KI',
        alpha3Code: 'KIR',
        numericCode: '296',
        callingCode: '+686'
    },
    {
        name: 'Koweït',
        alpha2Code: 'KW',
        alpha3Code: 'KWT',
        numericCode: '414',
        callingCode: '+965'
    },
    {
        name: 'Kirghizistan',
        alpha2Code: 'KG',
        alpha3Code: 'KGZ',
        numericCode: '417',
        callingCode: '+996'
    },
    {
        name: 'Laos',
        alpha2Code: 'LA',
        alpha3Code: 'LAO',
        numericCode: '418',
        callingCode: '+856'
    },
    {
        name: 'Lettonie',
        alpha2Code: 'LV',
        alpha3Code: 'LVA',
        numericCode: '428',
        callingCode: '+371'
    },
    {
        name: 'Liban',
        alpha2Code: 'LB',
        alpha3Code: 'LBN',
        numericCode: '422',
        callingCode: '+961'
    },
    {
        name: 'Lesotho',
        alpha2Code: 'LS',
        alpha3Code: 'LSO',
        numericCode: '426',
        callingCode: '+266'
    },
    {
        name: 'Liberia',
        alpha2Code: 'LR',
        alpha3Code: 'LBR',
        numericCode: '430',
        callingCode: '+231'
    },
    {
        name: 'Libye',
        alpha2Code: 'LY',
        alpha3Code: 'LBY',
        numericCode: '434',
        callingCode: '+218'
    },
    {
        name: 'Liechtenstein',
        alpha2Code: 'LI',
        alpha3Code: 'LIE',
        numericCode: '438',
        callingCode: '+423'
    },
    {
        name: 'Lituanie',
        alpha2Code: 'LT',
        alpha3Code: 'LTU',
        numericCode: '440',
        callingCode: '+370'
    },
    {
        name: 'Luxembourg',
        alpha2Code: 'LU',
        alpha3Code: 'LUX',
        numericCode: '442',
        callingCode: '+352'
    },
    {
        name: 'Macao',
        alpha2Code: 'MO',
        alpha3Code: 'MAC',
        numericCode: '446',
        callingCode: '+853'
    },
    {
        name: 'Macédoine',
        alpha2Code: 'MK',
        alpha3Code: 'MKD',
        numericCode: '807',
        callingCode: '+389'
    },
    {
        name: 'Madagascar',
        alpha2Code: 'MG',
        alpha3Code: 'MDG',
        numericCode: '450',
        callingCode: '+261'
    },
    {
        name: 'Malawi',
        alpha2Code: 'MW',
        alpha3Code: 'MWI',
        numericCode: '454',
        callingCode: '+265'
    },
    {
        name: 'Malaisie',
        alpha2Code: 'MY',
        alpha3Code: 'MYS',
        numericCode: '458',
        callingCode: '+60'
    },
    {
        name: 'Maldives',
        alpha2Code: 'MV',
        alpha3Code: 'MDV',
        numericCode: '462',
        callingCode: '+960'
    },
    {
        name: 'Mali',
        alpha2Code: 'ML',
        alpha3Code: 'MLI',
        numericCode: '466',
        callingCode: '+223'
    },
    {
        name: 'Malte',
        alpha2Code: 'MT',
        alpha3Code: 'MLT',
        numericCode: '470',
        callingCode: '+356'
    },
    {
        name: 'Îles Marshall',
        alpha2Code: 'MH',
        alpha3Code: 'MHL',
        numericCode: '584',
        callingCode: '+692'
    },
    {
        name: 'Martinique',
        alpha2Code: 'MQ',
        alpha3Code: 'MTQ',
        numericCode: '474',
        callingCode: '+596'
    },
    {
        name: 'Mauritanie',
        alpha2Code: 'MR',
        alpha3Code: 'MRT',
        numericCode: '478',
        callingCode: '+222'
    },
    {
        name: 'Île Maurice',
        alpha2Code: 'MU',
        alpha3Code: 'MUS',
        numericCode: '480',
        callingCode: '+230'
    },
    {
        name: 'Mayotte',
        alpha2Code: 'YT',
        alpha3Code: 'MYT',
        numericCode: '175',
        callingCode: '+262'
    },
    {
        name: 'Mexique',
        alpha2Code: 'MX',
        alpha3Code: 'MEX',
        numericCode: '484',
        callingCode: '+52'
    },
    {
        name: 'Micronésie',
        alpha2Code: 'FM',
        alpha3Code: 'FSM',
        numericCode: '583',
        callingCode: '+691'
    },
    {
        name: 'Moldavie',
        alpha2Code: 'MD',
        alpha3Code: 'MDA',
        numericCode: '498',
        callingCode: '+373'
    },
    {
        name: 'Monaco',
        alpha2Code: 'MC',
        alpha3Code: 'MCO',
        numericCode: '492',
        callingCode: '+377'
    },
    {
        name: 'Mongolie',
        alpha2Code: 'MN',
        alpha3Code: 'MNG',
        numericCode: '496',
        callingCode: '+976'
    },
    {
        name: 'Monténégro',
        alpha2Code: 'ME',
        alpha3Code: 'MNE',
        numericCode: '499',
        callingCode: '+382'
    },
    {
        name: 'Montserrat',
        alpha2Code: 'MS',
        alpha3Code: 'MSR',
        numericCode: '500',
        callingCode: '+1664'
    },
    {
        name: 'Maroc',
        alpha2Code: 'MA',
        alpha3Code: 'MAR',
        numericCode: '504',
        callingCode: '+212'
    },
    {
        name: 'Mozambique',
        alpha2Code: 'MZ',
        alpha3Code: 'MOZ',
        numericCode: '508',
        callingCode: '+258'
    },
    {
        name: 'Myanmar',
        alpha2Code: 'MM',
        alpha3Code: 'MMR',
        numericCode: '104',
        callingCode: '+95'
    },
    {
        name: 'Namibie',
        alpha2Code: 'NA',
        alpha3Code: 'NAM',
        numericCode: '516',
        callingCode: '+264'
    },
    {
        name: 'Nauru',
        alpha2Code: 'NR',
        alpha3Code: 'NRU',
        numericCode: '520',
        callingCode: '+674'
    },
    {
        name: 'Népal',
        alpha2Code: 'NP',
        alpha3Code: 'NPL',
        numericCode: '524',
        callingCode: '+977'
    },
    {
        name: 'Pays-Bas',
        alpha2Code: 'NL',
        alpha3Code: 'NLD',
        numericCode: '528',
        callingCode: '+31'
    },
    {
        name: 'Nouvelle-Calédonie',
        alpha2Code: 'NC',
        alpha3Code: 'NCL',
        numericCode: '540',
        callingCode: '+687'
    },
    {
        name: 'Nouvelle-Zélande',
        alpha2Code: 'NZ',
        alpha3Code: 'NZL',
        numericCode: '554',
        callingCode: '+64'
    },
    {
        name: 'Nicaragua',
        alpha2Code: 'NI',
        alpha3Code: 'NIC',
        numericCode: '558',
        callingCode: '+505'
    },
    {
        name: 'Niger',
        alpha2Code: 'NE',
        alpha3Code: 'NER',
        numericCode: '562',
        callingCode: '+227'
    },
    {
        name: 'Nigéria',
        alpha2Code: 'NG',
        alpha3Code: 'NGA',
        numericCode: '566',
        callingCode: '+234'
    },
    {
        name: 'Niue',
        alpha2Code: 'NU',
        alpha3Code: 'NIU',
        numericCode: '570',
        callingCode: '+683'
    },
    {
        name: 'Île de Norfolk',
        alpha2Code: 'NF',
        alpha3Code: 'NFK',
        numericCode: '574',
        callingCode: '+672'
    },
    {
        name: 'Corée du Nord',
        alpha2Code: 'KP',
        alpha3Code: 'PRK',
        numericCode: '408',
        callingCode: '+850'
    },
    {
        name: 'Îles Mariannes du Nord',
        alpha2Code: 'MP',
        alpha3Code: 'MNP',
        numericCode: '580',
        callingCode: '+1670'
    },
    {
        name: 'Norvège',
        alpha2Code: 'NO',
        alpha3Code: 'NOR',
        numericCode: '578',
        callingCode: '+47'
    },
    {
        name: 'Oman',
        alpha2Code: 'OM',
        alpha3Code: 'OMN',
        numericCode: '512',
        callingCode: '+968'
    },
    {
        name: 'Pakistan',
        alpha2Code: 'PK',
        alpha3Code: 'PAK',
        numericCode: '586',
        callingCode: '+92'
    },
    {
        name: 'Palaos',
        alpha2Code: 'PW',
        alpha3Code: 'PLW',
        numericCode: '585',
        callingCode: '+680'
    },
    {
        name: 'Palestine',
        alpha2Code: 'PS',
        alpha3Code: 'PSE',
        numericCode: '275',
        callingCode: '+970'
    },
    {
        name: 'Panama',
        alpha2Code: 'PA',
        alpha3Code: 'PAN',
        numericCode: '591',
        callingCode: '+507'
    },
    {
        name: 'Papouasie-Nouvelle-Guinée',
        alpha2Code: 'PG',
        alpha3Code: 'PNG',
        numericCode: '598',
        callingCode: '+675'
    },
    {
        name: 'Paraguay',
        alpha2Code: 'PY',
        alpha3Code: 'PRY',
        numericCode: '600',
        callingCode: '+595'
    },
    {
        name: 'Pérou',
        alpha2Code: 'PE',
        alpha3Code: 'PER',
        numericCode: '604',
        callingCode: '+51'
    },
    {
        name: 'Philippines',
        alpha2Code: 'PH',
        alpha3Code: 'PHL',
        numericCode: '608',
        callingCode: '+63'
    },
    {
        name: 'Îles Pitcairn',
        alpha2Code: 'PN',
        alpha3Code: 'PCN',
        numericCode: '612',
        callingCode: '+64'
    },
    {
        name: 'Pologne',
        alpha2Code: 'PL',
        alpha3Code: 'POL',
        numericCode: '616',
        callingCode: '+48'
    },
    {
        name: 'Portugal',
        alpha2Code: 'PT',
        alpha3Code: 'PRT',
        numericCode: '620',
        callingCode: '+351'
    },
    {
        name: 'Porto Rico',
        alpha2Code: 'PR',
        alpha3Code: 'PRI',
        numericCode: '630',
        callingCode: '+1'
    },
    {
        name: 'Qatar',
        alpha2Code: 'QA',
        alpha3Code: 'QAT',
        numericCode: '634',
        callingCode: '+974'
    },
    {
        name: 'Kosovo',
        alpha2Code: 'XK',
        alpha3Code: 'KOS',
        numericCode: null,
        callingCode: '+383'
    },
    {
        name: 'Réunion',
        alpha2Code: 'RE',
        alpha3Code: 'REU',
        numericCode: '638',
        callingCode: '+262'
    },
    {
        name: 'Roumanie',
        alpha2Code: 'RO',
        alpha3Code: 'ROU',
        numericCode: '642',
        callingCode: '+40'
    },
    {
        name: 'Russie',
        alpha2Code: 'RU',
        alpha3Code: 'RUS',
        numericCode: '643',
        callingCode: '+7'
    },
    {
        name: 'Rwanda',
        alpha2Code: 'RW',
        alpha3Code: 'RWA',
        numericCode: '646',
        callingCode: '+250'
    },
    {
        name: 'Saint-Barthélemy',
        alpha2Code: 'BL',
        alpha3Code: 'BLM',
        numericCode: '652',
        callingCode: '+590'
    },
    {
        name: 'Sainte-Hélène',
        alpha2Code: 'SH',
        alpha3Code: 'SHN',
        numericCode: '654',
        callingCode: '+290'
    },
    {
        name: 'Saint-Christophe-et-Niévès',
        alpha2Code: 'KN',
        alpha3Code: 'KNA',
        numericCode: '659',
        callingCode: '+1869'
    },
    {
        name: 'Saint-Lucie',
        alpha2Code: 'LC',
        alpha3Code: 'LCA',
        numericCode: '662',
        callingCode: '+1758'
    },
    {
        name: 'Saint-Martin',
        alpha2Code: 'MF',
        alpha3Code: 'MAF',
        numericCode: '663',
        callingCode: '+590'
    },
    {
        name: 'Saint-Pierre-et-Miquelon',
        alpha2Code: 'PM',
        alpha3Code: 'SPM',
        numericCode: '666',
        callingCode: '+508'
    },
    {
        name: 'Saint-Vincent-et-les-Grenadines',
        alpha2Code: 'VC',
        alpha3Code: 'VCT',
        numericCode: '670',
        callingCode: '+1784'
    },
    {
        name: 'Samoa',
        alpha2Code: 'WS',
        alpha3Code: 'WSM',
        numericCode: '882',
        callingCode: '+685'
    },
    {
        name: 'Saint-Marin',
        alpha2Code: 'SM',
        alpha3Code: 'SMR',
        numericCode: '674',
        callingCode: '+378'
    },
    {
        name: 'Sao Tomé-et-Principe',
        alpha2Code: 'ST',
        alpha3Code: 'STP',
        numericCode: '678',
        callingCode: '+239'
    },
    {
        name: 'Arabie Saoudite',
        alpha2Code: 'SA',
        alpha3Code: 'SAU',
        numericCode: '682',
        callingCode: '+966'
    },
    {
        name: 'Sénégal',
        alpha2Code: 'SN',
        alpha3Code: 'SEN',
        numericCode: '686',
        callingCode: '+221'
    },
    {
        name: 'Serbie',
        alpha2Code: 'RS',
        alpha3Code: 'SRB',
        numericCode: '688',
        callingCode: '+381'
    },
    {
        name: 'Seychelles',
        alpha2Code: 'SC',
        alpha3Code: 'SYC',
        numericCode: '690',
        callingCode: '+248'
    },
    {
        name: 'Sierra Leone',
        alpha2Code: 'SL',
        alpha3Code: 'SLE',
        numericCode: '694',
        callingCode: '+232'
    },
    {
        name: 'Singapour',
        alpha2Code: 'SG',
        alpha3Code: 'SGP',
        numericCode: '702',
        callingCode: '+65'
    },
    {
        name: 'Saint Martin (partie néerlandaise)',
        alpha2Code: 'SX',
        alpha3Code: 'SXM',
        numericCode: '534',
        callingCode: '+1721'
    },
    {
        name: 'Slovaquie',
        alpha2Code: 'SK',
        alpha3Code: 'SVK',
        numericCode: '703',
        callingCode: '+421'
    },
    {
        name: 'Slovénie',
        alpha2Code: 'SI',
        alpha3Code: 'SVN',
        numericCode: '705',
        callingCode: '+386'
    },
    {
        name: 'Îles Salomon',
        alpha2Code: 'SB',
        alpha3Code: 'SLB',
        numericCode: '090',
        callingCode: '+677'
    },
    {
        name: 'Somalie',
        alpha2Code: 'SO',
        alpha3Code: 'SOM',
        numericCode: '706',
        callingCode: '+252'
    },
    {
        name: 'Afrique du Sud',
        alpha2Code: 'ZA',
        alpha3Code: 'ZAF',
        numericCode: '710',
        callingCode: '+27'
    },
    {
        name: 'Géorgie du Sud-et-les Îles Sandwich du Sud',
        alpha2Code: 'GS',
        alpha3Code: 'SGS',
        numericCode: '239',
        callingCode: '+500'
    },
    {
        name: 'Corée du Sud',
        alpha2Code: 'KR',
        alpha3Code: 'KOR',
        numericCode: '410',
        callingCode: '+82'
    },
    {
        name: 'Soudan du Sud',
        alpha2Code: 'SS',
        alpha3Code: 'SSD',
        numericCode: '728',
        callingCode: '+211'
    },
    {
        name: 'Espagne',
        alpha2Code: 'ES',
        alpha3Code: 'ESP',
        numericCode: '724',
        callingCode: '+34'
    },
    {
        name: 'Sri Lanka',
        alpha2Code: 'LK',
        alpha3Code: 'LKA',
        numericCode: '144',
        callingCode: '+94'
    },
    {
        name: 'Soudan',
        alpha2Code: 'SD',
        alpha3Code: 'SDN',
        numericCode: '729',
        callingCode: '+249'
    },
    {
        name: 'Surinam',
        alpha2Code: 'SR',
        alpha3Code: 'SUR',
        numericCode: '740',
        callingCode: '+597'
    },
    {
        name: 'Svalbard et Jan Mayen',
        alpha2Code: 'SJ',
        alpha3Code: 'SJM',
        numericCode: '744',
        callingCode: '+4779'
    },
    {
        name: 'Swaziland',
        alpha2Code: 'SZ',
        alpha3Code: 'SWZ',
        numericCode: '748',
        callingCode: '+268'
    },
    {
        name: 'Suède',
        alpha2Code: 'SE',
        alpha3Code: 'SWE',
        numericCode: '752',
        callingCode: '+46'
    },
    {
        name: 'Suisse',
        alpha2Code: 'CH',
        alpha3Code: 'CHE',
        numericCode: '756',
        callingCode: '+41'
    },
    {
        name: 'Syrie',
        alpha2Code: 'SY',
        alpha3Code: 'SYR',
        numericCode: '760',
        callingCode: '+963'
    },
    {
        name: 'Taïwan',
        alpha2Code: 'TW',
        alpha3Code: 'TWN',
        numericCode: '158',
        callingCode: '+886'
    },
    {
        name: 'Tadjikistan',
        alpha2Code: 'TJ',
        alpha3Code: 'TJK',
        numericCode: '762',
        callingCode: '+992'
    },
    {
        name: 'Tanzanie',
        alpha2Code: 'TZ',
        alpha3Code: 'TZA',
        numericCode: '834',
        callingCode: '+255'
    },
    {
        name: 'Thaïlande',
        alpha2Code: 'TH',
        alpha3Code: 'THA',
        numericCode: '764',
        callingCode: '+66'
    },
    {
        name: 'Timor oriental',
        alpha2Code: 'TL',
        alpha3Code: 'TLS',
        numericCode: '626',
        callingCode: '+670'
    },
    {
        name: 'Togo',
        alpha2Code: 'TG',
        alpha3Code: 'TGO',
        numericCode: '768',
        callingCode: '+228'
    },
    {
        name: 'Tokelau',
        alpha2Code: 'TK',
        alpha3Code: 'TKL',
        numericCode: '772',
        callingCode: '+690'
    },
    {
        name: 'Tonga',
        alpha2Code: 'TO',
        alpha3Code: 'TON',
        numericCode: '776',
        callingCode: '+676'
    },
    {
        name: 'Trinité et Tobago',
        alpha2Code: 'TT',
        alpha3Code: 'TTO',
        numericCode: '780',
        callingCode: '+1868'
    },
    {
        name: 'Tunisie',
        alpha2Code: 'TN',
        alpha3Code: 'TUN',
        numericCode: '788',
        callingCode: '+216'
    },
    {
        name: 'Turquie',
        alpha2Code: 'TR',
        alpha3Code: 'TUR',
        numericCode: '792',
        callingCode: '+90'
    },
    {
        name: 'Turkménistan',
        alpha2Code: 'TM',
        alpha3Code: 'TKM',
        numericCode: '795',
        callingCode: '+993'
    },
    {
        name: 'Îles Turques-et-Caïques',
        alpha2Code: 'TC',
        alpha3Code: 'TCA',
        numericCode: '796',
        callingCode: '+1649'
    },
    {
        name: 'Tuvalu',
        alpha2Code: 'TV',
        alpha3Code: 'TUV',
        numericCode: '798',
        callingCode: '+688'
    },
    {
        name: 'Uganda',
        alpha2Code: 'UG',
        alpha3Code: 'UGA',
        numericCode: '800',
        callingCode: '+256'
    },
    {
        name: 'Ukraine',
        alpha2Code: 'UA',
        alpha3Code: 'UKR',
        numericCode: '804',
        callingCode: '+380'
    },
    {
        name: 'Émirats arabes unis',
        alpha2Code: 'AE',
        alpha3Code: 'ARE',
        numericCode: '784',
        callingCode: '+971'
    },
    {
        name: 'Royaume-Uni',
        alpha2Code: 'GB',
        alpha3Code: 'GBR',
        numericCode: '826',
        callingCode: '+44'
    },
    {
        name: 'États-Unis',
        alpha2Code: 'US',
        alpha3Code: 'USA',
        numericCode: '840',
        callingCode: '+1'
    },
    {
        name: 'Uruguay',
        alpha2Code: 'UY',
        alpha3Code: 'URY',
        numericCode: '858',
        callingCode: '+598'
    },
    {
        name: 'Ouzbékistan',
        alpha2Code: 'UZ',
        alpha3Code: 'UZB',
        numericCode: '860',
        callingCode: '+998'
    },
    {
        name: 'Vanuatu',
        alpha2Code: 'VU',
        alpha3Code: 'VUT',
        numericCode: '548',
        callingCode: '+678'
    },
    {
        name: 'Venezuela',
        alpha2Code: 'VE',
        alpha3Code: 'VEN',
        numericCode: '862',
        callingCode: '+58'
    },
    {
        name: 'Viêt Nam',
        alpha2Code: 'VN',
        alpha3Code: 'VNM',
        numericCode: '704',
        callingCode: '+84'
    },
    {
        name: 'Wallis-et-Futuna',
        alpha2Code: 'WF',
        alpha3Code: 'WLF',
        numericCode: '876',
        callingCode: '+681'
    },
    {
        name: 'Sahara Occidental',
        alpha2Code: 'EH',
        alpha3Code: 'ESH',
        numericCode: '732',
        callingCode: '+212'
    },
    {
        name: 'Yémen',
        alpha2Code: 'YE',
        alpha3Code: 'YEM',
        numericCode: '887',
        callingCode: '+967'
    },
    {
        name: 'Zambie',
        alpha2Code: 'ZM',
        alpha3Code: 'ZMB',
        numericCode: '894',
        callingCode: '+260'
    },
    {
        name: 'Zimbabwe',
        alpha2Code: 'ZW',
        alpha3Code: 'ZWE',
        numericCode: '716',
        callingCode: '+263'
    }
];

var fr = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB_FR: COUNTRIES_DB_FR
});

const COUNTRIES_DB_HR = [
    {
        name: 'Afganistan',
        alpha2Code: 'AF',
        alpha3Code: 'AFG',
        numericCode: '004',
        callingCode: '+93'
    },
    {
        name: 'Ålandski otoci',
        alpha2Code: 'AX',
        alpha3Code: 'ALA',
        numericCode: '248',
        callingCode: '+358'
    },
    {
        name: 'Albanija',
        alpha2Code: 'AL',
        alpha3Code: 'ALB',
        numericCode: '008',
        callingCode: '+355'
    },
    {
        name: 'Alžir',
        alpha2Code: 'DZ',
        alpha3Code: 'DZA',
        numericCode: '012',
        callingCode: '+213'
    },
    {
        name: 'Američka Samoa',
        alpha2Code: 'AS',
        alpha3Code: 'ASM',
        numericCode: '016',
        callingCode: '+1684'
    },
    {
        name: 'Andora',
        alpha2Code: 'AD',
        alpha3Code: 'AND',
        numericCode: '020',
        callingCode: '+376'
    },
    {
        name: 'Angola',
        alpha2Code: 'AO',
        alpha3Code: 'AGO',
        numericCode: '024',
        callingCode: '+244'
    },
    {
        name: 'Angvila',
        alpha2Code: 'AI',
        alpha3Code: 'AIA',
        numericCode: '660',
        callingCode: '+1264'
    },
    {
        name: 'Antarktika',
        alpha2Code: 'AQ',
        alpha3Code: 'ATA',
        numericCode: '010',
        callingCode: '+672'
    },
    {
        name: 'Antigva i Barbuda',
        alpha2Code: 'AG',
        alpha3Code: 'ATG',
        numericCode: '028',
        callingCode: '+1268'
    },
    {
        name: 'Argentina',
        alpha2Code: 'AR',
        alpha3Code: 'ARG',
        numericCode: '032',
        callingCode: '+54'
    },
    {
        name: 'Armenija',
        alpha2Code: 'AM',
        alpha3Code: 'ARM',
        numericCode: '051',
        callingCode: '+374'
    },
    {
        name: 'Aruba',
        alpha2Code: 'AW',
        alpha3Code: 'ABW',
        numericCode: '533',
        callingCode: '+297'
    },
    {
        name: 'Australija',
        alpha2Code: 'AU',
        alpha3Code: 'AUS',
        numericCode: '036',
        callingCode: '+61'
    },
    {
        name: 'Austrija',
        alpha2Code: 'AT',
        alpha3Code: 'AUT',
        numericCode: '040',
        callingCode: '+43'
    },
    {
        name: 'Azerbajdžan',
        alpha2Code: 'AZ',
        alpha3Code: 'AZE',
        numericCode: '031',
        callingCode: '+994'
    },
    {
        name: 'Bahami',
        alpha2Code: 'BS',
        alpha3Code: 'BHS',
        numericCode: '044',
        callingCode: '+1242'
    },
    {
        name: 'Bahrein',
        alpha2Code: 'BH',
        alpha3Code: 'BHR',
        numericCode: '048',
        callingCode: '+973'
    },
    {
        name: 'Bangladeš',
        alpha2Code: 'BD',
        alpha3Code: 'BGD',
        numericCode: '050',
        callingCode: '+880'
    },
    {
        name: 'Barbados',
        alpha2Code: 'BB',
        alpha3Code: 'BRB',
        numericCode: '052',
        callingCode: '+1246'
    },
    {
        name: 'Bjelorusija',
        alpha2Code: 'BY',
        alpha3Code: 'BLR',
        numericCode: '112',
        callingCode: '+375'
    },
    {
        name: 'Belgija',
        alpha2Code: 'BE',
        alpha3Code: 'BEL',
        numericCode: '056',
        callingCode: '+32'
    },
    {
        name: 'Belize',
        alpha2Code: 'BZ',
        alpha3Code: 'BLZ',
        numericCode: '084',
        callingCode: '+501'
    },
    {
        name: 'Benin',
        alpha2Code: 'BJ',
        alpha3Code: 'BEN',
        numericCode: '204',
        callingCode: '+229'
    },
    {
        name: 'Bermudi',
        alpha2Code: 'BM',
        alpha3Code: 'BMU',
        numericCode: '060',
        callingCode: '+1441'
    },
    {
        name: 'Butan',
        alpha2Code: 'BT',
        alpha3Code: 'BTN',
        numericCode: '064',
        callingCode: '+975'
    },
    {
        name: 'Bolivija',
        alpha2Code: 'BO',
        alpha3Code: 'BOL',
        numericCode: '068',
        callingCode: '+591'
    },
    {
        name: 'Bonaire, Sint Eustacije i Saba',
        alpha2Code: 'BQ',
        alpha3Code: 'BES',
        numericCode: '535',
        callingCode: '+5997'
    },
    {
        name: 'Bosna i Hercegovina',
        alpha2Code: 'BA',
        alpha3Code: 'BIH',
        numericCode: '070',
        callingCode: '+387'
    },
    {
        name: 'Bocvana',
        alpha2Code: 'BW',
        alpha3Code: 'BWA',
        numericCode: '072',
        callingCode: '+267'
    },
    {
        name: 'Otok Bouvet',
        alpha2Code: 'BV',
        alpha3Code: 'BVT',
        numericCode: '074',
        callingCode: '+'
    },
    {
        name: 'Brazil',
        alpha2Code: 'BR',
        alpha3Code: 'BRA',
        numericCode: '076',
        callingCode: '+55'
    },
    {
        name: 'Britanski Indijskooceanski teritorij',
        alpha2Code: 'IO',
        alpha3Code: 'IOT',
        numericCode: '086',
        callingCode: '+246'
    },
    {
        name: 'Mali udaljeni otoci SAD-a',
        alpha2Code: 'UM',
        alpha3Code: 'UMI',
        numericCode: '581',
        callingCode: '+'
    },
    {
        name: 'Britanski Djevičanski Otoci',
        alpha2Code: 'VG',
        alpha3Code: 'VGB',
        numericCode: '092',
        callingCode: '+1284'
    },
    {
        name: 'Djevičanski Otoci (Britanski)',
        alpha2Code: 'VI',
        alpha3Code: 'VIR',
        numericCode: '850',
        callingCode: '+1 340'
    },
    {
        name: 'Brunej',
        alpha2Code: 'BN',
        alpha3Code: 'BRN',
        numericCode: '096',
        callingCode: '+673'
    },
    {
        name: 'Bugarska',
        alpha2Code: 'BG',
        alpha3Code: 'BGR',
        numericCode: '100',
        callingCode: '+359'
    },
    {
        name: 'Burkina Faso',
        alpha2Code: 'BF',
        alpha3Code: 'BFA',
        numericCode: '854',
        callingCode: '+226'
    },
    {
        name: 'Burundi',
        alpha2Code: 'BI',
        alpha3Code: 'BDI',
        numericCode: '108',
        callingCode: '+257'
    },
    {
        name: 'Kambodža',
        alpha2Code: 'KH',
        alpha3Code: 'KHM',
        numericCode: '116',
        callingCode: '+855'
    },
    {
        name: 'Kamerun',
        alpha2Code: 'CM',
        alpha3Code: 'CMR',
        numericCode: '120',
        callingCode: '+237'
    },
    {
        name: 'Kanada',
        alpha2Code: 'CA',
        alpha3Code: 'CAN',
        numericCode: '124',
        callingCode: '+1'
    },
    {
        name: 'Zelenortska Republika',
        alpha2Code: 'CV',
        alpha3Code: 'CPV',
        numericCode: '132',
        callingCode: '+238'
    },
    {
        name: 'Kajmanski otoci',
        alpha2Code: 'KY',
        alpha3Code: 'CYM',
        numericCode: '136',
        callingCode: '+1345'
    },
    {
        name: 'Srednjoafrička Republika',
        alpha2Code: 'CF',
        alpha3Code: 'CAF',
        numericCode: '140',
        callingCode: '+236'
    },
    {
        name: 'Čad',
        alpha2Code: 'TD',
        alpha3Code: 'TCD',
        numericCode: '148',
        callingCode: '+235'
    },
    {
        name: 'Čile',
        alpha2Code: 'CL',
        alpha3Code: 'CHL',
        numericCode: '152',
        callingCode: '+56'
    },
    {
        name: 'Kina',
        alpha2Code: 'CN',
        alpha3Code: 'CHN',
        numericCode: '156',
        callingCode: '+86'
    },
    {
        name: 'Božićni otok',
        alpha2Code: 'CX',
        alpha3Code: 'CXR',
        numericCode: '162',
        callingCode: '+61'
    },
    {
        name: 'Kokosovi Otoci',
        alpha2Code: 'CC',
        alpha3Code: 'CCK',
        numericCode: '166',
        callingCode: '+61'
    },
    {
        name: 'Kolumbija',
        alpha2Code: 'CO',
        alpha3Code: 'COL',
        numericCode: '170',
        callingCode: '+57'
    },
    {
        name: 'Komori',
        alpha2Code: 'KM',
        alpha3Code: 'COM',
        numericCode: '174',
        callingCode: '+269'
    },
    {
        name: 'Kongo',
        alpha2Code: 'CG',
        alpha3Code: 'COG',
        numericCode: '178',
        callingCode: '+242'
    },
    {
        name: 'Kongo, Demokratska Republika',
        alpha2Code: 'CD',
        alpha3Code: 'COD',
        numericCode: '180',
        callingCode: '+243'
    },
    {
        name: 'Cookovo Otočje',
        alpha2Code: 'CK',
        alpha3Code: 'COK',
        numericCode: '184',
        callingCode: '+682'
    },
    {
        name: 'Kostarika',
        alpha2Code: 'CR',
        alpha3Code: 'CRI',
        numericCode: '188',
        callingCode: '+506'
    },
    {
        name: 'Hrvatska',
        alpha2Code: 'HR',
        alpha3Code: 'HRV',
        numericCode: '191',
        callingCode: '+385'
    },
    {
        name: 'Kuba',
        alpha2Code: 'CU',
        alpha3Code: 'CUB',
        numericCode: '192',
        callingCode: '+53'
    },
    {
        name: 'Curaçao',
        alpha2Code: 'CW',
        alpha3Code: 'CUW',
        numericCode: '531',
        callingCode: '+599'
    },
    {
        name: 'Cipar',
        alpha2Code: 'CY',
        alpha3Code: 'CYP',
        numericCode: '196',
        callingCode: '+357'
    },
    {
        name: 'Češka',
        alpha2Code: 'CZ',
        alpha3Code: 'CZE',
        numericCode: '203',
        callingCode: '+420'
    },
    {
        name: 'Danska',
        alpha2Code: 'DK',
        alpha3Code: 'DNK',
        numericCode: '208',
        callingCode: '+45'
    },
    {
        name: 'Džibuti',
        alpha2Code: 'DJ',
        alpha3Code: 'DJI',
        numericCode: '262',
        callingCode: '+253'
    },
    {
        name: 'Dominika',
        alpha2Code: 'DM',
        alpha3Code: 'DMA',
        numericCode: '212',
        callingCode: '+1767'
    },
    {
        name: 'Dominikanska Republika',
        alpha2Code: 'DO',
        alpha3Code: 'DOM',
        numericCode: '214',
        callingCode: '+1809'
    },
    {
        name: 'Ekvador',
        alpha2Code: 'EC',
        alpha3Code: 'ECU',
        numericCode: '218',
        callingCode: '+593'
    },
    {
        name: 'Egipat',
        alpha2Code: 'EG',
        alpha3Code: 'EGY',
        numericCode: '818',
        callingCode: '+20'
    },
    {
        name: 'Salvador',
        alpha2Code: 'SV',
        alpha3Code: 'SLV',
        numericCode: '222',
        callingCode: '+503'
    },
    {
        name: 'Ekvatorijalna Gvineja',
        alpha2Code: 'GQ',
        alpha3Code: 'GNQ',
        numericCode: '226',
        callingCode: '+240'
    },
    {
        name: 'Eritreja',
        alpha2Code: 'ER',
        alpha3Code: 'ERI',
        numericCode: '232',
        callingCode: '+291'
    },
    {
        name: 'Estonija',
        alpha2Code: 'EE',
        alpha3Code: 'EST',
        numericCode: '233',
        callingCode: '+372'
    },
    {
        name: 'Etiopija',
        alpha2Code: 'ET',
        alpha3Code: 'ETH',
        numericCode: '231',
        callingCode: '+251'
    },
    {
        name: 'Falklandski Otoci',
        alpha2Code: 'FK',
        alpha3Code: 'FLK',
        numericCode: '238',
        callingCode: '+500'
    },
    {
        name: 'Farski Otoci',
        alpha2Code: 'FO',
        alpha3Code: 'FRO',
        numericCode: '234',
        callingCode: '+298'
    },
    {
        name: 'Fiđi',
        alpha2Code: 'FJ',
        alpha3Code: 'FJI',
        numericCode: '242',
        callingCode: '+679'
    },
    {
        name: 'Finska',
        alpha2Code: 'FI',
        alpha3Code: 'FIN',
        numericCode: '246',
        callingCode: '+358'
    },
    {
        name: 'Francuska',
        alpha2Code: 'FR',
        alpha3Code: 'FRA',
        numericCode: '250',
        callingCode: '+33'
    },
    {
        name: 'Francuska Gvajana',
        alpha2Code: 'GF',
        alpha3Code: 'GUF',
        numericCode: '254',
        callingCode: '+594'
    },
    {
        name: 'Francuska Polinezija',
        alpha2Code: 'PF',
        alpha3Code: 'PYF',
        numericCode: '258',
        callingCode: '+689'
    },
    {
        name: 'Francuski južni i antarktički teritoriji',
        alpha2Code: 'TF',
        alpha3Code: 'ATF',
        numericCode: '260',
        callingCode: '+'
    },
    {
        name: 'Gabon',
        alpha2Code: 'GA',
        alpha3Code: 'GAB',
        numericCode: '266',
        callingCode: '+241'
    },
    {
        name: 'Gambija',
        alpha2Code: 'GM',
        alpha3Code: 'GMB',
        numericCode: '270',
        callingCode: '+220'
    },
    {
        name: 'Gruzija',
        alpha2Code: 'GE',
        alpha3Code: 'GEO',
        numericCode: '268',
        callingCode: '+995'
    },
    {
        name: 'Njemačka',
        alpha2Code: 'DE',
        alpha3Code: 'DEU',
        numericCode: '276',
        callingCode: '+49'
    },
    {
        name: 'Gana',
        alpha2Code: 'GH',
        alpha3Code: 'GHA',
        numericCode: '288',
        callingCode: '+233'
    },
    {
        name: 'Gibraltar',
        alpha2Code: 'GI',
        alpha3Code: 'GIB',
        numericCode: '292',
        callingCode: '+350'
    },
    {
        name: 'Grčka',
        alpha2Code: 'GR',
        alpha3Code: 'GRC',
        numericCode: '300',
        callingCode: '+30'
    },
    {
        name: 'Grenland',
        alpha2Code: 'GL',
        alpha3Code: 'GRL',
        numericCode: '304',
        callingCode: '+299'
    },
    {
        name: 'Grenada',
        alpha2Code: 'GD',
        alpha3Code: 'GRD',
        numericCode: '308',
        callingCode: '+1473'
    },
    {
        name: 'Gvadalupa',
        alpha2Code: 'GP',
        alpha3Code: 'GLP',
        numericCode: '312',
        callingCode: '+590'
    },
    {
        name: 'Guam',
        alpha2Code: 'GU',
        alpha3Code: 'GUM',
        numericCode: '316',
        callingCode: '+1671'
    },
    {
        name: 'Gvatemala',
        alpha2Code: 'GT',
        alpha3Code: 'GTM',
        numericCode: '320',
        callingCode: '+502'
    },
    {
        name: 'Guernsey',
        alpha2Code: 'GG',
        alpha3Code: 'GGY',
        numericCode: '831',
        callingCode: '+44'
    },
    {
        name: 'Gvineja',
        alpha2Code: 'GN',
        alpha3Code: 'GIN',
        numericCode: '324',
        callingCode: '+224'
    },
    {
        name: 'Gvineja Bisau',
        alpha2Code: 'GW',
        alpha3Code: 'GNB',
        numericCode: '624',
        callingCode: '+245'
    },
    {
        name: 'Gvajana',
        alpha2Code: 'GY',
        alpha3Code: 'GUY',
        numericCode: '328',
        callingCode: '+592'
    },
    {
        name: 'Haiti',
        alpha2Code: 'HT',
        alpha3Code: 'HTI',
        numericCode: '332',
        callingCode: '+509'
    },
    {
        name: 'Otok Heard i otočje McDonald',
        alpha2Code: 'HM',
        alpha3Code: 'HMD',
        numericCode: '334',
        callingCode: '+'
    },
    {
        name: 'Sveta Stolica',
        alpha2Code: 'VA',
        alpha3Code: 'VAT',
        numericCode: '336',
        callingCode: '+379'
    },
    {
        name: 'Honduras',
        alpha2Code: 'HN',
        alpha3Code: 'HND',
        numericCode: '340',
        callingCode: '+504'
    },
    {
        name: 'Hong Kong',
        alpha2Code: 'HK',
        alpha3Code: 'HKG',
        numericCode: '344',
        callingCode: '+852'
    },
    {
        name: 'Mađarska',
        alpha2Code: 'HU',
        alpha3Code: 'HUN',
        numericCode: '348',
        callingCode: '+36'
    },
    {
        name: 'Island',
        alpha2Code: 'IS',
        alpha3Code: 'ISL',
        numericCode: '352',
        callingCode: '+354'
    },
    {
        name: 'Indija',
        alpha2Code: 'IN',
        alpha3Code: 'IND',
        numericCode: '356',
        callingCode: '+91'
    },
    {
        name: 'Indonezija',
        alpha2Code: 'ID',
        alpha3Code: 'IDN',
        numericCode: '360',
        callingCode: '+62'
    },
    {
        name: 'Obala Bjelokosti',
        alpha2Code: 'CI',
        alpha3Code: 'CIV',
        numericCode: '384',
        callingCode: '+225'
    },
    {
        name: 'Iran',
        alpha2Code: 'IR',
        alpha3Code: 'IRN',
        numericCode: '364',
        callingCode: '+98'
    },
    {
        name: 'Irak',
        alpha2Code: 'IQ',
        alpha3Code: 'IRQ',
        numericCode: '368',
        callingCode: '+964'
    },
    {
        name: 'Irska',
        alpha2Code: 'IE',
        alpha3Code: 'IRL',
        numericCode: '372',
        callingCode: '+353'
    },
    {
        name: 'Otok Man',
        alpha2Code: 'IM',
        alpha3Code: 'IMN',
        numericCode: '833',
        callingCode: '+44'
    },
    {
        name: 'Izrael',
        alpha2Code: 'IL',
        alpha3Code: 'ISR',
        numericCode: '376',
        callingCode: '+972'
    },
    {
        name: 'Italija',
        alpha2Code: 'IT',
        alpha3Code: 'ITA',
        numericCode: '380',
        callingCode: '+39'
    },
    {
        name: 'Jamajka',
        alpha2Code: 'JM',
        alpha3Code: 'JAM',
        numericCode: '388',
        callingCode: '+1876'
    },
    {
        name: 'Japan',
        alpha2Code: 'JP',
        alpha3Code: 'JPN',
        numericCode: '392',
        callingCode: '+81'
    },
    {
        name: 'Jersey',
        alpha2Code: 'JE',
        alpha3Code: 'JEY',
        numericCode: '832',
        callingCode: '+44'
    },
    {
        name: 'Jordan',
        alpha2Code: 'JO',
        alpha3Code: 'JOR',
        numericCode: '400',
        callingCode: '+962'
    },
    {
        name: 'Kazahstan',
        alpha2Code: 'KZ',
        alpha3Code: 'KAZ',
        numericCode: '398',
        callingCode: '+76'
    },
    {
        name: 'Kenija',
        alpha2Code: 'KE',
        alpha3Code: 'KEN',
        numericCode: '404',
        callingCode: '+254'
    },
    {
        name: 'Kiribati',
        alpha2Code: 'KI',
        alpha3Code: 'KIR',
        numericCode: '296',
        callingCode: '+686'
    },
    {
        name: 'Kuvajt',
        alpha2Code: 'KW',
        alpha3Code: 'KWT',
        numericCode: '414',
        callingCode: '+965'
    },
    {
        name: 'Kirgistan',
        alpha2Code: 'KG',
        alpha3Code: 'KGZ',
        numericCode: '417',
        callingCode: '+996'
    },
    {
        name: 'Laos',
        alpha2Code: 'LA',
        alpha3Code: 'LAO',
        numericCode: '418',
        callingCode: '+856'
    },
    {
        name: 'Latvija',
        alpha2Code: 'LV',
        alpha3Code: 'LVA',
        numericCode: '428',
        callingCode: '+371'
    },
    {
        name: 'Libanon',
        alpha2Code: 'LB',
        alpha3Code: 'LBN',
        numericCode: '422',
        callingCode: '+961'
    },
    {
        name: 'Lesoto',
        alpha2Code: 'LS',
        alpha3Code: 'LSO',
        numericCode: '426',
        callingCode: '+266'
    },
    {
        name: 'Liberija',
        alpha2Code: 'LR',
        alpha3Code: 'LBR',
        numericCode: '430',
        callingCode: '+231'
    },
    {
        name: 'Libija',
        alpha2Code: 'LY',
        alpha3Code: 'LBY',
        numericCode: '434',
        callingCode: '+218'
    },
    {
        name: 'Lihtenštajn',
        alpha2Code: 'LI',
        alpha3Code: 'LIE',
        numericCode: '438',
        callingCode: '+423'
    },
    {
        name: 'Litva',
        alpha2Code: 'LT',
        alpha3Code: 'LTU',
        numericCode: '440',
        callingCode: '+370'
    },
    {
        name: 'Luksemburg',
        alpha2Code: 'LU',
        alpha3Code: 'LUX',
        numericCode: '442',
        callingCode: '+352'
    },
    {
        name: 'Makao',
        alpha2Code: 'MO',
        alpha3Code: 'MAC',
        numericCode: '446',
        callingCode: '+853'
    },
    {
        name: 'Makedonija',
        alpha2Code: 'MK',
        alpha3Code: 'MKD',
        numericCode: '807',
        callingCode: '+389'
    },
    {
        name: 'Madagaskar',
        alpha2Code: 'MG',
        alpha3Code: 'MDG',
        numericCode: '450',
        callingCode: '+261'
    },
    {
        name: 'Malavi',
        alpha2Code: 'MW',
        alpha3Code: 'MWI',
        numericCode: '454',
        callingCode: '+265'
    },
    {
        name: 'Malezija',
        alpha2Code: 'MY',
        alpha3Code: 'MYS',
        numericCode: '458',
        callingCode: '+60'
    },
    {
        name: 'Maldivi',
        alpha2Code: 'MV',
        alpha3Code: 'MDV',
        numericCode: '462',
        callingCode: '+960'
    },
    {
        name: 'Mali',
        alpha2Code: 'ML',
        alpha3Code: 'MLI',
        numericCode: '466',
        callingCode: '+223'
    },
    {
        name: 'Malta',
        alpha2Code: 'MT',
        alpha3Code: 'MLT',
        numericCode: '470',
        callingCode: '+356'
    },
    {
        name: 'Maršalovi Otoci',
        alpha2Code: 'MH',
        alpha3Code: 'MHL',
        numericCode: '584',
        callingCode: '+692'
    },
    {
        name: 'Martinique',
        alpha2Code: 'MQ',
        alpha3Code: 'MTQ',
        numericCode: '474',
        callingCode: '+596'
    },
    {
        name: 'Mauritanija',
        alpha2Code: 'MR',
        alpha3Code: 'MRT',
        numericCode: '478',
        callingCode: '+222'
    },
    {
        name: 'Mauricijus',
        alpha2Code: 'MU',
        alpha3Code: 'MUS',
        numericCode: '480',
        callingCode: '+230'
    },
    {
        name: 'Mayotte',
        alpha2Code: 'YT',
        alpha3Code: 'MYT',
        numericCode: '175',
        callingCode: '+262'
    },
    {
        name: 'Meksiko',
        alpha2Code: 'MX',
        alpha3Code: 'MEX',
        numericCode: '484',
        callingCode: '+52'
    },
    {
        name: 'Mikronezija',
        alpha2Code: 'FM',
        alpha3Code: 'FSM',
        numericCode: '583',
        callingCode: '+691'
    },
    {
        name: 'Moldova',
        alpha2Code: 'MD',
        alpha3Code: 'MDA',
        numericCode: '498',
        callingCode: '+373'
    },
    {
        name: 'Monako',
        alpha2Code: 'MC',
        alpha3Code: 'MCO',
        numericCode: '492',
        callingCode: '+377'
    },
    {
        name: 'Mongolija',
        alpha2Code: 'MN',
        alpha3Code: 'MNG',
        numericCode: '496',
        callingCode: '+976'
    },
    {
        name: 'Crna Gora',
        alpha2Code: 'ME',
        alpha3Code: 'MNE',
        numericCode: '499',
        callingCode: '+382'
    },
    {
        name: 'Montserrat',
        alpha2Code: 'MS',
        alpha3Code: 'MSR',
        numericCode: '500',
        callingCode: '+1664'
    },
    {
        name: 'Maroko',
        alpha2Code: 'MA',
        alpha3Code: 'MAR',
        numericCode: '504',
        callingCode: '+212'
    },
    {
        name: 'Mozambik',
        alpha2Code: 'MZ',
        alpha3Code: 'MOZ',
        numericCode: '508',
        callingCode: '+258'
    },
    {
        name: 'Mijanmar',
        alpha2Code: 'MM',
        alpha3Code: 'MMR',
        numericCode: '104',
        callingCode: '+95'
    },
    {
        name: 'Namibija',
        alpha2Code: 'NA',
        alpha3Code: 'NAM',
        numericCode: '516',
        callingCode: '+264'
    },
    {
        name: 'Nauru',
        alpha2Code: 'NR',
        alpha3Code: 'NRU',
        numericCode: '520',
        callingCode: '+674'
    },
    {
        name: 'Nepal',
        alpha2Code: 'NP',
        alpha3Code: 'NPL',
        numericCode: '524',
        callingCode: '+977'
    },
    {
        name: 'Nizozemska',
        alpha2Code: 'NL',
        alpha3Code: 'NLD',
        numericCode: '528',
        callingCode: '+31'
    },
    {
        name: 'Nova Kaledonija',
        alpha2Code: 'NC',
        alpha3Code: 'NCL',
        numericCode: '540',
        callingCode: '+687'
    },
    {
        name: 'Novi Zeland',
        alpha2Code: 'NZ',
        alpha3Code: 'NZL',
        numericCode: '554',
        callingCode: '+64'
    },
    {
        name: 'Nikaragva',
        alpha2Code: 'NI',
        alpha3Code: 'NIC',
        numericCode: '558',
        callingCode: '+505'
    },
    {
        name: 'Niger',
        alpha2Code: 'NE',
        alpha3Code: 'NER',
        numericCode: '562',
        callingCode: '+227'
    },
    {
        name: 'Nigerija',
        alpha2Code: 'NG',
        alpha3Code: 'NGA',
        numericCode: '566',
        callingCode: '+234'
    },
    {
        name: 'Niue',
        alpha2Code: 'NU',
        alpha3Code: 'NIU',
        numericCode: '570',
        callingCode: '+683'
    },
    {
        name: 'Otok Norfolk',
        alpha2Code: 'NF',
        alpha3Code: 'NFK',
        numericCode: '574',
        callingCode: '+672'
    },
    {
        name: 'Sjeverna Koreja',
        alpha2Code: 'KP',
        alpha3Code: 'PRK',
        numericCode: '408',
        callingCode: '+850'
    },
    {
        name: 'Sjevernomarijanski otoci',
        alpha2Code: 'MP',
        alpha3Code: 'MNP',
        numericCode: '580',
        callingCode: '+1670'
    },
    {
        name: 'Norveška',
        alpha2Code: 'NO',
        alpha3Code: 'NOR',
        numericCode: '578',
        callingCode: '+47'
    },
    {
        name: 'Oman',
        alpha2Code: 'OM',
        alpha3Code: 'OMN',
        numericCode: '512',
        callingCode: '+968'
    },
    {
        name: 'Pakistan',
        alpha2Code: 'PK',
        alpha3Code: 'PAK',
        numericCode: '586',
        callingCode: '+92'
    },
    {
        name: 'Palau',
        alpha2Code: 'PW',
        alpha3Code: 'PLW',
        numericCode: '585',
        callingCode: '+680'
    },
    {
        name: 'Palestina',
        alpha2Code: 'PS',
        alpha3Code: 'PSE',
        numericCode: '275',
        callingCode: '+970'
    },
    {
        name: 'Panama',
        alpha2Code: 'PA',
        alpha3Code: 'PAN',
        numericCode: '591',
        callingCode: '+507'
    },
    {
        name: 'Papua Nova Gvineja',
        alpha2Code: 'PG',
        alpha3Code: 'PNG',
        numericCode: '598',
        callingCode: '+675'
    },
    {
        name: 'Paragvaj',
        alpha2Code: 'PY',
        alpha3Code: 'PRY',
        numericCode: '600',
        callingCode: '+595'
    },
    {
        name: 'Peru',
        alpha2Code: 'PE',
        alpha3Code: 'PER',
        numericCode: '604',
        callingCode: '+51'
    },
    {
        name: 'Filipini',
        alpha2Code: 'PH',
        alpha3Code: 'PHL',
        numericCode: '608',
        callingCode: '+63'
    },
    {
        name: 'Pitcairnovo otočje',
        alpha2Code: 'PN',
        alpha3Code: 'PCN',
        numericCode: '612',
        callingCode: '+64'
    },
    {
        name: 'Poljska',
        alpha2Code: 'PL',
        alpha3Code: 'POL',
        numericCode: '616',
        callingCode: '+48'
    },
    {
        name: 'Portugal',
        alpha2Code: 'PT',
        alpha3Code: 'PRT',
        numericCode: '620',
        callingCode: '+351'
    },
    {
        name: 'Portoriko',
        alpha2Code: 'PR',
        alpha3Code: 'PRI',
        numericCode: '630',
        callingCode: '+1787'
    },
    {
        name: 'Katar',
        alpha2Code: 'QA',
        alpha3Code: 'QAT',
        numericCode: '634',
        callingCode: '+974'
    },
    {
        name: 'Kosovo',
        alpha2Code: 'XK',
        alpha3Code: 'KOS',
        numericCode: null,
        callingCode: '+383'
    },
    {
        name: 'Réunion',
        alpha2Code: 'RE',
        alpha3Code: 'REU',
        numericCode: '638',
        callingCode: '+262'
    },
    {
        name: 'Rumunjska',
        alpha2Code: 'RO',
        alpha3Code: 'ROU',
        numericCode: '642',
        callingCode: '+40'
    },
    {
        name: 'Rusija',
        alpha2Code: 'RU',
        alpha3Code: 'RUS',
        numericCode: '643',
        callingCode: '+7'
    },
    {
        name: 'Ruanda',
        alpha2Code: 'RW',
        alpha3Code: 'RWA',
        numericCode: '646',
        callingCode: '+250'
    },
    {
        name: 'Saint Barthélemy',
        alpha2Code: 'BL',
        alpha3Code: 'BLM',
        numericCode: '652',
        callingCode: '+590'
    },
    {
        name: 'Sveta Helena',
        alpha2Code: 'SH',
        alpha3Code: 'SHN',
        numericCode: '654',
        callingCode: '+290'
    },
    {
        name: 'Sveti Kristof i Nevis',
        alpha2Code: 'KN',
        alpha3Code: 'KNA',
        numericCode: '659',
        callingCode: '+1869'
    },
    {
        name: 'Sveta Lucija',
        alpha2Code: 'LC',
        alpha3Code: 'LCA',
        numericCode: '662',
        callingCode: '+1758'
    },
    {
        name: 'Sveti Martin',
        alpha2Code: 'MF',
        alpha3Code: 'MAF',
        numericCode: '663',
        callingCode: '+590'
    },
    {
        name: 'Sveti Petar i Mikelon',
        alpha2Code: 'PM',
        alpha3Code: 'SPM',
        numericCode: '666',
        callingCode: '+508'
    },
    {
        name: 'Sveti Vincent i Grenadini',
        alpha2Code: 'VC',
        alpha3Code: 'VCT',
        numericCode: '670',
        callingCode: '+1784'
    },
    {
        name: 'Samoa',
        alpha2Code: 'WS',
        alpha3Code: 'WSM',
        numericCode: '882',
        callingCode: '+685'
    },
    {
        name: 'San Marino',
        alpha2Code: 'SM',
        alpha3Code: 'SMR',
        numericCode: '674',
        callingCode: '+378'
    },
    {
        name: 'Sveti Toma i Princip',
        alpha2Code: 'ST',
        alpha3Code: 'STP',
        numericCode: '678',
        callingCode: '+239'
    },
    {
        name: 'Saudijska Arabija',
        alpha2Code: 'SA',
        alpha3Code: 'SAU',
        numericCode: '682',
        callingCode: '+966'
    },
    {
        name: 'Senegal',
        alpha2Code: 'SN',
        alpha3Code: 'SEN',
        numericCode: '686',
        callingCode: '+221'
    },
    {
        name: 'Srbija',
        alpha2Code: 'RS',
        alpha3Code: 'SRB',
        numericCode: '688',
        callingCode: '+381'
    },
    {
        name: 'Sejšeli',
        alpha2Code: 'SC',
        alpha3Code: 'SYC',
        numericCode: '690',
        callingCode: '+248'
    },
    {
        name: 'Sijera Leone',
        alpha2Code: 'SL',
        alpha3Code: 'SLE',
        numericCode: '694',
        callingCode: '+232'
    },
    {
        name: 'Singapur',
        alpha2Code: 'SG',
        alpha3Code: 'SGP',
        numericCode: '702',
        callingCode: '+65'
    },
    {
        name: 'Sveti Martin (nizozemski dio)',
        alpha2Code: 'SX',
        alpha3Code: 'SXM',
        numericCode: '534',
        callingCode: '+1721'
    },
    {
        name: 'Slovačka',
        alpha2Code: 'SK',
        alpha3Code: 'SVK',
        numericCode: '703',
        callingCode: '+421'
    },
    {
        name: 'Slovenija',
        alpha2Code: 'SI',
        alpha3Code: 'SVN',
        numericCode: '705',
        callingCode: '+386'
    },
    {
        name: 'Solomonski Otoci',
        alpha2Code: 'SB',
        alpha3Code: 'SLB',
        numericCode: '090',
        callingCode: '+677'
    },
    {
        name: 'Somalija',
        alpha2Code: 'SO',
        alpha3Code: 'SOM',
        numericCode: '706',
        callingCode: '+252'
    },
    {
        name: 'Južnoafrička Republika',
        alpha2Code: 'ZA',
        alpha3Code: 'ZAF',
        numericCode: '710',
        callingCode: '+27'
    },
    {
        name: 'Južna Georgija i otočje Južni Sandwich',
        alpha2Code: 'GS',
        alpha3Code: 'SGS',
        numericCode: '239',
        callingCode: '+500'
    },
    {
        name: 'Južna Koreja',
        alpha2Code: 'KR',
        alpha3Code: 'KOR',
        numericCode: '410',
        callingCode: '+82'
    },
    {
        name: 'Južni Sudan',
        alpha2Code: 'SS',
        alpha3Code: 'SSD',
        numericCode: '728',
        callingCode: '+211'
    },
    {
        name: 'Španjolska',
        alpha2Code: 'ES',
        alpha3Code: 'ESP',
        numericCode: '724',
        callingCode: '+34'
    },
    {
        name: 'Šri Lanka',
        alpha2Code: 'LK',
        alpha3Code: 'LKA',
        numericCode: '144',
        callingCode: '+94'
    },
    {
        name: 'Sudan',
        alpha2Code: 'SD',
        alpha3Code: 'SDN',
        numericCode: '729',
        callingCode: '+249'
    },
    {
        name: 'Surinam',
        alpha2Code: 'SR',
        alpha3Code: 'SUR',
        numericCode: '740',
        callingCode: '+597'
    },
    {
        name: 'Svalbard i Jan Mayen',
        alpha2Code: 'SJ',
        alpha3Code: 'SJM',
        numericCode: '744',
        callingCode: '+4779'
    },
    {
        name: 'Svazi',
        alpha2Code: 'SZ',
        alpha3Code: 'SWZ',
        numericCode: '748',
        callingCode: '+268'
    },
    {
        name: 'Švedska',
        alpha2Code: 'SE',
        alpha3Code: 'SWE',
        numericCode: '752',
        callingCode: '+46'
    },
    {
        name: 'Švicarska',
        alpha2Code: 'CH',
        alpha3Code: 'CHE',
        numericCode: '756',
        callingCode: '+41'
    },
    {
        name: 'Sirija',
        alpha2Code: 'SY',
        alpha3Code: 'SYR',
        numericCode: '760',
        callingCode: '+963'
    },
    {
        name: 'Tajvan',
        alpha2Code: 'TW',
        alpha3Code: 'TWN',
        numericCode: '158',
        callingCode: '+886'
    },
    {
        name: 'Tađikistan',
        alpha2Code: 'TJ',
        alpha3Code: 'TJK',
        numericCode: '762',
        callingCode: '+992'
    },
    {
        name: 'Tanzanija',
        alpha2Code: 'TZ',
        alpha3Code: 'TZA',
        numericCode: '834',
        callingCode: '+255'
    },
    {
        name: 'Tajland',
        alpha2Code: 'TH',
        alpha3Code: 'THA',
        numericCode: '764',
        callingCode: '+66'
    },
    {
        name: 'Istočni Timor',
        alpha2Code: 'TL',
        alpha3Code: 'TLS',
        numericCode: '626',
        callingCode: '+670'
    },
    {
        name: 'Togo',
        alpha2Code: 'TG',
        alpha3Code: 'TGO',
        numericCode: '768',
        callingCode: '+228'
    },
    {
        name: 'Tokelau',
        alpha2Code: 'TK',
        alpha3Code: 'TKL',
        numericCode: '772',
        callingCode: '+690'
    },
    {
        name: 'Tonga',
        alpha2Code: 'TO',
        alpha3Code: 'TON',
        numericCode: '776',
        callingCode: '+676'
    },
    {
        name: 'Trinidad i Tobago',
        alpha2Code: 'TT',
        alpha3Code: 'TTO',
        numericCode: '780',
        callingCode: '+1868'
    },
    {
        name: 'Tunis',
        alpha2Code: 'TN',
        alpha3Code: 'TUN',
        numericCode: '788',
        callingCode: '+216'
    },
    {
        name: 'Turska',
        alpha2Code: 'TR',
        alpha3Code: 'TUR',
        numericCode: '792',
        callingCode: '+90'
    },
    {
        name: 'Turkmenistan',
        alpha2Code: 'TM',
        alpha3Code: 'TKM',
        numericCode: '795',
        callingCode: '+993'
    },
    {
        name: 'Otoci Turks i Caicos',
        alpha2Code: 'TC',
        alpha3Code: 'TCA',
        numericCode: '796',
        callingCode: '+1649'
    },
    {
        name: 'Tuvalu',
        alpha2Code: 'TV',
        alpha3Code: 'TUV',
        numericCode: '798',
        callingCode: '+688'
    },
    {
        name: 'Uganda',
        alpha2Code: 'UG',
        alpha3Code: 'UGA',
        numericCode: '800',
        callingCode: '+256'
    },
    {
        name: 'Ukrajina',
        alpha2Code: 'UA',
        alpha3Code: 'UKR',
        numericCode: '804',
        callingCode: '+380'
    },
    {
        name: 'Ujedinjeni Arapski Emirati',
        alpha2Code: 'AE',
        alpha3Code: 'ARE',
        numericCode: '784',
        callingCode: '+971'
    },
    {
        name: 'Ujedinjeno Kraljevstvo',
        alpha2Code: 'GB',
        alpha3Code: 'GBR',
        numericCode: '826',
        callingCode: '+44'
    },
    {
        name: 'Sjedinjene Američke Države',
        alpha2Code: 'US',
        alpha3Code: 'USA',
        numericCode: '840',
        callingCode: '+1'
    },
    {
        name: 'Urugvaj',
        alpha2Code: 'UY',
        alpha3Code: 'URY',
        numericCode: '858',
        callingCode: '+598'
    },
    {
        name: 'Uzbekistan',
        alpha2Code: 'UZ',
        alpha3Code: 'UZB',
        numericCode: '860',
        callingCode: '+998'
    },
    {
        name: 'Vanuatu',
        alpha2Code: 'VU',
        alpha3Code: 'VUT',
        numericCode: '548',
        callingCode: '+678'
    },
    {
        name: 'Venezuela',
        alpha2Code: 'VE',
        alpha3Code: 'VEN',
        numericCode: '862',
        callingCode: '+58'
    },
    {
        name: 'Vijetnam',
        alpha2Code: 'VN',
        alpha3Code: 'VNM',
        numericCode: '704',
        callingCode: '+84'
    },
    {
        name: 'Wallis i Fortuna',
        alpha2Code: 'WF',
        alpha3Code: 'WLF',
        numericCode: '876',
        callingCode: '+681'
    },
    {
        name: 'Zapadna Sahara',
        alpha2Code: 'EH',
        alpha3Code: 'ESH',
        numericCode: '732',
        callingCode: '+212'
    },
    {
        name: 'Jemen',
        alpha2Code: 'YE',
        alpha3Code: 'YEM',
        numericCode: '887',
        callingCode: '+967'
    },
    {
        name: 'Zambija',
        alpha2Code: 'ZM',
        alpha3Code: 'ZMB',
        numericCode: '894',
        callingCode: '+260'
    },
    {
        name: 'Zimbabve',
        alpha2Code: 'ZW',
        alpha3Code: 'ZWE',
        numericCode: '716',
        callingCode: '+263'
    }
];

var hr = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB_HR: COUNTRIES_DB_HR
});

const COUNTRIES_DB_HU = [
    {
        name: 'Afganisztán',
        alpha2Code: 'AF',
        alpha3Code: 'AFG',
        numericCode: '004',
        callingCode: '+93'
    },
    {
        name: 'Åland-szigetek',
        alpha2Code: 'AX',
        alpha3Code: 'ALA',
        numericCode: '248',
        callingCode: '+358'
    },
    {
        name: 'Albánia',
        alpha2Code: 'AL',
        alpha3Code: 'ALB',
        numericCode: '008',
        callingCode: '+355'
    },
    {
        name: 'Algéria',
        alpha2Code: 'DZ',
        alpha3Code: 'DZA',
        numericCode: '012',
        callingCode: '+213'
    },
    {
        name: 'Amerikai Szamoa',
        alpha2Code: 'AS',
        alpha3Code: 'ASM',
        numericCode: '016',
        callingCode: '+1684'
    },
    {
        name: 'Andorra',
        alpha2Code: 'AD',
        alpha3Code: 'AND',
        numericCode: '020',
        callingCode: '+376'
    },
    {
        name: 'Angola',
        alpha2Code: 'AO',
        alpha3Code: 'AGO',
        numericCode: '024',
        callingCode: '+244'
    },
    {
        name: 'Anguilla',
        alpha2Code: 'AI',
        alpha3Code: 'AIA',
        numericCode: '660',
        callingCode: '+1264'
    },
    {
        name: 'Antarktisz',
        alpha2Code: 'AQ',
        alpha3Code: 'ATA',
        numericCode: '010',
        callingCode: '+672'
    },
    {
        name: 'Antigua és Barbuda',
        alpha2Code: 'AG',
        alpha3Code: 'ATG',
        numericCode: '028',
        callingCode: '+1268'
    },
    {
        name: 'Argentína',
        alpha2Code: 'AR',
        alpha3Code: 'ARG',
        numericCode: '032',
        callingCode: '+54'
    },
    {
        name: 'Örményország',
        alpha2Code: 'AM',
        alpha3Code: 'ARM',
        numericCode: '051',
        callingCode: '+374'
    },
    {
        name: 'Aruba',
        alpha2Code: 'AW',
        alpha3Code: 'ABW',
        numericCode: '533',
        callingCode: '+297'
    },
    {
        name: 'Ausztrália',
        alpha2Code: 'AU',
        alpha3Code: 'AUS',
        numericCode: '036',
        callingCode: '+61'
    },
    {
        name: 'Ausztria',
        alpha2Code: 'AT',
        alpha3Code: 'AUT',
        numericCode: '040',
        callingCode: '+43'
    },
    {
        name: 'Azerbajdzsán',
        alpha2Code: 'AZ',
        alpha3Code: 'AZE',
        numericCode: '031',
        callingCode: '+994'
    },
    {
        name: 'Bahama-szigetek',
        alpha2Code: 'BS',
        alpha3Code: 'BHS',
        numericCode: '044',
        callingCode: '+1242'
    },
    {
        name: 'Bahrein',
        alpha2Code: 'BH',
        alpha3Code: 'BHR',
        numericCode: '048',
        callingCode: '+973'
    },
    {
        name: 'Banglades',
        alpha2Code: 'BD',
        alpha3Code: 'BGD',
        numericCode: '050',
        callingCode: '+880'
    },
    {
        name: 'Barbados',
        alpha2Code: 'BB',
        alpha3Code: 'BRB',
        numericCode: '052',
        callingCode: '+1246'
    },
    {
        name: 'Fehéroroszország',
        alpha2Code: 'BY',
        alpha3Code: 'BLR',
        numericCode: '112',
        callingCode: '+375'
    },
    {
        name: 'Belgium',
        alpha2Code: 'BE',
        alpha3Code: 'BEL',
        numericCode: '056',
        callingCode: '+32'
    },
    {
        name: 'Belize',
        alpha2Code: 'BZ',
        alpha3Code: 'BLZ',
        numericCode: '084',
        callingCode: '+501'
    },
    {
        name: 'Benin',
        alpha2Code: 'BJ',
        alpha3Code: 'BEN',
        numericCode: '204',
        callingCode: '+229'
    },
    {
        name: 'Bermuda',
        alpha2Code: 'BM',
        alpha3Code: 'BMU',
        numericCode: '060',
        callingCode: '+1441'
    },
    {
        name: 'Bhután',
        alpha2Code: 'BT',
        alpha3Code: 'BTN',
        numericCode: '064',
        callingCode: '+975'
    },
    {
        name: 'Bolívia (többnemzetiségű állam)',
        alpha2Code: 'BO',
        alpha3Code: 'BOL',
        numericCode: '068',
        callingCode: '+591'
    },
    {
        name: 'Bonaire, Sint Eustatius és Saba',
        alpha2Code: 'BQ',
        alpha3Code: 'BES',
        numericCode: '535',
        callingCode: '+5997'
    },
    {
        name: 'Bosznia és Hercegovina',
        alpha2Code: 'BA',
        alpha3Code: 'BIH',
        numericCode: '070',
        callingCode: '+387'
    },
    {
        name: 'Botswana',
        alpha2Code: 'BW',
        alpha3Code: 'BWA',
        numericCode: '072',
        callingCode: '+267'
    },
    {
        name: 'Bouvet-sziget',
        alpha2Code: 'BV',
        alpha3Code: 'BVT',
        numericCode: '074',
        callingCode: '+'
    },
    {
        name: 'Brazília',
        alpha2Code: 'BR',
        alpha3Code: 'BRA',
        numericCode: '076',
        callingCode: '+55'
    },
    {
        name: 'Brit Indiai-óceáni terület',
        alpha2Code: 'IO',
        alpha3Code: 'IOT',
        numericCode: '086',
        callingCode: '+246'
    },
    {
        name: 'Egyesült Államok Kisebb peremszigetek',
        alpha2Code: 'UM',
        alpha3Code: 'UMI',
        numericCode: '581',
        callingCode: '+'
    },
    {
        name: 'Virgin-szigetek (brit)',
        alpha2Code: 'VG',
        alpha3Code: 'VGB',
        numericCode: '092',
        callingCode: '+1284'
    },
    {
        name: 'Virgin-szigetek (Egyesült Államok)',
        alpha2Code: 'VI',
        alpha3Code: 'VIR',
        numericCode: '850',
        callingCode: '+1 340'
    },
    {
        name: 'Brunei Darussalam',
        alpha2Code: 'BN',
        alpha3Code: 'BRN',
        numericCode: '096',
        callingCode: '+673'
    },
    {
        name: 'Bulgária',
        alpha2Code: 'BG',
        alpha3Code: 'BGR',
        numericCode: '100',
        callingCode: '+359'
    },
    {
        name: 'Burkina Faso',
        alpha2Code: 'BF',
        alpha3Code: 'BFA',
        numericCode: '854',
        callingCode: '+226'
    },
    {
        name: 'Burundi',
        alpha2Code: 'BI',
        alpha3Code: 'BDI',
        numericCode: '108',
        callingCode: '+257'
    },
    {
        name: 'Kambodzsa',
        alpha2Code: 'KH',
        alpha3Code: 'KHM',
        numericCode: '116',
        callingCode: '+855'
    },
    {
        name: 'Kamerun',
        alpha2Code: 'CM',
        alpha3Code: 'CMR',
        numericCode: '120',
        callingCode: '+237'
    },
    {
        name: 'Kanada',
        alpha2Code: 'CA',
        alpha3Code: 'CAN',
        numericCode: '124',
        callingCode: '+1'
    },
    {
        name: 'Cabo Verde',
        alpha2Code: 'CV',
        alpha3Code: 'CPV',
        numericCode: '132',
        callingCode: '+238'
    },
    {
        name: 'Kajmán-szigetek',
        alpha2Code: 'KY',
        alpha3Code: 'CYM',
        numericCode: '136',
        callingCode: '+1345'
    },
    {
        name: 'Közép-afrikai Köztársaság',
        alpha2Code: 'CF',
        alpha3Code: 'CAF',
        numericCode: '140',
        callingCode: '+236'
    },
    {
        name: 'Csád',
        alpha2Code: 'TD',
        alpha3Code: 'TCD',
        numericCode: '148',
        callingCode: '+235'
    },
    {
        name: 'Chile',
        alpha2Code: 'CL',
        alpha3Code: 'CHL',
        numericCode: '152',
        callingCode: '+56'
    },
    {
        name: 'Kína',
        alpha2Code: 'CN',
        alpha3Code: 'CHN',
        numericCode: '156',
        callingCode: '+86'
    },
    {
        name: 'Karácsony-sziget',
        alpha2Code: 'CX',
        alpha3Code: 'CXR',
        numericCode: '162',
        callingCode: '+61'
    },
    {
        name: 'Kókusz-szigetek',
        alpha2Code: 'CC',
        alpha3Code: 'CCK',
        numericCode: '166',
        callingCode: '+61'
    },
    {
        name: 'Kolumbia',
        alpha2Code: 'CO',
        alpha3Code: 'COL',
        numericCode: '170',
        callingCode: '+57'
    },
    {
        name: 'Comore-szigetek',
        alpha2Code: 'KM',
        alpha3Code: 'COM',
        numericCode: '174',
        callingCode: '+269'
    },
    {
        name: 'Kongó',
        alpha2Code: 'CG',
        alpha3Code: 'COG',
        numericCode: '178',
        callingCode: '+242'
    },
    {
        name: 'Kongó (Demokratikus Köztársaság)',
        alpha2Code: 'CD',
        alpha3Code: 'COD',
        numericCode: '180',
        callingCode: '+243'
    },
    {
        name: 'Cook-szigetek',
        alpha2Code: 'CK',
        alpha3Code: 'COK',
        numericCode: '184',
        callingCode: '+682'
    },
    {
        name: 'Costa Rica',
        alpha2Code: 'CR',
        alpha3Code: 'CRI',
        numericCode: '188',
        callingCode: '+506'
    },
    {
        name: 'Horvátország',
        alpha2Code: 'HR',
        alpha3Code: 'HRV',
        numericCode: '191',
        callingCode: '+385'
    },
    {
        name: 'Kuba',
        alpha2Code: 'CU',
        alpha3Code: 'CUB',
        numericCode: '192',
        callingCode: '+53'
    },
    {
        name: 'Curaçao',
        alpha2Code: 'CW',
        alpha3Code: 'CUW',
        numericCode: '531',
        callingCode: '+599'
    },
    {
        name: 'Ciprus',
        alpha2Code: 'CY',
        alpha3Code: 'CYP',
        numericCode: '196',
        callingCode: '+357'
    },
    {
        name: 'Csehország',
        alpha2Code: 'CZ',
        alpha3Code: 'CZE',
        numericCode: '203',
        callingCode: '+420'
    },
    {
        name: 'Dánia',
        alpha2Code: 'DK',
        alpha3Code: 'DNK',
        numericCode: '208',
        callingCode: '+45'
    },
    {
        name: 'Dzsibuti',
        alpha2Code: 'DJ',
        alpha3Code: 'DJI',
        numericCode: '262',
        callingCode: '+253'
    },
    {
        name: 'Dominika',
        alpha2Code: 'DM',
        alpha3Code: 'DMA',
        numericCode: '212',
        callingCode: '+1767'
    },
    {
        name: 'Dominikai Köztársaság',
        alpha2Code: 'DO',
        alpha3Code: 'DOM',
        numericCode: '214',
        callingCode: '+1809'
    },
    {
        name: 'Ecuador',
        alpha2Code: 'EC',
        alpha3Code: 'ECU',
        numericCode: '218',
        callingCode: '+593'
    },
    {
        name: 'Egyiptom',
        alpha2Code: 'EG',
        alpha3Code: 'EGY',
        numericCode: '818',
        callingCode: '+20'
    },
    {
        name: 'El Salvador',
        alpha2Code: 'SV',
        alpha3Code: 'SLV',
        numericCode: '222',
        callingCode: '+503'
    },
    {
        name: 'Egyenlítői-Guinea',
        alpha2Code: 'GQ',
        alpha3Code: 'GNQ',
        numericCode: '226',
        callingCode: '+240'
    },
    {
        name: 'Eritrea',
        alpha2Code: 'ER',
        alpha3Code: 'ERI',
        numericCode: '232',
        callingCode: '+291'
    },
    {
        name: 'Észtország',
        alpha2Code: 'EE',
        alpha3Code: 'EST',
        numericCode: '233',
        callingCode: '+372'
    },
    {
        name: 'Etiópia',
        alpha2Code: 'ET',
        alpha3Code: 'ETH',
        numericCode: '231',
        callingCode: '+251'
    },
    {
        name: 'Falkland-szigetek (Malvin-szigetek)',
        alpha2Code: 'FK',
        alpha3Code: 'FLK',
        numericCode: '238',
        callingCode: '+500'
    },
    {
        name: 'Feröer-szigetek',
        alpha2Code: 'FO',
        alpha3Code: 'FRO',
        numericCode: '234',
        callingCode: '+298'
    },
    {
        name: 'Fidzsi-szigetek',
        alpha2Code: 'FJ',
        alpha3Code: 'FJI',
        numericCode: '242',
        callingCode: '+679'
    },
    {
        name: 'Finnország',
        alpha2Code: 'FI',
        alpha3Code: 'FIN',
        numericCode: '246',
        callingCode: '+358'
    },
    {
        name: 'Franciaország',
        alpha2Code: 'FR',
        alpha3Code: 'FRA',
        numericCode: '250',
        callingCode: '+33'
    },
    {
        name: 'Francia Guyana',
        alpha2Code: 'GF',
        alpha3Code: 'GUF',
        numericCode: '254',
        callingCode: '+594'
    },
    {
        name: 'Francia Polinézia',
        alpha2Code: 'PF',
        alpha3Code: 'PYF',
        numericCode: '258',
        callingCode: '+689'
    },
    {
        name: 'Francia déli területek',
        alpha2Code: 'TF',
        alpha3Code: 'ATF',
        numericCode: '260',
        callingCode: '+'
    },
    {
        name: 'Gabon',
        alpha2Code: 'GA',
        alpha3Code: 'GAB',
        numericCode: '266',
        callingCode: '+241'
    },
    {
        name: 'Gambia',
        alpha2Code: 'GM',
        alpha3Code: 'GMB',
        numericCode: '270',
        callingCode: '+220'
    },
    {
        name: 'Grúzia',
        alpha2Code: 'GE',
        alpha3Code: 'GEO',
        numericCode: '268',
        callingCode: '+995'
    },
    {
        name: 'Németország',
        alpha2Code: 'DE',
        alpha3Code: 'DEU',
        numericCode: '276',
        callingCode: '+49'
    },
    {
        name: 'Ghána',
        alpha2Code: 'GH',
        alpha3Code: 'GHA',
        numericCode: '288',
        callingCode: '+233'
    },
    {
        name: 'Gibraltár',
        alpha2Code: 'GI',
        alpha3Code: 'GIB',
        numericCode: '292',
        callingCode: '+350'
    },
    {
        name: 'Görögország',
        alpha2Code: 'GR',
        alpha3Code: 'GRC',
        numericCode: '300',
        callingCode: '+30'
    },
    {
        name: 'Grönland',
        alpha2Code: 'GL',
        alpha3Code: 'GRL',
        numericCode: '304',
        callingCode: '+299'
    },
    {
        name: 'Grenada',
        alpha2Code: 'GD',
        alpha3Code: 'GRD',
        numericCode: '308',
        callingCode: '+1473'
    },
    {
        name: 'Guadeloupe',
        alpha2Code: 'GP',
        alpha3Code: 'GLP',
        numericCode: '312',
        callingCode: '+590'
    },
    {
        name: 'Guam',
        alpha2Code: 'GU',
        alpha3Code: 'GUM',
        numericCode: '316',
        callingCode: '+1671'
    },
    {
        name: 'Guatemala',
        alpha2Code: 'GT',
        alpha3Code: 'GTM',
        numericCode: '320',
        callingCode: '+502'
    },
    {
        name: 'Guernsey',
        alpha2Code: 'GG',
        alpha3Code: 'GGY',
        numericCode: '831',
        callingCode: '+44'
    },
    {
        name: 'Guinea',
        alpha2Code: 'GN',
        alpha3Code: 'GIN',
        numericCode: '324',
        callingCode: '+224'
    },
    {
        name: 'Bissau-Guinea',
        alpha2Code: 'GW',
        alpha3Code: 'GNB',
        numericCode: '624',
        callingCode: '+245'
    },
    {
        name: 'Guyana',
        alpha2Code: 'GY',
        alpha3Code: 'GUY',
        numericCode: '328',
        callingCode: '+592'
    },
    {
        name: 'Haiti',
        alpha2Code: 'HT',
        alpha3Code: 'HTI',
        numericCode: '332',
        callingCode: '+509'
    },
    {
        name: 'Heard-sziget és McDonald-szigetek',
        alpha2Code: 'HM',
        alpha3Code: 'HMD',
        numericCode: '334',
        callingCode: '+'
    },
    {
        name: 'Szentszék',
        alpha2Code: 'VA',
        alpha3Code: 'VAT',
        numericCode: '336',
        callingCode: '+379'
    },
    {
        name: 'Honduras',
        alpha2Code: 'HN',
        alpha3Code: 'HND',
        numericCode: '340',
        callingCode: '+504'
    },
    {
        name: 'Hong Kong',
        alpha2Code: 'HK',
        alpha3Code: 'HKG',
        numericCode: '344',
        callingCode: '+852'
    },
    {
        name: 'Magyarország',
        alpha2Code: 'HU',
        alpha3Code: 'HUN',
        numericCode: '348',
        callingCode: '+36'
    },
    {
        name: 'Izland',
        alpha2Code: 'IS',
        alpha3Code: 'ISL',
        numericCode: '352',
        callingCode: '+354'
    },
    {
        name: 'India',
        alpha2Code: 'IN',
        alpha3Code: 'IND',
        numericCode: '356',
        callingCode: '+91'
    },
    {
        name: 'Indonézia',
        alpha2Code: 'ID',
        alpha3Code: 'IDN',
        numericCode: '360',
        callingCode: '+62'
    },
    {
        name: "Elefántcsontpart",
        alpha2Code: 'CI',
        alpha3Code: 'CIV',
        numericCode: '384',
        callingCode: '+225'
    },
    {
        name: 'Irán (Iszlám Köztársaság)',
        alpha2Code: 'IR',
        alpha3Code: 'IRN',
        numericCode: '364',
        callingCode: '+98'
    },
    {
        name: 'Irak',
        alpha2Code: 'IQ',
        alpha3Code: 'IRQ',
        numericCode: '368',
        callingCode: '+964'
    },
    {
        name: 'Írország',
        alpha2Code: 'IE',
        alpha3Code: 'IRL',
        numericCode: '372',
        callingCode: '+353'
    },
    {
        name: 'Man-sziget',
        alpha2Code: 'IM',
        alpha3Code: 'IMN',
        numericCode: '833',
        callingCode: '+44'
    },
    {
        name: 'Izrael',
        alpha2Code: 'IL',
        alpha3Code: 'ISR',
        numericCode: '376',
        callingCode: '+972'
    },
    {
        name: 'Olaszország',
        alpha2Code: 'IT',
        alpha3Code: 'ITA',
        numericCode: '380',
        callingCode: '+39'
    },
    {
        name: 'Jamaica',
        alpha2Code: 'JM',
        alpha3Code: 'JAM',
        numericCode: '388',
        callingCode: '+1876'
    },
    {
        name: 'Japán',
        alpha2Code: 'JP',
        alpha3Code: 'JPN',
        numericCode: '392',
        callingCode: '+81'
    },
    {
        name: 'Jersey',
        alpha2Code: 'JE',
        alpha3Code: 'JEY',
        numericCode: '832',
        callingCode: '+44'
    },
    {
        name: 'Jordánia',
        alpha2Code: 'JO',
        alpha3Code: 'JOR',
        numericCode: '400',
        callingCode: '+962'
    },
    {
        name: 'Kazahsztán',
        alpha2Code: 'KZ',
        alpha3Code: 'KAZ',
        numericCode: '398',
        callingCode: '+7'
    },
    {
        name: 'Kenya',
        alpha2Code: 'KE',
        alpha3Code: 'KEN',
        numericCode: '404',
        callingCode: '+254'
    },
    {
        name: 'Kiribati',
        alpha2Code: 'KI',
        alpha3Code: 'KIR',
        numericCode: '296',
        callingCode: '+686'
    },
    {
        name: 'Kuvait',
        alpha2Code: 'KW',
        alpha3Code: 'KWT',
        numericCode: '414',
        callingCode: '+965'
    },
    {
        name: 'Kirgizisztán',
        alpha2Code: 'KG',
        alpha3Code: 'KGZ',
        numericCode: '417',
        callingCode: '+996'
    },
    {
        name: "Laoszi Népi Demokratikus Köztársaság",
        alpha2Code: 'LA',
        alpha3Code: 'LAO',
        numericCode: '418',
        callingCode: '+856'
    },
    {
        name: 'Lettország',
        alpha2Code: 'LV',
        alpha3Code: 'LVA',
        numericCode: '428',
        callingCode: '+371'
    },
    {
        name: 'Libanon',
        alpha2Code: 'LB',
        alpha3Code: 'LBN',
        numericCode: '422',
        callingCode: '+961'
    },
    {
        name: 'Lesotho',
        alpha2Code: 'LS',
        alpha3Code: 'LSO',
        numericCode: '426',
        callingCode: '+266'
    },
    {
        name: 'Libéria',
        alpha2Code: 'LR',
        alpha3Code: 'LBR',
        numericCode: '430',
        callingCode: '+231'
    },
    {
        name: 'Líbia',
        alpha2Code: 'LY',
        alpha3Code: 'LBY',
        numericCode: '434',
        callingCode: '+218'
    },
    {
        name: 'Liechtenstein',
        alpha2Code: 'LI',
        alpha3Code: 'LIE',
        numericCode: '438',
        callingCode: '+423'
    },
    {
        name: 'Litvánia',
        alpha2Code: 'LT',
        alpha3Code: 'LTU',
        numericCode: '440',
        callingCode: '+370'
    },
    {
        name: 'Luxemburg',
        alpha2Code: 'LU',
        alpha3Code: 'LUX',
        numericCode: '442',
        callingCode: '+352'
    },
    {
        name: 'Makaó',
        alpha2Code: 'MO',
        alpha3Code: 'MAC',
        numericCode: '446',
        callingCode: '+853'
    },
    {
        name: 'Macedónia (volt Jugoszláv Köztársaság)',
        alpha2Code: 'MK',
        alpha3Code: 'MKD',
        numericCode: '807',
        callingCode: '+389'
    },
    {
        name: 'Madagaszkár',
        alpha2Code: 'MG',
        alpha3Code: 'MDG',
        numericCode: '450',
        callingCode: '+261'
    },
    {
        name: 'Malawi',
        alpha2Code: 'MW',
        alpha3Code: 'MWI',
        numericCode: '454',
        callingCode: '+265'
    },
    {
        name: 'Malajzia',
        alpha2Code: 'MY',
        alpha3Code: 'MYS',
        numericCode: '458',
        callingCode: '+60'
    },
    {
        name: 'Maldív-szigetek',
        alpha2Code: 'MV',
        alpha3Code: 'MDV',
        numericCode: '462',
        callingCode: '+960'
    },
    {
        name: 'Mali',
        alpha2Code: 'ML',
        alpha3Code: 'MLI',
        numericCode: '466',
        callingCode: '+223'
    },
    {
        name: 'Málta',
        alpha2Code: 'MT',
        alpha3Code: 'MLT',
        numericCode: '470',
        callingCode: '+356'
    },
    {
        name: 'Marshall-szigetek',
        alpha2Code: 'MH',
        alpha3Code: 'MHL',
        numericCode: '584',
        callingCode: '+692'
    },
    {
        name: 'Martinique',
        alpha2Code: 'MQ',
        alpha3Code: 'MTQ',
        numericCode: '474',
        callingCode: '+596'
    },
    {
        name: 'Mauritánia',
        alpha2Code: 'MR',
        alpha3Code: 'MRT',
        numericCode: '478',
        callingCode: '+222'
    },
    {
        name: 'Mauritius',
        alpha2Code: 'MU',
        alpha3Code: 'MUS',
        numericCode: '480',
        callingCode: '+230'
    },
    {
        name: 'Mayotte',
        alpha2Code: 'YT',
        alpha3Code: 'MYT',
        numericCode: '175',
        callingCode: '+262'
    },
    {
        name: 'Mexikó',
        alpha2Code: 'MX',
        alpha3Code: 'MEX',
        numericCode: '484',
        callingCode: '+52'
    },
    {
        name: 'Mikronézia (Szövetségi Államok)',
        alpha2Code: 'FM',
        alpha3Code: 'FSM',
        numericCode: '583',
        callingCode: '+691'
    },
    {
        name: 'Moldova (Köztársaság)',
        alpha2Code: 'MD',
        alpha3Code: 'MDA',
        numericCode: '498',
        callingCode: '+373'
    },
    {
        name: 'Monaco',
        alpha2Code: 'MC',
        alpha3Code: 'MCO',
        numericCode: '492',
        callingCode: '+377'
    },
    {
        name: 'Mongólia',
        alpha2Code: 'MN',
        alpha3Code: 'MNG',
        numericCode: '496',
        callingCode: '+976'
    },
    {
        name: 'Montenegró',
        alpha2Code: 'ME',
        alpha3Code: 'MNE',
        numericCode: '499',
        callingCode: '+382'
    },
    {
        name: 'Montserrat',
        alpha2Code: 'MS',
        alpha3Code: 'MSR',
        numericCode: '500',
        callingCode: '+1664'
    },
    {
        name: 'Marokkó',
        alpha2Code: 'MA',
        alpha3Code: 'MAR',
        numericCode: '504',
        callingCode: '+212'
    },
    {
        name: 'Mozambik',
        alpha2Code: 'MZ',
        alpha3Code: 'MOZ',
        numericCode: '508',
        callingCode: '+258'
    },
    {
        name: 'Mianmar',
        alpha2Code: 'MM',
        alpha3Code: 'MMR',
        numericCode: '104',
        callingCode: '+95'
    },
    {
        name: 'Namíbia',
        alpha2Code: 'NA',
        alpha3Code: 'NAM',
        numericCode: '516',
        callingCode: '+264'
    },
    {
        name: 'Nauru',
        alpha2Code: 'NR',
        alpha3Code: 'NRU',
        numericCode: '520',
        callingCode: '+674'
    },
    {
        name: 'Nepál',
        alpha2Code: 'NP',
        alpha3Code: 'NPL',
        numericCode: '524',
        callingCode: '+977'
    },
    {
        name: 'Hollandia',
        alpha2Code: 'NL',
        alpha3Code: 'NLD',
        numericCode: '528',
        callingCode: '+31'
    },
    {
        name: 'Új-Kaledónia',
        alpha2Code: 'NC',
        alpha3Code: 'NCL',
        numericCode: '540',
        callingCode: '+687'
    },
    {
        name: 'Új-Zéland',
        alpha2Code: 'NZ',
        alpha3Code: 'NZL',
        numericCode: '554',
        callingCode: '+64'
    },
    {
        name: 'Nicaragua',
        alpha2Code: 'NI',
        alpha3Code: 'NIC',
        numericCode: '558',
        callingCode: '+505'
    },
    {
        name: 'Niger',
        alpha2Code: 'NE',
        alpha3Code: 'NER',
        numericCode: '562',
        callingCode: '+227'
    },
    {
        name: 'Nigéria',
        alpha2Code: 'NG',
        alpha3Code: 'NGA',
        numericCode: '566',
        callingCode: '+234'
    },
    {
        name: 'Niue',
        alpha2Code: 'NU',
        alpha3Code: 'NIU',
        numericCode: '570',
        callingCode: '+683'
    },
    {
        name: 'Norfolk-sziget',
        alpha2Code: 'NF',
        alpha3Code: 'NFK',
        numericCode: '574',
        callingCode: '+672'
    },
    {
        name: "Korea (Népi Demokratikus Köztársaság)",
        alpha2Code: 'KP',
        alpha3Code: 'PRK',
        numericCode: '408',
        callingCode: '+850'
    },
    {
        name: 'Északi-Mariana-szigetek',
        alpha2Code: 'MP',
        alpha3Code: 'MNP',
        numericCode: '580',
        callingCode: '+1670'
    },
    {
        name: 'Norvégia',
        alpha2Code: 'NO',
        alpha3Code: 'NOR',
        numericCode: '578',
        callingCode: '+47'
    },
    {
        name: 'Omán',
        alpha2Code: 'OM',
        alpha3Code: 'OMN',
        numericCode: '512',
        callingCode: '+968'
    },
    {
        name: 'Pakisztán',
        alpha2Code: 'PK',
        alpha3Code: 'PAK',
        numericCode: '586',
        callingCode: '+92'
    },
    {
        name: 'Palau',
        alpha2Code: 'PW',
        alpha3Code: 'PLW',
        numericCode: '585',
        callingCode: '+680'
    },
    {
        name: 'Palesztina, Palesztin Állam',
        alpha2Code: 'PS',
        alpha3Code: 'PSE',
        numericCode: '275',
        callingCode: '+970'
    },
    {
        name: 'Panama',
        alpha2Code: 'PA',
        alpha3Code: 'PAN',
        numericCode: '591',
        callingCode: '+507'
    },
    {
        name: 'Pápua Új-Guinea',
        alpha2Code: 'PG',
        alpha3Code: 'PNG',
        numericCode: '598',
        callingCode: '+675'
    },
    {
        name: 'Paraguay',
        alpha2Code: 'PY',
        alpha3Code: 'PRY',
        numericCode: '600',
        callingCode: '+595'
    },
    {
        name: 'Peru',
        alpha2Code: 'PE',
        alpha3Code: 'PER',
        numericCode: '604',
        callingCode: '+51'
    },
    {
        name: 'Fülöp-szigetek',
        alpha2Code: 'PH',
        alpha3Code: 'PHL',
        numericCode: '608',
        callingCode: '+63'
    },
    {
        name: 'Pitcairn',
        alpha2Code: 'PN',
        alpha3Code: 'PCN',
        numericCode: '612',
        callingCode: '+64'
    },
    {
        name: 'Lengyelország',
        alpha2Code: 'PL',
        alpha3Code: 'POL',
        numericCode: '616',
        callingCode: '+48'
    },
    {
        name: 'Portugália',
        alpha2Code: 'PT',
        alpha3Code: 'PRT',
        numericCode: '620',
        callingCode: '+351'
    },
    {
        name: 'Puerto Rico',
        alpha2Code: 'PR',
        alpha3Code: 'PRI',
        numericCode: '630',
        callingCode: '+1'
    },
    {
        name: 'Katar',
        alpha2Code: 'QA',
        alpha3Code: 'QAT',
        numericCode: '634',
        callingCode: '+974'
    },
    {
        name: 'Koszovói Köztársaság',
        alpha2Code: 'XK',
        alpha3Code: 'KOS',
        numericCode: null,
        callingCode: '+383'
    },
    {
        name: 'Réunion',
        alpha2Code: 'RE',
        alpha3Code: 'REU',
        numericCode: '638',
        callingCode: '+262'
    },
    {
        name: 'Románia',
        alpha2Code: 'RO',
        alpha3Code: 'ROU',
        numericCode: '642',
        callingCode: '+40'
    },
    {
        name: 'Orosz Föderáció',
        alpha2Code: 'RU',
        alpha3Code: 'RUS',
        numericCode: '643',
        callingCode: '+7'
    },
    {
        name: 'Ruanda',
        alpha2Code: 'RW',
        alpha3Code: 'RWA',
        numericCode: '646',
        callingCode: '+250'
    },
    {
        name: 'Saint Barthélemy',
        alpha2Code: 'BL',
        alpha3Code: 'BLM',
        numericCode: '652',
        callingCode: '+590'
    },
    {
        name: 'Szent Heléna, Ascension és Tristan da Cunha',
        alpha2Code: 'SH',
        alpha3Code: 'SHN',
        numericCode: '654',
        callingCode: '+290'
    },
    {
        name: 'Saint Kitts és Nevis',
        alpha2Code: 'KN',
        alpha3Code: 'KNA',
        numericCode: '659',
        callingCode: '+1869'
    },
    {
        name: 'Saint Lucia',
        alpha2Code: 'LC',
        alpha3Code: 'LCA',
        numericCode: '662',
        callingCode: '+1758'
    },
    {
        name: 'Saint Martin (francia rész)',
        alpha2Code: 'MF',
        alpha3Code: 'MAF',
        numericCode: '663',
        callingCode: '+590'
    },
    {
        name: 'Saint Pierre és Miquelon',
        alpha2Code: 'PM',
        alpha3Code: 'SPM',
        numericCode: '666',
        callingCode: '+508'
    },
    {
        name: 'Saint Vincent és a Grenadine-szigetek',
        alpha2Code: 'VC',
        alpha3Code: 'VCT',
        numericCode: '670',
        callingCode: '+1784'
    },
    {
        name: 'Szamoa',
        alpha2Code: 'WS',
        alpha3Code: 'WSM',
        numericCode: '882',
        callingCode: '+685'
    },
    {
        name: 'San Marino',
        alpha2Code: 'SM',
        alpha3Code: 'SMR',
        numericCode: '674',
        callingCode: '+378'
    },
    {
        name: 'São Tomé és Príncipe',
        alpha2Code: 'ST',
        alpha3Code: 'STP',
        numericCode: '678',
        callingCode: '+239'
    },
    {
        name: 'Szaúd-Arábia',
        alpha2Code: 'SA',
        alpha3Code: 'SAU',
        numericCode: '682',
        callingCode: '+966'
    },
    {
        name: 'Szenegál',
        alpha2Code: 'SN',
        alpha3Code: 'SEN',
        numericCode: '686',
        callingCode: '+221'
    },
    {
        name: 'Szerbia',
        alpha2Code: 'RS',
        alpha3Code: 'SRB',
        numericCode: '688',
        callingCode: '+381'
    },
    {
        name: 'Seychelle-szigetek',
        alpha2Code: 'SC',
        alpha3Code: 'SYC',
        numericCode: '690',
        callingCode: '+248'
    },
    {
        name: 'Sierra Leone',
        alpha2Code: 'SL',
        alpha3Code: 'SLE',
        numericCode: '694',
        callingCode: '+232'
    },
    {
        name: 'Szingapúr',
        alpha2Code: 'SG',
        alpha3Code: 'SGP',
        numericCode: '702',
        callingCode: '+65'
    },
    {
        name: 'Sint Maarten (holland rész)',
        alpha2Code: 'SX',
        alpha3Code: 'SXM',
        numericCode: '534',
        callingCode: '+1721'
    },
    {
        name: 'Szlovákia',
        alpha2Code: 'SK',
        alpha3Code: 'SVK',
        numericCode: '703',
        callingCode: '+421'
    },
    {
        name: 'Szlovénia',
        alpha2Code: 'SI',
        alpha3Code: 'SVN',
        numericCode: '705',
        callingCode: '+386'
    },
    {
        name: 'Salamon-szigetek',
        alpha2Code: 'SB',
        alpha3Code: 'SLB',
        numericCode: '090',
        callingCode: '+677'
    },
    {
        name: 'Szomália',
        alpha2Code: 'SO',
        alpha3Code: 'SOM',
        numericCode: '706',
        callingCode: '+252'
    },
    {
        name: 'Dél-Afrika',
        alpha2Code: 'ZA',
        alpha3Code: 'ZAF',
        numericCode: '710',
        callingCode: '+27'
    },
    {
        name: 'Dél-Georgia és a Déli-Sandwich-szigetek',
        alpha2Code: 'GS',
        alpha3Code: 'SGS',
        numericCode: '239',
        callingCode: '+500'
    },
    {
        name: 'Korea (Köztársaság)',
        alpha2Code: 'KR',
        alpha3Code: 'KOR',
        numericCode: '410',
        callingCode: '+82'
    },
    {
        name: 'Dél-Szudán',
        alpha2Code: 'SS',
        alpha3Code: 'SSD',
        numericCode: '728',
        callingCode: '+211'
    },
    {
        name: 'Spanyolország',
        alpha2Code: 'ES',
        alpha3Code: 'ESP',
        numericCode: '724',
        callingCode: '+34'
    },
    {
        name: 'Srí Lanka',
        alpha2Code: 'LK',
        alpha3Code: 'LKA',
        numericCode: '144',
        callingCode: '+94'
    },
    {
        name: 'Szudán',
        alpha2Code: 'SD',
        alpha3Code: 'SDN',
        numericCode: '729',
        callingCode: '+249'
    },
    {
        name: 'Suriname',
        alpha2Code: 'SR',
        alpha3Code: 'SUR',
        numericCode: '740',
        callingCode: '+597'
    },
    {
        name: 'Svalbard és Jan Mayen',
        alpha2Code: 'SJ',
        alpha3Code: 'SJM',
        numericCode: '744',
        callingCode: '+4779'
    },
    {
        name: 'Szváziföld',
        alpha2Code: 'SZ',
        alpha3Code: 'SWZ',
        numericCode: '748',
        callingCode: '+268'
    },
    {
        name: 'Svédország',
        alpha2Code: 'SE',
        alpha3Code: 'SWE',
        numericCode: '752',
        callingCode: '+46'
    },
    {
        name: 'Svájc',
        alpha2Code: 'CH',
        alpha3Code: 'CHE',
        numericCode: '756',
        callingCode: '+41'
    },
    {
        name: 'Szíriai Arab Köztársaság',
        alpha2Code: 'SY',
        alpha3Code: 'SYR',
        numericCode: '760',
        callingCode: '+963'
    },
    {
        name: 'Tajvan, Kína',
        alpha2Code: 'TW',
        alpha3Code: 'TWN',
        numericCode: '158',
        callingCode: '+886'
    },
    {
        name: 'Tádzsikisztán',
        alpha2Code: 'TJ',
        alpha3Code: 'TJK',
        numericCode: '762',
        callingCode: '+992'
    },
    {
        name: 'Tanzánia, Egyesült Köztársaság',
        alpha2Code: 'TZ',
        alpha3Code: 'TZA',
        numericCode: '834',
        callingCode: '+255'
    },
    {
        name: 'Thaiföld',
        alpha2Code: 'TH',
        alpha3Code: 'THA',
        numericCode: '764',
        callingCode: '+66'
    },
    {
        name: 'Kelet-Timor',
        alpha2Code: 'TL',
        alpha3Code: 'TLS',
        numericCode: '626',
        callingCode: '+670'
    },
    {
        name: 'Togo',
        alpha2Code: 'TG',
        alpha3Code: 'TGO',
        numericCode: '768',
        callingCode: '+228'
    },
    {
        name: 'Tokelau',
        alpha2Code: 'TK',
        alpha3Code: 'TKL',
        numericCode: '772',
        callingCode: '+690'
    },
    {
        name: 'Tonga',
        alpha2Code: 'TO',
        alpha3Code: 'TON',
        numericCode: '776',
        callingCode: '+676'
    },
    {
        name: 'Trinidad és Tobago',
        alpha2Code: 'TT',
        alpha3Code: 'TTO',
        numericCode: '780',
        callingCode: '+1868'
    },
    {
        name: 'Tunézia',
        alpha2Code: 'TN',
        alpha3Code: 'TUN',
        numericCode: '788',
        callingCode: '+216'
    },
    {
        name: 'Törökország',
        alpha2Code: 'TR',
        alpha3Code: 'TUR',
        numericCode: '792',
        callingCode: '+90'
    },
    {
        name: 'Türkmenisztán',
        alpha2Code: 'TM',
        alpha3Code: 'TKM',
        numericCode: '795',
        callingCode: '+993'
    },
    {
        name: 'Turks és Caicos-szigetek',
        alpha2Code: 'TC',
        alpha3Code: 'TCA',
        numericCode: '796',
        callingCode: '+1649'
    },
    {
        name: 'Tuvalu',
        alpha2Code: 'TV',
        alpha3Code: 'TUV',
        numericCode: '798',
        callingCode: '+688'
    },
    {
        name: 'Uganda',
        alpha2Code: 'UG',
        alpha3Code: 'UGA',
        numericCode: '800',
        callingCode: '+256'
    },
    {
        name: 'Ukrajna',
        alpha2Code: 'UA',
        alpha3Code: 'UKR',
        numericCode: '804',
        callingCode: '+380'
    },
    {
        name: 'Egyesült Arab Emírségek',
        alpha2Code: 'AE',
        alpha3Code: 'ARE',
        numericCode: '784',
        callingCode: '+971'
    },
    {
        name: 'Nagy-Britannia és Észak-Írország Egyesült Királysága',
        alpha2Code: 'GB',
        alpha3Code: 'GBR',
        numericCode: '826',
        callingCode: '+44'
    },
    {
        name: 'Amerikai Egyesült Államok',
        alpha2Code: 'US',
        alpha3Code: 'USA',
        numericCode: '840',
        callingCode: '+1'
    },
    {
        name: 'Uruguay',
        alpha2Code: 'UY',
        alpha3Code: 'URY',
        numericCode: '858',
        callingCode: '+598'
    },
    {
        name: 'Üzbegisztán',
        alpha2Code: 'UZ',
        alpha3Code: 'UZB',
        numericCode: '860',
        callingCode: '+998'
    },
    {
        name: 'Vanuatu',
        alpha2Code: 'VU',
        alpha3Code: 'VUT',
        numericCode: '548',
        callingCode: '+678'
    },
    {
        name: 'Venezuela (Bolivári Köztársaság)',
        alpha2Code: 'VE',
        alpha3Code: 'VEN',
        numericCode: '862',
        callingCode: '+58'
    },
    {
        name: 'Vietnám',
        alpha2Code: 'VN',
        alpha3Code: 'VNM',
        numericCode: '704',
        callingCode: '+84'
    },
    {
        name: 'Wallis és Futuna',
        alpha2Code: 'WF',
        alpha3Code: 'WLF',
        numericCode: '876',
        callingCode: '+681'
    },
    {
        name: 'Nyugat-Szahara',
        alpha2Code: 'EH',
        alpha3Code: 'ESH',
        numericCode: '732',
        callingCode: '+212'
    },
    {
        name: 'Jemen',
        alpha2Code: 'YE',
        alpha3Code: 'YEM',
        numericCode: '887',
        callingCode: '+967'
    },
    {
        name: 'Zambia',
        alpha2Code: 'ZM',
        alpha3Code: 'ZMB',
        numericCode: '894',
        callingCode: '+260'
    },
    {
        name: 'Zimbabwe',
        alpha2Code: 'ZW',
        alpha3Code: 'ZWE',
        numericCode: '716',
        callingCode: '+263'
    }
];

var hu = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB_HU: COUNTRIES_DB_HU
});

const COUNTRIES_DB_IT = [
    {
        name: 'Afghanistan',
        alpha2Code: 'AF',
        alpha3Code: 'AFG',
        numericCode: '004',
        callingCode: '+93'
    },
    {
        name: 'Isole Aland',
        alpha2Code: 'AX',
        alpha3Code: 'ALA',
        numericCode: '248',
        callingCode: '+358'
    },
    {
        name: 'Albania',
        alpha2Code: 'AL',
        alpha3Code: 'ALB',
        numericCode: '008',
        callingCode: '+355'
    },
    {
        name: 'Algeria',
        alpha2Code: 'DZ',
        alpha3Code: 'DZA',
        numericCode: '012',
        callingCode: '+213'
    },
    {
        name: 'Samoa Americane',
        alpha2Code: 'AS',
        alpha3Code: 'ASM',
        numericCode: '016',
        callingCode: '+1684'
    },
    {
        name: 'Andorra',
        alpha2Code: 'AD',
        alpha3Code: 'AND',
        numericCode: '020',
        callingCode: '+376'
    },
    {
        name: 'Angola',
        alpha2Code: 'AO',
        alpha3Code: 'AGO',
        numericCode: '024',
        callingCode: '+244'
    },
    {
        name: 'Anguilla',
        alpha2Code: 'AI',
        alpha3Code: 'AIA',
        numericCode: '660',
        callingCode: '+1264'
    },
    {
        name: 'Antartide',
        alpha2Code: 'AQ',
        alpha3Code: 'ATA',
        numericCode: '010',
        callingCode: '+672'
    },
    {
        name: 'Antigua e Barbuda',
        alpha2Code: 'AG',
        alpha3Code: 'ATG',
        numericCode: '028',
        callingCode: '+1268'
    },
    {
        name: 'Argentina',
        alpha2Code: 'AR',
        alpha3Code: 'ARG',
        numericCode: '032',
        callingCode: '+54'
    },
    {
        name: 'Armenia',
        alpha2Code: 'AM',
        alpha3Code: 'ARM',
        numericCode: '051',
        callingCode: '+374'
    },
    {
        name: 'Aruba',
        alpha2Code: 'AW',
        alpha3Code: 'ABW',
        numericCode: '533',
        callingCode: '+297'
    },
    {
        name: 'Australia',
        alpha2Code: 'AU',
        alpha3Code: 'AUS',
        numericCode: '036',
        callingCode: '+61'
    },
    {
        name: 'Austria',
        alpha2Code: 'AT',
        alpha3Code: 'AUT',
        numericCode: '040',
        callingCode: '+43'
    },
    {
        name: 'Azerbaijan',
        alpha2Code: 'AZ',
        alpha3Code: 'AZE',
        numericCode: '031',
        callingCode: '+994'
    },
    {
        name: 'Bahamas',
        alpha2Code: 'BS',
        alpha3Code: 'BHS',
        numericCode: '044',
        callingCode: '+1242'
    },
    {
        name: 'Bahrein',
        alpha2Code: 'BH',
        alpha3Code: 'BHR',
        numericCode: '048',
        callingCode: '+973'
    },
    {
        name: 'Bangladesh',
        alpha2Code: 'BD',
        alpha3Code: 'BGD',
        numericCode: '050',
        callingCode: '+880'
    },
    {
        name: 'Barbados',
        alpha2Code: 'BB',
        alpha3Code: 'BRB',
        numericCode: '052',
        callingCode: '+1246'
    },
    {
        name: 'Bielorussia',
        alpha2Code: 'BY',
        alpha3Code: 'BLR',
        numericCode: '112',
        callingCode: '+375'
    },
    {
        name: 'Belgio',
        alpha2Code: 'BE',
        alpha3Code: 'BEL',
        numericCode: '056',
        callingCode: '+32'
    },
    {
        name: 'Belize',
        alpha2Code: 'BZ',
        alpha3Code: 'BLZ',
        numericCode: '084',
        callingCode: '+501'
    },
    {
        name: 'Benin',
        alpha2Code: 'BJ',
        alpha3Code: 'BEN',
        numericCode: '204',
        callingCode: '+229'
    },
    {
        name: 'Bermuda',
        alpha2Code: 'BM',
        alpha3Code: 'BMU',
        numericCode: '060',
        callingCode: '+1441'
    },
    {
        name: 'Bhutan',
        alpha2Code: 'BT',
        alpha3Code: 'BTN',
        numericCode: '064',
        callingCode: '+975'
    },
    {
        name: 'Bolivia',
        alpha2Code: 'BO',
        alpha3Code: 'BOL',
        numericCode: '068',
        callingCode: '+591'
    },
    {
        name: 'Bonaire, Saint-Eustache e Saba',
        alpha2Code: 'BQ',
        alpha3Code: 'BES',
        numericCode: '535',
        callingCode: '+5997'
    },
    {
        name: 'Bosnia ed Erzegovina',
        alpha2Code: 'BA',
        alpha3Code: 'BIH',
        numericCode: '070',
        callingCode: '+387'
    },
    {
        name: 'Botswana',
        alpha2Code: 'BW',
        alpha3Code: 'BWA',
        numericCode: '072',
        callingCode: '+267'
    },
    {
        name: 'Isola Bouvet',
        alpha2Code: 'BV',
        alpha3Code: 'BVT',
        numericCode: '074',
        callingCode: '+'
    },
    {
        name: 'Brasile',
        alpha2Code: 'BR',
        alpha3Code: 'BRA',
        numericCode: '076',
        callingCode: '+55'
    },
    {
        name: "Territorio britannico dell'oceano indiano",
        alpha2Code: 'IO',
        alpha3Code: 'IOT',
        numericCode: '086',
        callingCode: '+246'
    },
    {
        name: "Isole minori esterne degli Stati Uniti d'America",
        alpha2Code: 'UM',
        alpha3Code: 'UMI',
        numericCode: '581',
        callingCode: '+'
    },
    {
        name: 'Isole Vergini Britanniche',
        alpha2Code: 'VG',
        alpha3Code: 'VGB',
        numericCode: '092',
        callingCode: '+1284'
    },
    {
        name: 'Isole Vergini americane',
        alpha2Code: 'VI',
        alpha3Code: 'VIR',
        numericCode: '850',
        callingCode: '+1 340'
    },
    {
        name: 'Brunei',
        alpha2Code: 'BN',
        alpha3Code: 'BRN',
        numericCode: '096',
        callingCode: '+673'
    },
    {
        name: 'Bulgaria',
        alpha2Code: 'BG',
        alpha3Code: 'BGR',
        numericCode: '100',
        callingCode: '+359'
    },
    {
        name: 'Burkina Faso',
        alpha2Code: 'BF',
        alpha3Code: 'BFA',
        numericCode: '854',
        callingCode: '+226'
    },
    {
        name: 'Burundi',
        alpha2Code: 'BI',
        alpha3Code: 'BDI',
        numericCode: '108',
        callingCode: '+257'
    },
    {
        name: 'Cambogia',
        alpha2Code: 'KH',
        alpha3Code: 'KHM',
        numericCode: '116',
        callingCode: '+855'
    },
    {
        name: 'Camerun',
        alpha2Code: 'CM',
        alpha3Code: 'CMR',
        numericCode: '120',
        callingCode: '+237'
    },
    {
        name: 'Canada',
        alpha2Code: 'CA',
        alpha3Code: 'CAN',
        numericCode: '124',
        callingCode: '+1'
    },
    {
        name: 'Capo Verde',
        alpha2Code: 'CV',
        alpha3Code: 'CPV',
        numericCode: '132',
        callingCode: '+238'
    },
    {
        name: 'Isole Cayman',
        alpha2Code: 'KY',
        alpha3Code: 'CYM',
        numericCode: '136',
        callingCode: '+1345'
    },
    {
        name: 'Repubblica Centrafricana',
        alpha2Code: 'CF',
        alpha3Code: 'CAF',
        numericCode: '140',
        callingCode: '+236'
    },
    {
        name: 'Ciad',
        alpha2Code: 'TD',
        alpha3Code: 'TCD',
        numericCode: '148',
        callingCode: '+235'
    },
    {
        name: 'Cile',
        alpha2Code: 'CL',
        alpha3Code: 'CHL',
        numericCode: '152',
        callingCode: '+56'
    },
    {
        name: 'Cina',
        alpha2Code: 'CN',
        alpha3Code: 'CHN',
        numericCode: '156',
        callingCode: '+86'
    },
    {
        name: 'Isola di Natale',
        alpha2Code: 'CX',
        alpha3Code: 'CXR',
        numericCode: '162',
        callingCode: '+61'
    },
    {
        name: 'Isole Cocos e Keeling',
        alpha2Code: 'CC',
        alpha3Code: 'CCK',
        numericCode: '166',
        callingCode: '+61'
    },
    {
        name: 'Colombia',
        alpha2Code: 'CO',
        alpha3Code: 'COL',
        numericCode: '170',
        callingCode: '+57'
    },
    {
        name: 'Comore',
        alpha2Code: 'KM',
        alpha3Code: 'COM',
        numericCode: '174',
        callingCode: '+269'
    },
    {
        name: 'Congo',
        alpha2Code: 'CG',
        alpha3Code: 'COG',
        numericCode: '178',
        callingCode: '+242'
    },
    {
        name: 'Congo (Rep. Dem.)',
        alpha2Code: 'CD',
        alpha3Code: 'COD',
        numericCode: '180',
        callingCode: '+243'
    },
    {
        name: 'Isole Cook',
        alpha2Code: 'CK',
        alpha3Code: 'COK',
        numericCode: '184',
        callingCode: '+682'
    },
    {
        name: 'Costa Rica',
        alpha2Code: 'CR',
        alpha3Code: 'CRI',
        numericCode: '188',
        callingCode: '+506'
    },
    {
        name: 'Croazia',
        alpha2Code: 'HR',
        alpha3Code: 'HRV',
        numericCode: '191',
        callingCode: '+385'
    },
    {
        name: 'Cuba',
        alpha2Code: 'CU',
        alpha3Code: 'CUB',
        numericCode: '192',
        callingCode: '+53'
    },
    {
        name: 'Curaçao',
        alpha2Code: 'CW',
        alpha3Code: 'CUW',
        numericCode: '531',
        callingCode: '+599'
    },
    {
        name: 'Cipro',
        alpha2Code: 'CY',
        alpha3Code: 'CYP',
        numericCode: '196',
        callingCode: '+357'
    },
    {
        name: 'Repubblica Ceca',
        alpha2Code: 'CZ',
        alpha3Code: 'CZE',
        numericCode: '203',
        callingCode: '+420'
    },
    {
        name: 'Danimarca',
        alpha2Code: 'DK',
        alpha3Code: 'DNK',
        numericCode: '208',
        callingCode: '+45'
    },
    {
        name: 'Gibuti',
        alpha2Code: 'DJ',
        alpha3Code: 'DJI',
        numericCode: '262',
        callingCode: '+253'
    },
    {
        name: 'Dominica',
        alpha2Code: 'DM',
        alpha3Code: 'DMA',
        numericCode: '212',
        callingCode: '+1767'
    },
    {
        name: 'Repubblica Dominicana',
        alpha2Code: 'DO',
        alpha3Code: 'DOM',
        numericCode: '214',
        callingCode: '+1809'
    },
    {
        name: 'Ecuador',
        alpha2Code: 'EC',
        alpha3Code: 'ECU',
        numericCode: '218',
        callingCode: '+593'
    },
    {
        name: 'Egitto',
        alpha2Code: 'EG',
        alpha3Code: 'EGY',
        numericCode: '818',
        callingCode: '+20'
    },
    {
        name: 'El Salvador',
        alpha2Code: 'SV',
        alpha3Code: 'SLV',
        numericCode: '222',
        callingCode: '+503'
    },
    {
        name: 'Guinea Equatoriale',
        alpha2Code: 'GQ',
        alpha3Code: 'GNQ',
        numericCode: '226',
        callingCode: '+240'
    },
    {
        name: 'Eritrea',
        alpha2Code: 'ER',
        alpha3Code: 'ERI',
        numericCode: '232',
        callingCode: '+291'
    },
    {
        name: 'Estonia',
        alpha2Code: 'EE',
        alpha3Code: 'EST',
        numericCode: '233',
        callingCode: '+372'
    },
    {
        name: 'Etiopia',
        alpha2Code: 'ET',
        alpha3Code: 'ETH',
        numericCode: '231',
        callingCode: '+251'
    },
    {
        name: 'Isole Falkland o Isole Malvine',
        alpha2Code: 'FK',
        alpha3Code: 'FLK',
        numericCode: '238',
        callingCode: '+500'
    },
    {
        name: 'Isole Far Oer',
        alpha2Code: 'FO',
        alpha3Code: 'FRO',
        numericCode: '234',
        callingCode: '+298'
    },
    {
        name: 'Figi',
        alpha2Code: 'FJ',
        alpha3Code: 'FJI',
        numericCode: '242',
        callingCode: '+679'
    },
    {
        name: 'Finlandia',
        alpha2Code: 'FI',
        alpha3Code: 'FIN',
        numericCode: '246',
        callingCode: '+358'
    },
    {
        name: 'Francia',
        alpha2Code: 'FR',
        alpha3Code: 'FRA',
        numericCode: '250',
        callingCode: '+33'
    },
    {
        name: 'Guyana francese',
        alpha2Code: 'GF',
        alpha3Code: 'GUF',
        numericCode: '254',
        callingCode: '+594'
    },
    {
        name: 'Polinesia Francese',
        alpha2Code: 'PF',
        alpha3Code: 'PYF',
        numericCode: '258',
        callingCode: '+689'
    },
    {
        name: 'Territori Francesi del Sud',
        alpha2Code: 'TF',
        alpha3Code: 'ATF',
        numericCode: '260',
        callingCode: '+'
    },
    {
        name: 'Gabon',
        alpha2Code: 'GA',
        alpha3Code: 'GAB',
        numericCode: '266',
        callingCode: '+241'
    },
    {
        name: 'Gambia',
        alpha2Code: 'GM',
        alpha3Code: 'GMB',
        numericCode: '270',
        callingCode: '+220'
    },
    {
        name: 'Georgia',
        alpha2Code: 'GE',
        alpha3Code: 'GEO',
        numericCode: '268',
        callingCode: '+995'
    },
    {
        name: 'Germania',
        alpha2Code: 'DE',
        alpha3Code: 'DEU',
        numericCode: '276',
        callingCode: '+49'
    },
    {
        name: 'Ghana',
        alpha2Code: 'GH',
        alpha3Code: 'GHA',
        numericCode: '288',
        callingCode: '+233'
    },
    {
        name: 'Gibilterra',
        alpha2Code: 'GI',
        alpha3Code: 'GIB',
        numericCode: '292',
        callingCode: '+350'
    },
    {
        name: 'Grecia',
        alpha2Code: 'GR',
        alpha3Code: 'GRC',
        numericCode: '300',
        callingCode: '+30'
    },
    {
        name: 'Groenlandia',
        alpha2Code: 'GL',
        alpha3Code: 'GRL',
        numericCode: '304',
        callingCode: '+299'
    },
    {
        name: 'Grenada',
        alpha2Code: 'GD',
        alpha3Code: 'GRD',
        numericCode: '308',
        callingCode: '+1473'
    },
    {
        name: 'Guadeloupa',
        alpha2Code: 'GP',
        alpha3Code: 'GLP',
        numericCode: '312',
        callingCode: '+590'
    },
    {
        name: 'Guam',
        alpha2Code: 'GU',
        alpha3Code: 'GUM',
        numericCode: '316',
        callingCode: '+1671'
    },
    {
        name: 'Guatemala',
        alpha2Code: 'GT',
        alpha3Code: 'GTM',
        numericCode: '320',
        callingCode: '+502'
    },
    {
        name: 'Guernsey',
        alpha2Code: 'GG',
        alpha3Code: 'GGY',
        numericCode: '831',
        callingCode: '+44'
    },
    {
        name: 'Guinea',
        alpha2Code: 'GN',
        alpha3Code: 'GIN',
        numericCode: '324',
        callingCode: '+224'
    },
    {
        name: 'Guinea-Bissau',
        alpha2Code: 'GW',
        alpha3Code: 'GNB',
        numericCode: '624',
        callingCode: '+245'
    },
    {
        name: 'Guyana',
        alpha2Code: 'GY',
        alpha3Code: 'GUY',
        numericCode: '328',
        callingCode: '+592'
    },
    {
        name: 'Haiti',
        alpha2Code: 'HT',
        alpha3Code: 'HTI',
        numericCode: '332',
        callingCode: '+509'
    },
    {
        name: 'Isole Heard e McDonald',
        alpha2Code: 'HM',
        alpha3Code: 'HMD',
        numericCode: '334',
        callingCode: '+'
    },
    {
        name: 'Santa Sede',
        alpha2Code: 'VA',
        alpha3Code: 'VAT',
        numericCode: '336',
        callingCode: '+379'
    },
    {
        name: 'Honduras',
        alpha2Code: 'HN',
        alpha3Code: 'HND',
        numericCode: '340',
        callingCode: '+504'
    },
    {
        name: 'Hong Kong',
        alpha2Code: 'HK',
        alpha3Code: 'HKG',
        numericCode: '344',
        callingCode: '+852'
    },
    {
        name: 'Ungheria',
        alpha2Code: 'HU',
        alpha3Code: 'HUN',
        numericCode: '348',
        callingCode: '+36'
    },
    {
        name: 'Islanda',
        alpha2Code: 'IS',
        alpha3Code: 'ISL',
        numericCode: '352',
        callingCode: '+354'
    },
    {
        name: 'India',
        alpha2Code: 'IN',
        alpha3Code: 'IND',
        numericCode: '356',
        callingCode: '+91'
    },
    {
        name: 'Indonesia',
        alpha2Code: 'ID',
        alpha3Code: 'IDN',
        numericCode: '360',
        callingCode: '+62'
    },
    {
        name: "Costa D'Avorio",
        alpha2Code: 'CI',
        alpha3Code: 'CIV',
        numericCode: '384',
        callingCode: '+225'
    },
    {
        name: 'Iran',
        alpha2Code: 'IR',
        alpha3Code: 'IRN',
        numericCode: '364',
        callingCode: '+98'
    },
    {
        name: 'Iraq',
        alpha2Code: 'IQ',
        alpha3Code: 'IRQ',
        numericCode: '368',
        callingCode: '+964'
    },
    {
        name: 'Irlanda',
        alpha2Code: 'IE',
        alpha3Code: 'IRL',
        numericCode: '372',
        callingCode: '+353'
    },
    {
        name: 'Isola di Man',
        alpha2Code: 'IM',
        alpha3Code: 'IMN',
        numericCode: '833',
        callingCode: '+44'
    },
    {
        name: 'Israele',
        alpha2Code: 'IL',
        alpha3Code: 'ISR',
        numericCode: '376',
        callingCode: '+972'
    },
    {
        name: 'Italia',
        alpha2Code: 'IT',
        alpha3Code: 'ITA',
        numericCode: '380',
        callingCode: '+39'
    },
    {
        name: 'Giamaica',
        alpha2Code: 'JM',
        alpha3Code: 'JAM',
        numericCode: '388',
        callingCode: '+1876'
    },
    {
        name: 'Giappone',
        alpha2Code: 'JP',
        alpha3Code: 'JPN',
        numericCode: '392',
        callingCode: '+81'
    },
    {
        name: 'Isola di Jersey',
        alpha2Code: 'JE',
        alpha3Code: 'JEY',
        numericCode: '832',
        callingCode: '+44'
    },
    {
        name: 'Giordania',
        alpha2Code: 'JO',
        alpha3Code: 'JOR',
        numericCode: '400',
        callingCode: '+962'
    },
    {
        name: 'Kazakistan',
        alpha2Code: 'KZ',
        alpha3Code: 'KAZ',
        numericCode: '398',
        callingCode: '+7'
    },
    {
        name: 'Kenya',
        alpha2Code: 'KE',
        alpha3Code: 'KEN',
        numericCode: '404',
        callingCode: '+254'
    },
    {
        name: 'Kiribati',
        alpha2Code: 'KI',
        alpha3Code: 'KIR',
        numericCode: '296',
        callingCode: '+686'
    },
    {
        name: 'Kuwait',
        alpha2Code: 'KW',
        alpha3Code: 'KWT',
        numericCode: '414',
        callingCode: '+965'
    },
    {
        name: 'Kirghizistan',
        alpha2Code: 'KG',
        alpha3Code: 'KGZ',
        numericCode: '417',
        callingCode: '+996'
    },
    {
        name: 'Laos',
        alpha2Code: 'LA',
        alpha3Code: 'LAO',
        numericCode: '418',
        callingCode: '+856'
    },
    {
        name: 'Lettonia',
        alpha2Code: 'LV',
        alpha3Code: 'LVA',
        numericCode: '428',
        callingCode: '+371'
    },
    {
        name: 'Libano',
        alpha2Code: 'LB',
        alpha3Code: 'LBN',
        numericCode: '422',
        callingCode: '+961'
    },
    {
        name: 'Lesotho',
        alpha2Code: 'LS',
        alpha3Code: 'LSO',
        numericCode: '426',
        callingCode: '+266'
    },
    {
        name: 'Liberia',
        alpha2Code: 'LR',
        alpha3Code: 'LBR',
        numericCode: '430',
        callingCode: '+231'
    },
    {
        name: 'Libia',
        alpha2Code: 'LY',
        alpha3Code: 'LBY',
        numericCode: '434',
        callingCode: '+218'
    },
    {
        name: 'Liechtenstein',
        alpha2Code: 'LI',
        alpha3Code: 'LIE',
        numericCode: '438',
        callingCode: '+423'
    },
    {
        name: 'Lituania',
        alpha2Code: 'LT',
        alpha3Code: 'LTU',
        numericCode: '440',
        callingCode: '+370'
    },
    {
        name: 'Lussemburgo',
        alpha2Code: 'LU',
        alpha3Code: 'LUX',
        numericCode: '442',
        callingCode: '+352'
    },
    {
        name: 'Macao',
        alpha2Code: 'MO',
        alpha3Code: 'MAC',
        numericCode: '446',
        callingCode: '+853'
    },
    {
        name: 'Macedonia',
        alpha2Code: 'MK',
        alpha3Code: 'MKD',
        numericCode: '807',
        callingCode: '+389'
    },
    {
        name: 'Madagascar',
        alpha2Code: 'MG',
        alpha3Code: 'MDG',
        numericCode: '450',
        callingCode: '+261'
    },
    {
        name: 'Malawi',
        alpha2Code: 'MW',
        alpha3Code: 'MWI',
        numericCode: '454',
        callingCode: '+265'
    },
    {
        name: 'Malesia',
        alpha2Code: 'MY',
        alpha3Code: 'MYS',
        numericCode: '458',
        callingCode: '+60'
    },
    {
        name: 'Maldive',
        alpha2Code: 'MV',
        alpha3Code: 'MDV',
        numericCode: '462',
        callingCode: '+960'
    },
    {
        name: 'Mali',
        alpha2Code: 'ML',
        alpha3Code: 'MLI',
        numericCode: '466',
        callingCode: '+223'
    },
    {
        name: 'Malta',
        alpha2Code: 'MT',
        alpha3Code: 'MLT',
        numericCode: '470',
        callingCode: '+356'
    },
    {
        name: 'Isole Marshall',
        alpha2Code: 'MH',
        alpha3Code: 'MHL',
        numericCode: '584',
        callingCode: '+692'
    },
    {
        name: 'Martinica',
        alpha2Code: 'MQ',
        alpha3Code: 'MTQ',
        numericCode: '474',
        callingCode: '+596'
    },
    {
        name: 'Mauritania',
        alpha2Code: 'MR',
        alpha3Code: 'MRT',
        numericCode: '478',
        callingCode: '+222'
    },
    {
        name: 'Mauritius',
        alpha2Code: 'MU',
        alpha3Code: 'MUS',
        numericCode: '480',
        callingCode: '+230'
    },
    {
        name: 'Mayotte',
        alpha2Code: 'YT',
        alpha3Code: 'MYT',
        numericCode: '175',
        callingCode: '+262'
    },
    {
        name: 'Messico',
        alpha2Code: 'MX',
        alpha3Code: 'MEX',
        numericCode: '484',
        callingCode: '+52'
    },
    {
        name: 'Micronesia',
        alpha2Code: 'FM',
        alpha3Code: 'FSM',
        numericCode: '583',
        callingCode: '+691'
    },
    {
        name: 'Moldavia',
        alpha2Code: 'MD',
        alpha3Code: 'MDA',
        numericCode: '498',
        callingCode: '+373'
    },
    {
        name: 'Principato di Monaco',
        alpha2Code: 'MC',
        alpha3Code: 'MCO',
        numericCode: '492',
        callingCode: '+377'
    },
    {
        name: 'Mongolia',
        alpha2Code: 'MN',
        alpha3Code: 'MNG',
        numericCode: '496',
        callingCode: '+976'
    },
    {
        name: 'Montenegro',
        alpha2Code: 'ME',
        alpha3Code: 'MNE',
        numericCode: '499',
        callingCode: '+382'
    },
    {
        name: 'Montserrat',
        alpha2Code: 'MS',
        alpha3Code: 'MSR',
        numericCode: '500',
        callingCode: '+1664'
    },
    {
        name: 'Marocco',
        alpha2Code: 'MA',
        alpha3Code: 'MAR',
        numericCode: '504',
        callingCode: '+212'
    },
    {
        name: 'Mozambico',
        alpha2Code: 'MZ',
        alpha3Code: 'MOZ',
        numericCode: '508',
        callingCode: '+258'
    },
    {
        name: 'Birmania',
        alpha2Code: 'MM',
        alpha3Code: 'MMR',
        numericCode: '104',
        callingCode: '+95'
    },
    {
        name: 'Namibia',
        alpha2Code: 'NA',
        alpha3Code: 'NAM',
        numericCode: '516',
        callingCode: '+264'
    },
    {
        name: 'Nauru',
        alpha2Code: 'NR',
        alpha3Code: 'NRU',
        numericCode: '520',
        callingCode: '+674'
    },
    {
        name: 'Nepal',
        alpha2Code: 'NP',
        alpha3Code: 'NPL',
        numericCode: '524',
        callingCode: '+977'
    },
    {
        name: 'Paesi Bassi',
        alpha2Code: 'NL',
        alpha3Code: 'NLD',
        numericCode: '528',
        callingCode: '+31'
    },
    {
        name: 'Nuova Caledonia',
        alpha2Code: 'NC',
        alpha3Code: 'NCL',
        numericCode: '540',
        callingCode: '+687'
    },
    {
        name: 'Nuova Zelanda',
        alpha2Code: 'NZ',
        alpha3Code: 'NZL',
        numericCode: '554',
        callingCode: '+64'
    },
    {
        name: 'Nicaragua',
        alpha2Code: 'NI',
        alpha3Code: 'NIC',
        numericCode: '558',
        callingCode: '+505'
    },
    {
        name: 'Niger',
        alpha2Code: 'NE',
        alpha3Code: 'NER',
        numericCode: '562',
        callingCode: '+227'
    },
    {
        name: 'Nigeria',
        alpha2Code: 'NG',
        alpha3Code: 'NGA',
        numericCode: '566',
        callingCode: '+234'
    },
    {
        name: 'Niue',
        alpha2Code: 'NU',
        alpha3Code: 'NIU',
        numericCode: '570',
        callingCode: '+683'
    },
    {
        name: 'Isola Norfolk',
        alpha2Code: 'NF',
        alpha3Code: 'NFK',
        numericCode: '574',
        callingCode: '+672'
    },
    {
        name: 'Corea del Nord',
        alpha2Code: 'KP',
        alpha3Code: 'PRK',
        numericCode: '408',
        callingCode: '+850'
    },
    {
        name: 'Isole Marianne Settentrionali',
        alpha2Code: 'MP',
        alpha3Code: 'MNP',
        numericCode: '580',
        callingCode: '+1670'
    },
    {
        name: 'Norvegia',
        alpha2Code: 'NO',
        alpha3Code: 'NOR',
        numericCode: '578',
        callingCode: '+47'
    },
    {
        name: 'oman',
        alpha2Code: 'OM',
        alpha3Code: 'OMN',
        numericCode: '512',
        callingCode: '+968'
    },
    {
        name: 'Pakistan',
        alpha2Code: 'PK',
        alpha3Code: 'PAK',
        numericCode: '586',
        callingCode: '+92'
    },
    {
        name: 'Palau',
        alpha2Code: 'PW',
        alpha3Code: 'PLW',
        numericCode: '585',
        callingCode: '+680'
    },
    {
        name: 'Palestina',
        alpha2Code: 'PS',
        alpha3Code: 'PSE',
        numericCode: '275',
        callingCode: '+970'
    },
    {
        name: 'Panama',
        alpha2Code: 'PA',
        alpha3Code: 'PAN',
        numericCode: '591',
        callingCode: '+507'
    },
    {
        name: 'Papua Nuova Guinea',
        alpha2Code: 'PG',
        alpha3Code: 'PNG',
        numericCode: '598',
        callingCode: '+675'
    },
    {
        name: 'Paraguay',
        alpha2Code: 'PY',
        alpha3Code: 'PRY',
        numericCode: '600',
        callingCode: '+595'
    },
    {
        name: 'Perù',
        alpha2Code: 'PE',
        alpha3Code: 'PER',
        numericCode: '604',
        callingCode: '+51'
    },
    {
        name: 'Filippine',
        alpha2Code: 'PH',
        alpha3Code: 'PHL',
        numericCode: '608',
        callingCode: '+63'
    },
    {
        name: 'Isole Pitcairn',
        alpha2Code: 'PN',
        alpha3Code: 'PCN',
        numericCode: '612',
        callingCode: '+64'
    },
    {
        name: 'Polonia',
        alpha2Code: 'PL',
        alpha3Code: 'POL',
        numericCode: '616',
        callingCode: '+48'
    },
    {
        name: 'Portogallo',
        alpha2Code: 'PT',
        alpha3Code: 'PRT',
        numericCode: '620',
        callingCode: '+351'
    },
    {
        name: 'Porto Rico',
        alpha2Code: 'PR',
        alpha3Code: 'PRI',
        numericCode: '630',
        callingCode: '+1'
    },
    {
        name: 'Qatar',
        alpha2Code: 'QA',
        alpha3Code: 'QAT',
        numericCode: '634',
        callingCode: '+974'
    },
    {
        name: 'Kosovo',
        alpha2Code: 'XK',
        alpha3Code: 'KOS',
        numericCode: null,
        callingCode: '+383'
    },
    {
        name: 'Riunione',
        alpha2Code: 'RE',
        alpha3Code: 'REU',
        numericCode: '638',
        callingCode: '+262'
    },
    {
        name: 'Romania',
        alpha2Code: 'RO',
        alpha3Code: 'ROU',
        numericCode: '642',
        callingCode: '+40'
    },
    {
        name: 'Russia',
        alpha2Code: 'RU',
        alpha3Code: 'RUS',
        numericCode: '643',
        callingCode: '+7'
    },
    {
        name: 'Ruanda',
        alpha2Code: 'RW',
        alpha3Code: 'RWA',
        numericCode: '646',
        callingCode: '+250'
    },
    {
        name: 'Antille Francesi',
        alpha2Code: 'BL',
        alpha3Code: 'BLM',
        numericCode: '652',
        callingCode: '+590'
    },
    {
        name: "Sant'Elena",
        alpha2Code: 'SH',
        alpha3Code: 'SHN',
        numericCode: '654',
        callingCode: '+290'
    },
    {
        name: 'Saint Kitts e Nevis',
        alpha2Code: 'KN',
        alpha3Code: 'KNA',
        numericCode: '659',
        callingCode: '+1869'
    },
    {
        name: 'Santa Lucia',
        alpha2Code: 'LC',
        alpha3Code: 'LCA',
        numericCode: '662',
        callingCode: '+1758'
    },
    {
        name: 'Saint Martin',
        alpha2Code: 'MF',
        alpha3Code: 'MAF',
        numericCode: '663',
        callingCode: '+590'
    },
    {
        name: 'Saint-Pierre e Miquelon',
        alpha2Code: 'PM',
        alpha3Code: 'SPM',
        numericCode: '666',
        callingCode: '+508'
    },
    {
        name: 'Saint Vincent e Grenadine',
        alpha2Code: 'VC',
        alpha3Code: 'VCT',
        numericCode: '670',
        callingCode: '+1784'
    },
    {
        name: 'Samoa',
        alpha2Code: 'WS',
        alpha3Code: 'WSM',
        numericCode: '882',
        callingCode: '+685'
    },
    {
        name: 'San Marino',
        alpha2Code: 'SM',
        alpha3Code: 'SMR',
        numericCode: '674',
        callingCode: '+378'
    },
    {
        name: 'São Tomé e Príncipe',
        alpha2Code: 'ST',
        alpha3Code: 'STP',
        numericCode: '678',
        callingCode: '+239'
    },
    {
        name: 'Arabia Saudita',
        alpha2Code: 'SA',
        alpha3Code: 'SAU',
        numericCode: '682',
        callingCode: '+966'
    },
    {
        name: 'Senegal',
        alpha2Code: 'SN',
        alpha3Code: 'SEN',
        numericCode: '686',
        callingCode: '+221'
    },
    {
        name: 'Serbia',
        alpha2Code: 'RS',
        alpha3Code: 'SRB',
        numericCode: '688',
        callingCode: '+381'
    },
    {
        name: 'Seychelles',
        alpha2Code: 'SC',
        alpha3Code: 'SYC',
        numericCode: '690',
        callingCode: '+248'
    },
    {
        name: 'Sierra Leone',
        alpha2Code: 'SL',
        alpha3Code: 'SLE',
        numericCode: '694',
        callingCode: '+232'
    },
    {
        name: 'Singapore',
        alpha2Code: 'SG',
        alpha3Code: 'SGP',
        numericCode: '702',
        callingCode: '+65'
    },
    {
        name: 'Saint Martin (parte olandese)',
        alpha2Code: 'SX',
        alpha3Code: 'SXM',
        numericCode: '534',
        callingCode: '+1721'
    },
    {
        name: 'Slovacchia',
        alpha2Code: 'SK',
        alpha3Code: 'SVK',
        numericCode: '703',
        callingCode: '+421'
    },
    {
        name: 'Slovenia',
        alpha2Code: 'SI',
        alpha3Code: 'SVN',
        numericCode: '705',
        callingCode: '+386'
    },
    {
        name: 'Isole Salomone',
        alpha2Code: 'SB',
        alpha3Code: 'SLB',
        numericCode: '090',
        callingCode: '+677'
    },
    {
        name: 'Somalia',
        alpha2Code: 'SO',
        alpha3Code: 'SOM',
        numericCode: '706',
        callingCode: '+252'
    },
    {
        name: 'Sud Africa',
        alpha2Code: 'ZA',
        alpha3Code: 'ZAF',
        numericCode: '710',
        callingCode: '+27'
    },
    {
        name: 'Georgia del Sud e Isole Sandwich Meridionali',
        alpha2Code: 'GS',
        alpha3Code: 'SGS',
        numericCode: '239',
        callingCode: '+500'
    },
    {
        name: 'Corea del Sud',
        alpha2Code: 'KR',
        alpha3Code: 'KOR',
        numericCode: '410',
        callingCode: '+82'
    },
    {
        name: 'Sudan del sud',
        alpha2Code: 'SS',
        alpha3Code: 'SSD',
        numericCode: '728',
        callingCode: '+211'
    },
    {
        name: 'Spagna',
        alpha2Code: 'ES',
        alpha3Code: 'ESP',
        numericCode: '724',
        callingCode: '+34'
    },
    {
        name: 'Sri Lanka',
        alpha2Code: 'LK',
        alpha3Code: 'LKA',
        numericCode: '144',
        callingCode: '+94'
    },
    {
        name: 'Sudan',
        alpha2Code: 'SD',
        alpha3Code: 'SDN',
        numericCode: '729',
        callingCode: '+249'
    },
    {
        name: 'Suriname',
        alpha2Code: 'SR',
        alpha3Code: 'SUR',
        numericCode: '740',
        callingCode: '+597'
    },
    {
        name: 'Svalbard e Jan Mayen',
        alpha2Code: 'SJ',
        alpha3Code: 'SJM',
        numericCode: '744',
        callingCode: '+4779'
    },
    {
        name: 'Swaziland',
        alpha2Code: 'SZ',
        alpha3Code: 'SWZ',
        numericCode: '748',
        callingCode: '+268'
    },
    {
        name: 'Svezia',
        alpha2Code: 'SE',
        alpha3Code: 'SWE',
        numericCode: '752',
        callingCode: '+46'
    },
    {
        name: 'Svizzera',
        alpha2Code: 'CH',
        alpha3Code: 'CHE',
        numericCode: '756',
        callingCode: '+41'
    },
    {
        name: 'Siria',
        alpha2Code: 'SY',
        alpha3Code: 'SYR',
        numericCode: '760',
        callingCode: '+963'
    },
    {
        name: 'Taiwan, China',
        alpha2Code: 'TW',
        alpha3Code: 'TWN',
        numericCode: '158',
        callingCode: '+886'
    },
    {
        name: 'Tagikistan',
        alpha2Code: 'TJ',
        alpha3Code: 'TJK',
        numericCode: '762',
        callingCode: '+992'
    },
    {
        name: 'Tanzania',
        alpha2Code: 'TZ',
        alpha3Code: 'TZA',
        numericCode: '834',
        callingCode: '+255'
    },
    {
        name: 'Tailandia',
        alpha2Code: 'TH',
        alpha3Code: 'THA',
        numericCode: '764',
        callingCode: '+66'
    },
    {
        name: 'Timor Est',
        alpha2Code: 'TL',
        alpha3Code: 'TLS',
        numericCode: '626',
        callingCode: '+670'
    },
    {
        name: 'Togo',
        alpha2Code: 'TG',
        alpha3Code: 'TGO',
        numericCode: '768',
        callingCode: '+228'
    },
    {
        name: 'Isole Tokelau',
        alpha2Code: 'TK',
        alpha3Code: 'TKL',
        numericCode: '772',
        callingCode: '+690'
    },
    {
        name: 'Tonga',
        alpha2Code: 'TO',
        alpha3Code: 'TON',
        numericCode: '776',
        callingCode: '+676'
    },
    {
        name: 'Trinidad e Tobago',
        alpha2Code: 'TT',
        alpha3Code: 'TTO',
        numericCode: '780',
        callingCode: '+1868'
    },
    {
        name: 'Tunisia',
        alpha2Code: 'TN',
        alpha3Code: 'TUN',
        numericCode: '788',
        callingCode: '+216'
    },
    {
        name: 'Turchia',
        alpha2Code: 'TR',
        alpha3Code: 'TUR',
        numericCode: '792',
        callingCode: '+90'
    },
    {
        name: 'Turkmenistan',
        alpha2Code: 'TM',
        alpha3Code: 'TKM',
        numericCode: '795',
        callingCode: '+993'
    },
    {
        name: 'Isole Turks e Caicos',
        alpha2Code: 'TC',
        alpha3Code: 'TCA',
        numericCode: '796',
        callingCode: '+1649'
    },
    {
        name: 'Tuvalu',
        alpha2Code: 'TV',
        alpha3Code: 'TUV',
        numericCode: '798',
        callingCode: '+688'
    },
    {
        name: 'Uganda',
        alpha2Code: 'UG',
        alpha3Code: 'UGA',
        numericCode: '800',
        callingCode: '+256'
    },
    {
        name: 'Ucraina',
        alpha2Code: 'UA',
        alpha3Code: 'UKR',
        numericCode: '804',
        callingCode: '+380'
    },
    {
        name: 'Emirati Arabi Uniti',
        alpha2Code: 'AE',
        alpha3Code: 'ARE',
        numericCode: '784',
        callingCode: '+971'
    },
    {
        name: 'Regno Unito',
        alpha2Code: 'GB',
        alpha3Code: 'GBR',
        numericCode: '826',
        callingCode: '+44'
    },
    {
        name: "Stati Uniti D'America",
        alpha2Code: 'US',
        alpha3Code: 'USA',
        numericCode: '840',
        callingCode: '+1'
    },
    {
        name: 'Uruguay',
        alpha2Code: 'UY',
        alpha3Code: 'URY',
        numericCode: '858',
        callingCode: '+598'
    },
    {
        name: 'Uzbekistan',
        alpha2Code: 'UZ',
        alpha3Code: 'UZB',
        numericCode: '860',
        callingCode: '+998'
    },
    {
        name: 'Vanuatu',
        alpha2Code: 'VU',
        alpha3Code: 'VUT',
        numericCode: '548',
        callingCode: '+678'
    },
    {
        name: 'Venezuela',
        alpha2Code: 'VE',
        alpha3Code: 'VEN',
        numericCode: '862',
        callingCode: '+58'
    },
    {
        name: 'Vietnam',
        alpha2Code: 'VN',
        alpha3Code: 'VNM',
        numericCode: '704',
        callingCode: '+84'
    },
    {
        name: 'Wallis e Futuna',
        alpha2Code: 'WF',
        alpha3Code: 'WLF',
        numericCode: '876',
        callingCode: '+681'
    },
    {
        name: 'Sahara Occidentale',
        alpha2Code: 'EH',
        alpha3Code: 'ESH',
        numericCode: '732',
        callingCode: '+212'
    },
    {
        name: 'Yemen',
        alpha2Code: 'YE',
        alpha3Code: 'YEM',
        numericCode: '887',
        callingCode: '+967'
    },
    {
        name: 'Zambia',
        alpha2Code: 'ZM',
        alpha3Code: 'ZMB',
        numericCode: '894',
        callingCode: '+260'
    },
    {
        name: 'Zimbabwe',
        alpha2Code: 'ZW',
        alpha3Code: 'ZWE',
        numericCode: '716',
        callingCode: '+263'
    }
];

var it = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB_IT: COUNTRIES_DB_IT
});

const COUNTRIES_DB_GL = [
    {
        "name": "Afganistán",
        "alpha2Code": "AF",
        "alpha3Code": "AFG",
        "numericCode": "004",
        "callingCode": "+93"
    },
    {
        "name": "Alandia",
        "alpha2Code": "AX",
        "alpha3Code": "ALA",
        "numericCode": "248",
        "callingCode": "+358"
    },
    {
        "name": "Albania",
        "alpha2Code": "AL",
        "alpha3Code": "ALB",
        "numericCode": "008",
        "callingCode": "+355"
    },
    {
        "name": "Alxeria",
        "alpha2Code": "DZ",
        "alpha3Code": "DZA",
        "numericCode": "012",
        "callingCode": "+213"
    },
    {
        "name": "Samoa Americana",
        "alpha2Code": "AS",
        "alpha3Code": "ASM",
        "numericCode": "016",
        "callingCode": "+1684"
    },
    {
        "name": "Andorra",
        "alpha2Code": "AD",
        "alpha3Code": "AND",
        "numericCode": "020",
        "callingCode": "+376"
    },
    {
        "name": "Angola",
        "alpha2Code": "AO",
        "alpha3Code": "AGO",
        "numericCode": "024",
        "callingCode": "+244"
    },
    {
        "name": "Anguilla",
        "alpha2Code": "AI",
        "alpha3Code": "AIA",
        "numericCode": "660",
        "callingCode": "+1264"
    },
    {
        "name": "Antártida",
        "alpha2Code": "AQ",
        "alpha3Code": "ATA",
        "numericCode": "010",
        "callingCode": "+672"
    },
    {
        "name": "Antigua e Barbuda",
        "alpha2Code": "AG",
        "alpha3Code": "ATG",
        "numericCode": "028",
        "callingCode": "+1268"
    },
    {
        "name": "Arxentina",
        "alpha2Code": "AR",
        "alpha3Code": "ARG",
        "numericCode": "032",
        "callingCode": "+54"
    },
    {
        "name": "Armenia",
        "alpha2Code": "AM",
        "alpha3Code": "ARM",
        "numericCode": "051",
        "callingCode": "+374"
    },
    {
        "name": "Aruba",
        "alpha2Code": "AW",
        "alpha3Code": "ABW",
        "numericCode": "533",
        "callingCode": "+297"
    },
    {
        "name": "Australia",
        "alpha2Code": "AU",
        "alpha3Code": "AUS",
        "numericCode": "036",
        "callingCode": "+61"
    },
    {
        "name": "Austria",
        "alpha2Code": "AT",
        "alpha3Code": "AUT",
        "numericCode": "040",
        "callingCode": "+43"
    },
    {
        "name": "Acerbaixán",
        "alpha2Code": "AZ",
        "alpha3Code": "AZE",
        "numericCode": "031",
        "callingCode": "+994"
    },
    {
        "name": "Bahamas",
        "alpha2Code": "BS",
        "alpha3Code": "BHS",
        "numericCode": "044",
        "callingCode": "+1242"
    },
    {
        "name": "Bahrein",
        "alpha2Code": "BH",
        "alpha3Code": "BHR",
        "numericCode": "048",
        "callingCode": "+973"
    },
    {
        "name": "Bangladesh",
        "alpha2Code": "BD",
        "alpha3Code": "BGD",
        "numericCode": "050",
        "callingCode": "+880"
    },
    {
        "name": "Barbados",
        "alpha2Code": "BB",
        "alpha3Code": "BRB",
        "numericCode": "052",
        "callingCode": "+1246"
    },
    {
        "name": "Bielorrusia",
        "alpha2Code": "BY",
        "alpha3Code": "BLR",
        "numericCode": "112",
        "callingCode": "+375"
    },
    {
        "name": "Bélxica",
        "alpha2Code": "BE",
        "alpha3Code": "BEL",
        "numericCode": "056",
        "callingCode": "+32"
    },
    {
        "name": "Belice",
        "alpha2Code": "BZ",
        "alpha3Code": "BLZ",
        "numericCode": "084",
        "callingCode": "+501"
    },
    {
        "name": "Benín",
        "alpha2Code": "BJ",
        "alpha3Code": "BEN",
        "numericCode": "204",
        "callingCode": "+229"
    },
    {
        "name": "Bermudas",
        "alpha2Code": "BM",
        "alpha3Code": "BMU",
        "numericCode": "060",
        "callingCode": "+1441"
    },
    {
        "name": "Bután",
        "alpha2Code": "BT",
        "alpha3Code": "BTN",
        "numericCode": "064",
        "callingCode": "+975"
    },
    {
        "name": "Bolivia",
        "alpha2Code": "BO",
        "alpha3Code": "BOL",
        "numericCode": "068",
        "callingCode": "+591"
    },
    {
        "name": "Bonaire, Saint-Eustache e Saba",
        "alpha2Code": "BQ",
        "alpha3Code": "BES",
        "numericCode": "535",
        "callingCode": "+5997"
    },
    {
        "name": "Bosnia e Hercegovina",
        "alpha2Code": "BA",
        "alpha3Code": "BIH",
        "numericCode": "070",
        "callingCode": "+387"
    },
    {
        "name": "Botswana",
        "alpha2Code": "BW",
        "alpha3Code": "BWA",
        "numericCode": "072",
        "callingCode": "+267"
    },
    {
        "name": "Isla Bouvet",
        "alpha2Code": "BV",
        "alpha3Code": "BVT",
        "numericCode": "074",
        "callingCode": "+"
    },
    {
        "name": "Brasil",
        "alpha2Code": "BR",
        "alpha3Code": "BRA",
        "numericCode": "076",
        "callingCode": "+55"
    },
    {
        "name": "Territorio Británico do Océano Índico",
        "alpha2Code": "IO",
        "alpha3Code": "IOT",
        "numericCode": "086",
        "callingCode": "+246"
    },
    {
        "name": "Illas Ultramarinas Menores de Estados Unidos",
        "alpha2Code": "UM",
        "alpha3Code": "UMI",
        "numericCode": "581",
        "callingCode": "+"
    },
    {
        "name": "Illas Virxes do Reino Unido",
        "alpha2Code": "VG",
        "alpha3Code": "VGB",
        "numericCode": "092",
        "callingCode": "+1284"
    },
    {
        "name": "Illas Virxes dos Estados Unidos",
        "alpha2Code": "VI",
        "alpha3Code": "VIR",
        "numericCode": "850",
        "callingCode": "+1 340"
    },
    {
        "name": "Brunei",
        "alpha2Code": "BN",
        "alpha3Code": "BRN",
        "numericCode": "096",
        "callingCode": "+673"
    },
    {
        "name": "Bulgaria",
        "alpha2Code": "BG",
        "alpha3Code": "BGR",
        "numericCode": "100",
        "callingCode": "+359"
    },
    {
        "name": "Burkina Faso",
        "alpha2Code": "BF",
        "alpha3Code": "BFA",
        "numericCode": "854",
        "callingCode": "+226"
    },
    {
        "name": "Burundi",
        "alpha2Code": "BI",
        "alpha3Code": "BDI",
        "numericCode": "108",
        "callingCode": "+257"
    },
    {
        "name": "Cambodia",
        "alpha2Code": "KH",
        "alpha3Code": "KHM",
        "numericCode": "116",
        "callingCode": "+855"
    },
    {
        "name": "Camerún",
        "alpha2Code": "CM",
        "alpha3Code": "CMR",
        "numericCode": "120",
        "callingCode": "+237"
    },
    {
        "name": "Canadá",
        "alpha2Code": "CA",
        "alpha3Code": "CAN",
        "numericCode": "124",
        "callingCode": "+1"
    },
    {
        "name": "Cabo Verde",
        "alpha2Code": "CV",
        "alpha3Code": "CPV",
        "numericCode": "132",
        "callingCode": "+238"
    },
    {
        "name": "Illas Caimán",
        "alpha2Code": "KY",
        "alpha3Code": "CYM",
        "numericCode": "136",
        "callingCode": "+1345"
    },
    {
        "name": "República Centroafricana",
        "alpha2Code": "CF",
        "alpha3Code": "CAF",
        "numericCode": "140",
        "callingCode": "+236"
    },
    {
        "name": "Chad",
        "alpha2Code": "TD",
        "alpha3Code": "TCD",
        "numericCode": "148",
        "callingCode": "+235"
    },
    {
        "name": "Chile",
        "alpha2Code": "CL",
        "alpha3Code": "CHL",
        "numericCode": "152",
        "callingCode": "+56"
    },
    {
        "name": "China",
        "alpha2Code": "CN",
        "alpha3Code": "CHN",
        "numericCode": "156",
        "callingCode": "+86"
    },
    {
        "name": "Isla de Nadal",
        "alpha2Code": "CX",
        "alpha3Code": "CXR",
        "numericCode": "162",
        "callingCode": "+61"
    },
    {
        "name": "Illas Cocos ou Illas Keeling",
        "alpha2Code": "CC",
        "alpha3Code": "CCK",
        "numericCode": "166",
        "callingCode": "+61"
    },
    {
        "name": "Colombia",
        "alpha2Code": "CO",
        "alpha3Code": "COL",
        "numericCode": "170",
        "callingCode": "+57"
    },
    {
        "name": "Comoras",
        "alpha2Code": "KM",
        "alpha3Code": "COM",
        "numericCode": "174",
        "callingCode": "+269"
    },
    {
        "name": "Congo",
        "alpha2Code": "CG",
        "alpha3Code": "COG",
        "numericCode": "178",
        "callingCode": "+242"
    },
    {
        "name": "Congo (Rep. Dem.)",
        "alpha2Code": "CD",
        "alpha3Code": "COD",
        "numericCode": "180",
        "callingCode": "+243"
    },
    {
        "name": "Illas Cook",
        "alpha2Code": "CK",
        "alpha3Code": "COK",
        "numericCode": "184",
        "callingCode": "+682"
    },
    {
        "name": "Costa Rica",
        "alpha2Code": "CR",
        "alpha3Code": "CRI",
        "numericCode": "188",
        "callingCode": "+506"
    },
    {
        "name": "Croacia",
        "alpha2Code": "HR",
        "alpha3Code": "HRV",
        "numericCode": "191",
        "callingCode": "+385"
    },
    {
        "name": "Cuba",
        "alpha2Code": "CU",
        "alpha3Code": "CUB",
        "numericCode": "192",
        "callingCode": "+53"
    },
    {
        "name": "Curaçao",
        "alpha2Code": "CW",
        "alpha3Code": "CUW",
        "numericCode": "531",
        "callingCode": "+599"
    },
    {
        "name": "Chipre",
        "alpha2Code": "CY",
        "alpha3Code": "CYP",
        "numericCode": "196",
        "callingCode": "+357"
    },
    {
        "name": "República Checa",
        "alpha2Code": "CZ",
        "alpha3Code": "CZE",
        "numericCode": "203",
        "callingCode": "+420"
    },
    {
        "name": "Dinamarca",
        "alpha2Code": "DK",
        "alpha3Code": "DNK",
        "numericCode": "208",
        "callingCode": "+45"
    },
    {
        "name": "Xibutí",
        "alpha2Code": "DJ",
        "alpha3Code": "DJI",
        "numericCode": "262",
        "callingCode": "+253"
    },
    {
        "name": "Dominica",
        "alpha2Code": "DM",
        "alpha3Code": "DMA",
        "numericCode": "212",
        "callingCode": "+1767"
    },
    {
        "name": "República Dominicana",
        "alpha2Code": "DO",
        "alpha3Code": "DOM",
        "numericCode": "214",
        "callingCode": "+1809"
    },
    {
        "name": "Ecuador",
        "alpha2Code": "EC",
        "alpha3Code": "ECU",
        "numericCode": "218",
        "callingCode": "+593"
    },
    {
        "name": "Exipto",
        "alpha2Code": "EG",
        "alpha3Code": "EGY",
        "numericCode": "818",
        "callingCode": "+20"
    },
    {
        "name": "O Salvador",
        "alpha2Code": "SV",
        "alpha3Code": "SLV",
        "numericCode": "222",
        "callingCode": "+503"
    },
    {
        "name": "Guinea Ecuatorial",
        "alpha2Code": "GQ",
        "alpha3Code": "GNQ",
        "numericCode": "226",
        "callingCode": "+240"
    },
    {
        "name": "Eritrea",
        "alpha2Code": "ER",
        "alpha3Code": "ERI",
        "numericCode": "232",
        "callingCode": "+291"
    },
    {
        "name": "Estonia",
        "alpha2Code": "EE",
        "alpha3Code": "EST",
        "numericCode": "233",
        "callingCode": "+372"
    },
    {
        "name": "Etiopía",
        "alpha2Code": "ET",
        "alpha3Code": "ETH",
        "numericCode": "231",
        "callingCode": "+251"
    },
    {
        "name": "Illas Malvinas",
        "alpha2Code": "FK",
        "alpha3Code": "FLK",
        "numericCode": "238",
        "callingCode": "+500"
    },
    {
        "name": "Illas Faroe",
        "alpha2Code": "FO",
        "alpha3Code": "FRO",
        "numericCode": "234",
        "callingCode": "+298"
    },
    {
        "name": "Fiyi",
        "alpha2Code": "FJ",
        "alpha3Code": "FJI",
        "numericCode": "242",
        "callingCode": "+679"
    },
    {
        "name": "Finlandia",
        "alpha2Code": "FI",
        "alpha3Code": "FIN",
        "numericCode": "246",
        "callingCode": "+358"
    },
    {
        "name": "Francia",
        "alpha2Code": "FR",
        "alpha3Code": "FRA",
        "numericCode": "250",
        "callingCode": "+33"
    },
    {
        "name": "Guayana Francesa",
        "alpha2Code": "GF",
        "alpha3Code": "GUF",
        "numericCode": "254",
        "callingCode": "+594"
    },
    {
        "name": "Polinesia Francesa",
        "alpha2Code": "PF",
        "alpha3Code": "PYF",
        "numericCode": "258",
        "callingCode": "+689"
    },
    {
        "name": "Terras Austrais e Antárticas Francesas",
        "alpha2Code": "TF",
        "alpha3Code": "ATF",
        "numericCode": "260",
        "callingCode": "+"
    },
    {
        "name": "Gabón",
        "alpha2Code": "GA",
        "alpha3Code": "GAB",
        "numericCode": "266",
        "callingCode": "+241"
    },
    {
        "name": "Gambia",
        "alpha2Code": "GM",
        "alpha3Code": "GMB",
        "numericCode": "270",
        "callingCode": "+220"
    },
    {
        "name": "Xeorxia",
        "alpha2Code": "GE",
        "alpha3Code": "GEO",
        "numericCode": "268",
        "callingCode": "+995"
    },
    {
        "name": "Alemaña",
        "alpha2Code": "DE",
        "alpha3Code": "DEU",
        "numericCode": "276",
        "callingCode": "+49"
    },
    {
        "name": "Ghana",
        "alpha2Code": "GH",
        "alpha3Code": "GHA",
        "numericCode": "288",
        "callingCode": "+233"
    },
    {
        "name": "Xibraltar",
        "alpha2Code": "GI",
        "alpha3Code": "GIB",
        "numericCode": "292",
        "callingCode": "+350"
    },
    {
        "name": "Grecia",
        "alpha2Code": "GR",
        "alpha3Code": "GRC",
        "numericCode": "300",
        "callingCode": "+30"
    },
    {
        "name": "Groenlandia",
        "alpha2Code": "GL",
        "alpha3Code": "GRL",
        "numericCode": "304",
        "callingCode": "+299"
    },
    {
        "name": "Grenada",
        "alpha2Code": "GD",
        "alpha3Code": "GRD",
        "numericCode": "308",
        "callingCode": "+1473"
    },
    {
        "name": "Guadalupe",
        "alpha2Code": "GP",
        "alpha3Code": "GLP",
        "numericCode": "312",
        "callingCode": "+590"
    },
    {
        "name": "Guam",
        "alpha2Code": "GU",
        "alpha3Code": "GUM",
        "numericCode": "316",
        "callingCode": "+1671"
    },
    {
        "name": "Guatemala",
        "alpha2Code": "GT",
        "alpha3Code": "GTM",
        "numericCode": "320",
        "callingCode": "+502"
    },
    {
        "name": "Guernsey",
        "alpha2Code": "GG",
        "alpha3Code": "GGY",
        "numericCode": "831",
        "callingCode": "+44"
    },
    {
        "name": "Guinea",
        "alpha2Code": "GN",
        "alpha3Code": "GIN",
        "numericCode": "324",
        "callingCode": "+224"
    },
    {
        "name": "Guinea-Bisáu",
        "alpha2Code": "GW",
        "alpha3Code": "GNB",
        "numericCode": "624",
        "callingCode": "+245"
    },
    {
        "name": "Güiana",
        "alpha2Code": "GY",
        "alpha3Code": "GUY",
        "numericCode": "328",
        "callingCode": "+592"
    },
    {
        "name": "Haiti",
        "alpha2Code": "HT",
        "alpha3Code": "HTI",
        "numericCode": "332",
        "callingCode": "+509"
    },
    {
        "name": "Illas Heard e McDonald",
        "alpha2Code": "HM",
        "alpha3Code": "HMD",
        "numericCode": "334",
        "callingCode": "+"
    },
    {
        "name": "Santa Sé",
        "alpha2Code": "VA",
        "alpha3Code": "VAT",
        "numericCode": "336",
        "callingCode": "+379"
    },
    {
        "name": "Honduras",
        "alpha2Code": "HN",
        "alpha3Code": "HND",
        "numericCode": "340",
        "callingCode": "+504"
    },
    {
        "name": "Hong Kong",
        "alpha2Code": "HK",
        "alpha3Code": "HKG",
        "numericCode": "344",
        "callingCode": "+852"
    },
    {
        "name": "Hungría",
        "alpha2Code": "HU",
        "alpha3Code": "HUN",
        "numericCode": "348",
        "callingCode": "+36"
    },
    {
        "name": "Islandia",
        "alpha2Code": "IS",
        "alpha3Code": "ISL",
        "numericCode": "352",
        "callingCode": "+354"
    },
    {
        "name": "India",
        "alpha2Code": "IN",
        "alpha3Code": "IND",
        "numericCode": "356",
        "callingCode": "+91"
    },
    {
        "name": "Indonesia",
        "alpha2Code": "ID",
        "alpha3Code": "IDN",
        "numericCode": "360",
        "callingCode": "+62"
    },
    {
        "name": "Costa do Marfil",
        "alpha2Code": "CI",
        "alpha3Code": "CIV",
        "numericCode": "384",
        "callingCode": "+225"
    },
    {
        "name": "Iran",
        "alpha2Code": "IR",
        "alpha3Code": "IRN",
        "numericCode": "364",
        "callingCode": "+98"
    },
    {
        "name": "Iraq",
        "alpha2Code": "IQ",
        "alpha3Code": "IRQ",
        "numericCode": "368",
        "callingCode": "+964"
    },
    {
        "name": "Irlanda",
        "alpha2Code": "IE",
        "alpha3Code": "IRL",
        "numericCode": "372",
        "callingCode": "+353"
    },
    {
        "name": "Isla de Man",
        "alpha2Code": "IM",
        "alpha3Code": "IMN",
        "numericCode": "833",
        "callingCode": "+44"
    },
    {
        "name": "Israel",
        "alpha2Code": "IL",
        "alpha3Code": "ISR",
        "numericCode": "376",
        "callingCode": "+972"
    },
    {
        "name": "Italia",
        "alpha2Code": "IT",
        "alpha3Code": "ITA",
        "numericCode": "380",
        "callingCode": "+39"
    },
    {
        "name": "Xamaica",
        "alpha2Code": "JM",
        "alpha3Code": "JAM",
        "numericCode": "388",
        "callingCode": "+1876"
    },
    {
        "name": "Xapón",
        "alpha2Code": "JP",
        "alpha3Code": "JPN",
        "numericCode": "392",
        "callingCode": "+81"
    },
    {
        "name": "Xersei",
        "alpha2Code": "JE",
        "alpha3Code": "JEY",
        "numericCode": "832",
        "callingCode": "+44"
    },
    {
        "name": "Xordania",
        "alpha2Code": "JO",
        "alpha3Code": "JOR",
        "numericCode": "400",
        "callingCode": "+962"
    },
    {
        "name": "Casaquistán",
        "alpha2Code": "KZ",
        "alpha3Code": "KAZ",
        "numericCode": "398",
        "callingCode": "+7"
    },
    {
        "name": "Quenia",
        "alpha2Code": "KE",
        "alpha3Code": "KEN",
        "numericCode": "404",
        "callingCode": "+254"
    },
    {
        "name": "Kiribati",
        "alpha2Code": "KI",
        "alpha3Code": "KIR",
        "numericCode": "296",
        "callingCode": "+686"
    },
    {
        "name": "Kuwait",
        "alpha2Code": "KW",
        "alpha3Code": "KWT",
        "numericCode": "414",
        "callingCode": "+965"
    },
    {
        "name": "Kirguizistán",
        "alpha2Code": "KG",
        "alpha3Code": "KGZ",
        "numericCode": "417",
        "callingCode": "+996"
    },
    {
        "name": "Laos",
        "alpha2Code": "LA",
        "alpha3Code": "LAO",
        "numericCode": "418",
        "callingCode": "+856"
    },
    {
        "name": "Letonia",
        "alpha2Code": "LV",
        "alpha3Code": "LVA",
        "numericCode": "428",
        "callingCode": "+371"
    },
    {
        "name": "Líbano",
        "alpha2Code": "LB",
        "alpha3Code": "LBN",
        "numericCode": "422",
        "callingCode": "+961"
    },
    {
        "name": "Lesotho",
        "alpha2Code": "LS",
        "alpha3Code": "LSO",
        "numericCode": "426",
        "callingCode": "+266"
    },
    {
        "name": "Liberia",
        "alpha2Code": "LR",
        "alpha3Code": "LBR",
        "numericCode": "430",
        "callingCode": "+231"
    },
    {
        "name": "Libia",
        "alpha2Code": "LY",
        "alpha3Code": "LBY",
        "numericCode": "434",
        "callingCode": "+218"
    },
    {
        "name": "Liechtenstein",
        "alpha2Code": "LI",
        "alpha3Code": "LIE",
        "numericCode": "438",
        "callingCode": "+423"
    },
    {
        "name": "Lituania",
        "alpha2Code": "LT",
        "alpha3Code": "LTU",
        "numericCode": "440",
        "callingCode": "+370"
    },
    {
        "name": "Luxemburgo",
        "alpha2Code": "LU",
        "alpha3Code": "LUX",
        "numericCode": "442",
        "callingCode": "+352"
    },
    {
        "name": "Macao",
        "alpha2Code": "MO",
        "alpha3Code": "MAC",
        "numericCode": "446",
        "callingCode": "+853"
    },
    {
        "name": "Macedonia",
        "alpha2Code": "MK",
        "alpha3Code": "MKD",
        "numericCode": "807",
        "callingCode": "+389"
    },
    {
        "name": "Madagascar",
        "alpha2Code": "MG",
        "alpha3Code": "MDG",
        "numericCode": "450",
        "callingCode": "+261"
    },
    {
        "name": "Malawi",
        "alpha2Code": "MW",
        "alpha3Code": "MWI",
        "numericCode": "454",
        "callingCode": "+265"
    },
    {
        "name": "Malaisia",
        "alpha2Code": "MY",
        "alpha3Code": "MYS",
        "numericCode": "458",
        "callingCode": "+60"
    },
    {
        "name": "Maldivas",
        "alpha2Code": "MV",
        "alpha3Code": "MDV",
        "numericCode": "462",
        "callingCode": "+960"
    },
    {
        "name": "Mali",
        "alpha2Code": "ML",
        "alpha3Code": "MLI",
        "numericCode": "466",
        "callingCode": "+223"
    },
    {
        "name": "Malta",
        "alpha2Code": "MT",
        "alpha3Code": "MLT",
        "numericCode": "470",
        "callingCode": "+356"
    },
    {
        "name": "Illas Marshall",
        "alpha2Code": "MH",
        "alpha3Code": "MHL",
        "numericCode": "584",
        "callingCode": "+692"
    },
    {
        "name": "Martinica",
        "alpha2Code": "MQ",
        "alpha3Code": "MTQ",
        "numericCode": "474",
        "callingCode": "+596"
    },
    {
        "name": "Mauritania",
        "alpha2Code": "MR",
        "alpha3Code": "MRT",
        "numericCode": "478",
        "callingCode": "+222"
    },
    {
        "name": "Mauricio",
        "alpha2Code": "MU",
        "alpha3Code": "MUS",
        "numericCode": "480",
        "callingCode": "+230"
    },
    {
        "name": "Mayotte",
        "alpha2Code": "YT",
        "alpha3Code": "MYT",
        "numericCode": "175",
        "callingCode": "+262"
    },
    {
        "name": "México",
        "alpha2Code": "MX",
        "alpha3Code": "MEX",
        "numericCode": "484",
        "callingCode": "+52"
    },
    {
        "name": "Micronesia",
        "alpha2Code": "FM",
        "alpha3Code": "FSM",
        "numericCode": "583",
        "callingCode": "+691"
    },
    {
        "name": "Moldavia",
        "alpha2Code": "MD",
        "alpha3Code": "MDA",
        "numericCode": "498",
        "callingCode": "+373"
    },
    {
        "name": "Mónaco",
        "alpha2Code": "MC",
        "alpha3Code": "MCO",
        "numericCode": "492",
        "callingCode": "+377"
    },
    {
        "name": "Mongolia",
        "alpha2Code": "MN",
        "alpha3Code": "MNG",
        "numericCode": "496",
        "callingCode": "+976"
    },
    {
        "name": "Montenegro",
        "alpha2Code": "ME",
        "alpha3Code": "MNE",
        "numericCode": "499",
        "callingCode": "+382"
    },
    {
        "name": "Montserrat",
        "alpha2Code": "MS",
        "alpha3Code": "MSR",
        "numericCode": "500",
        "callingCode": "+1664"
    },
    {
        "name": "Marrocos",
        "alpha2Code": "MA",
        "alpha3Code": "MAR",
        "numericCode": "504",
        "callingCode": "+212"
    },
    {
        "name": "Mozambique",
        "alpha2Code": "MZ",
        "alpha3Code": "MOZ",
        "numericCode": "508",
        "callingCode": "+258"
    },
    {
        "name": "Myanmar",
        "alpha2Code": "MM",
        "alpha3Code": "MMR",
        "numericCode": "104",
        "callingCode": "+95"
    },
    {
        "name": "Namibia",
        "alpha2Code": "NA",
        "alpha3Code": "NAM",
        "numericCode": "516",
        "callingCode": "+264"
    },
    {
        "name": "Nauru",
        "alpha2Code": "NR",
        "alpha3Code": "NRU",
        "numericCode": "520",
        "callingCode": "+674"
    },
    {
        "name": "Nepal",
        "alpha2Code": "NP",
        "alpha3Code": "NPL",
        "numericCode": "524",
        "callingCode": "+977"
    },
    {
        "name": "Países Baixos",
        "alpha2Code": "NL",
        "alpha3Code": "NLD",
        "numericCode": "528",
        "callingCode": "+31"
    },
    {
        "name": "Nova Caledonia",
        "alpha2Code": "NC",
        "alpha3Code": "NCL",
        "numericCode": "540",
        "callingCode": "+687"
    },
    {
        "name": "Nova Zelandia",
        "alpha2Code": "NZ",
        "alpha3Code": "NZL",
        "numericCode": "554",
        "callingCode": "+64"
    },
    {
        "name": "Nicaragua",
        "alpha2Code": "NI",
        "alpha3Code": "NIC",
        "numericCode": "558",
        "callingCode": "+505"
    },
    {
        "name": "Níxer",
        "alpha2Code": "NE",
        "alpha3Code": "NER",
        "numericCode": "562",
        "callingCode": "+227"
    },
    {
        "name": "Nixeria",
        "alpha2Code": "NG",
        "alpha3Code": "NGA",
        "numericCode": "566",
        "callingCode": "+234"
    },
    {
        "name": "Niue",
        "alpha2Code": "NU",
        "alpha3Code": "NIU",
        "numericCode": "570",
        "callingCode": "+683"
    },
    {
        "name": "Isla de Norfolk",
        "alpha2Code": "NF",
        "alpha3Code": "NFK",
        "numericCode": "574",
        "callingCode": "+672"
    },
    {
        "name": "Corea do Norte",
        "alpha2Code": "KP",
        "alpha3Code": "PRK",
        "numericCode": "408",
        "callingCode": "+850"
    },
    {
        "name": "Illas Marianas do Norte",
        "alpha2Code": "MP",
        "alpha3Code": "MNP",
        "numericCode": "580",
        "callingCode": "+1670"
    },
    {
        "name": "Noruega",
        "alpha2Code": "NO",
        "alpha3Code": "NOR",
        "numericCode": "578",
        "callingCode": "+47"
    },
    {
        "name": "Omán",
        "alpha2Code": "OM",
        "alpha3Code": "OMN",
        "numericCode": "512",
        "callingCode": "+968"
    },
    {
        "name": "Paquistán",
        "alpha2Code": "PK",
        "alpha3Code": "PAK",
        "numericCode": "586",
        "callingCode": "+92"
    },
    {
        "name": "Palau",
        "alpha2Code": "PW",
        "alpha3Code": "PLW",
        "numericCode": "585",
        "callingCode": "+680"
    },
    {
        "name": "Palestina",
        "alpha2Code": "PS",
        "alpha3Code": "PSE",
        "numericCode": "275",
        "callingCode": "+970"
    },
    {
        "name": "Panamá",
        "alpha2Code": "PA",
        "alpha3Code": "PAN",
        "numericCode": "591",
        "callingCode": "+507"
    },
    {
        "name": "Papúa Nova Guinea",
        "alpha2Code": "PG",
        "alpha3Code": "PNG",
        "numericCode": "598",
        "callingCode": "+675"
    },
    {
        "name": "Paraguai",
        "alpha2Code": "PY",
        "alpha3Code": "PRY",
        "numericCode": "600",
        "callingCode": "+595"
    },
    {
        "name": "Perú",
        "alpha2Code": "PE",
        "alpha3Code": "PER",
        "numericCode": "604",
        "callingCode": "+51"
    },
    {
        "name": "Filipinas",
        "alpha2Code": "PH",
        "alpha3Code": "PHL",
        "numericCode": "608",
        "callingCode": "+63"
    },
    {
        "name": "Illas Pitcairn",
        "alpha2Code": "PN",
        "alpha3Code": "PCN",
        "numericCode": "612",
        "callingCode": "+64"
    },
    {
        "name": "Polonia",
        "alpha2Code": "PL",
        "alpha3Code": "POL",
        "numericCode": "616",
        "callingCode": "+48"
    },
    {
        "name": "Portugal",
        "alpha2Code": "PT",
        "alpha3Code": "PRT",
        "numericCode": "620",
        "callingCode": "+351"
    },
    {
        "name": "Porto Rico",
        "alpha2Code": "PR",
        "alpha3Code": "PRI",
        "numericCode": "630",
        "callingCode": "+1"
    },
    {
        "name": "Catar",
        "alpha2Code": "QA",
        "alpha3Code": "QAT",
        "numericCode": "634",
        "callingCode": "+974"
    },
    {
        "name": "Kósovo",
        "alpha2Code": "XK",
        "alpha3Code": "KOS",
        "numericCode": null,
        "callingCode": "+383"
    },
    {
        "name": "Reunión",
        "alpha2Code": "RE",
        "alpha3Code": "REU",
        "numericCode": "638",
        "callingCode": "+262"
    },
    {
        "name": "Rumania",
        "alpha2Code": "RO",
        "alpha3Code": "ROU",
        "numericCode": "642",
        "callingCode": "+40"
    },
    {
        "name": "Rusia",
        "alpha2Code": "RU",
        "alpha3Code": "RUS",
        "numericCode": "643",
        "callingCode": "+7"
    },
    {
        "name": "Ruanda",
        "alpha2Code": "RW",
        "alpha3Code": "RWA",
        "numericCode": "646",
        "callingCode": "+250"
    },
    {
        "name": "San Bartolomé",
        "alpha2Code": "BL",
        "alpha3Code": "BLM",
        "numericCode": "652",
        "callingCode": "+590"
    },
    {
        "name": "Santa Helena",
        "alpha2Code": "SH",
        "alpha3Code": "SHN",
        "numericCode": "654",
        "callingCode": "+290"
    },
    {
        "name": "San Cristóbal e Nieves",
        "alpha2Code": "KN",
        "alpha3Code": "KNA",
        "numericCode": "659",
        "callingCode": "+1869"
    },
    {
        "name": "Santa Lucía",
        "alpha2Code": "LC",
        "alpha3Code": "LCA",
        "numericCode": "662",
        "callingCode": "+1758"
    },
    {
        "name": "Saint Martin",
        "alpha2Code": "MF",
        "alpha3Code": "MAF",
        "numericCode": "663",
        "callingCode": "+590"
    },
    {
        "name": "San Pedro e Miquelón",
        "alpha2Code": "PM",
        "alpha3Code": "SPM",
        "numericCode": "666",
        "callingCode": "+508"
    },
    {
        "name": "San Vicente e Granadinas",
        "alpha2Code": "VC",
        "alpha3Code": "VCT",
        "numericCode": "670",
        "callingCode": "+1784"
    },
    {
        "name": "Samoa",
        "alpha2Code": "WS",
        "alpha3Code": "WSM",
        "numericCode": "882",
        "callingCode": "+685"
    },
    {
        "name": "San Mariño",
        "alpha2Code": "SM",
        "alpha3Code": "SMR",
        "numericCode": "674",
        "callingCode": "+378"
    },
    {
        "name": "San Tomé e Príncipe",
        "alpha2Code": "ST",
        "alpha3Code": "STP",
        "numericCode": "678",
        "callingCode": "+239"
    },
    {
        "name": "Arabia Saudita",
        "alpha2Code": "SA",
        "alpha3Code": "SAU",
        "numericCode": "682",
        "callingCode": "+966"
    },
    {
        "name": "Senegal",
        "alpha2Code": "SN",
        "alpha3Code": "SEN",
        "numericCode": "686",
        "callingCode": "+221"
    },
    {
        "name": "Serbia",
        "alpha2Code": "RS",
        "alpha3Code": "SRB",
        "numericCode": "688",
        "callingCode": "+381"
    },
    {
        "name": "Seychelles",
        "alpha2Code": "SC",
        "alpha3Code": "SYC",
        "numericCode": "690",
        "callingCode": "+248"
    },
    {
        "name": "Sierra Leone",
        "alpha2Code": "SL",
        "alpha3Code": "SLE",
        "numericCode": "694",
        "callingCode": "+232"
    },
    {
        "name": "Singapura",
        "alpha2Code": "SG",
        "alpha3Code": "SGP",
        "numericCode": "702",
        "callingCode": "+65"
    },
    {
        "name": "San Martín (parte holandesa)",
        "alpha2Code": "SX",
        "alpha3Code": "SXM",
        "numericCode": "534",
        "callingCode": "+1721"
    },
    {
        "name": "República Eslovaca",
        "alpha2Code": "SK",
        "alpha3Code": "SVK",
        "numericCode": "703",
        "callingCode": "+421"
    },
    {
        "name": "Eslovenia",
        "alpha2Code": "SI",
        "alpha3Code": "SVN",
        "numericCode": "705",
        "callingCode": "+386"
    },
    {
        "name": "Illas Salomón",
        "alpha2Code": "SB",
        "alpha3Code": "SLB",
        "numericCode": "090",
        "callingCode": "+677"
    },
    {
        "name": "Somalia",
        "alpha2Code": "SO",
        "alpha3Code": "SOM",
        "numericCode": "706",
        "callingCode": "+252"
    },
    {
        "name": "República de Sudáfrica",
        "alpha2Code": "ZA",
        "alpha3Code": "ZAF",
        "numericCode": "710",
        "callingCode": "+27"
    },
    {
        "name": "Illas Georgias do Sur e Sandwich do Sur",
        "alpha2Code": "GS",
        "alpha3Code": "SGS",
        "numericCode": "239",
        "callingCode": "+500"
    },
    {
        "name": "Corea do Sur",
        "alpha2Code": "KR",
        "alpha3Code": "KOR",
        "numericCode": "410",
        "callingCode": "+82"
    },
    {
        "name": "Sudán do Sur",
        "alpha2Code": "SS",
        "alpha3Code": "SSD",
        "numericCode": "728",
        "callingCode": "+211"
    },
    {
        "name": "España",
        "alpha2Code": "ES",
        "alpha3Code": "ESP",
        "numericCode": "724",
        "callingCode": "+34"
    },
    {
        "name": "Sri Lanka",
        "alpha2Code": "LK",
        "alpha3Code": "LKA",
        "numericCode": "144",
        "callingCode": "+94"
    },
    {
        "name": "Sudán",
        "alpha2Code": "SD",
        "alpha3Code": "SDN",
        "numericCode": "729",
        "callingCode": "+249"
    },
    {
        "name": "Suriname",
        "alpha2Code": "SR",
        "alpha3Code": "SUR",
        "numericCode": "740",
        "callingCode": "+597"
    },
    {
        "name": "Illas Svalbard e Jan Mayen",
        "alpha2Code": "SJ",
        "alpha3Code": "SJM",
        "numericCode": "744",
        "callingCode": "+4779"
    },
    {
        "name": "Suazilandia",
        "alpha2Code": "SZ",
        "alpha3Code": "SWZ",
        "numericCode": "748",
        "callingCode": "+268"
    },
    {
        "name": "Suecia",
        "alpha2Code": "SE",
        "alpha3Code": "SWE",
        "numericCode": "752",
        "callingCode": "+46"
    },
    {
        "name": "Suíza",
        "alpha2Code": "CH",
        "alpha3Code": "CHE",
        "numericCode": "756",
        "callingCode": "+41"
    },
    {
        "name": "Siria",
        "alpha2Code": "SY",
        "alpha3Code": "SYR",
        "numericCode": "760",
        "callingCode": "+963"
    },
    {
        "name": "Taiwan",
        "alpha2Code": "TW",
        "alpha3Code": "TWN",
        "numericCode": "158",
        "callingCode": "+886"
    },
    {
        "name": "Tadxiquistán",
        "alpha2Code": "TJ",
        "alpha3Code": "TJK",
        "numericCode": "762",
        "callingCode": "+992"
    },
    {
        "name": "Tanzania",
        "alpha2Code": "TZ",
        "alpha3Code": "TZA",
        "numericCode": "834",
        "callingCode": "+255"
    },
    {
        "name": "Tailandia",
        "alpha2Code": "TH",
        "alpha3Code": "THA",
        "numericCode": "764",
        "callingCode": "+66"
    },
    {
        "name": "Timor Oriental",
        "alpha2Code": "TL",
        "alpha3Code": "TLS",
        "numericCode": "626",
        "callingCode": "+670"
    },
    {
        "name": "Togo",
        "alpha2Code": "TG",
        "alpha3Code": "TGO",
        "numericCode": "768",
        "callingCode": "+228"
    },
    {
        "name": "Illas Tokelau",
        "alpha2Code": "TK",
        "alpha3Code": "TKL",
        "numericCode": "772",
        "callingCode": "+690"
    },
    {
        "name": "Tonga",
        "alpha2Code": "TO",
        "alpha3Code": "TON",
        "numericCode": "776",
        "callingCode": "+676"
    },
    {
        "name": "Trinidade e Tobago",
        "alpha2Code": "TT",
        "alpha3Code": "TTO",
        "numericCode": "780",
        "callingCode": "+1868"
    },
    {
        "name": "Tunes",
        "alpha2Code": "TN",
        "alpha3Code": "TUN",
        "numericCode": "788",
        "callingCode": "+216"
    },
    {
        "name": "Turquía",
        "alpha2Code": "TR",
        "alpha3Code": "TUR",
        "numericCode": "792",
        "callingCode": "+90"
    },
    {
        "name": "Turcomenistán",
        "alpha2Code": "TM",
        "alpha3Code": "TKM",
        "numericCode": "795",
        "callingCode": "+993"
    },
    {
        "name": "Illas Turks e Caicos",
        "alpha2Code": "TC",
        "alpha3Code": "TCA",
        "numericCode": "796",
        "callingCode": "+1649"
    },
    {
        "name": "Tuvalu",
        "alpha2Code": "TV",
        "alpha3Code": "TUV",
        "numericCode": "798",
        "callingCode": "+688"
    },
    {
        "name": "Uganda",
        "alpha2Code": "UG",
        "alpha3Code": "UGA",
        "numericCode": "800",
        "callingCode": "+256"
    },
    {
        "name": "Ucraína",
        "alpha2Code": "UA",
        "alpha3Code": "UKR",
        "numericCode": "804",
        "callingCode": "+380"
    },
    {
        "name": "Emiratos Árabes Unidos",
        "alpha2Code": "AE",
        "alpha3Code": "ARE",
        "numericCode": "784",
        "callingCode": "+971"
    },
    {
        "name": "Reino Unido",
        "alpha2Code": "GB",
        "alpha3Code": "GBR",
        "numericCode": "826",
        "callingCode": "+44"
    },
    {
        "name": "Estados Unidos",
        "alpha2Code": "US",
        "alpha3Code": "USA",
        "numericCode": "840",
        "callingCode": "+1"
    },
    {
        "name": "Uruguai",
        "alpha2Code": "UY",
        "alpha3Code": "URY",
        "numericCode": "858",
        "callingCode": "+598"
    },
    {
        "name": "Usbequistán",
        "alpha2Code": "UZ",
        "alpha3Code": "UZB",
        "numericCode": "860",
        "callingCode": "+998"
    },
    {
        "name": "Vanuatu",
        "alpha2Code": "VU",
        "alpha3Code": "VUT",
        "numericCode": "548",
        "callingCode": "+678"
    },
    {
        "name": "Venezuela",
        "alpha2Code": "VE",
        "alpha3Code": "VEN",
        "numericCode": "862",
        "callingCode": "+58"
    },
    {
        "name": "Vietnam",
        "alpha2Code": "VN",
        "alpha3Code": "VNM",
        "numericCode": "704",
        "callingCode": "+84"
    },
    {
        "name": "Wallis e Futuna",
        "alpha2Code": "WF",
        "alpha3Code": "WLF",
        "numericCode": "876",
        "callingCode": "+681"
    },
    {
        "name": "Sahara Occidental",
        "alpha2Code": "EH",
        "alpha3Code": "ESH",
        "numericCode": "732",
        "callingCode": "+212"
    },
    {
        "name": "Iemen",
        "alpha2Code": "YE",
        "alpha3Code": "YEM",
        "numericCode": "887",
        "callingCode": "+967"
    },
    {
        "name": "Zambia",
        "alpha2Code": "ZM",
        "alpha3Code": "ZMB",
        "numericCode": "894",
        "callingCode": "+260"
    },
    {
        "name": "Zimbabue",
        "alpha2Code": "ZW",
        "alpha3Code": "ZWE",
        "numericCode": "716",
        "callingCode": "+263"
    }
];

var gl = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB_GL: COUNTRIES_DB_GL
});

const COUNTRIES_DB_CA = [
    {
        "name": "l'Afganistan",
        "alpha2Code": "AF",
        "alpha3Code": "AFG",
        "numericCode": "004",
        "callingCode": "+93"
    },
    {
        "name": "Alandia",
        "alpha2Code": "AX",
        "alpha3Code": "ALA",
        "numericCode": "248",
        "callingCode": "+358"
    },
    {
        "name": "Albània",
        "alpha2Code": "AL",
        "alpha3Code": "ALB",
        "numericCode": "008",
        "callingCode": "+355"
    },
    {
        "name": "Algèria",
        "alpha2Code": "DZ",
        "alpha3Code": "DZA",
        "numericCode": "012",
        "callingCode": "+213"
    },
    {
        "name": "Samoa Americana",
        "alpha2Code": "AS",
        "alpha3Code": "ASM",
        "numericCode": "016",
        "callingCode": "+1684"
    },
    {
        "name": "Andorra",
        "alpha2Code": "AD",
        "alpha3Code": "AND",
        "numericCode": "020",
        "callingCode": "+376"
    },
    {
        "name": "Angola",
        "alpha2Code": "AO",
        "alpha3Code": "AGO",
        "numericCode": "024",
        "callingCode": "+244"
    },
    {
        "name": "Anguilla",
        "alpha2Code": "AI",
        "alpha3Code": "AIA",
        "numericCode": "660",
        "callingCode": "+1264"
    },
    {
        "name": "Antàrtida",
        "alpha2Code": "AQ",
        "alpha3Code": "ATA",
        "numericCode": "010",
        "callingCode": "+672"
    },
    {
        "name": "Antigua i Barbuda",
        "alpha2Code": "AG",
        "alpha3Code": "ATG",
        "numericCode": "028",
        "callingCode": "+1268"
    },
    {
        "name": "Argentina",
        "alpha2Code": "AR",
        "alpha3Code": "ARG",
        "numericCode": "032",
        "callingCode": "+54"
    },
    {
        "name": "Armènia",
        "alpha2Code": "AM",
        "alpha3Code": "ARM",
        "numericCode": "051",
        "callingCode": "+374"
    },
    {
        "name": "Aruba",
        "alpha2Code": "AW",
        "alpha3Code": "ABW",
        "numericCode": "533",
        "callingCode": "+297"
    },
    {
        "name": "Austràlia",
        "alpha2Code": "AU",
        "alpha3Code": "AUS",
        "numericCode": "036",
        "callingCode": "+61"
    },
    {
        "name": "Àustria",
        "alpha2Code": "AT",
        "alpha3Code": "AUT",
        "numericCode": "040",
        "callingCode": "+43"
    },
    {
        "name": "Azerbaidjan",
        "alpha2Code": "AZ",
        "alpha3Code": "AZE",
        "numericCode": "031",
        "callingCode": "+994"
    },
    {
        "name": "Bahames",
        "alpha2Code": "BS",
        "alpha3Code": "BHS",
        "numericCode": "044",
        "callingCode": "+1242"
    },
    {
        "name": "Bahrain",
        "alpha2Code": "BH",
        "alpha3Code": "BHR",
        "numericCode": "048",
        "callingCode": "+973"
    },
    {
        "name": "Bangladesh",
        "alpha2Code": "BD",
        "alpha3Code": "BGD",
        "numericCode": "050",
        "callingCode": "+880"
    },
    {
        "name": "Barbados",
        "alpha2Code": "BB",
        "alpha3Code": "BRB",
        "numericCode": "052",
        "callingCode": "+1246"
    },
    {
        "name": "Bielorússia",
        "alpha2Code": "BY",
        "alpha3Code": "BLR",
        "numericCode": "112",
        "callingCode": "+375"
    },
    {
        "name": "Bèlgica",
        "alpha2Code": "BE",
        "alpha3Code": "BEL",
        "numericCode": "056",
        "callingCode": "+32"
    },
    {
        "name": "Belize",
        "alpha2Code": "BZ",
        "alpha3Code": "BLZ",
        "numericCode": "084",
        "callingCode": "+501"
    },
    {
        "name": "Benín",
        "alpha2Code": "BJ",
        "alpha3Code": "BEN",
        "numericCode": "204",
        "callingCode": "+229"
    },
    {
        "name": "Bermudes",
        "alpha2Code": "BM",
        "alpha3Code": "BMU",
        "numericCode": "060",
        "callingCode": "+1441"
    },
    {
        "name": "Bhutan",
        "alpha2Code": "BT",
        "alpha3Code": "BTN",
        "numericCode": "064",
        "callingCode": "+975"
    },
    {
        "name": "Bolívia",
        "alpha2Code": "BO",
        "alpha3Code": "BOL",
        "numericCode": "068",
        "callingCode": "+591"
    },
    {
        "name": "Bonaire, Saint-Eustache i Saba",
        "alpha2Code": "BQ",
        "alpha3Code": "BES",
        "numericCode": "535",
        "callingCode": "+5997"
    },
    {
        "name": "Bòsnia i Hercegovina",
        "alpha2Code": "BA",
        "alpha3Code": "BIH",
        "numericCode": "070",
        "callingCode": "+387"
    },
    {
        "name": "Botswana",
        "alpha2Code": "BW",
        "alpha3Code": "BWA",
        "numericCode": "072",
        "callingCode": "+267"
    },
    {
        "name": "Bouvet",
        "alpha2Code": "BV",
        "alpha3Code": "BVT",
        "numericCode": "074",
        "callingCode": "+"
    },
    {
        "name": "Brasil",
        "alpha2Code": "BR",
        "alpha3Code": "BRA",
        "numericCode": "076",
        "callingCode": "+55"
    },
    {
        "name": "Territori Britànic de l'Oceà Índic",
        "alpha2Code": "IO",
        "alpha3Code": "IOT",
        "numericCode": "086",
        "callingCode": "+246"
    },
    {
        "name": "Illes Ultramarines Menors dels Estats Units",
        "alpha2Code": "UM",
        "alpha3Code": "UMI",
        "numericCode": "581",
        "callingCode": "+"
    },
    {
        "name": "Illes Verges del Regne Unit",
        "alpha2Code": "VG",
        "alpha3Code": "VGB",
        "numericCode": "092",
        "callingCode": "+1284"
    },
    {
        "name": "Illes Verges dels Estats Units",
        "alpha2Code": "VI",
        "alpha3Code": "VIR",
        "numericCode": "850",
        "callingCode": "+1 340"
    },
    {
        "name": "Brunei",
        "alpha2Code": "BN",
        "alpha3Code": "BRN",
        "numericCode": "096",
        "callingCode": "+673"
    },
    {
        "name": "Bulgària",
        "alpha2Code": "BG",
        "alpha3Code": "BGR",
        "numericCode": "100",
        "callingCode": "+359"
    },
    {
        "name": "Burkina Faso",
        "alpha2Code": "BF",
        "alpha3Code": "BFA",
        "numericCode": "854",
        "callingCode": "+226"
    },
    {
        "name": "Burundi",
        "alpha2Code": "BI",
        "alpha3Code": "BDI",
        "numericCode": "108",
        "callingCode": "+257"
    },
    {
        "name": "Cambodja",
        "alpha2Code": "KH",
        "alpha3Code": "KHM",
        "numericCode": "116",
        "callingCode": "+855"
    },
    {
        "name": "Camerun",
        "alpha2Code": "CM",
        "alpha3Code": "CMR",
        "numericCode": "120",
        "callingCode": "+237"
    },
    {
        "name": "Canadà",
        "alpha2Code": "CA",
        "alpha3Code": "CAN",
        "numericCode": "124",
        "callingCode": "+1"
    },
    {
        "name": "Cap Verd",
        "alpha2Code": "CV",
        "alpha3Code": "CPV",
        "numericCode": "132",
        "callingCode": "+238"
    },
    {
        "name": "Illes Caiman",
        "alpha2Code": "KY",
        "alpha3Code": "CYM",
        "numericCode": "136",
        "callingCode": "+1345"
    },
    {
        "name": "República Centreafricana",
        "alpha2Code": "CF",
        "alpha3Code": "CAF",
        "numericCode": "140",
        "callingCode": "+236"
    },
    {
        "name": "Txad",
        "alpha2Code": "TD",
        "alpha3Code": "TCD",
        "numericCode": "148",
        "callingCode": "+235"
    },
    {
        "name": "Xile",
        "alpha2Code": "CL",
        "alpha3Code": "CHL",
        "numericCode": "152",
        "callingCode": "+56"
    },
    {
        "name": "Xina",
        "alpha2Code": "CN",
        "alpha3Code": "CHN",
        "numericCode": "156",
        "callingCode": "+86"
    },
    {
        "name": "Illa Christmas",
        "alpha2Code": "CX",
        "alpha3Code": "CXR",
        "numericCode": "162",
        "callingCode": "+61"
    },
    {
        "name": "Illes Cocos o Illes Keeling",
        "alpha2Code": "CC",
        "alpha3Code": "CCK",
        "numericCode": "166",
        "callingCode": "+61"
    },
    {
        "name": "Colòmbia",
        "alpha2Code": "CO",
        "alpha3Code": "COL",
        "numericCode": "170",
        "callingCode": "+57"
    },
    {
        "name": "Comores",
        "alpha2Code": "KM",
        "alpha3Code": "COM",
        "numericCode": "174",
        "callingCode": "+269"
    },
    {
        "name": "Congo",
        "alpha2Code": "CG",
        "alpha3Code": "COG",
        "numericCode": "178",
        "callingCode": "+242"
    },
    {
        "name": "Congo (Rep. Dem.)",
        "alpha2Code": "CD",
        "alpha3Code": "COD",
        "numericCode": "180",
        "callingCode": "+243"
    },
    {
        "name": "Illes Cook",
        "alpha2Code": "CK",
        "alpha3Code": "COK",
        "numericCode": "184",
        "callingCode": "+682"
    },
    {
        "name": "Costa Rica",
        "alpha2Code": "CR",
        "alpha3Code": "CRI",
        "numericCode": "188",
        "callingCode": "+506"
    },
    {
        "name": "Croàcia",
        "alpha2Code": "HR",
        "alpha3Code": "HRV",
        "numericCode": "191",
        "callingCode": "+385"
    },
    {
        "name": "Cuba",
        "alpha2Code": "CU",
        "alpha3Code": "CUB",
        "numericCode": "192",
        "callingCode": "+53"
    },
    {
        "name": "Curaçao",
        "alpha2Code": "CW",
        "alpha3Code": "CUW",
        "numericCode": "531",
        "callingCode": "+599"
    },
    {
        "name": "Xipre",
        "alpha2Code": "CY",
        "alpha3Code": "CYP",
        "numericCode": "196",
        "callingCode": "+357"
    },
    {
        "name": "República Txeca",
        "alpha2Code": "CZ",
        "alpha3Code": "CZE",
        "numericCode": "203",
        "callingCode": "+420"
    },
    {
        "name": "Dinamarca",
        "alpha2Code": "DK",
        "alpha3Code": "DNK",
        "numericCode": "208",
        "callingCode": "+45"
    },
    {
        "name": "Djibouti",
        "alpha2Code": "DJ",
        "alpha3Code": "DJI",
        "numericCode": "262",
        "callingCode": "+253"
    },
    {
        "name": "Dominica",
        "alpha2Code": "DM",
        "alpha3Code": "DMA",
        "numericCode": "212",
        "callingCode": "+1767"
    },
    {
        "name": "República Dominicana",
        "alpha2Code": "DO",
        "alpha3Code": "DOM",
        "numericCode": "214",
        "callingCode": "+1809"
    },
    {
        "name": "Equador",
        "alpha2Code": "EC",
        "alpha3Code": "ECU",
        "numericCode": "218",
        "callingCode": "+593"
    },
    {
        "name": "Egipte",
        "alpha2Code": "EG",
        "alpha3Code": "EGY",
        "numericCode": "818",
        "callingCode": "+20"
    },
    {
        "name": "El Salvador",
        "alpha2Code": "SV",
        "alpha3Code": "SLV",
        "numericCode": "222",
        "callingCode": "+503"
    },
    {
        "name": "Guinea Equatorial",
        "alpha2Code": "GQ",
        "alpha3Code": "GNQ",
        "numericCode": "226",
        "callingCode": "+240"
    },
    {
        "name": "Eritrea",
        "alpha2Code": "ER",
        "alpha3Code": "ERI",
        "numericCode": "232",
        "callingCode": "+291"
    },
    {
        "name": "Estònia",
        "alpha2Code": "EE",
        "alpha3Code": "EST",
        "numericCode": "233",
        "callingCode": "+372"
    },
    {
        "name": "Etiòpia",
        "alpha2Code": "ET",
        "alpha3Code": "ETH",
        "numericCode": "231",
        "callingCode": "+251"
    },
    {
        "name": "Illes Malvines",
        "alpha2Code": "FK",
        "alpha3Code": "FLK",
        "numericCode": "238",
        "callingCode": "+500"
    },
    {
        "name": "Illes Faroe",
        "alpha2Code": "FO",
        "alpha3Code": "FRO",
        "numericCode": "234",
        "callingCode": "+298"
    },
    {
        "name": "Fiji",
        "alpha2Code": "FJ",
        "alpha3Code": "FJI",
        "numericCode": "242",
        "callingCode": "+679"
    },
    {
        "name": "Finlàndia",
        "alpha2Code": "FI",
        "alpha3Code": "FIN",
        "numericCode": "246",
        "callingCode": "+358"
    },
    {
        "name": "França",
        "alpha2Code": "FR",
        "alpha3Code": "FRA",
        "numericCode": "250",
        "callingCode": "+33"
    },
    {
        "name": "Guaiana Francesa",
        "alpha2Code": "GF",
        "alpha3Code": "GUF",
        "numericCode": "254",
        "callingCode": "+594"
    },
    {
        "name": "Polinèsia Francesa",
        "alpha2Code": "PF",
        "alpha3Code": "PYF",
        "numericCode": "258",
        "callingCode": "+689"
    },
    {
        "name": "Terres Australs i Antàrtiques Franceses",
        "alpha2Code": "TF",
        "alpha3Code": "ATF",
        "numericCode": "260",
        "callingCode": "+"
    },
    {
        "name": "Gabon",
        "alpha2Code": "GA",
        "alpha3Code": "GAB",
        "numericCode": "266",
        "callingCode": "+241"
    },
    {
        "name": "Gàmbia",
        "alpha2Code": "GM",
        "alpha3Code": "GMB",
        "numericCode": "270",
        "callingCode": "+220"
    },
    {
        "name": "Geòrgia",
        "alpha2Code": "GE",
        "alpha3Code": "GEO",
        "numericCode": "268",
        "callingCode": "+995"
    },
    {
        "name": "Alemanya",
        "alpha2Code": "DE",
        "alpha3Code": "DEU",
        "numericCode": "276",
        "callingCode": "+49"
    },
    {
        "name": "Ghana",
        "alpha2Code": "GH",
        "alpha3Code": "GHA",
        "numericCode": "288",
        "callingCode": "+233"
    },
    {
        "name": "Gibraltar",
        "alpha2Code": "GI",
        "alpha3Code": "GIB",
        "numericCode": "292",
        "callingCode": "+350"
    },
    {
        "name": "Grècia",
        "alpha2Code": "GR",
        "alpha3Code": "GRC",
        "numericCode": "300",
        "callingCode": "+30"
    },
    {
        "name": "Groenlàndia",
        "alpha2Code": "GL",
        "alpha3Code": "GRL",
        "numericCode": "304",
        "callingCode": "+299"
    },
    {
        "name": "Grenada",
        "alpha2Code": "GD",
        "alpha3Code": "GRD",
        "numericCode": "308",
        "callingCode": "+1473"
    },
    {
        "name": "Guadalupe",
        "alpha2Code": "GP",
        "alpha3Code": "GLP",
        "numericCode": "312",
        "callingCode": "+590"
    },
    {
        "name": "Guam",
        "alpha2Code": "GU",
        "alpha3Code": "GUM",
        "numericCode": "316",
        "callingCode": "+1671"
    },
    {
        "name": "Guatemala",
        "alpha2Code": "GT",
        "alpha3Code": "GTM",
        "numericCode": "320",
        "callingCode": "+502"
    },
    {
        "name": "Guernsey",
        "alpha2Code": "GG",
        "alpha3Code": "GGY",
        "numericCode": "831",
        "callingCode": "+44"
    },
    {
        "name": "Guinea",
        "alpha2Code": "GN",
        "alpha3Code": "GIN",
        "numericCode": "324",
        "callingCode": "+224"
    },
    {
        "name": "Guinea Bissau",
        "alpha2Code": "GW",
        "alpha3Code": "GNB",
        "numericCode": "624",
        "callingCode": "+245"
    },
    {
        "name": "Guyana",
        "alpha2Code": "GY",
        "alpha3Code": "GUY",
        "numericCode": "328",
        "callingCode": "+592"
    },
    {
        "name": "Haiti",
        "alpha2Code": "HT",
        "alpha3Code": "HTI",
        "numericCode": "332",
        "callingCode": "+509"
    },
    {
        "name": "Illes Heard i McDonald",
        "alpha2Code": "HM",
        "alpha3Code": "HMD",
        "numericCode": "334",
        "callingCode": "+"
    },
    {
        "name": "Santa Seu",
        "alpha2Code": "VA",
        "alpha3Code": "VAT",
        "numericCode": "336",
        "callingCode": "+379"
    },
    {
        "name": "Hondures",
        "alpha2Code": "HN",
        "alpha3Code": "HND",
        "numericCode": "340",
        "callingCode": "+504"
    },
    {
        "name": "Hong Kong",
        "alpha2Code": "HK",
        "alpha3Code": "HKG",
        "numericCode": "344",
        "callingCode": "+852"
    },
    {
        "name": "Hongria",
        "alpha2Code": "HU",
        "alpha3Code": "HUN",
        "numericCode": "348",
        "callingCode": "+36"
    },
    {
        "name": "Islàndia",
        "alpha2Code": "IS",
        "alpha3Code": "ISL",
        "numericCode": "352",
        "callingCode": "+354"
    },
    {
        "name": "Índia",
        "alpha2Code": "IN",
        "alpha3Code": "IND",
        "numericCode": "356",
        "callingCode": "+91"
    },
    {
        "name": "Indonèsia",
        "alpha2Code": "ID",
        "alpha3Code": "IDN",
        "numericCode": "360",
        "callingCode": "+62"
    },
    {
        "name": "Costa d'Ivori",
        "alpha2Code": "CI",
        "alpha3Code": "CIV",
        "numericCode": "384",
        "callingCode": "+225"
    },
    {
        "name": "Iran",
        "alpha2Code": "IR",
        "alpha3Code": "IRN",
        "numericCode": "364",
        "callingCode": "+98"
    },
    {
        "name": "l'Iraq",
        "alpha2Code": "IQ",
        "alpha3Code": "IRQ",
        "numericCode": "368",
        "callingCode": "+964"
    },
    {
        "name": "Irlanda",
        "alpha2Code": "IE",
        "alpha3Code": "IRL",
        "numericCode": "372",
        "callingCode": "+353"
    },
    {
        "name": "Illa de Man",
        "alpha2Code": "IM",
        "alpha3Code": "IMN",
        "numericCode": "833",
        "callingCode": "+44"
    },
    {
        "name": "Israel",
        "alpha2Code": "IL",
        "alpha3Code": "ISR",
        "numericCode": "376",
        "callingCode": "+972"
    },
    {
        "name": "Itàlia",
        "alpha2Code": "IT",
        "alpha3Code": "ITA",
        "numericCode": "380",
        "callingCode": "+39"
    },
    {
        "name": "Jamaica",
        "alpha2Code": "JM",
        "alpha3Code": "JAM",
        "numericCode": "388",
        "callingCode": "+1876"
    },
    {
        "name": "Japó",
        "alpha2Code": "JP",
        "alpha3Code": "JPN",
        "numericCode": "392",
        "callingCode": "+81"
    },
    {
        "name": "Jersey",
        "alpha2Code": "JE",
        "alpha3Code": "JEY",
        "numericCode": "832",
        "callingCode": "+44"
    },
    {
        "name": "Jordània",
        "alpha2Code": "JO",
        "alpha3Code": "JOR",
        "numericCode": "400",
        "callingCode": "+962"
    },
    {
        "name": "Kazakhstan",
        "alpha2Code": "KZ",
        "alpha3Code": "KAZ",
        "numericCode": "398",
        "callingCode": "+7"
    },
    {
        "name": "Kenya",
        "alpha2Code": "KE",
        "alpha3Code": "KEN",
        "numericCode": "404",
        "callingCode": "+254"
    },
    {
        "name": "Kiribati",
        "alpha2Code": "KI",
        "alpha3Code": "KIR",
        "numericCode": "296",
        "callingCode": "+686"
    },
    {
        "name": "Kuwait",
        "alpha2Code": "KW",
        "alpha3Code": "KWT",
        "numericCode": "414",
        "callingCode": "+965"
    },
    {
        "name": "Kirguizistan",
        "alpha2Code": "KG",
        "alpha3Code": "KGZ",
        "numericCode": "417",
        "callingCode": "+996"
    },
    {
        "name": "Laos",
        "alpha2Code": "LA",
        "alpha3Code": "LAO",
        "numericCode": "418",
        "callingCode": "+856"
    },
    {
        "name": "Letònia",
        "alpha2Code": "LV",
        "alpha3Code": "LVA",
        "numericCode": "428",
        "callingCode": "+371"
    },
    {
        "name": "Líban",
        "alpha2Code": "LB",
        "alpha3Code": "LBN",
        "numericCode": "422",
        "callingCode": "+961"
    },
    {
        "name": "Lesotho",
        "alpha2Code": "LS",
        "alpha3Code": "LSO",
        "numericCode": "426",
        "callingCode": "+266"
    },
    {
        "name": "Libèria",
        "alpha2Code": "LR",
        "alpha3Code": "LBR",
        "numericCode": "430",
        "callingCode": "+231"
    },
    {
        "name": "Líbia",
        "alpha2Code": "LY",
        "alpha3Code": "LBY",
        "numericCode": "434",
        "callingCode": "+218"
    },
    {
        "name": "Liechtenstein",
        "alpha2Code": "LI",
        "alpha3Code": "LIE",
        "numericCode": "438",
        "callingCode": "+423"
    },
    {
        "name": "Lituània",
        "alpha2Code": "LT",
        "alpha3Code": "LTU",
        "numericCode": "440",
        "callingCode": "+370"
    },
    {
        "name": "Luxemburg",
        "alpha2Code": "LU",
        "alpha3Code": "LUX",
        "numericCode": "442",
        "callingCode": "+352"
    },
    {
        "name": "Macau",
        "alpha2Code": "MO",
        "alpha3Code": "MAC",
        "numericCode": "446",
        "callingCode": "+853"
    },
    {
        "name": "Macedònia",
        "alpha2Code": "MK",
        "alpha3Code": "MKD",
        "numericCode": "807",
        "callingCode": "+389"
    },
    {
        "name": "Madagascar",
        "alpha2Code": "MG",
        "alpha3Code": "MDG",
        "numericCode": "450",
        "callingCode": "+261"
    },
    {
        "name": "Malawi",
        "alpha2Code": "MW",
        "alpha3Code": "MWI",
        "numericCode": "454",
        "callingCode": "+265"
    },
    {
        "name": "Malàisia",
        "alpha2Code": "MY",
        "alpha3Code": "MYS",
        "numericCode": "458",
        "callingCode": "+60"
    },
    {
        "name": "Maldives",
        "alpha2Code": "MV",
        "alpha3Code": "MDV",
        "numericCode": "462",
        "callingCode": "+960"
    },
    {
        "name": "Mali",
        "alpha2Code": "ML",
        "alpha3Code": "MLI",
        "numericCode": "466",
        "callingCode": "+223"
    },
    {
        "name": "Malta",
        "alpha2Code": "MT",
        "alpha3Code": "MLT",
        "numericCode": "470",
        "callingCode": "+356"
    },
    {
        "name": "Illes Marshall",
        "alpha2Code": "MH",
        "alpha3Code": "MHL",
        "numericCode": "584",
        "callingCode": "+692"
    },
    {
        "name": "Martinica",
        "alpha2Code": "MQ",
        "alpha3Code": "MTQ",
        "numericCode": "474",
        "callingCode": "+596"
    },
    {
        "name": "Mauritània",
        "alpha2Code": "MR",
        "alpha3Code": "MRT",
        "numericCode": "478",
        "callingCode": "+222"
    },
    {
        "name": "Maurici",
        "alpha2Code": "MU",
        "alpha3Code": "MUS",
        "numericCode": "480",
        "callingCode": "+230"
    },
    {
        "name": "Mayotte",
        "alpha2Code": "YT",
        "alpha3Code": "MYT",
        "numericCode": "175",
        "callingCode": "+262"
    },
    {
        "name": "Mèxic",
        "alpha2Code": "MX",
        "alpha3Code": "MEX",
        "numericCode": "484",
        "callingCode": "+52"
    },
    {
        "name": "Micronèsia",
        "alpha2Code": "FM",
        "alpha3Code": "FSM",
        "numericCode": "583",
        "callingCode": "+691"
    },
    {
        "name": "Moldàvia",
        "alpha2Code": "MD",
        "alpha3Code": "MDA",
        "numericCode": "498",
        "callingCode": "+373"
    },
    {
        "name": "Mònaco",
        "alpha2Code": "MC",
        "alpha3Code": "MCO",
        "numericCode": "492",
        "callingCode": "+377"
    },
    {
        "name": "Mongòlia",
        "alpha2Code": "MN",
        "alpha3Code": "MNG",
        "numericCode": "496",
        "callingCode": "+976"
    },
    {
        "name": "Montenegro",
        "alpha2Code": "ME",
        "alpha3Code": "MNE",
        "numericCode": "499",
        "callingCode": "+382"
    },
    {
        "name": "Montserrat",
        "alpha2Code": "MS",
        "alpha3Code": "MSR",
        "numericCode": "500",
        "callingCode": "+1664"
    },
    {
        "name": "Marroc",
        "alpha2Code": "MA",
        "alpha3Code": "MAR",
        "numericCode": "504",
        "callingCode": "+212"
    },
    {
        "name": "Moçambic",
        "alpha2Code": "MZ",
        "alpha3Code": "MOZ",
        "numericCode": "508",
        "callingCode": "+258"
    },
    {
        "name": "Myanmar",
        "alpha2Code": "MM",
        "alpha3Code": "MMR",
        "numericCode": "104",
        "callingCode": "+95"
    },
    {
        "name": "Namíbia",
        "alpha2Code": "NA",
        "alpha3Code": "NAM",
        "numericCode": "516",
        "callingCode": "+264"
    },
    {
        "name": "Nauru",
        "alpha2Code": "NR",
        "alpha3Code": "NRU",
        "numericCode": "520",
        "callingCode": "+674"
    },
    {
        "name": "Nepal",
        "alpha2Code": "NP",
        "alpha3Code": "NPL",
        "numericCode": "524",
        "callingCode": "+977"
    },
    {
        "name": "Països Baixos",
        "alpha2Code": "NL",
        "alpha3Code": "NLD",
        "numericCode": "528",
        "callingCode": "+31"
    },
    {
        "name": "Nova Caledònia",
        "alpha2Code": "NC",
        "alpha3Code": "NCL",
        "numericCode": "540",
        "callingCode": "+687"
    },
    {
        "name": "Nova Zelanda",
        "alpha2Code": "NZ",
        "alpha3Code": "NZL",
        "numericCode": "554",
        "callingCode": "+64"
    },
    {
        "name": "Nicaragua",
        "alpha2Code": "NI",
        "alpha3Code": "NIC",
        "numericCode": "558",
        "callingCode": "+505"
    },
    {
        "name": "Níger",
        "alpha2Code": "NE",
        "alpha3Code": "NER",
        "numericCode": "562",
        "callingCode": "+227"
    },
    {
        "name": "Nigèria",
        "alpha2Code": "NG",
        "alpha3Code": "NGA",
        "numericCode": "566",
        "callingCode": "+234"
    },
    {
        "name": "Niue",
        "alpha2Code": "NU",
        "alpha3Code": "NIU",
        "numericCode": "570",
        "callingCode": "+683"
    },
    {
        "name": "Illa de Norfolk",
        "alpha2Code": "NF",
        "alpha3Code": "NFK",
        "numericCode": "574",
        "callingCode": "+672"
    },
    {
        "name": "Corea del Nord",
        "alpha2Code": "KP",
        "alpha3Code": "PRK",
        "numericCode": "408",
        "callingCode": "+850"
    },
    {
        "name": "illes Mariannes del Nord",
        "alpha2Code": "MP",
        "alpha3Code": "MNP",
        "numericCode": "580",
        "callingCode": "+1670"
    },
    {
        "name": "Noruega",
        "alpha2Code": "NO",
        "alpha3Code": "NOR",
        "numericCode": "578",
        "callingCode": "+47"
    },
    {
        "name": "Oman",
        "alpha2Code": "OM",
        "alpha3Code": "OMN",
        "numericCode": "512",
        "callingCode": "+968"
    },
    {
        "name": "Pakistan",
        "alpha2Code": "PK",
        "alpha3Code": "PAK",
        "numericCode": "586",
        "callingCode": "+92"
    },
    {
        "name": "Palau",
        "alpha2Code": "PW",
        "alpha3Code": "PLW",
        "numericCode": "585",
        "callingCode": "+680"
    },
    {
        "name": "Palestina",
        "alpha2Code": "PS",
        "alpha3Code": "PSE",
        "numericCode": "275",
        "callingCode": "+970"
    },
    {
        "name": "Panamà",
        "alpha2Code": "PA",
        "alpha3Code": "PAN",
        "numericCode": "591",
        "callingCode": "+507"
    },
    {
        "name": "Papua Nova Guinea",
        "alpha2Code": "PG",
        "alpha3Code": "PNG",
        "numericCode": "598",
        "callingCode": "+675"
    },
    {
        "name": "Paraguai",
        "alpha2Code": "PY",
        "alpha3Code": "PRY",
        "numericCode": "600",
        "callingCode": "+595"
    },
    {
        "name": "Perú",
        "alpha2Code": "PE",
        "alpha3Code": "PER",
        "numericCode": "604",
        "callingCode": "+51"
    },
    {
        "name": "Filipines",
        "alpha2Code": "PH",
        "alpha3Code": "PHL",
        "numericCode": "608",
        "callingCode": "+63"
    },
    {
        "name": "Illes Pitcairn",
        "alpha2Code": "PN",
        "alpha3Code": "PCN",
        "numericCode": "612",
        "callingCode": "+64"
    },
    {
        "name": "Polònia",
        "alpha2Code": "PL",
        "alpha3Code": "POL",
        "numericCode": "616",
        "callingCode": "+48"
    },
    {
        "name": "Portugal",
        "alpha2Code": "PT",
        "alpha3Code": "PRT",
        "numericCode": "620",
        "callingCode": "+351"
    },
    {
        "name": "Puerto Rico",
        "alpha2Code": "PR",
        "alpha3Code": "PRI",
        "numericCode": "630",
        "callingCode": "+1"
    },
    {
        "name": "Catar",
        "alpha2Code": "QA",
        "alpha3Code": "QAT",
        "numericCode": "634",
        "callingCode": "+974"
    },
    {
        "name": "Kosovo",
        "alpha2Code": "XK",
        "alpha3Code": "KOS",
        "numericCode": null,
        "callingCode": "+383"
    },
    {
        "name": "Reunió",
        "alpha2Code": "RE",
        "alpha3Code": "REU",
        "numericCode": "638",
        "callingCode": "+262"
    },
    {
        "name": "Romania",
        "alpha2Code": "RO",
        "alpha3Code": "ROU",
        "numericCode": "642",
        "callingCode": "+40"
    },
    {
        "name": "Rússia",
        "alpha2Code": "RU",
        "alpha3Code": "RUS",
        "numericCode": "643",
        "callingCode": "+7"
    },
    {
        "name": "Ruanda",
        "alpha2Code": "RW",
        "alpha3Code": "RWA",
        "numericCode": "646",
        "callingCode": "+250"
    },
    {
        "name": "Sant Bartolomé",
        "alpha2Code": "BL",
        "alpha3Code": "BLM",
        "numericCode": "652",
        "callingCode": "+590"
    },
    {
        "name": "Santa Helena",
        "alpha2Code": "SH",
        "alpha3Code": "SHN",
        "numericCode": "654",
        "callingCode": "+290"
    },
    {
        "name": "Saint Kitts i Nevis",
        "alpha2Code": "KN",
        "alpha3Code": "KNA",
        "numericCode": "659",
        "callingCode": "+1869"
    },
    {
        "name": "Saint Lucia",
        "alpha2Code": "LC",
        "alpha3Code": "LCA",
        "numericCode": "662",
        "callingCode": "+1758"
    },
    {
        "name": "Saint Martin",
        "alpha2Code": "MF",
        "alpha3Code": "MAF",
        "numericCode": "663",
        "callingCode": "+590"
    },
    {
        "name": "Sant Pierre i Miquelon",
        "alpha2Code": "PM",
        "alpha3Code": "SPM",
        "numericCode": "666",
        "callingCode": "+508"
    },
    {
        "name": "Sant Vicent i Grenadines",
        "alpha2Code": "VC",
        "alpha3Code": "VCT",
        "numericCode": "670",
        "callingCode": "+1784"
    },
    {
        "name": "Samoa",
        "alpha2Code": "WS",
        "alpha3Code": "WSM",
        "numericCode": "882",
        "callingCode": "+685"
    },
    {
        "name": "San Marino",
        "alpha2Code": "SM",
        "alpha3Code": "SMR",
        "numericCode": "674",
        "callingCode": "+378"
    },
    {
        "name": "Sao Tomé i Príncipe",
        "alpha2Code": "ST",
        "alpha3Code": "STP",
        "numericCode": "678",
        "callingCode": "+239"
    },
    {
        "name": "Aràbia Saudita",
        "alpha2Code": "SA",
        "alpha3Code": "SAU",
        "numericCode": "682",
        "callingCode": "+966"
    },
    {
        "name": "Senegal",
        "alpha2Code": "SN",
        "alpha3Code": "SEN",
        "numericCode": "686",
        "callingCode": "+221"
    },
    {
        "name": "Sèrbia",
        "alpha2Code": "RS",
        "alpha3Code": "SRB",
        "numericCode": "688",
        "callingCode": "+381"
    },
    {
        "name": "Seychelles",
        "alpha2Code": "SC",
        "alpha3Code": "SYC",
        "numericCode": "690",
        "callingCode": "+248"
    },
    {
        "name": "Sierra Leone",
        "alpha2Code": "SL",
        "alpha3Code": "SLE",
        "numericCode": "694",
        "callingCode": "+232"
    },
    {
        "name": "Singapur",
        "alpha2Code": "SG",
        "alpha3Code": "SGP",
        "numericCode": "702",
        "callingCode": "+65"
    },
    {
        "name": "Sant Martí (part holandesa)",
        "alpha2Code": "SX",
        "alpha3Code": "SXM",
        "numericCode": "534",
        "callingCode": "+1721"
    },
    {
        "name": "República Eslovaca",
        "alpha2Code": "SK",
        "alpha3Code": "SVK",
        "numericCode": "703",
        "callingCode": "+421"
    },
    {
        "name": "Eslovènia",
        "alpha2Code": "SI",
        "alpha3Code": "SVN",
        "numericCode": "705",
        "callingCode": "+386"
    },
    {
        "name": "Illes Salomó",
        "alpha2Code": "SB",
        "alpha3Code": "SLB",
        "numericCode": "090",
        "callingCode": "+677"
    },
    {
        "name": "Somàlia",
        "alpha2Code": "SO",
        "alpha3Code": "SOM",
        "numericCode": "706",
        "callingCode": "+252"
    },
    {
        "name": "República de Sud-àfrica",
        "alpha2Code": "ZA",
        "alpha3Code": "ZAF",
        "numericCode": "710",
        "callingCode": "+27"
    },
    {
        "name": "Illes Gòrgies del Sud i Sandvitx del Sud",
        "alpha2Code": "GS",
        "alpha3Code": "SGS",
        "numericCode": "239",
        "callingCode": "+500"
    },
    {
        "name": "Corea del Sud",
        "alpha2Code": "KR",
        "alpha3Code": "KOR",
        "numericCode": "410",
        "callingCode": "+82"
    },
    {
        "name": "Sudan del Sud",
        "alpha2Code": "SS",
        "alpha3Code": "SSD",
        "numericCode": "728",
        "callingCode": "+211"
    },
    {
        "name": "Espanya",
        "alpha2Code": "ES",
        "alpha3Code": "ESP",
        "numericCode": "724",
        "callingCode": "+34"
    },
    {
        "name": "Sri Lanka",
        "alpha2Code": "LK",
        "alpha3Code": "LKA",
        "numericCode": "144",
        "callingCode": "+94"
    },
    {
        "name": "Sudan",
        "alpha2Code": "SD",
        "alpha3Code": "SDN",
        "numericCode": "729",
        "callingCode": "+249"
    },
    {
        "name": "Surinam",
        "alpha2Code": "SR",
        "alpha3Code": "SUR",
        "numericCode": "740",
        "callingCode": "+597"
    },
    {
        "name": "Illes Svalbard i Jan Mayen",
        "alpha2Code": "SJ",
        "alpha3Code": "SJM",
        "numericCode": "744",
        "callingCode": "+4779"
    },
    {
        "name": "Swazilàndia",
        "alpha2Code": "SZ",
        "alpha3Code": "SWZ",
        "numericCode": "748",
        "callingCode": "+268"
    },
    {
        "name": "Suècia",
        "alpha2Code": "SE",
        "alpha3Code": "SWE",
        "numericCode": "752",
        "callingCode": "+46"
    },
    {
        "name": "Suïssa",
        "alpha2Code": "CH",
        "alpha3Code": "CHE",
        "numericCode": "756",
        "callingCode": "+41"
    },
    {
        "name": "Síria",
        "alpha2Code": "SY",
        "alpha3Code": "SYR",
        "numericCode": "760",
        "callingCode": "+963"
    },
    {
        "name": "Taiwan",
        "alpha2Code": "TW",
        "alpha3Code": "TWN",
        "numericCode": "158",
        "callingCode": "+886"
    },
    {
        "name": "Tadjikistan",
        "alpha2Code": "TJ",
        "alpha3Code": "TJK",
        "numericCode": "762",
        "callingCode": "+992"
    },
    {
        "name": "Tanzània",
        "alpha2Code": "TZ",
        "alpha3Code": "TZA",
        "numericCode": "834",
        "callingCode": "+255"
    },
    {
        "name": "Tailàndia",
        "alpha2Code": "TH",
        "alpha3Code": "THA",
        "numericCode": "764",
        "callingCode": "+66"
    },
    {
        "name": "Timor-Leste",
        "alpha2Code": "TL",
        "alpha3Code": "TLS",
        "numericCode": "626",
        "callingCode": "+670"
    },
    {
        "name": "Togo",
        "alpha2Code": "TG",
        "alpha3Code": "TGO",
        "numericCode": "768",
        "callingCode": "+228"
    },
    {
        "name": "Illes Tokelau",
        "alpha2Code": "TK",
        "alpha3Code": "TKL",
        "numericCode": "772",
        "callingCode": "+690"
    },
    {
        "name": "Tonga",
        "alpha2Code": "TO",
        "alpha3Code": "TON",
        "numericCode": "776",
        "callingCode": "+676"
    },
    {
        "name": "Trinitat i Tobago",
        "alpha2Code": "TT",
        "alpha3Code": "TTO",
        "numericCode": "780",
        "callingCode": "+1868"
    },
    {
        "name": "Tunísia",
        "alpha2Code": "TN",
        "alpha3Code": "TUN",
        "numericCode": "788",
        "callingCode": "+216"
    },
    {
        "name": "Turquia",
        "alpha2Code": "TR",
        "alpha3Code": "TUR",
        "numericCode": "792",
        "callingCode": "+90"
    },
    {
        "name": "Turkmenistan",
        "alpha2Code": "TM",
        "alpha3Code": "TKM",
        "numericCode": "795",
        "callingCode": "+993"
    },
    {
        "name": "Illes Turks i Caicos",
        "alpha2Code": "TC",
        "alpha3Code": "TCA",
        "numericCode": "796",
        "callingCode": "+1649"
    },
    {
        "name": "Tuvalu",
        "alpha2Code": "TV",
        "alpha3Code": "TUV",
        "numericCode": "798",
        "callingCode": "+688"
    },
    {
        "name": "Uganda",
        "alpha2Code": "UG",
        "alpha3Code": "UGA",
        "numericCode": "800",
        "callingCode": "+256"
    },
    {
        "name": "Ucraïna",
        "alpha2Code": "UA",
        "alpha3Code": "UKR",
        "numericCode": "804",
        "callingCode": "+380"
    },
    {
        "name": "Unió dels Emirats Àrabs",
        "alpha2Code": "AE",
        "alpha3Code": "ARE",
        "numericCode": "784",
        "callingCode": "+971"
    },
    {
        "name": "Regne Unit",
        "alpha2Code": "GB",
        "alpha3Code": "GBR",
        "numericCode": "826",
        "callingCode": "+44"
    },
    {
        "name": "Estats Units",
        "alpha2Code": "US",
        "alpha3Code": "USA",
        "numericCode": "840",
        "callingCode": "+1"
    },
    {
        "name": "Uruguai",
        "alpha2Code": "UY",
        "alpha3Code": "URY",
        "numericCode": "858",
        "callingCode": "+598"
    },
    {
        "name": "Uzbekistan",
        "alpha2Code": "UZ",
        "alpha3Code": "UZB",
        "numericCode": "860",
        "callingCode": "+998"
    },
    {
        "name": "Vanuatu",
        "alpha2Code": "VU",
        "alpha3Code": "VUT",
        "numericCode": "548",
        "callingCode": "+678"
    },
    {
        "name": "Veneçuela",
        "alpha2Code": "VE",
        "alpha3Code": "VEN",
        "numericCode": "862",
        "callingCode": "+58"
    },
    {
        "name": "Vietnam",
        "alpha2Code": "VN",
        "alpha3Code": "VNM",
        "numericCode": "704",
        "callingCode": "+84"
    },
    {
        "name": "Wallis i Futuna",
        "alpha2Code": "WF",
        "alpha3Code": "WLF",
        "numericCode": "876",
        "callingCode": "+681"
    },
    {
        "name": "Sàhara Occidental",
        "alpha2Code": "EH",
        "alpha3Code": "ESH",
        "numericCode": "732",
        "callingCode": "+212"
    },
    {
        "name": "Iemen",
        "alpha2Code": "YE",
        "alpha3Code": "YEM",
        "numericCode": "887",
        "callingCode": "+967"
    },
    {
        "name": "Zàmbia",
        "alpha2Code": "ZM",
        "alpha3Code": "ZMB",
        "numericCode": "894",
        "callingCode": "+260"
    },
    {
        "name": "Zimbàbue",
        "alpha2Code": "ZW",
        "alpha3Code": "ZWE",
        "numericCode": "716",
        "callingCode": "+263"
    }
];

var ca = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB_CA: COUNTRIES_DB_CA
});

const COUNTRIES_DB_EU = [
    {
        "name": "Afganistan",
        "alpha2Code": "AF",
        "alpha3Code": "AFG",
        "numericCode": "004",
        "callingCode": "+93"
    },
    {
        "name": "Alandia",
        "alpha2Code": "AX",
        "alpha3Code": "ALA",
        "numericCode": "248",
        "callingCode": "+358"
    },
    {
        "name": "Albania",
        "alpha2Code": "AL",
        "alpha3Code": "ALB",
        "numericCode": "008",
        "callingCode": "+355"
    },
    {
        "name": "Aljeria",
        "alpha2Code": "DZ",
        "alpha3Code": "DZA",
        "numericCode": "012",
        "callingCode": "+213"
    },
    {
        "name": "Samoa Americana",
        "alpha2Code": "AS",
        "alpha3Code": "ASM",
        "numericCode": "016",
        "callingCode": "+1684"
    },
    {
        "name": "Andorra",
        "alpha2Code": "AD",
        "alpha3Code": "AND",
        "numericCode": "020",
        "callingCode": "+376"
    },
    {
        "name": "Angola",
        "alpha2Code": "AO",
        "alpha3Code": "AGO",
        "numericCode": "024",
        "callingCode": "+244"
    },
    {
        "name": "Anguilla",
        "alpha2Code": "AI",
        "alpha3Code": "AIA",
        "numericCode": "660",
        "callingCode": "+1264"
    },
    {
        "name": "Antartika",
        "alpha2Code": "AQ",
        "alpha3Code": "ATA",
        "numericCode": "010",
        "callingCode": "+672"
    },
    {
        "name": "Antigua eta Barbuda",
        "alpha2Code": "AG",
        "alpha3Code": "ATG",
        "numericCode": "028",
        "callingCode": "+1268"
    },
    {
        "name": "Argentina",
        "alpha2Code": "AR",
        "alpha3Code": "ARG",
        "numericCode": "032",
        "callingCode": "+54"
    },
    {
        "name": "Armenia",
        "alpha2Code": "AM",
        "alpha3Code": "ARM",
        "numericCode": "051",
        "callingCode": "+374"
    },
    {
        "name": "Aruba",
        "alpha2Code": "AW",
        "alpha3Code": "ABW",
        "numericCode": "533",
        "callingCode": "+297"
    },
    {
        "name": "Australia",
        "alpha2Code": "AU",
        "alpha3Code": "AUS",
        "numericCode": "036",
        "callingCode": "+61"
    },
    {
        "name": "Austria",
        "alpha2Code": "AT",
        "alpha3Code": "AUT",
        "numericCode": "040",
        "callingCode": "+43"
    },
    {
        "name": "Azerbaijan",
        "alpha2Code": "AZ",
        "alpha3Code": "AZE",
        "numericCode": "031",
        "callingCode": "+994"
    },
    {
        "name": "Bahamak",
        "alpha2Code": "BS",
        "alpha3Code": "BHS",
        "numericCode": "044",
        "callingCode": "+1242"
    },
    {
        "name": "Bahrein",
        "alpha2Code": "BH",
        "alpha3Code": "BHR",
        "numericCode": "048",
        "callingCode": "+973"
    },
    {
        "name": "Bangladesh",
        "alpha2Code": "BD",
        "alpha3Code": "BGD",
        "numericCode": "050",
        "callingCode": "+880"
    },
    {
        "name": "Barbados",
        "alpha2Code": "BB",
        "alpha3Code": "BRB",
        "numericCode": "052",
        "callingCode": "+1246"
    },
    {
        "name": "Bielorrusia",
        "alpha2Code": "BY",
        "alpha3Code": "BLR",
        "numericCode": "112",
        "callingCode": "+375"
    },
    {
        "name": "Belgika",
        "alpha2Code": "BE",
        "alpha3Code": "BEL",
        "numericCode": "056",
        "callingCode": "+32"
    },
    {
        "name": "Belice",
        "alpha2Code": "BZ",
        "alpha3Code": "BLZ",
        "numericCode": "084",
        "callingCode": "+501"
    },
    {
        "name": "Benin",
        "alpha2Code": "BJ",
        "alpha3Code": "BEN",
        "numericCode": "204",
        "callingCode": "+229"
    },
    {
        "name": "Bermudak",
        "alpha2Code": "BM",
        "alpha3Code": "BMU",
        "numericCode": "060",
        "callingCode": "+1441"
    },
    {
        "name": "Bután",
        "alpha2Code": "BT",
        "alpha3Code": "BTN",
        "numericCode": "064",
        "callingCode": "+975"
    },
    {
        "name": "Bolivia",
        "alpha2Code": "BO",
        "alpha3Code": "BOL",
        "numericCode": "068",
        "callingCode": "+591"
    },
    {
        "name": "Bonaire, Saint-Eustache eta Saba",
        "alpha2Code": "BQ",
        "alpha3Code": "BES",
        "numericCode": "535",
        "callingCode": "+5997"
    },
    {
        "name": "Bosnia eta Herzegovina",
        "alpha2Code": "BA",
        "alpha3Code": "BIH",
        "numericCode": "070",
        "callingCode": "+387"
    },
    {
        "name": "Botswana",
        "alpha2Code": "BW",
        "alpha3Code": "BWA",
        "numericCode": "072",
        "callingCode": "+267"
    },
    {
        "name": "Bouvet uhartea",
        "alpha2Code": "BV",
        "alpha3Code": "BVT",
        "numericCode": "074",
        "callingCode": "+"
    },
    {
        "name": "Brasil",
        "alpha2Code": "BR",
        "alpha3Code": "BRA",
        "numericCode": "076",
        "callingCode": "+55"
    },
    {
        "name": "Indiako Ozeanoko Britainiar Lurraldea",
        "alpha2Code": "IO",
        "alpha3Code": "IOT",
        "numericCode": "086",
        "callingCode": "+246"
    },
    {
        "name": "Itsasoz Haraindiko Uharte Txikiak",
        "alpha2Code": "UM",
        "alpha3Code": "UMI",
        "numericCode": "581",
        "callingCode": "+"
    },
    {
        "name": "Erresuma Batuko Birjina Uharteak",
        "alpha2Code": "VG",
        "alpha3Code": "VGB",
        "numericCode": "092",
        "callingCode": "+1284"
    },
    {
        "name": "Estatu Batuetako Birjina Uharteak",
        "alpha2Code": "VI",
        "alpha3Code": "VIR",
        "numericCode": "850",
        "callingCode": "+1 340"
    },
    {
        "name": "Brunei",
        "alpha2Code": "BN",
        "alpha3Code": "BRN",
        "numericCode": "096",
        "callingCode": "+673"
    },
    {
        "name": "Bulgaria",
        "alpha2Code": "BG",
        "alpha3Code": "BGR",
        "numericCode": "100",
        "callingCode": "+359"
    },
    {
        "name": "Burkina Faso",
        "alpha2Code": "BF",
        "alpha3Code": "BFA",
        "numericCode": "854",
        "callingCode": "+226"
    },
    {
        "name": "Burundi",
        "alpha2Code": "BI",
        "alpha3Code": "BDI",
        "numericCode": "108",
        "callingCode": "+257"
    },
    {
        "name": "Kanputxea",
        "alpha2Code": "KH",
        "alpha3Code": "KHM",
        "numericCode": "116",
        "callingCode": "+855"
    },
    {
        "name": "Kamerun",
        "alpha2Code": "CM",
        "alpha3Code": "CMR",
        "numericCode": "120",
        "callingCode": "+237"
    },
    {
        "name": "Kanada",
        "alpha2Code": "CA",
        "alpha3Code": "CAN",
        "numericCode": "124",
        "callingCode": "+1"
    },
    {
        "name": "Cabo Verde",
        "alpha2Code": "CV",
        "alpha3Code": "CPV",
        "numericCode": "132",
        "callingCode": "+238"
    },
    {
        "name": "Kaiman uharteak",
        "alpha2Code": "KY",
        "alpha3Code": "CYM",
        "numericCode": "136",
        "callingCode": "+1345"
    },
    {
        "name": "Afrika Erdiko Errepublika",
        "alpha2Code": "CF",
        "alpha3Code": "CAF",
        "numericCode": "140",
        "callingCode": "+236"
    },
    {
        "name": "Txad",
        "alpha2Code": "TD",
        "alpha3Code": "TCD",
        "numericCode": "148",
        "callingCode": "+235"
    },
    {
        "name": "Txile",
        "alpha2Code": "CL",
        "alpha3Code": "CHL",
        "numericCode": "152",
        "callingCode": "+56"
    },
    {
        "name": "Txina",
        "alpha2Code": "CN",
        "alpha3Code": "CHN",
        "numericCode": "156",
        "callingCode": "+86"
    },
    {
        "name": "Gabonetako uhartea",
        "alpha2Code": "CX",
        "alpha3Code": "CXR",
        "numericCode": "162",
        "callingCode": "+61"
    },
    {
        "name": "Cocos uharteak edo Keeling uharteak",
        "alpha2Code": "CC",
        "alpha3Code": "CCK",
        "numericCode": "166",
        "callingCode": "+61"
    },
    {
        "name": "Kolonbia",
        "alpha2Code": "CO",
        "alpha3Code": "COL",
        "numericCode": "170",
        "callingCode": "+57"
    },
    {
        "name": "Komorak",
        "alpha2Code": "KM",
        "alpha3Code": "COM",
        "numericCode": "174",
        "callingCode": "+269"
    },
    {
        "name": "Kongo",
        "alpha2Code": "CG",
        "alpha3Code": "COG",
        "numericCode": "178",
        "callingCode": "+242"
    },
    {
        "name": "Kongo (Rep. Dem.)",
        "alpha2Code": "CD",
        "alpha3Code": "COD",
        "numericCode": "180",
        "callingCode": "+243"
    },
    {
        "name": "Cook uharteak",
        "alpha2Code": "CK",
        "alpha3Code": "COK",
        "numericCode": "184",
        "callingCode": "+682"
    },
    {
        "name": "Costa Rica",
        "alpha2Code": "CR",
        "alpha3Code": "CRI",
        "numericCode": "188",
        "callingCode": "+506"
    },
    {
        "name": "Kroazia",
        "alpha2Code": "HR",
        "alpha3Code": "HRV",
        "numericCode": "191",
        "callingCode": "+385"
    },
    {
        "name": "Kuba",
        "alpha2Code": "CU",
        "alpha3Code": "CUB",
        "numericCode": "192",
        "callingCode": "+53"
    },
    {
        "name": "Curaçao",
        "alpha2Code": "CW",
        "alpha3Code": "CUW",
        "numericCode": "531",
        "callingCode": "+599"
    },
    {
        "name": "Zipre",
        "alpha2Code": "CY",
        "alpha3Code": "CYP",
        "numericCode": "196",
        "callingCode": "+357"
    },
    {
        "name": "Txekiar Errepublika",
        "alpha2Code": "CZ",
        "alpha3Code": "CZE",
        "numericCode": "203",
        "callingCode": "+420"
    },
    {
        "name": "Danimarka",
        "alpha2Code": "DK",
        "alpha3Code": "DNK",
        "numericCode": "208",
        "callingCode": "+45"
    },
    {
        "name": "Yibuti",
        "alpha2Code": "DJ",
        "alpha3Code": "DJI",
        "numericCode": "262",
        "callingCode": "+253"
    },
    {
        "name": "Dominika",
        "alpha2Code": "DM",
        "alpha3Code": "DMA",
        "numericCode": "212",
        "callingCode": "+1767"
    },
    {
        "name": "Dominikar Errepublika",
        "alpha2Code": "DO",
        "alpha3Code": "DOM",
        "numericCode": "214",
        "callingCode": "+1809"
    },
    {
        "name": "Ekuador",
        "alpha2Code": "EC",
        "alpha3Code": "ECU",
        "numericCode": "218",
        "callingCode": "+593"
    },
    {
        "name": "Egipto",
        "alpha2Code": "EG",
        "alpha3Code": "EGY",
        "numericCode": "818",
        "callingCode": "+20"
    },
    {
        "name": "El Salvador",
        "alpha2Code": "SV",
        "alpha3Code": "SLV",
        "numericCode": "222",
        "callingCode": "+503"
    },
    {
        "name": "Ekuatore Ginea",
        "alpha2Code": "GQ",
        "alpha3Code": "GNQ",
        "numericCode": "226",
        "callingCode": "+240"
    },
    {
        "name": "Eritrea",
        "alpha2Code": "ER",
        "alpha3Code": "ERI",
        "numericCode": "232",
        "callingCode": "+291"
    },
    {
        "name": "Estonia",
        "alpha2Code": "EE",
        "alpha3Code": "EST",
        "numericCode": "233",
        "callingCode": "+372"
    },
    {
        "name": "Etiopia",
        "alpha2Code": "ET",
        "alpha3Code": "ETH",
        "numericCode": "231",
        "callingCode": "+251"
    },
    {
        "name": "Malvinas Uharteak",
        "alpha2Code": "FK",
        "alpha3Code": "FLK",
        "numericCode": "238",
        "callingCode": "+500"
    },
    {
        "name": "Faroe uharteak",
        "alpha2Code": "FO",
        "alpha3Code": "FRO",
        "numericCode": "234",
        "callingCode": "+298"
    },
    {
        "name": "Fiyi",
        "alpha2Code": "FJ",
        "alpha3Code": "FJI",
        "numericCode": "242",
        "callingCode": "+679"
    },
    {
        "name": "Finlandia",
        "alpha2Code": "FI",
        "alpha3Code": "FIN",
        "numericCode": "246",
        "callingCode": "+358"
    },
    {
        "name": "Frantzia",
        "alpha2Code": "FR",
        "alpha3Code": "FRA",
        "numericCode": "250",
        "callingCode": "+33"
    },
    {
        "name": "Guyana Frantsesa",
        "alpha2Code": "GF",
        "alpha3Code": "GUF",
        "numericCode": "254",
        "callingCode": "+594"
    },
    {
        "name": "Polinesia Frantsesa",
        "alpha2Code": "PF",
        "alpha3Code": "PYF",
        "numericCode": "258",
        "callingCode": "+689"
    },
    {
        "name": "Frantziako Lurralde Australak eta Antartikoak",
        "alpha2Code": "TF",
        "alpha3Code": "ATF",
        "numericCode": "260",
        "callingCode": "+"
    },
    {
        "name": "Gabon",
        "alpha2Code": "GA",
        "alpha3Code": "GAB",
        "numericCode": "266",
        "callingCode": "+241"
    },
    {
        "name": "Gambia",
        "alpha2Code": "GM",
        "alpha3Code": "GMB",
        "numericCode": "270",
        "callingCode": "+220"
    },
    {
        "name": "Georgia",
        "alpha2Code": "GE",
        "alpha3Code": "GEO",
        "numericCode": "268",
        "callingCode": "+995"
    },
    {
        "name": "Alemania",
        "alpha2Code": "DE",
        "alpha3Code": "DEU",
        "numericCode": "276",
        "callingCode": "+49"
    },
    {
        "name": "Ghana",
        "alpha2Code": "GH",
        "alpha3Code": "GHA",
        "numericCode": "288",
        "callingCode": "+233"
    },
    {
        "name": "Gibraltar",
        "alpha2Code": "GI",
        "alpha3Code": "GIB",
        "numericCode": "292",
        "callingCode": "+350"
    },
    {
        "name": "Grezia",
        "alpha2Code": "GR",
        "alpha3Code": "GRC",
        "numericCode": "300",
        "callingCode": "+30"
    },
    {
        "name": "Groenlandia",
        "alpha2Code": "GL",
        "alpha3Code": "GRL",
        "numericCode": "304",
        "callingCode": "+299"
    },
    {
        "name": "Grenada",
        "alpha2Code": "GD",
        "alpha3Code": "GRD",
        "numericCode": "308",
        "callingCode": "+1473"
    },
    {
        "name": "Guadalupe",
        "alpha2Code": "GP",
        "alpha3Code": "GLP",
        "numericCode": "312",
        "callingCode": "+590"
    },
    {
        "name": "Guam",
        "alpha2Code": "GU",
        "alpha3Code": "GUM",
        "numericCode": "316",
        "callingCode": "+1671"
    },
    {
        "name": "Guatemala",
        "alpha2Code": "GT",
        "alpha3Code": "GTM",
        "numericCode": "320",
        "callingCode": "+502"
    },
    {
        "name": "Guernsey",
        "alpha2Code": "GG",
        "alpha3Code": "GGY",
        "numericCode": "831",
        "callingCode": "+44"
    },
    {
        "name": "Ginea",
        "alpha2Code": "GN",
        "alpha3Code": "GIN",
        "numericCode": "324",
        "callingCode": "+224"
    },
    {
        "name": "Ginea-Bisau",
        "alpha2Code": "GW",
        "alpha3Code": "GNB",
        "numericCode": "624",
        "callingCode": "+245"
    },
    {
        "name": "Guyana",
        "alpha2Code": "GY",
        "alpha3Code": "GUY",
        "numericCode": "328",
        "callingCode": "+592"
    },
    {
        "name": "Haiti",
        "alpha2Code": "HT",
        "alpha3Code": "HTI",
        "numericCode": "332",
        "callingCode": "+509"
    },
    {
        "name": "Heard eta McDonald uharteak",
        "alpha2Code": "HM",
        "alpha3Code": "HMD",
        "numericCode": "334",
        "callingCode": "+"
    },
    {
        "name": "Egoitza Santua",
        "alpha2Code": "VA",
        "alpha3Code": "VAT",
        "numericCode": "336",
        "callingCode": "+379"
    },
    {
        "name": "Honduras",
        "alpha2Code": "HN",
        "alpha3Code": "HND",
        "numericCode": "340",
        "callingCode": "+504"
    },
    {
        "name": "Hong Kong",
        "alpha2Code": "HK",
        "alpha3Code": "HKG",
        "numericCode": "344",
        "callingCode": "+852"
    },
    {
        "name": "Hungaria",
        "alpha2Code": "HU",
        "alpha3Code": "HUN",
        "numericCode": "348",
        "callingCode": "+36"
    },
    {
        "name": "Islandia",
        "alpha2Code": "IS",
        "alpha3Code": "ISL",
        "numericCode": "352",
        "callingCode": "+354"
    },
    {
        "name": "India",
        "alpha2Code": "IN",
        "alpha3Code": "IND",
        "numericCode": "356",
        "callingCode": "+91"
    },
    {
        "name": "Indonesia",
        "alpha2Code": "ID",
        "alpha3Code": "IDN",
        "numericCode": "360",
        "callingCode": "+62"
    },
    {
        "name": "Boli Kosta",
        "alpha2Code": "CI",
        "alpha3Code": "CIV",
        "numericCode": "384",
        "callingCode": "+225"
    },
    {
        "name": "Iran",
        "alpha2Code": "IR",
        "alpha3Code": "IRN",
        "numericCode": "364",
        "callingCode": "+98"
    },
    {
        "name": "Irak",
        "alpha2Code": "IQ",
        "alpha3Code": "IRQ",
        "numericCode": "368",
        "callingCode": "+964"
    },
    {
        "name": "Irlanda",
        "alpha2Code": "IE",
        "alpha3Code": "IRL",
        "numericCode": "372",
        "callingCode": "+353"
    },
    {
        "name": "Man uhartea",
        "alpha2Code": "IM",
        "alpha3Code": "IMN",
        "numericCode": "833",
        "callingCode": "+44"
    },
    {
        "name": "Israel",
        "alpha2Code": "IL",
        "alpha3Code": "ISR",
        "numericCode": "376",
        "callingCode": "+972"
    },
    {
        "name": "Italia",
        "alpha2Code": "IT",
        "alpha3Code": "ITA",
        "numericCode": "380",
        "callingCode": "+39"
    },
    {
        "name": "Jamaika",
        "alpha2Code": "JM",
        "alpha3Code": "JAM",
        "numericCode": "388",
        "callingCode": "+1876"
    },
    {
        "name": "Japonia",
        "alpha2Code": "JP",
        "alpha3Code": "JPN",
        "numericCode": "392",
        "callingCode": "+81"
    },
    {
        "name": "Jersey",
        "alpha2Code": "JE",
        "alpha3Code": "JEY",
        "numericCode": "832",
        "callingCode": "+44"
    },
    {
        "name": "Jordania",
        "alpha2Code": "JO",
        "alpha3Code": "JOR",
        "numericCode": "400",
        "callingCode": "+962"
    },
    {
        "name": "Kazakhstan",
        "alpha2Code": "KZ",
        "alpha3Code": "KAZ",
        "numericCode": "398",
        "callingCode": "+7"
    },
    {
        "name": "Kenya",
        "alpha2Code": "KE",
        "alpha3Code": "KEN",
        "numericCode": "404",
        "callingCode": "+254"
    },
    {
        "name": "Kiribati",
        "alpha2Code": "KI",
        "alpha3Code": "KIR",
        "numericCode": "296",
        "callingCode": "+686"
    },
    {
        "name": "Kuwait",
        "alpha2Code": "KW",
        "alpha3Code": "KWT",
        "numericCode": "414",
        "callingCode": "+965"
    },
    {
        "name": "Kirgizistan",
        "alpha2Code": "KG",
        "alpha3Code": "KGZ",
        "numericCode": "417",
        "callingCode": "+996"
    },
    {
        "name": "Laos",
        "alpha2Code": "LA",
        "alpha3Code": "LAO",
        "numericCode": "418",
        "callingCode": "+856"
    },
    {
        "name": "Letonia",
        "alpha2Code": "LV",
        "alpha3Code": "LVA",
        "numericCode": "428",
        "callingCode": "+371"
    },
    {
        "name": "Libano",
        "alpha2Code": "LB",
        "alpha3Code": "LBN",
        "numericCode": "422",
        "callingCode": "+961"
    },
    {
        "name": "Lesotho",
        "alpha2Code": "LS",
        "alpha3Code": "LSO",
        "numericCode": "426",
        "callingCode": "+266"
    },
    {
        "name": "Liberia",
        "alpha2Code": "LR",
        "alpha3Code": "LBR",
        "numericCode": "430",
        "callingCode": "+231"
    },
    {
        "name": "Libia",
        "alpha2Code": "LY",
        "alpha3Code": "LBY",
        "numericCode": "434",
        "callingCode": "+218"
    },
    {
        "name": "Liechtenstein",
        "alpha2Code": "LI",
        "alpha3Code": "LIE",
        "numericCode": "438",
        "callingCode": "+423"
    },
    {
        "name": "Lituania",
        "alpha2Code": "LT",
        "alpha3Code": "LTU",
        "numericCode": "440",
        "callingCode": "+370"
    },
    {
        "name": "Luxenburgo",
        "alpha2Code": "LU",
        "alpha3Code": "LUX",
        "numericCode": "442",
        "callingCode": "+352"
    },
    {
        "name": "Macao",
        "alpha2Code": "MO",
        "alpha3Code": "MAC",
        "numericCode": "446",
        "callingCode": "+853"
    },
    {
        "name": "Mazedonia",
        "alpha2Code": "MK",
        "alpha3Code": "MKD",
        "numericCode": "807",
        "callingCode": "+389"
    },
    {
        "name": "Madagaskar",
        "alpha2Code": "MG",
        "alpha3Code": "MDG",
        "numericCode": "450",
        "callingCode": "+261"
    },
    {
        "name": "Malawi",
        "alpha2Code": "MW",
        "alpha3Code": "MWI",
        "numericCode": "454",
        "callingCode": "+265"
    },
    {
        "name": "Malaysia",
        "alpha2Code": "MY",
        "alpha3Code": "MYS",
        "numericCode": "458",
        "callingCode": "+60"
    },
    {
        "name": "Maldivak",
        "alpha2Code": "MV",
        "alpha3Code": "MDV",
        "numericCode": "462",
        "callingCode": "+960"
    },
    {
        "name": "Mali",
        "alpha2Code": "ML",
        "alpha3Code": "MLI",
        "numericCode": "466",
        "callingCode": "+223"
    },
    {
        "name": "Malta",
        "alpha2Code": "MT",
        "alpha3Code": "MLT",
        "numericCode": "470",
        "callingCode": "+356"
    },
    {
        "name": "Marshall uharteak",
        "alpha2Code": "MH",
        "alpha3Code": "MHL",
        "numericCode": "584",
        "callingCode": "+692"
    },
    {
        "name": "Martinika",
        "alpha2Code": "MQ",
        "alpha3Code": "MTQ",
        "numericCode": "474",
        "callingCode": "+596"
    },
    {
        "name": "Mauritania",
        "alpha2Code": "MR",
        "alpha3Code": "MRT",
        "numericCode": "478",
        "callingCode": "+222"
    },
    {
        "name": "Maurizio",
        "alpha2Code": "MU",
        "alpha3Code": "MUS",
        "numericCode": "480",
        "callingCode": "+230"
    },
    {
        "name": "Mayotte",
        "alpha2Code": "YT",
        "alpha3Code": "MYT",
        "numericCode": "175",
        "callingCode": "+262"
    },
    {
        "name": "Mexiko",
        "alpha2Code": "MX",
        "alpha3Code": "MEX",
        "numericCode": "484",
        "callingCode": "+52"
    },
    {
        "name": "Mikronesia",
        "alpha2Code": "FM",
        "alpha3Code": "FSM",
        "numericCode": "583",
        "callingCode": "+691"
    },
    {
        "name": "Moldavia",
        "alpha2Code": "MD",
        "alpha3Code": "MDA",
        "numericCode": "498",
        "callingCode": "+373"
    },
    {
        "name": "Monako",
        "alpha2Code": "MC",
        "alpha3Code": "MCO",
        "numericCode": "492",
        "callingCode": "+377"
    },
    {
        "name": "Mongolia",
        "alpha2Code": "MN",
        "alpha3Code": "MNG",
        "numericCode": "496",
        "callingCode": "+976"
    },
    {
        "name": "Montenegro",
        "alpha2Code": "ME",
        "alpha3Code": "MNE",
        "numericCode": "499",
        "callingCode": "+382"
    },
    {
        "name": "Montserrat",
        "alpha2Code": "MS",
        "alpha3Code": "MSR",
        "numericCode": "500",
        "callingCode": "+1664"
    },
    {
        "name": "Maroko",
        "alpha2Code": "MA",
        "alpha3Code": "MAR",
        "numericCode": "504",
        "callingCode": "+212"
    },
    {
        "name": "Mozambike",
        "alpha2Code": "MZ",
        "alpha3Code": "MOZ",
        "numericCode": "508",
        "callingCode": "+258"
    },
    {
        "name": "Myanmar",
        "alpha2Code": "MM",
        "alpha3Code": "MMR",
        "numericCode": "104",
        "callingCode": "+95"
    },
    {
        "name": "Namibia",
        "alpha2Code": "NA",
        "alpha3Code": "NAM",
        "numericCode": "516",
        "callingCode": "+264"
    },
    {
        "name": "Nauru",
        "alpha2Code": "NR",
        "alpha3Code": "NRU",
        "numericCode": "520",
        "callingCode": "+674"
    },
    {
        "name": "Nepal",
        "alpha2Code": "NP",
        "alpha3Code": "NPL",
        "numericCode": "524",
        "callingCode": "+977"
    },
    {
        "name": "Herbehereak",
        "alpha2Code": "NL",
        "alpha3Code": "NLD",
        "numericCode": "528",
        "callingCode": "+31"
    },
    {
        "name": "Kaledonia Berria",
        "alpha2Code": "NC",
        "alpha3Code": "NCL",
        "numericCode": "540",
        "callingCode": "+687"
    },
    {
        "name": "Zeelanda Berria",
        "alpha2Code": "NZ",
        "alpha3Code": "NZL",
        "numericCode": "554",
        "callingCode": "+64"
    },
    {
        "name": "Nikaragua",
        "alpha2Code": "NI",
        "alpha3Code": "NIC",
        "numericCode": "558",
        "callingCode": "+505"
    },
    {
        "name": "Niger",
        "alpha2Code": "NE",
        "alpha3Code": "NER",
        "numericCode": "562",
        "callingCode": "+227"
    },
    {
        "name": "Nigeria",
        "alpha2Code": "NG",
        "alpha3Code": "NGA",
        "numericCode": "566",
        "callingCode": "+234"
    },
    {
        "name": "Niue",
        "alpha2Code": "NU",
        "alpha3Code": "NIU",
        "numericCode": "570",
        "callingCode": "+683"
    },
    {
        "name": "Norfolk uhartea",
        "alpha2Code": "NF",
        "alpha3Code": "NFK",
        "numericCode": "574",
        "callingCode": "+672"
    },
    {
        "name": "Ipar Korea",
        "alpha2Code": "KP",
        "alpha3Code": "PRK",
        "numericCode": "408",
        "callingCode": "+850"
    },
    {
        "name": "Iparraldeko Mariana Uharteak",
        "alpha2Code": "MP",
        "alpha3Code": "MNP",
        "numericCode": "580",
        "callingCode": "+1670"
    },
    {
        "name": "Norvegia",
        "alpha2Code": "NO",
        "alpha3Code": "NOR",
        "numericCode": "578",
        "callingCode": "+47"
    },
    {
        "name": "Oman",
        "alpha2Code": "OM",
        "alpha3Code": "OMN",
        "numericCode": "512",
        "callingCode": "+968"
    },
    {
        "name": "Pakistan",
        "alpha2Code": "PK",
        "alpha3Code": "PAK",
        "numericCode": "586",
        "callingCode": "+92"
    },
    {
        "name": "Palau",
        "alpha2Code": "PW",
        "alpha3Code": "PLW",
        "numericCode": "585",
        "callingCode": "+680"
    },
    {
        "name": "Palestina",
        "alpha2Code": "PS",
        "alpha3Code": "PSE",
        "numericCode": "275",
        "callingCode": "+970"
    },
    {
        "name": "Panama",
        "alpha2Code": "PA",
        "alpha3Code": "PAN",
        "numericCode": "591",
        "callingCode": "+507"
    },
    {
        "name": "Papua Ginea Berria",
        "alpha2Code": "PG",
        "alpha3Code": "PNG",
        "numericCode": "598",
        "callingCode": "+675"
    },
    {
        "name": "Paraguai",
        "alpha2Code": "PY",
        "alpha3Code": "PRY",
        "numericCode": "600",
        "callingCode": "+595"
    },
    {
        "name": "Peru",
        "alpha2Code": "PE",
        "alpha3Code": "PER",
        "numericCode": "604",
        "callingCode": "+51"
    },
    {
        "name": "Filipinak",
        "alpha2Code": "PH",
        "alpha3Code": "PHL",
        "numericCode": "608",
        "callingCode": "+63"
    },
    {
        "name": "Pitcairn uharteak",
        "alpha2Code": "PN",
        "alpha3Code": "PCN",
        "numericCode": "612",
        "callingCode": "+64"
    },
    {
        "name": "Polonia",
        "alpha2Code": "PL",
        "alpha3Code": "POL",
        "numericCode": "616",
        "callingCode": "+48"
    },
    {
        "name": "Portugal",
        "alpha2Code": "PT",
        "alpha3Code": "PRT",
        "numericCode": "620",
        "callingCode": "+351"
    },
    {
        "name": "Puerto Rico",
        "alpha2Code": "PR",
        "alpha3Code": "PRI",
        "numericCode": "630",
        "callingCode": "+1"
    },
    {
        "name": "Qatar",
        "alpha2Code": "QA",
        "alpha3Code": "QAT",
        "numericCode": "634",
        "callingCode": "+974"
    },
    {
        "name": "Kosovo",
        "alpha2Code": "XK",
        "alpha3Code": "KOS",
        "numericCode": null,
        "callingCode": "+383"
    },
    {
        "name": "Bilera",
        "alpha2Code": "RE",
        "alpha3Code": "REU",
        "numericCode": "638",
        "callingCode": "+262"
    },
    {
        "name": "Errumania",
        "alpha2Code": "RO",
        "alpha3Code": "ROU",
        "numericCode": "642",
        "callingCode": "+40"
    },
    {
        "name": "Errusia",
        "alpha2Code": "RU",
        "alpha3Code": "RUS",
        "numericCode": "643",
        "callingCode": "+7"
    },
    {
        "name": "Ruanda",
        "alpha2Code": "RW",
        "alpha3Code": "RWA",
        "numericCode": "646",
        "callingCode": "+250"
    },
    {
        "name": "San Bartolome",
        "alpha2Code": "BL",
        "alpha3Code": "BLM",
        "numericCode": "652",
        "callingCode": "+590"
    },
    {
        "name": "Santa Helena",
        "alpha2Code": "SH",
        "alpha3Code": "SHN",
        "numericCode": "654",
        "callingCode": "+290"
    },
    {
        "name": "San Kristobal eta Nieves",
        "alpha2Code": "KN",
        "alpha3Code": "KNA",
        "numericCode": "659",
        "callingCode": "+1869"
    },
    {
        "name": "Santa Luzia",
        "alpha2Code": "LC",
        "alpha3Code": "LCA",
        "numericCode": "662",
        "callingCode": "+1758"
    },
    {
        "name": "Saint Martin",
        "alpha2Code": "MF",
        "alpha3Code": "MAF",
        "numericCode": "663",
        "callingCode": "+590"
    },
    {
        "name": "San Pedro eta Miquelon",
        "alpha2Code": "PM",
        "alpha3Code": "SPM",
        "numericCode": "666",
        "callingCode": "+508"
    },
    {
        "name": "San Bizente eta Granadinak",
        "alpha2Code": "VC",
        "alpha3Code": "VCT",
        "numericCode": "670",
        "callingCode": "+1784"
    },
    {
        "name": "Samoa",
        "alpha2Code": "WS",
        "alpha3Code": "WSM",
        "numericCode": "882",
        "callingCode": "+685"
    },
    {
        "name": "San Marino",
        "alpha2Code": "SM",
        "alpha3Code": "SMR",
        "numericCode": "674",
        "callingCode": "+378"
    },
    {
        "name": "Santo Tomas eta Printzea",
        "alpha2Code": "ST",
        "alpha3Code": "STP",
        "numericCode": "678",
        "callingCode": "+239"
    },
    {
        "name": "Saudi Arabia",
        "alpha2Code": "SA",
        "alpha3Code": "SAU",
        "numericCode": "682",
        "callingCode": "+966"
    },
    {
        "name": "Senegal",
        "alpha2Code": "SN",
        "alpha3Code": "SEN",
        "numericCode": "686",
        "callingCode": "+221"
    },
    {
        "name": "Serbia",
        "alpha2Code": "RS",
        "alpha3Code": "SRB",
        "numericCode": "688",
        "callingCode": "+381"
    },
    {
        "name": "Seychelleak",
        "alpha2Code": "SC",
        "alpha3Code": "SYC",
        "numericCode": "690",
        "callingCode": "+248"
    },
    {
        "name": "Sierra Leone",
        "alpha2Code": "SL",
        "alpha3Code": "SLE",
        "numericCode": "694",
        "callingCode": "+232"
    },
    {
        "name": "Singapur",
        "alpha2Code": "SG",
        "alpha3Code": "SGP",
        "numericCode": "702",
        "callingCode": "+65"
    },
    {
        "name": "San Martin (Holandako zatia)",
        "alpha2Code": "SX",
        "alpha3Code": "SXM",
        "numericCode": "534",
        "callingCode": "+1721"
    },
    {
        "name": "Eslovakiar Errepublika",
        "alpha2Code": "SK",
        "alpha3Code": "SVK",
        "numericCode": "703",
        "callingCode": "+421"
    },
    {
        "name": "Eslovenia",
        "alpha2Code": "SI",
        "alpha3Code": "SVN",
        "numericCode": "705",
        "callingCode": "+386"
    },
    {
        "name": "Salomon uharteak",
        "alpha2Code": "SB",
        "alpha3Code": "SLB",
        "numericCode": "090",
        "callingCode": "+677"
    },
    {
        "name": "Somalia",
        "alpha2Code": "SO",
        "alpha3Code": "SOM",
        "numericCode": "706",
        "callingCode": "+252"
    },
    {
        "name": "Hegoafrikako Errepublika",
        "alpha2Code": "ZA",
        "alpha3Code": "ZAF",
        "numericCode": "710",
        "callingCode": "+27"
    },
    {
        "name": "Hego Georgia eta Hego Sandwich uharteak",
        "alpha2Code": "GS",
        "alpha3Code": "SGS",
        "numericCode": "239",
        "callingCode": "+500"
    },
    {
        "name": "Hego Korea",
        "alpha2Code": "KR",
        "alpha3Code": "KOR",
        "numericCode": "410",
        "callingCode": "+82"
    },
    {
        "name": "Hego Sudan",
        "alpha2Code": "SS",
        "alpha3Code": "SSD",
        "numericCode": "728",
        "callingCode": "+211"
    },
    {
        "name": "Espainia",
        "alpha2Code": "ES",
        "alpha3Code": "ESP",
        "numericCode": "724",
        "callingCode": "+34"
    },
    {
        "name": "Sri Lanka",
        "alpha2Code": "LK",
        "alpha3Code": "LKA",
        "numericCode": "144",
        "callingCode": "+94"
    },
    {
        "name": "Sudan",
        "alpha2Code": "SD",
        "alpha3Code": "SDN",
        "numericCode": "729",
        "callingCode": "+249"
    },
    {
        "name": "Surinam",
        "alpha2Code": "SR",
        "alpha3Code": "SUR",
        "numericCode": "740",
        "callingCode": "+597"
    },
    {
        "name": "Svalbard eta Jan Mayen uharteak",
        "alpha2Code": "SJ",
        "alpha3Code": "SJM",
        "numericCode": "744",
        "callingCode": "+4779"
    },
    {
        "name": "Suazilandia",
        "alpha2Code": "SZ",
        "alpha3Code": "SWZ",
        "numericCode": "748",
        "callingCode": "+268"
    },
    {
        "name": "Suedia",
        "alpha2Code": "SE",
        "alpha3Code": "SWE",
        "numericCode": "752",
        "callingCode": "+46"
    },
    {
        "name": "Suitza",
        "alpha2Code": "CH",
        "alpha3Code": "CHE",
        "numericCode": "756",
        "callingCode": "+41"
    },
    {
        "name": "Siria",
        "alpha2Code": "SY",
        "alpha3Code": "SYR",
        "numericCode": "760",
        "callingCode": "+963"
    },
    {
        "name": "Taiwan",
        "alpha2Code": "TW",
        "alpha3Code": "TWN",
        "numericCode": "158",
        "callingCode": "+886"
    },
    {
        "name": "Tajikistan",
        "alpha2Code": "TJ",
        "alpha3Code": "TJK",
        "numericCode": "762",
        "callingCode": "+992"
    },
    {
        "name": "Tanzania",
        "alpha2Code": "TZ",
        "alpha3Code": "TZA",
        "numericCode": "834",
        "callingCode": "+255"
    },
    {
        "name": "Thailandia",
        "alpha2Code": "TH",
        "alpha3Code": "THA",
        "numericCode": "764",
        "callingCode": "+66"
    },
    {
        "name": "Ekialdeko Timor",
        "alpha2Code": "TL",
        "alpha3Code": "TLS",
        "numericCode": "626",
        "callingCode": "+670"
    },
    {
        "name": "Togo",
        "alpha2Code": "TG",
        "alpha3Code": "TGO",
        "numericCode": "768",
        "callingCode": "+228"
    },
    {
        "name": "Tokelau uharteak",
        "alpha2Code": "TK",
        "alpha3Code": "TKL",
        "numericCode": "772",
        "callingCode": "+690"
    },
    {
        "name": "Tonga",
        "alpha2Code": "TO",
        "alpha3Code": "TON",
        "numericCode": "776",
        "callingCode": "+676"
    },
    {
        "name": "Trinidad eta Tobago",
        "alpha2Code": "TT",
        "alpha3Code": "TTO",
        "numericCode": "780",
        "callingCode": "+1868"
    },
    {
        "name": "Tunisia",
        "alpha2Code": "TN",
        "alpha3Code": "TUN",
        "numericCode": "788",
        "callingCode": "+216"
    },
    {
        "name": "Turkia",
        "alpha2Code": "TR",
        "alpha3Code": "TUR",
        "numericCode": "792",
        "callingCode": "+90"
    },
    {
        "name": "Turkmenistan",
        "alpha2Code": "TM",
        "alpha3Code": "TKM",
        "numericCode": "795",
        "callingCode": "+993"
    },
    {
        "name": "Turks eta Caicos uharteak",
        "alpha2Code": "TC",
        "alpha3Code": "TCA",
        "numericCode": "796",
        "callingCode": "+1649"
    },
    {
        "name": "Tuvalu",
        "alpha2Code": "TV",
        "alpha3Code": "TUV",
        "numericCode": "798",
        "callingCode": "+688"
    },
    {
        "name": "Uganda",
        "alpha2Code": "UG",
        "alpha3Code": "UGA",
        "numericCode": "800",
        "callingCode": "+256"
    },
    {
        "name": "Ukraina",
        "alpha2Code": "UA",
        "alpha3Code": "UKR",
        "numericCode": "804",
        "callingCode": "+380"
    },
    {
        "name": "Arabiar Emirerri Batuak",
        "alpha2Code": "AE",
        "alpha3Code": "ARE",
        "numericCode": "784",
        "callingCode": "+971"
    },
    {
        "name": "Erresuma Batua",
        "alpha2Code": "GB",
        "alpha3Code": "GBR",
        "numericCode": "826",
        "callingCode": "+44"
    },
    {
        "name": "Estatu Batuak",
        "alpha2Code": "US",
        "alpha3Code": "USA",
        "numericCode": "840",
        "callingCode": "+1"
    },
    {
        "name": "Uruguai",
        "alpha2Code": "UY",
        "alpha3Code": "URY",
        "numericCode": "858",
        "callingCode": "+598"
    },
    {
        "name": "Uzbekistan",
        "alpha2Code": "UZ",
        "alpha3Code": "UZB",
        "numericCode": "860",
        "callingCode": "+998"
    },
    {
        "name": "Vanuatu",
        "alpha2Code": "VU",
        "alpha3Code": "VUT",
        "numericCode": "548",
        "callingCode": "+678"
    },
    {
        "name": "Venezuela",
        "alpha2Code": "VE",
        "alpha3Code": "VEN",
        "numericCode": "862",
        "callingCode": "+58"
    },
    {
        "name": "Vietnam",
        "alpha2Code": "VN",
        "alpha3Code": "VNM",
        "numericCode": "704",
        "callingCode": "+84"
    },
    {
        "name": "Wallis eta Futuna",
        "alpha2Code": "WF",
        "alpha3Code": "WLF",
        "numericCode": "876",
        "callingCode": "+681"
    },
    {
        "name": "Mendebaldeko Sahara",
        "alpha2Code": "EH",
        "alpha3Code": "ESH",
        "numericCode": "732",
        "callingCode": "+212"
    },
    {
        "name": "Yemen",
        "alpha2Code": "YE",
        "alpha3Code": "YEM",
        "numericCode": "887",
        "callingCode": "+967"
    },
    {
        "name": "Zambia",
        "alpha2Code": "ZM",
        "alpha3Code": "ZMB",
        "numericCode": "894",
        "callingCode": "+260"
    },
    {
        "name": "Zimbabwe",
        "alpha2Code": "ZW",
        "alpha3Code": "ZWE",
        "numericCode": "716",
        "callingCode": "+263"
    }
];

var eu = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB_EU: COUNTRIES_DB_EU
});

const COUNTRIES_DB_NL = [
    {
        name: 'Afghanistan',
        alpha2Code: 'AF',
        alpha3Code: 'AFG',
        numericCode: '004',
        callingCode: '+93'
    },
    {
        name: 'Ålandeilanden',
        alpha2Code: 'AX',
        alpha3Code: 'ALA',
        numericCode: '248',
        callingCode: '+358'
    },
    {
        name: 'Albanië',
        alpha2Code: 'AL',
        alpha3Code: 'ALB',
        numericCode: '008',
        callingCode: '+355'
    },
    {
        name: 'Algerije',
        alpha2Code: 'DZ',
        alpha3Code: 'DZA',
        numericCode: '012',
        callingCode: '+213'
    },
    {
        name: 'Amerikaans Samoa',
        alpha2Code: 'AS',
        alpha3Code: 'ASM',
        numericCode: '016',
        callingCode: '+1684'
    },
    {
        name: 'Andorra',
        alpha2Code: 'AD',
        alpha3Code: 'AND',
        numericCode: '020',
        callingCode: '+376'
    },
    {
        name: 'Angola',
        alpha2Code: 'AO',
        alpha3Code: 'AGO',
        numericCode: '024',
        callingCode: '+244'
    },
    {
        name: 'Anguilla',
        alpha2Code: 'AI',
        alpha3Code: 'AIA',
        numericCode: '660',
        callingCode: '+1264'
    },
    {
        name: 'Antarctica',
        alpha2Code: 'AQ',
        alpha3Code: 'ATA',
        numericCode: '010',
        callingCode: '+672'
    },
    {
        name: 'Antigua en Barbuda',
        alpha2Code: 'AG',
        alpha3Code: 'ATG',
        numericCode: '028',
        callingCode: '+1268'
    },
    {
        name: 'Argentinië',
        alpha2Code: 'AR',
        alpha3Code: 'ARG',
        numericCode: '032',
        callingCode: '+54'
    },
    {
        name: 'Armenië',
        alpha2Code: 'AM',
        alpha3Code: 'ARM',
        numericCode: '051',
        callingCode: '+374'
    },
    {
        name: 'Aruba',
        alpha2Code: 'AW',
        alpha3Code: 'ABW',
        numericCode: '533',
        callingCode: '+297'
    },
    {
        name: 'Australië',
        alpha2Code: 'AU',
        alpha3Code: 'AUS',
        numericCode: '036',
        callingCode: '+61'
    },
    {
        name: 'Oostenrijk',
        alpha2Code: 'AT',
        alpha3Code: 'AUT',
        numericCode: '040',
        callingCode: '+43'
    },
    {
        name: 'Azerbeidzjan',
        alpha2Code: 'AZ',
        alpha3Code: 'AZE',
        numericCode: '031',
        callingCode: '+994'
    },
    {
        name: 'Bahama’s',
        alpha2Code: 'BS',
        alpha3Code: 'BHS',
        numericCode: '044',
        callingCode: '+1242'
    },
    {
        name: 'Bahrein',
        alpha2Code: 'BH',
        alpha3Code: 'BHR',
        numericCode: '048',
        callingCode: '+973'
    },
    {
        name: 'Bangladesh',
        alpha2Code: 'BD',
        alpha3Code: 'BGD',
        numericCode: '050',
        callingCode: '+880'
    },
    {
        name: 'Barbados',
        alpha2Code: 'BB',
        alpha3Code: 'BRB',
        numericCode: '052',
        callingCode: '+1246'
    },
    {
        name: 'Wit-Rusland',
        alpha2Code: 'BY',
        alpha3Code: 'BLR',
        numericCode: '112',
        callingCode: '+375'
    },
    {
        name: 'België',
        alpha2Code: 'BE',
        alpha3Code: 'BEL',
        numericCode: '056',
        callingCode: '+32'
    },
    {
        name: 'Belize',
        alpha2Code: 'BZ',
        alpha3Code: 'BLZ',
        numericCode: '084',
        callingCode: '+501'
    },
    {
        name: 'Benin',
        alpha2Code: 'BJ',
        alpha3Code: 'BEN',
        numericCode: '204',
        callingCode: '+229'
    },
    {
        name: 'Bermuda',
        alpha2Code: 'BM',
        alpha3Code: 'BMU',
        numericCode: '060',
        callingCode: '+1441'
    },
    {
        name: 'Bhutan',
        alpha2Code: 'BT',
        alpha3Code: 'BTN',
        numericCode: '064',
        callingCode: '+975'
    },
    {
        name: 'Bolivia',
        alpha2Code: 'BO',
        alpha3Code: 'BOL',
        numericCode: '068',
        callingCode: '+591'
    },
    {
        name: 'Bonaire, Sint Eustatius en Saba',
        alpha2Code: 'BQ',
        alpha3Code: 'BES',
        numericCode: '535',
        callingCode: '+5997'
    },
    {
        name: 'Bosnië en Herzegovina',
        alpha2Code: 'BA',
        alpha3Code: 'BIH',
        numericCode: '070',
        callingCode: '+387'
    },
    {
        name: 'Botswana',
        alpha2Code: 'BW',
        alpha3Code: 'BWA',
        numericCode: '072',
        callingCode: '+267'
    },
    {
        name: 'Bouveteiland',
        alpha2Code: 'BV',
        alpha3Code: 'BVT',
        numericCode: '074',
        callingCode: '+'
    },
    {
        name: 'Brazilië',
        alpha2Code: 'BR',
        alpha3Code: 'BRA',
        numericCode: '076',
        callingCode: '+55'
    },
    {
        name: 'Britse Gebieden in de Indische Oceaan',
        alpha2Code: 'IO',
        alpha3Code: 'IOT',
        numericCode: '086',
        callingCode: '+246'
    },
    {
        name: 'Kleine afgelegen eilanden van de Verenigde Staten',
        alpha2Code: 'UM',
        alpha3Code: 'UMI',
        numericCode: '581',
        callingCode: '+'
    },
    {
        name: 'Britse Maagdeneilanden',
        alpha2Code: 'VG',
        alpha3Code: 'VGB',
        numericCode: '092',
        callingCode: '+1284'
    },
    {
        name: 'Verenigde Staten Maagdeneilanden',
        alpha2Code: 'VI',
        alpha3Code: 'VIR',
        numericCode: '850',
        callingCode: '+1 340'
    },
    {
        name: 'Brunei',
        alpha2Code: 'BN',
        alpha3Code: 'BRN',
        numericCode: '096',
        callingCode: '+673'
    },
    {
        name: 'Bulgarije',
        alpha2Code: 'BG',
        alpha3Code: 'BGR',
        numericCode: '100',
        callingCode: '+359'
    },
    {
        name: 'Burkina Faso',
        alpha2Code: 'BF',
        alpha3Code: 'BFA',
        numericCode: '854',
        callingCode: '+226'
    },
    {
        name: 'Burundi',
        alpha2Code: 'BI',
        alpha3Code: 'BDI',
        numericCode: '108',
        callingCode: '+257'
    },
    {
        name: 'Cambodja',
        alpha2Code: 'KH',
        alpha3Code: 'KHM',
        numericCode: '116',
        callingCode: '+855'
    },
    {
        name: 'Kameroen',
        alpha2Code: 'CM',
        alpha3Code: 'CMR',
        numericCode: '120',
        callingCode: '+237'
    },
    {
        name: 'Canada',
        alpha2Code: 'CA',
        alpha3Code: 'CAN',
        numericCode: '124',
        callingCode: '+1'
    },
    {
        name: 'Kaapverdië',
        alpha2Code: 'CV',
        alpha3Code: 'CPV',
        numericCode: '132',
        callingCode: '+238'
    },
    {
        name: 'Caymaneilanden',
        alpha2Code: 'KY',
        alpha3Code: 'CYM',
        numericCode: '136',
        callingCode: '+1345'
    },
    {
        name: 'Centraal-Afrikaanse Republiek',
        alpha2Code: 'CF',
        alpha3Code: 'CAF',
        numericCode: '140',
        callingCode: '+236'
    },
    {
        name: 'Tsjaad',
        alpha2Code: 'TD',
        alpha3Code: 'TCD',
        numericCode: '148',
        callingCode: '+235'
    },
    {
        name: 'Chili',
        alpha2Code: 'CL',
        alpha3Code: 'CHL',
        numericCode: '152',
        callingCode: '+56'
    },
    {
        name: 'China',
        alpha2Code: 'CN',
        alpha3Code: 'CHN',
        numericCode: '156',
        callingCode: '+86'
    },
    {
        name: 'Christmaseiland',
        alpha2Code: 'CX',
        alpha3Code: 'CXR',
        numericCode: '162',
        callingCode: '+61'
    },
    {
        name: 'Cocoseilanden',
        alpha2Code: 'CC',
        alpha3Code: 'CCK',
        numericCode: '166',
        callingCode: '+61'
    },
    {
        name: 'Colombia',
        alpha2Code: 'CO',
        alpha3Code: 'COL',
        numericCode: '170',
        callingCode: '+57'
    },
    {
        name: 'Comoren',
        alpha2Code: 'KM',
        alpha3Code: 'COM',
        numericCode: '174',
        callingCode: '+269'
    },
    {
        name: 'Congo [Republiek]',
        alpha2Code: 'CG',
        alpha3Code: 'COG',
        numericCode: '178',
        callingCode: '+242'
    },
    {
        name: 'Congo [DRC]',
        alpha2Code: 'CD',
        alpha3Code: 'COD',
        numericCode: '180',
        callingCode: '+243'
    },
    {
        name: 'Cookeilanden',
        alpha2Code: 'CK',
        alpha3Code: 'COK',
        numericCode: '184',
        callingCode: '+682'
    },
    {
        name: 'Costa Rica',
        alpha2Code: 'CR',
        alpha3Code: 'CRI',
        numericCode: '188',
        callingCode: '+506'
    },
    {
        name: 'Kroatië',
        alpha2Code: 'HR',
        alpha3Code: 'HRV',
        numericCode: '191',
        callingCode: '+385'
    },
    {
        name: 'Cuba',
        alpha2Code: 'CU',
        alpha3Code: 'CUB',
        numericCode: '192',
        callingCode: '+53'
    },
    {
        name: 'Curaçao',
        alpha2Code: 'CW',
        alpha3Code: 'CUW',
        numericCode: '531',
        callingCode: '+599'
    },
    {
        name: 'Cyprus',
        alpha2Code: 'CY',
        alpha3Code: 'CYP',
        numericCode: '196',
        callingCode: '+357'
    },
    {
        name: 'Tsjechië',
        alpha2Code: 'CZ',
        alpha3Code: 'CZE',
        numericCode: '203',
        callingCode: '+420'
    },
    {
        name: 'Denemarken',
        alpha2Code: 'DK',
        alpha3Code: 'DNK',
        numericCode: '208',
        callingCode: '+45'
    },
    {
        name: 'Djibouti',
        alpha2Code: 'DJ',
        alpha3Code: 'DJI',
        numericCode: '262',
        callingCode: '+253'
    },
    {
        name: 'Dominica',
        alpha2Code: 'DM',
        alpha3Code: 'DMA',
        numericCode: '212',
        callingCode: '+1767'
    },
    {
        name: 'Dominicaanse Republiek',
        alpha2Code: 'DO',
        alpha3Code: 'DOM',
        numericCode: '214',
        callingCode: '+1809'
    },
    {
        name: 'Ecuador',
        alpha2Code: 'EC',
        alpha3Code: 'ECU',
        numericCode: '218',
        callingCode: '+593'
    },
    {
        name: 'Egypte',
        alpha2Code: 'EG',
        alpha3Code: 'EGY',
        numericCode: '818',
        callingCode: '+20'
    },
    {
        name: 'El Salvador',
        alpha2Code: 'SV',
        alpha3Code: 'SLV',
        numericCode: '222',
        callingCode: '+503'
    },
    {
        name: 'Equatoriaal-Guinea',
        alpha2Code: 'GQ',
        alpha3Code: 'GNQ',
        numericCode: '226',
        callingCode: '+240'
    },
    {
        name: 'Eritrea',
        alpha2Code: 'ER',
        alpha3Code: 'ERI',
        numericCode: '232',
        callingCode: '+291'
    },
    {
        name: 'Estland',
        alpha2Code: 'EE',
        alpha3Code: 'EST',
        numericCode: '233',
        callingCode: '+372'
    },
    {
        name: 'Ethiopië',
        alpha2Code: 'ET',
        alpha3Code: 'ETH',
        numericCode: '231',
        callingCode: '+251'
    },
    {
        name: 'Falklandeilanden [Islas Malvinas]',
        alpha2Code: 'FK',
        alpha3Code: 'FLK',
        numericCode: '238',
        callingCode: '+500'
    },
    {
        name: 'Faeröer',
        alpha2Code: 'FO',
        alpha3Code: 'FRO',
        numericCode: '234',
        callingCode: '+298'
    },
    {
        name: 'Fiji',
        alpha2Code: 'FJ',
        alpha3Code: 'FJI',
        numericCode: '242',
        callingCode: '+679'
    },
    {
        name: 'Finland',
        alpha2Code: 'FI',
        alpha3Code: 'FIN',
        numericCode: '246',
        callingCode: '+358'
    },
    {
        name: 'Frankrijk',
        alpha2Code: 'FR',
        alpha3Code: 'FRA',
        numericCode: '250',
        callingCode: '+33'
    },
    {
        name: 'Frans-Guyana',
        alpha2Code: 'GF',
        alpha3Code: 'GUF',
        numericCode: '254',
        callingCode: '+594'
    },
    {
        name: 'Frans-Polynesië',
        alpha2Code: 'PF',
        alpha3Code: 'PYF',
        numericCode: '258',
        callingCode: '+689'
    },
    {
        name: 'Franse Gebieden in de zuidelijke Indische Oceaan',
        alpha2Code: 'TF',
        alpha3Code: 'ATF',
        numericCode: '260',
        callingCode: '+'
    },
    {
        name: 'Gabon',
        alpha2Code: 'GA',
        alpha3Code: 'GAB',
        numericCode: '266',
        callingCode: '+241'
    },
    {
        name: 'Gambia',
        alpha2Code: 'GM',
        alpha3Code: 'GMB',
        numericCode: '270',
        callingCode: '+220'
    },
    {
        name: 'Georgië',
        alpha2Code: 'GE',
        alpha3Code: 'GEO',
        numericCode: '268',
        callingCode: '+995'
    },
    {
        name: 'Duitsland',
        alpha2Code: 'DE',
        alpha3Code: 'DEU',
        numericCode: '276',
        callingCode: '+49'
    },
    {
        name: 'Ghana',
        alpha2Code: 'GH',
        alpha3Code: 'GHA',
        numericCode: '288',
        callingCode: '+233'
    },
    {
        name: 'Gibraltar',
        alpha2Code: 'GI',
        alpha3Code: 'GIB',
        numericCode: '292',
        callingCode: '+350'
    },
    {
        name: 'Griekenland',
        alpha2Code: 'GR',
        alpha3Code: 'GRC',
        numericCode: '300',
        callingCode: '+30'
    },
    {
        name: 'Groenland',
        alpha2Code: 'GL',
        alpha3Code: 'GRL',
        numericCode: '304',
        callingCode: '+299'
    },
    {
        name: 'Grenada',
        alpha2Code: 'GD',
        alpha3Code: 'GRD',
        numericCode: '308',
        callingCode: '+1473'
    },
    {
        name: 'Guadeloupe',
        alpha2Code: 'GP',
        alpha3Code: 'GLP',
        numericCode: '312',
        callingCode: '+590'
    },
    {
        name: 'Guam',
        alpha2Code: 'GU',
        alpha3Code: 'GUM',
        numericCode: '316',
        callingCode: '+1671'
    },
    {
        name: 'Guatemala',
        alpha2Code: 'GT',
        alpha3Code: 'GTM',
        numericCode: '320',
        callingCode: '+502'
    },
    {
        name: 'Guernsey',
        alpha2Code: 'GG',
        alpha3Code: 'GGY',
        numericCode: '831',
        callingCode: '+44'
    },
    {
        name: 'Guinee',
        alpha2Code: 'GN',
        alpha3Code: 'GIN',
        numericCode: '324',
        callingCode: '+224'
    },
    {
        name: 'Guinee-Bissau',
        alpha2Code: 'GW',
        alpha3Code: 'GNB',
        numericCode: '624',
        callingCode: '+245'
    },
    {
        name: 'Guyana',
        alpha2Code: 'GY',
        alpha3Code: 'GUY',
        numericCode: '328',
        callingCode: '+592'
    },
    {
        name: 'Haïti',
        alpha2Code: 'HT',
        alpha3Code: 'HTI',
        numericCode: '332',
        callingCode: '+509'
    },
    {
        name: 'Heard- en McDonaldeilanden',
        alpha2Code: 'HM',
        alpha3Code: 'HMD',
        numericCode: '334',
        callingCode: '+'
    },
    {
        name: 'Heilige Stoel',
        alpha2Code: 'VA',
        alpha3Code: 'VAT',
        numericCode: '336',
        callingCode: '+379'
    },
    {
        name: 'Honduras',
        alpha2Code: 'HN',
        alpha3Code: 'HND',
        numericCode: '340',
        callingCode: '+504'
    },
    {
        name: 'Hongkong',
        alpha2Code: 'HK',
        alpha3Code: 'HKG',
        numericCode: '344',
        callingCode: '+852'
    },
    {
        name: 'Hongarije',
        alpha2Code: 'HU',
        alpha3Code: 'HUN',
        numericCode: '348',
        callingCode: '+36'
    },
    {
        name: 'IJsland',
        alpha2Code: 'IS',
        alpha3Code: 'ISL',
        numericCode: '352',
        callingCode: '+354'
    },
    {
        name: 'India',
        alpha2Code: 'IN',
        alpha3Code: 'IND',
        numericCode: '356',
        callingCode: '+91'
    },
    {
        name: 'Indonesië',
        alpha2Code: 'ID',
        alpha3Code: 'IDN',
        numericCode: '360',
        callingCode: '+62'
    },
    {
        name: 'Ivoorkust',
        alpha2Code: 'CI',
        alpha3Code: 'CIV',
        numericCode: '384',
        callingCode: '+225'
    },
    {
        name: 'Iran',
        alpha2Code: 'IR',
        alpha3Code: 'IRN',
        numericCode: '364',
        callingCode: '+98'
    },
    {
        name: 'Irak',
        alpha2Code: 'IQ',
        alpha3Code: 'IRQ',
        numericCode: '368',
        callingCode: '+964'
    },
    {
        name: 'Ierland',
        alpha2Code: 'IE',
        alpha3Code: 'IRL',
        numericCode: '372',
        callingCode: '+353'
    },
    {
        name: 'Isle of Man',
        alpha2Code: 'IM',
        alpha3Code: 'IMN',
        numericCode: '833',
        callingCode: '+44'
    },
    {
        name: 'Israël',
        alpha2Code: 'IL',
        alpha3Code: 'ISR',
        numericCode: '376',
        callingCode: '+972'
    },
    {
        name: 'Italië',
        alpha2Code: 'IT',
        alpha3Code: 'ITA',
        numericCode: '380',
        callingCode: '+39'
    },
    {
        name: 'Jamaica',
        alpha2Code: 'JM',
        alpha3Code: 'JAM',
        numericCode: '388',
        callingCode: '+1876'
    },
    {
        name: 'Japan',
        alpha2Code: 'JP',
        alpha3Code: 'JPN',
        numericCode: '392',
        callingCode: '+81'
    },
    {
        name: 'Jersey',
        alpha2Code: 'JE',
        alpha3Code: 'JEY',
        numericCode: '832',
        callingCode: '+44'
    },
    {
        name: 'Jordanië',
        alpha2Code: 'JO',
        alpha3Code: 'JOR',
        numericCode: '400',
        callingCode: '+962'
    },
    {
        name: 'Kazachstan',
        alpha2Code: 'KZ',
        alpha3Code: 'KAZ',
        numericCode: '398',
        callingCode: '+7'
    },
    {
        name: 'Kenia',
        alpha2Code: 'KE',
        alpha3Code: 'KEN',
        numericCode: '404',
        callingCode: '+254'
    },
    {
        name: 'Kiribati',
        alpha2Code: 'KI',
        alpha3Code: 'KIR',
        numericCode: '296',
        callingCode: '+686'
    },
    {
        name: 'Koeweit',
        alpha2Code: 'KW',
        alpha3Code: 'KWT',
        numericCode: '414',
        callingCode: '+965'
    },
    {
        name: 'Kirgizië',
        alpha2Code: 'KG',
        alpha3Code: 'KGZ',
        numericCode: '417',
        callingCode: '+996'
    },
    {
        name: 'Laos',
        alpha2Code: 'LA',
        alpha3Code: 'LAO',
        numericCode: '418',
        callingCode: '+856'
    },
    {
        name: 'Letland',
        alpha2Code: 'LV',
        alpha3Code: 'LVA',
        numericCode: '428',
        callingCode: '+371'
    },
    {
        name: 'Libanon',
        alpha2Code: 'LB',
        alpha3Code: 'LBN',
        numericCode: '422',
        callingCode: '+961'
    },
    {
        name: 'Lesotho',
        alpha2Code: 'LS',
        alpha3Code: 'LSO',
        numericCode: '426',
        callingCode: '+266'
    },
    {
        name: 'Liberia',
        alpha2Code: 'LR',
        alpha3Code: 'LBR',
        numericCode: '430',
        callingCode: '+231'
    },
    {
        name: 'Libië',
        alpha2Code: 'LY',
        alpha3Code: 'LBY',
        numericCode: '434',
        callingCode: '+218'
    },
    {
        name: 'Liechtenstein',
        alpha2Code: 'LI',
        alpha3Code: 'LIE',
        numericCode: '438',
        callingCode: '+423'
    },
    {
        name: 'Litouwen',
        alpha2Code: 'LT',
        alpha3Code: 'LTU',
        numericCode: '440',
        callingCode: '+370'
    },
    {
        name: 'Luxemburg',
        alpha2Code: 'LU',
        alpha3Code: 'LUX',
        numericCode: '442',
        callingCode: '+352'
    },
    {
        name: 'Macao',
        alpha2Code: 'MO',
        alpha3Code: 'MAC',
        numericCode: '446',
        callingCode: '+853'
    },
    {
        name: 'Macedonië',
        alpha2Code: 'MK',
        alpha3Code: 'MKD',
        numericCode: '807',
        callingCode: '+389'
    },
    {
        name: 'Madagaskar',
        alpha2Code: 'MG',
        alpha3Code: 'MDG',
        numericCode: '450',
        callingCode: '+261'
    },
    {
        name: 'Malawi',
        alpha2Code: 'MW',
        alpha3Code: 'MWI',
        numericCode: '454',
        callingCode: '+265'
    },
    {
        name: 'Maleisië',
        alpha2Code: 'MY',
        alpha3Code: 'MYS',
        numericCode: '458',
        callingCode: '+60'
    },
    {
        name: 'Maldiven',
        alpha2Code: 'MV',
        alpha3Code: 'MDV',
        numericCode: '462',
        callingCode: '+960'
    },
    {
        name: 'Mali',
        alpha2Code: 'ML',
        alpha3Code: 'MLI',
        numericCode: '466',
        callingCode: '+223'
    },
    {
        name: 'Malta',
        alpha2Code: 'MT',
        alpha3Code: 'MLT',
        numericCode: '470',
        callingCode: '+356'
    },
    {
        name: 'Marshalleilanden',
        alpha2Code: 'MH',
        alpha3Code: 'MHL',
        numericCode: '584',
        callingCode: '+692'
    },
    {
        name: 'Martinique',
        alpha2Code: 'MQ',
        alpha3Code: 'MTQ',
        numericCode: '474',
        callingCode: '+596'
    },
    {
        name: 'Mauritanië',
        alpha2Code: 'MR',
        alpha3Code: 'MRT',
        numericCode: '478',
        callingCode: '+222'
    },
    {
        name: 'Mauritius',
        alpha2Code: 'MU',
        alpha3Code: 'MUS',
        numericCode: '480',
        callingCode: '+230'
    },
    {
        name: 'Mayotte',
        alpha2Code: 'YT',
        alpha3Code: 'MYT',
        numericCode: '175',
        callingCode: '+262'
    },
    {
        name: 'Mexico',
        alpha2Code: 'MX',
        alpha3Code: 'MEX',
        numericCode: '484',
        callingCode: '+52'
    },
    {
        name: 'Micronesië',
        alpha2Code: 'FM',
        alpha3Code: 'FSM',
        numericCode: '583',
        callingCode: '+691'
    },
    {
        name: 'Moldavië',
        alpha2Code: 'MD',
        alpha3Code: 'MDA',
        numericCode: '498',
        callingCode: '+373'
    },
    {
        name: 'Monaco',
        alpha2Code: 'MC',
        alpha3Code: 'MCO',
        numericCode: '492',
        callingCode: '+377'
    },
    {
        name: 'Mongolië',
        alpha2Code: 'MN',
        alpha3Code: 'MNG',
        numericCode: '496',
        callingCode: '+976'
    },
    {
        name: 'Montenegro',
        alpha2Code: 'ME',
        alpha3Code: 'MNE',
        numericCode: '499',
        callingCode: '+382'
    },
    {
        name: 'Montserrat',
        alpha2Code: 'MS',
        alpha3Code: 'MSR',
        numericCode: '500',
        callingCode: '+1664'
    },
    {
        name: 'Marokko',
        alpha2Code: 'MA',
        alpha3Code: 'MAR',
        numericCode: '504',
        callingCode: '+212'
    },
    {
        name: 'Mozambique',
        alpha2Code: 'MZ',
        alpha3Code: 'MOZ',
        numericCode: '508',
        callingCode: '+258'
    },
    {
        name: 'Myanmar',
        alpha2Code: 'MM',
        alpha3Code: 'MMR',
        numericCode: '104',
        callingCode: '+95'
    },
    {
        name: 'Namibië',
        alpha2Code: 'NA',
        alpha3Code: 'NAM',
        numericCode: '516',
        callingCode: '+264'
    },
    {
        name: 'Nauru',
        alpha2Code: 'NR',
        alpha3Code: 'NRU',
        numericCode: '520',
        callingCode: '+674'
    },
    {
        name: 'Nepal',
        alpha2Code: 'NP',
        alpha3Code: 'NPL',
        numericCode: '524',
        callingCode: '+977'
    },
    {
        name: 'Nederland',
        alpha2Code: 'NL',
        alpha3Code: 'NLD',
        numericCode: '528',
        callingCode: '+31'
    },
    {
        name: 'Nieuw-Caledonië',
        alpha2Code: 'NC',
        alpha3Code: 'NCL',
        numericCode: '540',
        callingCode: '+687'
    },
    {
        name: 'Nieuw-Zeeland',
        alpha2Code: 'NZ',
        alpha3Code: 'NZL',
        numericCode: '554',
        callingCode: '+64'
    },
    {
        name: 'Nicaragua',
        alpha2Code: 'NI',
        alpha3Code: 'NIC',
        numericCode: '558',
        callingCode: '+505'
    },
    {
        name: 'Niger',
        alpha2Code: 'NE',
        alpha3Code: 'NER',
        numericCode: '562',
        callingCode: '+227'
    },
    {
        name: 'Nigeria',
        alpha2Code: 'NG',
        alpha3Code: 'NGA',
        numericCode: '566',
        callingCode: '+234'
    },
    {
        name: 'Niue',
        alpha2Code: 'NU',
        alpha3Code: 'NIU',
        numericCode: '570',
        callingCode: '+683'
    },
    {
        name: 'Norfolkeiland',
        alpha2Code: 'NF',
        alpha3Code: 'NFK',
        numericCode: '574',
        callingCode: '+672'
    },
    {
        name: 'Noord-Korea',
        alpha2Code: 'KP',
        alpha3Code: 'PRK',
        numericCode: '408',
        callingCode: '+850'
    },
    {
        name: 'Noordelijke Marianeneilanden',
        alpha2Code: 'MP',
        alpha3Code: 'MNP',
        numericCode: '580',
        callingCode: '+1670'
    },
    {
        name: 'Noorwegen',
        alpha2Code: 'NO',
        alpha3Code: 'NOR',
        numericCode: '578',
        callingCode: '+47'
    },
    {
        name: 'Oman',
        alpha2Code: 'OM',
        alpha3Code: 'OMN',
        numericCode: '512',
        callingCode: '+968'
    },
    {
        name: 'Pakistan',
        alpha2Code: 'PK',
        alpha3Code: 'PAK',
        numericCode: '586',
        callingCode: '+92'
    },
    {
        name: 'Palau',
        alpha2Code: 'PW',
        alpha3Code: 'PLW',
        numericCode: '585',
        callingCode: '+680'
    },
    {
        name: 'Palestijnse gebieden',
        alpha2Code: 'PS',
        alpha3Code: 'PSE',
        numericCode: '275',
        callingCode: '+970'
    },
    {
        name: 'Panama',
        alpha2Code: 'PA',
        alpha3Code: 'PAN',
        numericCode: '591',
        callingCode: '+507'
    },
    {
        name: 'Papoea-Nieuw-Guinea',
        alpha2Code: 'PG',
        alpha3Code: 'PNG',
        numericCode: '598',
        callingCode: '+675'
    },
    {
        name: 'Paraguay',
        alpha2Code: 'PY',
        alpha3Code: 'PRY',
        numericCode: '600',
        callingCode: '+595'
    },
    {
        name: 'Peru',
        alpha2Code: 'PE',
        alpha3Code: 'PER',
        numericCode: '604',
        callingCode: '+51'
    },
    {
        name: 'Filipijnen',
        alpha2Code: 'PH',
        alpha3Code: 'PHL',
        numericCode: '608',
        callingCode: '+63'
    },
    {
        name: 'Pitcairneilanden',
        alpha2Code: 'PN',
        alpha3Code: 'PCN',
        numericCode: '612',
        callingCode: '+64'
    },
    {
        name: 'Polen',
        alpha2Code: 'PL',
        alpha3Code: 'POL',
        numericCode: '616',
        callingCode: '+48'
    },
    {
        name: 'Portugal',
        alpha2Code: 'PT',
        alpha3Code: 'PRT',
        numericCode: '620',
        callingCode: '+351'
    },
    {
        name: 'Puerto Rico',
        alpha2Code: 'PR',
        alpha3Code: 'PRI',
        numericCode: '630',
        callingCode: '+1'
    },
    {
        name: 'Qatar',
        alpha2Code: 'QA',
        alpha3Code: 'QAT',
        numericCode: '634',
        callingCode: '+974'
    },
    {
        name: 'Kosovo',
        alpha2Code: 'XK',
        alpha3Code: 'KOS',
        numericCode: null,
        callingCode: '+383'
    },
    {
        name: 'Réunion',
        alpha2Code: 'RE',
        alpha3Code: 'REU',
        numericCode: '638',
        callingCode: '+262'
    },
    {
        name: 'Roemenië',
        alpha2Code: 'RO',
        alpha3Code: 'ROU',
        numericCode: '642',
        callingCode: '+40'
    },
    {
        name: 'Rusland',
        alpha2Code: 'RU',
        alpha3Code: 'RUS',
        numericCode: '643',
        callingCode: '+7'
    },
    {
        name: 'Rwanda',
        alpha2Code: 'RW',
        alpha3Code: 'RWA',
        numericCode: '646',
        callingCode: '+250'
    },
    {
        name: 'Saint Barthélemy',
        alpha2Code: 'BL',
        alpha3Code: 'BLM',
        numericCode: '652',
        callingCode: '+590'
    },
    {
        name: 'Sint-Helena',
        alpha2Code: 'SH',
        alpha3Code: 'SHN',
        numericCode: '654',
        callingCode: '+290'
    },
    {
        name: 'Saint Kitts en Nevis',
        alpha2Code: 'KN',
        alpha3Code: 'KNA',
        numericCode: '659',
        callingCode: '+1869'
    },
    {
        name: 'Saint Lucia',
        alpha2Code: 'LC',
        alpha3Code: 'LCA',
        numericCode: '662',
        callingCode: '+1758'
    },
    {
        name: 'Saint-Martin',
        alpha2Code: 'MF',
        alpha3Code: 'MAF',
        numericCode: '663',
        callingCode: '+590'
    },
    {
        name: 'Saint Pierre en Miquelon',
        alpha2Code: 'PM',
        alpha3Code: 'SPM',
        numericCode: '666',
        callingCode: '+508'
    },
    {
        name: 'Saint Vincent en de Grenadines',
        alpha2Code: 'VC',
        alpha3Code: 'VCT',
        numericCode: '670',
        callingCode: '+1784'
    },
    {
        name: 'Samoa',
        alpha2Code: 'WS',
        alpha3Code: 'WSM',
        numericCode: '882',
        callingCode: '+685'
    },
    {
        name: 'San Marino',
        alpha2Code: 'SM',
        alpha3Code: 'SMR',
        numericCode: '674',
        callingCode: '+378'
    },
    {
        name: 'Sao Tomé en Principe',
        alpha2Code: 'ST',
        alpha3Code: 'STP',
        numericCode: '678',
        callingCode: '+239'
    },
    {
        name: 'Saoedi-Arabië',
        alpha2Code: 'SA',
        alpha3Code: 'SAU',
        numericCode: '682',
        callingCode: '+966'
    },
    {
        name: 'Senegal',
        alpha2Code: 'SN',
        alpha3Code: 'SEN',
        numericCode: '686',
        callingCode: '+221'
    },
    {
        name: 'Servië',
        alpha2Code: 'RS',
        alpha3Code: 'SRB',
        numericCode: '688',
        callingCode: '+381'
    },
    {
        name: 'Seychellen',
        alpha2Code: 'SC',
        alpha3Code: 'SYC',
        numericCode: '690',
        callingCode: '+248'
    },
    {
        name: 'Sierra Leone',
        alpha2Code: 'SL',
        alpha3Code: 'SLE',
        numericCode: '694',
        callingCode: '+232'
    },
    {
        name: 'Singapore',
        alpha2Code: 'SG',
        alpha3Code: 'SGP',
        numericCode: '702',
        callingCode: '+65'
    },
    {
        name: 'Sint Maarten',
        alpha2Code: 'SX',
        alpha3Code: 'SXM',
        numericCode: '534',
        callingCode: '+1721'
    },
    {
        name: 'Slowakije',
        alpha2Code: 'SK',
        alpha3Code: 'SVK',
        numericCode: '703',
        callingCode: '+421'
    },
    {
        name: 'Slovenië',
        alpha2Code: 'SI',
        alpha3Code: 'SVN',
        numericCode: '705',
        callingCode: '+386'
    },
    {
        name: 'Salomonseilanden',
        alpha2Code: 'SB',
        alpha3Code: 'SLB',
        numericCode: '090',
        callingCode: '+677'
    },
    {
        name: 'Somalië',
        alpha2Code: 'SO',
        alpha3Code: 'SOM',
        numericCode: '706',
        callingCode: '+252'
    },
    {
        name: 'Zuid-Afrika',
        alpha2Code: 'ZA',
        alpha3Code: 'ZAF',
        numericCode: '710',
        callingCode: '+27'
    },
    {
        name: 'Zuid-Georgia en Zuidelijke Sandwicheilanden',
        alpha2Code: 'GS',
        alpha3Code: 'SGS',
        numericCode: '239',
        callingCode: '+500'
    },
    {
        name: 'Zuid-Korea',
        alpha2Code: 'KR',
        alpha3Code: 'KOR',
        numericCode: '410',
        callingCode: '+82'
    },
    {
        name: 'Zuid-Soedan',
        alpha2Code: 'SS',
        alpha3Code: 'SSD',
        numericCode: '728',
        callingCode: '+211'
    },
    {
        name: 'Spanje',
        alpha2Code: 'ES',
        alpha3Code: 'ESP',
        numericCode: '724',
        callingCode: '+34'
    },
    {
        name: 'Sri Lanka',
        alpha2Code: 'LK',
        alpha3Code: 'LKA',
        numericCode: '144',
        callingCode: '+94'
    },
    {
        name: 'Soedan',
        alpha2Code: 'SD',
        alpha3Code: 'SDN',
        numericCode: '729',
        callingCode: '+249'
    },
    {
        name: 'Suriname',
        alpha2Code: 'SR',
        alpha3Code: 'SUR',
        numericCode: '740',
        callingCode: '+597'
    },
    {
        name: 'Svalbard en Jan Mayen',
        alpha2Code: 'SJ',
        alpha3Code: 'SJM',
        numericCode: '744',
        callingCode: '+4779'
    },
    {
        name: 'Swaziland',
        alpha2Code: 'SZ',
        alpha3Code: 'SWZ',
        numericCode: '748',
        callingCode: '+268'
    },
    {
        name: 'Zweden',
        alpha2Code: 'SE',
        alpha3Code: 'SWE',
        numericCode: '752',
        callingCode: '+46'
    },
    {
        name: 'Zwitserland',
        alpha2Code: 'CH',
        alpha3Code: 'CHE',
        numericCode: '756',
        callingCode: '+41'
    },
    {
        name: 'Syrië',
        alpha2Code: 'SY',
        alpha3Code: 'SYR',
        numericCode: '760',
        callingCode: '+963'
    },
    {
        name: 'Taiwan, China',
        alpha2Code: 'TW',
        alpha3Code: 'TWN',
        numericCode: '158',
        callingCode: '+886'
    },
    {
        name: 'Tadzjikistan',
        alpha2Code: 'TJ',
        alpha3Code: 'TJK',
        numericCode: '762',
        callingCode: '+992'
    },
    {
        name: 'Tanzania',
        alpha2Code: 'TZ',
        alpha3Code: 'TZA',
        numericCode: '834',
        callingCode: '+255'
    },
    {
        name: 'Thailand',
        alpha2Code: 'TH',
        alpha3Code: 'THA',
        numericCode: '764',
        callingCode: '+66'
    },
    {
        name: 'Oost-Timor',
        alpha2Code: 'TL',
        alpha3Code: 'TLS',
        numericCode: '626',
        callingCode: '+670'
    },
    {
        name: 'Togo',
        alpha2Code: 'TG',
        alpha3Code: 'TGO',
        numericCode: '768',
        callingCode: '+228'
    },
    {
        name: 'Tokelau',
        alpha2Code: 'TK',
        alpha3Code: 'TKL',
        numericCode: '772',
        callingCode: '+690'
    },
    {
        name: 'Tonga',
        alpha2Code: 'TO',
        alpha3Code: 'TON',
        numericCode: '776',
        callingCode: '+676'
    },
    {
        name: 'Trinidad en Tobago',
        alpha2Code: 'TT',
        alpha3Code: 'TTO',
        numericCode: '780',
        callingCode: '+1868'
    },
    {
        name: 'Tunesië',
        alpha2Code: 'TN',
        alpha3Code: 'TUN',
        numericCode: '788',
        callingCode: '+216'
    },
    {
        name: 'Turkije',
        alpha2Code: 'TR',
        alpha3Code: 'TUR',
        numericCode: '792',
        callingCode: '+90'
    },
    {
        name: 'Turkmenistan',
        alpha2Code: 'TM',
        alpha3Code: 'TKM',
        numericCode: '795',
        callingCode: '+993'
    },
    {
        name: 'Turks- en Caicoseilanden',
        alpha2Code: 'TC',
        alpha3Code: 'TCA',
        numericCode: '796',
        callingCode: '+1649'
    },
    {
        name: 'Tuvalu',
        alpha2Code: 'TV',
        alpha3Code: 'TUV',
        numericCode: '798',
        callingCode: '+688'
    },
    {
        name: 'Oeganda',
        alpha2Code: 'UG',
        alpha3Code: 'UGA',
        numericCode: '800',
        callingCode: '+256'
    },
    {
        name: 'Oekraïne',
        alpha2Code: 'UA',
        alpha3Code: 'UKR',
        numericCode: '804',
        callingCode: '+380'
    },
    {
        name: 'Verenigde Arabische Emiraten',
        alpha2Code: 'AE',
        alpha3Code: 'ARE',
        numericCode: '784',
        callingCode: '+971'
    },
    {
        name: 'Verenigd Koninkrijk',
        alpha2Code: 'GB',
        alpha3Code: 'GBR',
        numericCode: '826',
        callingCode: '+44'
    },
    {
        name: 'Verenigde Staten',
        alpha2Code: 'US',
        alpha3Code: 'USA',
        numericCode: '840',
        callingCode: '+1'
    },
    {
        name: 'Uruguay',
        alpha2Code: 'UY',
        alpha3Code: 'URY',
        numericCode: '858',
        callingCode: '+598'
    },
    {
        name: 'Oezbekistan',
        alpha2Code: 'UZ',
        alpha3Code: 'UZB',
        numericCode: '860',
        callingCode: '+998'
    },
    {
        name: 'Vanuatu',
        alpha2Code: 'VU',
        alpha3Code: 'VUT',
        numericCode: '548',
        callingCode: '+678'
    },
    {
        name: 'Venezuela',
        alpha2Code: 'VE',
        alpha3Code: 'VEN',
        numericCode: '862',
        callingCode: '+58'
    },
    {
        name: 'Vietnam',
        alpha2Code: 'VN',
        alpha3Code: 'VNM',
        numericCode: '704',
        callingCode: '+84'
    },
    {
        name: 'Wallis en Futuna',
        alpha2Code: 'WF',
        alpha3Code: 'WLF',
        numericCode: '876',
        callingCode: '+681'
    },
    {
        name: 'Westelijke Sahara',
        alpha2Code: 'EH',
        alpha3Code: 'ESH',
        numericCode: '732',
        callingCode: '+212'
    },
    {
        name: 'Jemen',
        alpha2Code: 'YE',
        alpha3Code: 'YEM',
        numericCode: '887',
        callingCode: '+967'
    },
    {
        name: 'Zambia',
        alpha2Code: 'ZM',
        alpha3Code: 'ZMB',
        numericCode: '894',
        callingCode: '+260'
    },
    {
        name: 'Zimbabwe',
        alpha2Code: 'ZW',
        alpha3Code: 'ZWE',
        numericCode: '716',
        callingCode: '+263'
    }
];

var nl = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB_NL: COUNTRIES_DB_NL
});

const COUNTRIES_DB_PT = [
    {
        name: 'Afeganistão',
        alpha2Code: 'AF',
        alpha3Code: 'AFG',
        numericCode: '004',
        callingCode: '+93'
    },
    {
        name: 'Ilhas de Aland',
        alpha2Code: 'AX',
        alpha3Code: 'ALA',
        numericCode: '248',
        callingCode: '+358'
    },
    {
        name: 'Albânia',
        alpha2Code: 'AL',
        alpha3Code: 'ALB',
        numericCode: '008',
        callingCode: '+355'
    },
    {
        name: 'Argélia',
        alpha2Code: 'DZ',
        alpha3Code: 'DZA',
        numericCode: '012',
        callingCode: '+213'
    },
    {
        name: 'Samoa Americana',
        alpha2Code: 'AS',
        alpha3Code: 'ASM',
        numericCode: '016',
        callingCode: '+1684'
    },
    {
        name: 'Andorra',
        alpha2Code: 'AD',
        alpha3Code: 'AND',
        numericCode: '020',
        callingCode: '+376'
    },
    {
        name: 'Angola',
        alpha2Code: 'AO',
        alpha3Code: 'AGO',
        numericCode: '024',
        callingCode: '+244'
    },
    {
        name: 'Anguila',
        alpha2Code: 'AI',
        alpha3Code: 'AIA',
        numericCode: '660',
        callingCode: '+1264'
    },
    {
        name: 'Antárctida',
        alpha2Code: 'AQ',
        alpha3Code: 'ATA',
        numericCode: '010',
        callingCode: '+672'
    },
    {
        name: 'Antígua e Barbuda',
        alpha2Code: 'AG',
        alpha3Code: 'ATG',
        numericCode: '028',
        callingCode: '+1268'
    },
    {
        name: 'Argentina',
        alpha2Code: 'AR',
        alpha3Code: 'ARG',
        numericCode: '032',
        callingCode: '+54'
    },
    {
        name: 'Arménia',
        alpha2Code: 'AM',
        alpha3Code: 'ARM',
        numericCode: '051',
        callingCode: '+374'
    },
    {
        name: 'Aruba',
        alpha2Code: 'AW',
        alpha3Code: 'ABW',
        numericCode: '533',
        callingCode: '+297'
    },
    {
        name: 'Austrália',
        alpha2Code: 'AU',
        alpha3Code: 'AUS',
        numericCode: '036',
        callingCode: '+61'
    },
    {
        name: 'áustria',
        alpha2Code: 'AT',
        alpha3Code: 'AUT',
        numericCode: '040',
        callingCode: '+43'
    },
    {
        name: 'Azerbaijão',
        alpha2Code: 'AZ',
        alpha3Code: 'AZE',
        numericCode: '031',
        callingCode: '+994'
    },
    {
        name: 'Baamas',
        alpha2Code: 'BS',
        alpha3Code: 'BHS',
        numericCode: '044',
        callingCode: '+1242'
    },
    {
        name: 'Barém',
        alpha2Code: 'BH',
        alpha3Code: 'BHR',
        numericCode: '048',
        callingCode: '+973'
    },
    {
        name: 'Bangladeche',
        alpha2Code: 'BD',
        alpha3Code: 'BGD',
        numericCode: '050',
        callingCode: '+880'
    },
    {
        name: 'Barbados',
        alpha2Code: 'BB',
        alpha3Code: 'BRB',
        numericCode: '052',
        callingCode: '+1246'
    },
    {
        name: 'Bielorrússia',
        alpha2Code: 'BY',
        alpha3Code: 'BLR',
        numericCode: '112',
        callingCode: '+375'
    },
    {
        name: 'Bélgica',
        alpha2Code: 'BE',
        alpha3Code: 'BEL',
        numericCode: '056',
        callingCode: '+32'
    },
    {
        name: 'Belize',
        alpha2Code: 'BZ',
        alpha3Code: 'BLZ',
        numericCode: '084',
        callingCode: '+501'
    },
    {
        name: 'Benim',
        alpha2Code: 'BJ',
        alpha3Code: 'BEN',
        numericCode: '204',
        callingCode: '+229'
    },
    {
        name: 'Bermudas',
        alpha2Code: 'BM',
        alpha3Code: 'BMU',
        numericCode: '060',
        callingCode: '+1441'
    },
    {
        name: 'Butão',
        alpha2Code: 'BT',
        alpha3Code: 'BTN',
        numericCode: '064',
        callingCode: '+975'
    },
    {
        name: 'Bolívia',
        alpha2Code: 'BO',
        alpha3Code: 'BOL',
        numericCode: '068',
        callingCode: '+591'
    },
    {
        name: 'Bonaire',
        alpha2Code: 'BQ',
        alpha3Code: 'BES',
        numericCode: '535',
        callingCode: '+5997'
    },
    {
        name: 'Bósnia e Herzegovina',
        alpha2Code: 'BA',
        alpha3Code: 'BIH',
        numericCode: '070',
        callingCode: '+387'
    },
    {
        name: 'Botsuana',
        alpha2Code: 'BW',
        alpha3Code: 'BWA',
        numericCode: '072',
        callingCode: '+267'
    },
    {
        name: 'Ilha Bouvet',
        alpha2Code: 'BV',
        alpha3Code: 'BVT',
        numericCode: '074',
        callingCode: '+'
    },
    {
        name: 'Brasil',
        alpha2Code: 'BR',
        alpha3Code: 'BRA',
        numericCode: '076',
        callingCode: '+55'
    },
    {
        name: 'Território Britânico do Oceano Índico',
        alpha2Code: 'IO',
        alpha3Code: 'IOT',
        numericCode: '086',
        callingCode: '+246'
    },
    {
        name: 'Ilhas Menores Distantes dos Estados Unidos',
        alpha2Code: 'UM',
        alpha3Code: 'UMI',
        numericCode: '581',
        callingCode: '+'
    },
    {
        name: 'Ilhas Virgens Britânicas',
        alpha2Code: 'VG',
        alpha3Code: 'VGB',
        numericCode: '092',
        callingCode: '+1284'
    },
    {
        name: 'Ilhas Virgens Americanas',
        alpha2Code: 'VI',
        alpha3Code: 'VIR',
        numericCode: '850',
        callingCode: '+1 340'
    },
    {
        name: 'Brunei',
        alpha2Code: 'BN',
        alpha3Code: 'BRN',
        numericCode: '096',
        callingCode: '+673'
    },
    {
        name: 'Bulgária',
        alpha2Code: 'BG',
        alpha3Code: 'BGR',
        numericCode: '100',
        callingCode: '+359'
    },
    {
        name: 'Burquina Faso',
        alpha2Code: 'BF',
        alpha3Code: 'BFA',
        numericCode: '854',
        callingCode: '+226'
    },
    {
        name: 'Burúndi',
        alpha2Code: 'BI',
        alpha3Code: 'BDI',
        numericCode: '108',
        callingCode: '+257'
    },
    {
        name: 'Camboja',
        alpha2Code: 'KH',
        alpha3Code: 'KHM',
        numericCode: '116',
        callingCode: '+855'
    },
    {
        name: 'Camarões',
        alpha2Code: 'CM',
        alpha3Code: 'CMR',
        numericCode: '120',
        callingCode: '+237'
    },
    {
        name: 'Canadá',
        alpha2Code: 'CA',
        alpha3Code: 'CAN',
        numericCode: '124',
        callingCode: '+1'
    },
    {
        name: 'Cabo Verde',
        alpha2Code: 'CV',
        alpha3Code: 'CPV',
        numericCode: '132',
        callingCode: '+238'
    },
    {
        name: 'Ilhas Caimão',
        alpha2Code: 'KY',
        alpha3Code: 'CYM',
        numericCode: '136',
        callingCode: '+1345'
    },
    {
        name: 'República Centro-Africana',
        alpha2Code: 'CF',
        alpha3Code: 'CAF',
        numericCode: '140',
        callingCode: '+236'
    },
    {
        name: 'Chade',
        alpha2Code: 'TD',
        alpha3Code: 'TCD',
        numericCode: '148',
        callingCode: '+235'
    },
    {
        name: 'Chile',
        alpha2Code: 'CL',
        alpha3Code: 'CHL',
        numericCode: '152',
        callingCode: '+56'
    },
    {
        name: 'China',
        alpha2Code: 'CN',
        alpha3Code: 'CHN',
        numericCode: '156',
        callingCode: '+86'
    },
    {
        name: 'Ilha do Natal',
        alpha2Code: 'CX',
        alpha3Code: 'CXR',
        numericCode: '162',
        callingCode: '+61'
    },
    {
        name: 'Ilhas dos Cocos',
        alpha2Code: 'CC',
        alpha3Code: 'CCK',
        numericCode: '166',
        callingCode: '+61'
    },
    {
        name: 'Colômbia',
        alpha2Code: 'CO',
        alpha3Code: 'COL',
        numericCode: '170',
        callingCode: '+57'
    },
    {
        name: 'Comores',
        alpha2Code: 'KM',
        alpha3Code: 'COM',
        numericCode: '174',
        callingCode: '+269'
    },
    {
        name: 'Congo',
        alpha2Code: 'CG',
        alpha3Code: 'COG',
        numericCode: '178',
        callingCode: '+242'
    },
    {
        name: 'RD Congo',
        alpha2Code: 'CD',
        alpha3Code: 'COD',
        numericCode: '180',
        callingCode: '+243'
    },
    {
        name: 'Ilhas Cook',
        alpha2Code: 'CK',
        alpha3Code: 'COK',
        numericCode: '184',
        callingCode: '+682'
    },
    {
        name: 'Costa Rica',
        alpha2Code: 'CR',
        alpha3Code: 'CRI',
        numericCode: '188',
        callingCode: '+506'
    },
    {
        name: 'Croácia',
        alpha2Code: 'HR',
        alpha3Code: 'HRV',
        numericCode: '191',
        callingCode: '+385'
    },
    {
        name: 'Cuba',
        alpha2Code: 'CU',
        alpha3Code: 'CUB',
        numericCode: '192',
        callingCode: '+53'
    },
    {
        name: 'Curaçao',
        alpha2Code: 'CW',
        alpha3Code: 'CUW',
        numericCode: '531',
        callingCode: '+599'
    },
    {
        name: 'Chipre',
        alpha2Code: 'CY',
        alpha3Code: 'CYP',
        numericCode: '196',
        callingCode: '+357'
    },
    {
        name: 'República Checa',
        alpha2Code: 'CZ',
        alpha3Code: 'CZE',
        numericCode: '203',
        callingCode: '+420'
    },
    {
        name: 'Dinamarca',
        alpha2Code: 'DK',
        alpha3Code: 'DNK',
        numericCode: '208',
        callingCode: '+45'
    },
    {
        name: 'Djibuti',
        alpha2Code: 'DJ',
        alpha3Code: 'DJI',
        numericCode: '262',
        callingCode: '+253'
    },
    {
        name: 'Dominica',
        alpha2Code: 'DM',
        alpha3Code: 'DMA',
        numericCode: '212',
        callingCode: '+1767'
    },
    {
        name: 'República Dominicana',
        alpha2Code: 'DO',
        alpha3Code: 'DOM',
        numericCode: '214',
        callingCode: '+1809'
    },
    {
        name: 'Equador',
        alpha2Code: 'EC',
        alpha3Code: 'ECU',
        numericCode: '218',
        callingCode: '+593'
    },
    {
        name: 'Egipto',
        alpha2Code: 'EG',
        alpha3Code: 'EGY',
        numericCode: '818',
        callingCode: '+20'
    },
    {
        name: 'El Salvador',
        alpha2Code: 'SV',
        alpha3Code: 'SLV',
        numericCode: '222',
        callingCode: '+503'
    },
    {
        name: 'Guiné Equatorial',
        alpha2Code: 'GQ',
        alpha3Code: 'GNQ',
        numericCode: '226',
        callingCode: '+240'
    },
    {
        name: 'Eritreia',
        alpha2Code: 'ER',
        alpha3Code: 'ERI',
        numericCode: '232',
        callingCode: '+291'
    },
    {
        name: 'Estónia',
        alpha2Code: 'EE',
        alpha3Code: 'EST',
        numericCode: '233',
        callingCode: '+372'
    },
    {
        name: 'Etiópia',
        alpha2Code: 'ET',
        alpha3Code: 'ETH',
        numericCode: '231',
        callingCode: '+251'
    },
    {
        name: 'Ilhas Falkland',
        alpha2Code: 'FK',
        alpha3Code: 'FLK',
        numericCode: '238',
        callingCode: '+500'
    },
    {
        name: 'Ilhas Faroé',
        alpha2Code: 'FO',
        alpha3Code: 'FRO',
        numericCode: '234',
        callingCode: '+298'
    },
    {
        name: 'Fiji',
        alpha2Code: 'FJ',
        alpha3Code: 'FJI',
        numericCode: '242',
        callingCode: '+679'
    },
    {
        name: 'Finlândia',
        alpha2Code: 'FI',
        alpha3Code: 'FIN',
        numericCode: '246',
        callingCode: '+358'
    },
    {
        name: 'França',
        alpha2Code: 'FR',
        alpha3Code: 'FRA',
        numericCode: '250',
        callingCode: '+33'
    },
    {
        name: 'Guiana Francesa',
        alpha2Code: 'GF',
        alpha3Code: 'GUF',
        numericCode: '254',
        callingCode: '+594'
    },
    {
        name: 'Polinésia Francesa',
        alpha2Code: 'PF',
        alpha3Code: 'PYF',
        numericCode: '258',
        callingCode: '+689'
    },
    {
        name: 'Terras Austrais e Antárticas Francesas',
        alpha2Code: 'TF',
        alpha3Code: 'ATF',
        numericCode: '260',
        callingCode: '+'
    },
    {
        name: 'Gabão',
        alpha2Code: 'GA',
        alpha3Code: 'GAB',
        numericCode: '266',
        callingCode: '+241'
    },
    {
        name: 'Gâmbia',
        alpha2Code: 'GM',
        alpha3Code: 'GMB',
        numericCode: '270',
        callingCode: '+220'
    },
    {
        name: 'Geórgia',
        alpha2Code: 'GE',
        alpha3Code: 'GEO',
        numericCode: '268',
        callingCode: '+995'
    },
    {
        name: 'Alemanha',
        alpha2Code: 'DE',
        alpha3Code: 'DEU',
        numericCode: '276',
        callingCode: '+49'
    },
    {
        name: 'Gana',
        alpha2Code: 'GH',
        alpha3Code: 'GHA',
        numericCode: '288',
        callingCode: '+233'
    },
    {
        name: 'Gibraltar',
        alpha2Code: 'GI',
        alpha3Code: 'GIB',
        numericCode: '292',
        callingCode: '+350'
    },
    {
        name: 'Grécia',
        alpha2Code: 'GR',
        alpha3Code: 'GRC',
        numericCode: '300',
        callingCode: '+30'
    },
    {
        name: 'Gronelândia',
        alpha2Code: 'GL',
        alpha3Code: 'GRL',
        numericCode: '304',
        callingCode: '+299'
    },
    {
        name: 'Granada',
        alpha2Code: 'GD',
        alpha3Code: 'GRD',
        numericCode: '308',
        callingCode: '+1473'
    },
    {
        name: 'Guadalupe',
        alpha2Code: 'GP',
        alpha3Code: 'GLP',
        numericCode: '312',
        callingCode: '+590'
    },
    {
        name: 'Guame',
        alpha2Code: 'GU',
        alpha3Code: 'GUM',
        numericCode: '316',
        callingCode: '+1671'
    },
    {
        name: 'Guatemala',
        alpha2Code: 'GT',
        alpha3Code: 'GTM',
        numericCode: '320',
        callingCode: '+502'
    },
    {
        name: 'Guernsey',
        alpha2Code: 'GG',
        alpha3Code: 'GGY',
        numericCode: '831',
        callingCode: '+44'
    },
    {
        name: 'Guiné',
        alpha2Code: 'GN',
        alpha3Code: 'GIN',
        numericCode: '324',
        callingCode: '+224'
    },
    {
        name: 'Guiné-Bissau',
        alpha2Code: 'GW',
        alpha3Code: 'GNB',
        numericCode: '624',
        callingCode: '+245'
    },
    {
        name: 'Guiana',
        alpha2Code: 'GY',
        alpha3Code: 'GUY',
        numericCode: '328',
        callingCode: '+592'
    },
    {
        name: 'Haiti',
        alpha2Code: 'HT',
        alpha3Code: 'HTI',
        numericCode: '332',
        callingCode: '+509'
    },
    {
        name: 'Ilha Heard e Ilhas McDonald',
        alpha2Code: 'HM',
        alpha3Code: 'HMD',
        numericCode: '334',
        callingCode: '+'
    },
    {
        name: 'Vaticano',
        alpha2Code: 'VA',
        alpha3Code: 'VAT',
        numericCode: '336',
        callingCode: '+379'
    },
    {
        name: 'Honduras',
        alpha2Code: 'HN',
        alpha3Code: 'HND',
        numericCode: '340',
        callingCode: '+504'
    },
    {
        name: 'Hong Kong',
        alpha2Code: 'HK',
        alpha3Code: 'HKG',
        numericCode: '344',
        callingCode: '+852'
    },
    {
        name: 'Hungria',
        alpha2Code: 'HU',
        alpha3Code: 'HUN',
        numericCode: '348',
        callingCode: '+36'
    },
    {
        name: 'Islândia',
        alpha2Code: 'IS',
        alpha3Code: 'ISL',
        numericCode: '352',
        callingCode: '+354'
    },
    {
        name: 'Índia',
        alpha2Code: 'IN',
        alpha3Code: 'IND',
        numericCode: '356',
        callingCode: '+91'
    },
    {
        name: 'Indonésia',
        alpha2Code: 'ID',
        alpha3Code: 'IDN',
        numericCode: '360',
        callingCode: '+62'
    },
    {
        name: 'Costa do Marfim',
        alpha2Code: 'CI',
        alpha3Code: 'CIV',
        numericCode: '384',
        callingCode: '+225'
    },
    {
        name: 'Irão',
        alpha2Code: 'IR',
        alpha3Code: 'IRN',
        numericCode: '364',
        callingCode: '+98'
    },
    {
        name: 'Iraque',
        alpha2Code: 'IQ',
        alpha3Code: 'IRQ',
        numericCode: '368',
        callingCode: '+964'
    },
    {
        name: 'Irlanda',
        alpha2Code: 'IE',
        alpha3Code: 'IRL',
        numericCode: '372',
        callingCode: '+353'
    },
    {
        name: 'Ilha de Man',
        alpha2Code: 'IM',
        alpha3Code: 'IMN',
        numericCode: '833',
        callingCode: '+44'
    },
    {
        name: 'Israel',
        alpha2Code: 'IL',
        alpha3Code: 'ISR',
        numericCode: '376',
        callingCode: '+972'
    },
    {
        name: 'Itália',
        alpha2Code: 'IT',
        alpha3Code: 'ITA',
        numericCode: '380',
        callingCode: '+39'
    },
    {
        name: 'Jamaica',
        alpha2Code: 'JM',
        alpha3Code: 'JAM',
        numericCode: '388',
        callingCode: '+1876'
    },
    {
        name: 'Japão',
        alpha2Code: 'JP',
        alpha3Code: 'JPN',
        numericCode: '392',
        callingCode: '+81'
    },
    {
        name: 'Jersey',
        alpha2Code: 'JE',
        alpha3Code: 'JEY',
        numericCode: '832',
        callingCode: '+44'
    },
    {
        name: 'Jordânia',
        alpha2Code: 'JO',
        alpha3Code: 'JOR',
        numericCode: '400',
        callingCode: '+962'
    },
    {
        name: 'Cazaquistão',
        alpha2Code: 'KZ',
        alpha3Code: 'KAZ',
        numericCode: '398',
        callingCode: '+7'
    },
    {
        name: 'Quénia',
        alpha2Code: 'KE',
        alpha3Code: 'KEN',
        numericCode: '404',
        callingCode: '+254'
    },
    {
        name: 'Quiribáti',
        alpha2Code: 'KI',
        alpha3Code: 'KIR',
        numericCode: '296',
        callingCode: '+686'
    },
    {
        name: 'Kuwait',
        alpha2Code: 'KW',
        alpha3Code: 'KWT',
        numericCode: '414',
        callingCode: '+965'
    },
    {
        name: 'Quirguizistão',
        alpha2Code: 'KG',
        alpha3Code: 'KGZ',
        numericCode: '417',
        callingCode: '+996'
    },
    {
        name: 'Laos',
        alpha2Code: 'LA',
        alpha3Code: 'LAO',
        numericCode: '418',
        callingCode: '+856'
    },
    {
        name: 'Letónia',
        alpha2Code: 'LV',
        alpha3Code: 'LVA',
        numericCode: '428',
        callingCode: '+371'
    },
    {
        name: 'Líbano',
        alpha2Code: 'LB',
        alpha3Code: 'LBN',
        numericCode: '422',
        callingCode: '+961'
    },
    {
        name: 'Lesoto',
        alpha2Code: 'LS',
        alpha3Code: 'LSO',
        numericCode: '426',
        callingCode: '+266'
    },
    {
        name: 'Libéria',
        alpha2Code: 'LR',
        alpha3Code: 'LBR',
        numericCode: '430',
        callingCode: '+231'
    },
    {
        name: 'Líbia',
        alpha2Code: 'LY',
        alpha3Code: 'LBY',
        numericCode: '434',
        callingCode: '+218'
    },
    {
        name: 'Listenstaine',
        alpha2Code: 'LI',
        alpha3Code: 'LIE',
        numericCode: '438',
        callingCode: '+423'
    },
    {
        name: 'Lituânia',
        alpha2Code: 'LT',
        alpha3Code: 'LTU',
        numericCode: '440',
        callingCode: '+370'
    },
    {
        name: 'Luxemburgo',
        alpha2Code: 'LU',
        alpha3Code: 'LUX',
        numericCode: '442',
        callingCode: '+352'
    },
    {
        name: 'Macau',
        alpha2Code: 'MO',
        alpha3Code: 'MAC',
        numericCode: '446',
        callingCode: '+853'
    },
    {
        name: 'Macedónia',
        alpha2Code: 'MK',
        alpha3Code: 'MKD',
        numericCode: '807',
        callingCode: '+389'
    },
    {
        name: 'Madagáscar',
        alpha2Code: 'MG',
        alpha3Code: 'MDG',
        numericCode: '450',
        callingCode: '+261'
    },
    {
        name: 'Malávi',
        alpha2Code: 'MW',
        alpha3Code: 'MWI',
        numericCode: '454',
        callingCode: '+265'
    },
    {
        name: 'Malásia',
        alpha2Code: 'MY',
        alpha3Code: 'MYS',
        numericCode: '458',
        callingCode: '+60'
    },
    {
        name: 'Maldivas',
        alpha2Code: 'MV',
        alpha3Code: 'MDV',
        numericCode: '462',
        callingCode: '+960'
    },
    {
        name: 'Mali',
        alpha2Code: 'ML',
        alpha3Code: 'MLI',
        numericCode: '466',
        callingCode: '+223'
    },
    {
        name: 'Malta',
        alpha2Code: 'MT',
        alpha3Code: 'MLT',
        numericCode: '470',
        callingCode: '+356'
    },
    {
        name: 'Ilhas Marshall',
        alpha2Code: 'MH',
        alpha3Code: 'MHL',
        numericCode: '584',
        callingCode: '+692'
    },
    {
        name: 'Martinica',
        alpha2Code: 'MQ',
        alpha3Code: 'MTQ',
        numericCode: '474',
        callingCode: '+596'
    },
    {
        name: 'Mauritânia',
        alpha2Code: 'MR',
        alpha3Code: 'MRT',
        numericCode: '478',
        callingCode: '+222'
    },
    {
        name: 'Maurícia',
        alpha2Code: 'MU',
        alpha3Code: 'MUS',
        numericCode: '480',
        callingCode: '+230'
    },
    {
        name: 'Mayotte',
        alpha2Code: 'YT',
        alpha3Code: 'MYT',
        numericCode: '175',
        callingCode: '+262'
    },
    {
        name: 'México',
        alpha2Code: 'MX',
        alpha3Code: 'MEX',
        numericCode: '484',
        callingCode: '+52'
    },
    {
        name: 'Micronésia',
        alpha2Code: 'FM',
        alpha3Code: 'FSM',
        numericCode: '583',
        callingCode: '+691'
    },
    {
        name: 'Moldávia',
        alpha2Code: 'MD',
        alpha3Code: 'MDA',
        numericCode: '498',
        callingCode: '+373'
    },
    {
        name: 'Mónaco',
        alpha2Code: 'MC',
        alpha3Code: 'MCO',
        numericCode: '492',
        callingCode: '+377'
    },
    {
        name: 'Mongólia',
        alpha2Code: 'MN',
        alpha3Code: 'MNG',
        numericCode: '496',
        callingCode: '+976'
    },
    {
        name: 'Montenegro',
        alpha2Code: 'ME',
        alpha3Code: 'MNE',
        numericCode: '499',
        callingCode: '+382'
    },
    {
        name: 'Monserrate',
        alpha2Code: 'MS',
        alpha3Code: 'MSR',
        numericCode: '500',
        callingCode: '+1664'
    },
    {
        name: 'Marrocos',
        alpha2Code: 'MA',
        alpha3Code: 'MAR',
        numericCode: '504',
        callingCode: '+212'
    },
    {
        name: 'Moçambique',
        alpha2Code: 'MZ',
        alpha3Code: 'MOZ',
        numericCode: '508',
        callingCode: '+258'
    },
    {
        name: 'Myanmar',
        alpha2Code: 'MM',
        alpha3Code: 'MMR',
        numericCode: '104',
        callingCode: '+95'
    },
    {
        name: 'Namíbia',
        alpha2Code: 'NA',
        alpha3Code: 'NAM',
        numericCode: '516',
        callingCode: '+264'
    },
    {
        name: 'Nauru',
        alpha2Code: 'NR',
        alpha3Code: 'NRU',
        numericCode: '520',
        callingCode: '+674'
    },
    {
        name: 'Nepal',
        alpha2Code: 'NP',
        alpha3Code: 'NPL',
        numericCode: '524',
        callingCode: '+977'
    },
    {
        name: 'Países Baixos',
        alpha2Code: 'NL',
        alpha3Code: 'NLD',
        numericCode: '528',
        callingCode: '+31'
    },
    {
        name: 'Nova Caledónia',
        alpha2Code: 'NC',
        alpha3Code: 'NCL',
        numericCode: '540',
        callingCode: '+687'
    },
    {
        name: 'Nova Zelândia',
        alpha2Code: 'NZ',
        alpha3Code: 'NZL',
        numericCode: '554',
        callingCode: '+64'
    },
    {
        name: 'Nicarágua',
        alpha2Code: 'NI',
        alpha3Code: 'NIC',
        numericCode: '558',
        callingCode: '+505'
    },
    {
        name: 'Níger',
        alpha2Code: 'NE',
        alpha3Code: 'NER',
        numericCode: '562',
        callingCode: '+227'
    },
    {
        name: 'Nigéria',
        alpha2Code: 'NG',
        alpha3Code: 'NGA',
        numericCode: '566',
        callingCode: '+234'
    },
    {
        name: 'Niue',
        alpha2Code: 'NU',
        alpha3Code: 'NIU',
        numericCode: '570',
        callingCode: '+683'
    },
    {
        name: 'Ilha Norfolk',
        alpha2Code: 'NF',
        alpha3Code: 'NFK',
        numericCode: '574',
        callingCode: '+672'
    },
    {
        name: 'Coreia do Norte',
        alpha2Code: 'KP',
        alpha3Code: 'PRK',
        numericCode: '408',
        callingCode: '+850'
    },
    {
        name: 'Ilhas Marianas',
        alpha2Code: 'MP',
        alpha3Code: 'MNP',
        numericCode: '580',
        callingCode: '+1670'
    },
    {
        name: 'Noruega',
        alpha2Code: 'NO',
        alpha3Code: 'NOR',
        numericCode: '578',
        callingCode: '+47'
    },
    {
        name: 'Omã',
        alpha2Code: 'OM',
        alpha3Code: 'OMN',
        numericCode: '512',
        callingCode: '+968'
    },
    {
        name: 'Paquistão',
        alpha2Code: 'PK',
        alpha3Code: 'PAK',
        numericCode: '586',
        callingCode: '+92'
    },
    {
        name: 'Palau',
        alpha2Code: 'PW',
        alpha3Code: 'PLW',
        numericCode: '585',
        callingCode: '+680'
    },
    {
        name: 'Palestina',
        alpha2Code: 'PS',
        alpha3Code: 'PSE',
        numericCode: '275',
        callingCode: '+970'
    },
    {
        name: 'Panamá',
        alpha2Code: 'PA',
        alpha3Code: 'PAN',
        numericCode: '591',
        callingCode: '+507'
    },
    {
        name: 'Papua Nova Guiné',
        alpha2Code: 'PG',
        alpha3Code: 'PNG',
        numericCode: '598',
        callingCode: '+675'
    },
    {
        name: 'Paraguai',
        alpha2Code: 'PY',
        alpha3Code: 'PRY',
        numericCode: '600',
        callingCode: '+595'
    },
    {
        name: 'Peru',
        alpha2Code: 'PE',
        alpha3Code: 'PER',
        numericCode: '604',
        callingCode: '+51'
    },
    {
        name: 'Filipinas',
        alpha2Code: 'PH',
        alpha3Code: 'PHL',
        numericCode: '608',
        callingCode: '+63'
    },
    {
        name: 'Ilhas Picárnia',
        alpha2Code: 'PN',
        alpha3Code: 'PCN',
        numericCode: '612',
        callingCode: '+64'
    },
    {
        name: 'Polónia',
        alpha2Code: 'PL',
        alpha3Code: 'POL',
        numericCode: '616',
        callingCode: '+48'
    },
    {
        name: 'Portugal',
        alpha2Code: 'PT',
        alpha3Code: 'PRT',
        numericCode: '620',
        callingCode: '+351'
    },
    {
        name: 'Porto Rico',
        alpha2Code: 'PR',
        alpha3Code: 'PRI',
        numericCode: '630',
        callingCode: '+1'
    },
    {
        name: 'Catar',
        alpha2Code: 'QA',
        alpha3Code: 'QAT',
        numericCode: '634',
        callingCode: '+974'
    },
    {
        name: 'Kosovo',
        alpha2Code: 'XK',
        alpha3Code: 'KOS',
        numericCode: null,
        callingCode: '+383'
    },
    {
        name: 'Reunião',
        alpha2Code: 'RE',
        alpha3Code: 'REU',
        numericCode: '638',
        callingCode: '+262'
    },
    {
        name: 'Roménia',
        alpha2Code: 'RO',
        alpha3Code: 'ROU',
        numericCode: '642',
        callingCode: '+40'
    },
    {
        name: 'Rússia',
        alpha2Code: 'RU',
        alpha3Code: 'RUS',
        numericCode: '643',
        callingCode: '+7'
    },
    {
        name: 'Ruanda',
        alpha2Code: 'RW',
        alpha3Code: 'RWA',
        numericCode: '646',
        callingCode: '+250'
    },
    {
        name: 'São Bartolomeu',
        alpha2Code: 'BL',
        alpha3Code: 'BLM',
        numericCode: '652',
        callingCode: '+590'
    },
    {
        name: 'Santa Helena',
        alpha2Code: 'SH',
        alpha3Code: 'SHN',
        numericCode: '654',
        callingCode: '+290'
    },
    {
        name: 'São Cristóvão e Neves',
        alpha2Code: 'KN',
        alpha3Code: 'KNA',
        numericCode: '659',
        callingCode: '+1869'
    },
    {
        name: 'Santa Lúcia',
        alpha2Code: 'LC',
        alpha3Code: 'LCA',
        numericCode: '662',
        callingCode: '+1758'
    },
    {
        name: 'Ilha São Martinho',
        alpha2Code: 'MF',
        alpha3Code: 'MAF',
        numericCode: '663',
        callingCode: '+590'
    },
    {
        name: 'São Pedro e Miquelon',
        alpha2Code: 'PM',
        alpha3Code: 'SPM',
        numericCode: '666',
        callingCode: '+508'
    },
    {
        name: 'São Vicente e Granadinas',
        alpha2Code: 'VC',
        alpha3Code: 'VCT',
        numericCode: '670',
        callingCode: '+1784'
    },
    {
        name: 'Samoa',
        alpha2Code: 'WS',
        alpha3Code: 'WSM',
        numericCode: '882',
        callingCode: '+685'
    },
    {
        name: 'São Marinho',
        alpha2Code: 'SM',
        alpha3Code: 'SMR',
        numericCode: '674',
        callingCode: '+378'
    },
    {
        name: 'São Tomé e Príncipe',
        alpha2Code: 'ST',
        alpha3Code: 'STP',
        numericCode: '678',
        callingCode: '+239'
    },
    {
        name: 'Arábia Saudita',
        alpha2Code: 'SA',
        alpha3Code: 'SAU',
        numericCode: '682',
        callingCode: '+966'
    },
    {
        name: 'Senegal',
        alpha2Code: 'SN',
        alpha3Code: 'SEN',
        numericCode: '686',
        callingCode: '+221'
    },
    {
        name: 'Sérvia',
        alpha2Code: 'RS',
        alpha3Code: 'SRB',
        numericCode: '688',
        callingCode: '+381'
    },
    {
        name: 'Seicheles',
        alpha2Code: 'SC',
        alpha3Code: 'SYC',
        numericCode: '690',
        callingCode: '+248'
    },
    {
        name: 'Serra Leoa',
        alpha2Code: 'SL',
        alpha3Code: 'SLE',
        numericCode: '694',
        callingCode: '+232'
    },
    {
        name: 'Singapura',
        alpha2Code: 'SG',
        alpha3Code: 'SGP',
        numericCode: '702',
        callingCode: '+65'
    },
    {
        name: 'São Martinho',
        alpha2Code: 'SX',
        alpha3Code: 'SXM',
        numericCode: '534',
        callingCode: '+1721'
    },
    {
        name: 'Eslováquia',
        alpha2Code: 'SK',
        alpha3Code: 'SVK',
        numericCode: '703',
        callingCode: '+421'
    },
    {
        name: 'Eslovénia',
        alpha2Code: 'SI',
        alpha3Code: 'SVN',
        numericCode: '705',
        callingCode: '+386'
    },
    {
        name: 'Ilhas Salomão',
        alpha2Code: 'SB',
        alpha3Code: 'SLB',
        numericCode: '090',
        callingCode: '+677'
    },
    {
        name: 'Somália',
        alpha2Code: 'SO',
        alpha3Code: 'SOM',
        numericCode: '706',
        callingCode: '+252'
    },
    {
        name: 'República Sul-Africana',
        alpha2Code: 'ZA',
        alpha3Code: 'ZAF',
        numericCode: '710',
        callingCode: '+27'
    },
    {
        name: 'Ilhas Geórgia do Sul e Sanduíche do Sul',
        alpha2Code: 'GS',
        alpha3Code: 'SGS',
        numericCode: '239',
        callingCode: '+500'
    },
    {
        name: 'Coreia do Sul',
        alpha2Code: 'KR',
        alpha3Code: 'KOR',
        numericCode: '410',
        callingCode: '+82'
    },
    {
        name: 'Sudão do Sul',
        alpha2Code: 'SS',
        alpha3Code: 'SSD',
        numericCode: '728',
        callingCode: '+211'
    },
    {
        name: 'Espanha',
        alpha2Code: 'ES',
        alpha3Code: 'ESP',
        numericCode: '724',
        callingCode: '+34'
    },
    {
        name: 'Sri Lanka',
        alpha2Code: 'LK',
        alpha3Code: 'LKA',
        numericCode: '144',
        callingCode: '+94'
    },
    {
        name: 'Sudão',
        alpha2Code: 'SD',
        alpha3Code: 'SDN',
        numericCode: '729',
        callingCode: '+249'
    },
    {
        name: 'Suriname',
        alpha2Code: 'SR',
        alpha3Code: 'SUR',
        numericCode: '740',
        callingCode: '+597'
    },
    {
        name: 'Svalbard',
        alpha2Code: 'SJ',
        alpha3Code: 'SJM',
        numericCode: '744',
        callingCode: '+4779'
    },
    {
        name: 'Suazilândia',
        alpha2Code: 'SZ',
        alpha3Code: 'SWZ',
        numericCode: '748',
        callingCode: '+268'
    },
    {
        name: 'Suécia',
        alpha2Code: 'SE',
        alpha3Code: 'SWE',
        numericCode: '752',
        callingCode: '+46'
    },
    {
        name: 'Suíça',
        alpha2Code: 'CH',
        alpha3Code: 'CHE',
        numericCode: '756',
        callingCode: '+41'
    },
    {
        name: 'Síria',
        alpha2Code: 'SY',
        alpha3Code: 'SYR',
        numericCode: '760',
        callingCode: '+963'
    },
    {
        name: 'Taiwan, China',
        alpha2Code: 'TW',
        alpha3Code: 'TWN',
        numericCode: '158',
        callingCode: '+886'
    },
    {
        name: 'Tajiquistão',
        alpha2Code: 'TJ',
        alpha3Code: 'TJK',
        numericCode: '762',
        callingCode: '+992'
    },
    {
        name: 'Tanzânia',
        alpha2Code: 'TZ',
        alpha3Code: 'TZA',
        numericCode: '834',
        callingCode: '+255'
    },
    {
        name: 'Tailândia',
        alpha2Code: 'TH',
        alpha3Code: 'THA',
        numericCode: '764',
        callingCode: '+66'
    },
    {
        name: 'Timor Leste',
        alpha2Code: 'TL',
        alpha3Code: 'TLS',
        numericCode: '626',
        callingCode: '+670'
    },
    {
        name: 'Togo',
        alpha2Code: 'TG',
        alpha3Code: 'TGO',
        numericCode: '768',
        callingCode: '+228'
    },
    {
        name: 'Toquelau',
        alpha2Code: 'TK',
        alpha3Code: 'TKL',
        numericCode: '772',
        callingCode: '+690'
    },
    {
        name: 'Tonga',
        alpha2Code: 'TO',
        alpha3Code: 'TON',
        numericCode: '776',
        callingCode: '+676'
    },
    {
        name: 'Trindade e Tobago',
        alpha2Code: 'TT',
        alpha3Code: 'TTO',
        numericCode: '780',
        callingCode: '+1868'
    },
    {
        name: 'Tunísia',
        alpha2Code: 'TN',
        alpha3Code: 'TUN',
        numericCode: '788',
        callingCode: '+216'
    },
    {
        name: 'Turquia',
        alpha2Code: 'TR',
        alpha3Code: 'TUR',
        numericCode: '792',
        callingCode: '+90'
    },
    {
        name: 'Turquemenistão',
        alpha2Code: 'TM',
        alpha3Code: 'TKM',
        numericCode: '795',
        callingCode: '+993'
    },
    {
        name: 'Ilhas Turcas e Caicos',
        alpha2Code: 'TC',
        alpha3Code: 'TCA',
        numericCode: '796',
        callingCode: '+1649'
    },
    {
        name: 'Tuvalu',
        alpha2Code: 'TV',
        alpha3Code: 'TUV',
        numericCode: '798',
        callingCode: '+688'
    },
    {
        name: 'Uganda',
        alpha2Code: 'UG',
        alpha3Code: 'UGA',
        numericCode: '800',
        callingCode: '+256'
    },
    {
        name: 'Ucrânia',
        alpha2Code: 'UA',
        alpha3Code: 'UKR',
        numericCode: '804',
        callingCode: '+380'
    },
    {
        name: 'Emirados árabes Unidos',
        alpha2Code: 'AE',
        alpha3Code: 'ARE',
        numericCode: '784',
        callingCode: '+971'
    },
    {
        name: 'Reino Unido',
        alpha2Code: 'GB',
        alpha3Code: 'GBR',
        numericCode: '826',
        callingCode: '+44'
    },
    {
        name: 'Estados Unidos',
        alpha2Code: 'US',
        alpha3Code: 'USA',
        numericCode: '840',
        callingCode: '+1'
    },
    {
        name: 'Uruguai',
        alpha2Code: 'UY',
        alpha3Code: 'URY',
        numericCode: '858',
        callingCode: '+598'
    },
    {
        name: 'Usbequistão',
        alpha2Code: 'UZ',
        alpha3Code: 'UZB',
        numericCode: '860',
        callingCode: '+998'
    },
    {
        name: 'Vanuatu',
        alpha2Code: 'VU',
        alpha3Code: 'VUT',
        numericCode: '548',
        callingCode: '+678'
    },
    {
        name: 'Venezuela',
        alpha2Code: 'VE',
        alpha3Code: 'VEN',
        numericCode: '862',
        callingCode: '+58'
    },
    {
        name: 'Vietname',
        alpha2Code: 'VN',
        alpha3Code: 'VNM',
        numericCode: '704',
        callingCode: '+84'
    },
    {
        name: 'Wallis e Futuna',
        alpha2Code: 'WF',
        alpha3Code: 'WLF',
        numericCode: '876',
        callingCode: '+681'
    },
    {
        name: 'Saara Ocidental',
        alpha2Code: 'EH',
        alpha3Code: 'ESH',
        numericCode: '732',
        callingCode: '+212'
    },
    {
        name: 'Iémen',
        alpha2Code: 'YE',
        alpha3Code: 'YEM',
        numericCode: '887',
        callingCode: '+967'
    },
    {
        name: 'Zâmbia',
        alpha2Code: 'ZM',
        alpha3Code: 'ZMB',
        numericCode: '894',
        callingCode: '+260'
    },
    {
        name: 'Zimbabué',
        alpha2Code: 'ZW',
        alpha3Code: 'ZWE',
        numericCode: '716',
        callingCode: '+263'
    }
];

var pt = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB_PT: COUNTRIES_DB_PT
});

const COUNTRIES_DB_RU = [
    {
        name: 'Афганистан',
        alpha2Code: 'AF',
        alpha3Code: 'AFG',
        numericCode: '004',
        callingCode: '+93'
    },
    {
        name: 'Аландские острова',
        alpha2Code: 'AX',
        alpha3Code: 'ALA',
        numericCode: '248',
        callingCode: '+358'
    },
    {
        name: 'Албания',
        alpha2Code: 'AL',
        alpha3Code: 'ALB',
        numericCode: '008',
        callingCode: '+355'
    },
    {
        name: 'Алжир',
        alpha2Code: 'DZ',
        alpha3Code: 'DZA',
        numericCode: '012',
        callingCode: '+213'
    },
    {
        name: 'Американское Самоа',
        alpha2Code: 'AS',
        alpha3Code: 'ASM',
        numericCode: '016',
        callingCode: '+1684'
    },
    {
        name: 'Андорра',
        alpha2Code: 'AD',
        alpha3Code: 'AND',
        numericCode: '020',
        callingCode: '+376'
    },
    {
        name: 'Ангола',
        alpha2Code: 'AO',
        alpha3Code: 'AGO',
        numericCode: '024',
        callingCode: '+244'
    },
    {
        name: 'Ангилья',
        alpha2Code: 'AI',
        alpha3Code: 'AIA',
        numericCode: '660',
        callingCode: '+1264'
    },
    {
        name: 'Антарктика',
        alpha2Code: 'AQ',
        alpha3Code: 'ATA',
        numericCode: '010',
        callingCode: '+672'
    },
    {
        name: 'Антигуа и Барбуда',
        alpha2Code: 'AG',
        alpha3Code: 'ATG',
        numericCode: '028',
        callingCode: '+1268'
    },
    {
        name: 'Аргентина',
        alpha2Code: 'AR',
        alpha3Code: 'ARG',
        numericCode: '032',
        callingCode: '+54'
    },
    {
        name: 'Армения',
        alpha2Code: 'AM',
        alpha3Code: 'ARM',
        numericCode: '051',
        callingCode: '+374'
    },
    {
        name: 'Аруба',
        alpha2Code: 'AW',
        alpha3Code: 'ABW',
        numericCode: '533',
        callingCode: '+297'
    },
    {
        name: 'Австралия',
        alpha2Code: 'AU',
        alpha3Code: 'AUS',
        numericCode: '036',
        callingCode: '+61'
    },
    {
        name: 'Австрия',
        alpha2Code: 'AT',
        alpha3Code: 'AUT',
        numericCode: '040',
        callingCode: '+43'
    },
    {
        name: 'Азербайджан',
        alpha2Code: 'AZ',
        alpha3Code: 'AZE',
        numericCode: '031',
        callingCode: '+994'
    },
    {
        name: 'Багамские Острова',
        alpha2Code: 'BS',
        alpha3Code: 'BHS',
        numericCode: '044',
        callingCode: '+1242'
    },
    {
        name: 'Бахрейн',
        alpha2Code: 'BH',
        alpha3Code: 'BHR',
        numericCode: '048',
        callingCode: '+973'
    },
    {
        name: 'Бангладеш',
        alpha2Code: 'BD',
        alpha3Code: 'BGD',
        numericCode: '050',
        callingCode: '+880'
    },
    {
        name: 'Барбадос',
        alpha2Code: 'BB',
        alpha3Code: 'BRB',
        numericCode: '052',
        callingCode: '+1246'
    },
    {
        name: 'Беларусь',
        alpha2Code: 'BY',
        alpha3Code: 'BLR',
        numericCode: '112',
        callingCode: '+375'
    },
    {
        name: 'Бельгия',
        alpha2Code: 'BE',
        alpha3Code: 'BEL',
        numericCode: '056',
        callingCode: '+32'
    },
    {
        name: 'Белиз',
        alpha2Code: 'BZ',
        alpha3Code: 'BLZ',
        numericCode: '084',
        callingCode: '+501'
    },
    {
        name: 'Бенин',
        alpha2Code: 'BJ',
        alpha3Code: 'BEN',
        numericCode: '204',
        callingCode: '+229'
    },
    {
        name: 'Бермуды',
        alpha2Code: 'BM',
        alpha3Code: 'BMU',
        numericCode: '060',
        callingCode: '+1441'
    },
    {
        name: 'Бутан',
        alpha2Code: 'BT',
        alpha3Code: 'BTN',
        numericCode: '064',
        callingCode: '+975'
    },
    {
        name: 'Боливия',
        alpha2Code: 'BO',
        alpha3Code: 'BOL',
        numericCode: '068',
        callingCode: '+591'
    },
    {
        name: 'Бонэйр, Синт-Эстатиус и Саба',
        alpha2Code: 'BQ',
        alpha3Code: 'BES',
        numericCode: '535',
        callingCode: '+5997'
    },
    {
        name: 'Босния и Герцеговина',
        alpha2Code: 'BA',
        alpha3Code: 'BIH',
        numericCode: '070',
        callingCode: '+387'
    },
    {
        name: 'Ботсвана',
        alpha2Code: 'BW',
        alpha3Code: 'BWA',
        numericCode: '072',
        callingCode: '+267'
    },
    {
        name: 'Остров Буве',
        alpha2Code: 'BV',
        alpha3Code: 'BVT',
        numericCode: '074',
        callingCode: '+'
    },
    {
        name: 'Бразилия',
        alpha2Code: 'BR',
        alpha3Code: 'BRA',
        numericCode: '076',
        callingCode: '+55'
    },
    {
        name: 'Британская территория в Индийском океане',
        alpha2Code: 'IO',
        alpha3Code: 'IOT',
        numericCode: '086',
        callingCode: '+246'
    },
    {
        name: 'Внешние малые острова США',
        alpha2Code: 'UM',
        alpha3Code: 'UMI',
        numericCode: '581',
        callingCode: '+'
    },
    {
        name: 'Виргинские Острова (Великобритания)',
        alpha2Code: 'VG',
        alpha3Code: 'VGB',
        numericCode: '092',
        callingCode: '+1284'
    },
    {
        name: 'Виргинские Острова (США)',
        alpha2Code: 'VI',
        alpha3Code: 'VIR',
        numericCode: '850',
        callingCode: '+1 340'
    },
    {
        name: 'Бруней',
        alpha2Code: 'BN',
        alpha3Code: 'BRN',
        numericCode: '096',
        callingCode: '+673'
    },
    {
        name: 'Болгария',
        alpha2Code: 'BG',
        alpha3Code: 'BGR',
        numericCode: '100',
        callingCode: '+359'
    },
    {
        name: 'Буркина-Фасо',
        alpha2Code: 'BF',
        alpha3Code: 'BFA',
        numericCode: '854',
        callingCode: '+226'
    },
    {
        name: 'Бурунди',
        alpha2Code: 'BI',
        alpha3Code: 'BDI',
        numericCode: '108',
        callingCode: '+257'
    },
    {
        name: 'Камбоджа',
        alpha2Code: 'KH',
        alpha3Code: 'KHM',
        numericCode: '116',
        callingCode: '+855'
    },
    {
        name: 'Камерун',
        alpha2Code: 'CM',
        alpha3Code: 'CMR',
        numericCode: '120',
        callingCode: '+237'
    },
    {
        name: 'Канада',
        alpha2Code: 'CA',
        alpha3Code: 'CAN',
        numericCode: '124',
        callingCode: '+1'
    },
    {
        name: 'Кабо-Верде',
        alpha2Code: 'CV',
        alpha3Code: 'CPV',
        numericCode: '132',
        callingCode: '+238'
    },
    {
        name: 'Острова Кайман',
        alpha2Code: 'KY',
        alpha3Code: 'CYM',
        numericCode: '136',
        callingCode: '+1345'
    },
    {
        name: 'ЦАР',
        alpha2Code: 'CF',
        alpha3Code: 'CAF',
        numericCode: '140',
        callingCode: '+236'
    },
    {
        name: 'Чад',
        alpha2Code: 'TD',
        alpha3Code: 'TCD',
        numericCode: '148',
        callingCode: '+235'
    },
    {
        name: 'Чили',
        alpha2Code: 'CL',
        alpha3Code: 'CHL',
        numericCode: '152',
        callingCode: '+56'
    },
    {
        name: 'Китай (Китайская Народная Республика)',
        alpha2Code: 'CN',
        alpha3Code: 'CHN',
        numericCode: '156',
        callingCode: '+86'
    },
    {
        name: 'Остров Рождества',
        alpha2Code: 'CX',
        alpha3Code: 'CXR',
        numericCode: '162',
        callingCode: '+61'
    },
    {
        name: 'Кокосовые острова',
        alpha2Code: 'CC',
        alpha3Code: 'CCK',
        numericCode: '166',
        callingCode: '+61'
    },
    {
        name: 'Колумбия',
        alpha2Code: 'CO',
        alpha3Code: 'COL',
        numericCode: '170',
        callingCode: '+57'
    },
    {
        name: 'Коморы',
        alpha2Code: 'KM',
        alpha3Code: 'COM',
        numericCode: '174',
        callingCode: '+269'
    },
    {
        name: 'Республика Конго',
        alpha2Code: 'CG',
        alpha3Code: 'COG',
        numericCode: '178',
        callingCode: '+242'
    },
    {
        name: 'ДР Конго',
        alpha2Code: 'CD',
        alpha3Code: 'COD',
        numericCode: '180',
        callingCode: '+243'
    },
    {
        name: 'Острова Кука',
        alpha2Code: 'CK',
        alpha3Code: 'COK',
        numericCode: '184',
        callingCode: '+682'
    },
    {
        name: 'Коста-Рика',
        alpha2Code: 'CR',
        alpha3Code: 'CRI',
        numericCode: '188',
        callingCode: '+506'
    },
    {
        name: 'Хорватия',
        alpha2Code: 'HR',
        alpha3Code: 'HRV',
        numericCode: '191',
        callingCode: '+385'
    },
    {
        name: 'Куба',
        alpha2Code: 'CU',
        alpha3Code: 'CUB',
        numericCode: '192',
        callingCode: '+53'
    },
    {
        name: 'Кюрасао',
        alpha2Code: 'CW',
        alpha3Code: 'CUW',
        numericCode: '531',
        callingCode: '+599'
    },
    {
        name: 'Кипр',
        alpha2Code: 'CY',
        alpha3Code: 'CYP',
        numericCode: '196',
        callingCode: '+357'
    },
    {
        name: 'Чехия',
        alpha2Code: 'CZ',
        alpha3Code: 'CZE',
        numericCode: '203',
        callingCode: '+420'
    },
    {
        name: 'Дания',
        alpha2Code: 'DK',
        alpha3Code: 'DNK',
        numericCode: '208',
        callingCode: '+45'
    },
    {
        name: 'Джибути',
        alpha2Code: 'DJ',
        alpha3Code: 'DJI',
        numericCode: '262',
        callingCode: '+253'
    },
    {
        name: 'Доминика',
        alpha2Code: 'DM',
        alpha3Code: 'DMA',
        numericCode: '212',
        callingCode: '+1767'
    },
    {
        name: 'Доминиканская Республика',
        alpha2Code: 'DO',
        alpha3Code: 'DOM',
        numericCode: '214',
        callingCode: '+1809'
    },
    {
        name: 'Эквадор',
        alpha2Code: 'EC',
        alpha3Code: 'ECU',
        numericCode: '218',
        callingCode: '+593'
    },
    {
        name: 'Египет',
        alpha2Code: 'EG',
        alpha3Code: 'EGY',
        numericCode: '818',
        callingCode: '+20'
    },
    {
        name: 'Сальвадор',
        alpha2Code: 'SV',
        alpha3Code: 'SLV',
        numericCode: '222',
        callingCode: '+503'
    },
    {
        name: 'Экваториальная Гвинея',
        alpha2Code: 'GQ',
        alpha3Code: 'GNQ',
        numericCode: '226',
        callingCode: '+240'
    },
    {
        name: 'Эритрея',
        alpha2Code: 'ER',
        alpha3Code: 'ERI',
        numericCode: '232',
        callingCode: '+291'
    },
    {
        name: 'Эстония',
        alpha2Code: 'EE',
        alpha3Code: 'EST',
        numericCode: '233',
        callingCode: '+372'
    },
    {
        name: 'Эфиопия',
        alpha2Code: 'ET',
        alpha3Code: 'ETH',
        numericCode: '231',
        callingCode: '+251'
    },
    {
        name: 'Фолклендские острова',
        alpha2Code: 'FK',
        alpha3Code: 'FLK',
        numericCode: '238',
        callingCode: '+500'
    },
    {
        name: 'Фарерские острова',
        alpha2Code: 'FO',
        alpha3Code: 'FRO',
        numericCode: '234',
        callingCode: '+298'
    },
    {
        name: 'Фиджи',
        alpha2Code: 'FJ',
        alpha3Code: 'FJI',
        numericCode: '242',
        callingCode: '+679'
    },
    {
        name: 'Финляндия',
        alpha2Code: 'FI',
        alpha3Code: 'FIN',
        numericCode: '246',
        callingCode: '+358'
    },
    {
        name: 'Франция',
        alpha2Code: 'FR',
        alpha3Code: 'FRA',
        numericCode: '250',
        callingCode: '+33'
    },
    {
        name: 'Гвиана',
        alpha2Code: 'GF',
        alpha3Code: 'GUF',
        numericCode: '254',
        callingCode: '+594'
    },
    {
        name: 'Французская Полинезия',
        alpha2Code: 'PF',
        alpha3Code: 'PYF',
        numericCode: '258',
        callingCode: '+689'
    },
    {
        name: 'Французские Южные и Антарктические территории',
        alpha2Code: 'TF',
        alpha3Code: 'ATF',
        numericCode: '260',
        callingCode: '+'
    },
    {
        name: 'Габон',
        alpha2Code: 'GA',
        alpha3Code: 'GAB',
        numericCode: '266',
        callingCode: '+241'
    },
    {
        name: 'Гамбия',
        alpha2Code: 'GM',
        alpha3Code: 'GMB',
        numericCode: '270',
        callingCode: '+220'
    },
    {
        name: 'Грузия',
        alpha2Code: 'GE',
        alpha3Code: 'GEO',
        numericCode: '268',
        callingCode: '+995'
    },
    {
        name: 'Германия',
        alpha2Code: 'DE',
        alpha3Code: 'DEU',
        numericCode: '276',
        callingCode: '+49'
    },
    {
        name: 'Гана',
        alpha2Code: 'GH',
        alpha3Code: 'GHA',
        numericCode: '288',
        callingCode: '+233'
    },
    {
        name: 'Гибралтар',
        alpha2Code: 'GI',
        alpha3Code: 'GIB',
        numericCode: '292',
        callingCode: '+350'
    },
    {
        name: 'Греция',
        alpha2Code: 'GR',
        alpha3Code: 'GRC',
        numericCode: '300',
        callingCode: '+30'
    },
    {
        name: 'Гренландия',
        alpha2Code: 'GL',
        alpha3Code: 'GRL',
        numericCode: '304',
        callingCode: '+299'
    },
    {
        name: 'Гренада',
        alpha2Code: 'GD',
        alpha3Code: 'GRD',
        numericCode: '308',
        callingCode: '+1473'
    },
    {
        name: 'Гваделупа',
        alpha2Code: 'GP',
        alpha3Code: 'GLP',
        numericCode: '312',
        callingCode: '+590'
    },
    {
        name: 'Гуам',
        alpha2Code: 'GU',
        alpha3Code: 'GUM',
        numericCode: '316',
        callingCode: '+1671'
    },
    {
        name: 'Гватемала',
        alpha2Code: 'GT',
        alpha3Code: 'GTM',
        numericCode: '320',
        callingCode: '+502'
    },
    {
        name: 'Гернси',
        alpha2Code: 'GG',
        alpha3Code: 'GGY',
        numericCode: '831',
        callingCode: '+44'
    },
    {
        name: 'Гвинея',
        alpha2Code: 'GN',
        alpha3Code: 'GIN',
        numericCode: '324',
        callingCode: '+224'
    },
    {
        name: 'Гвинея-Бисау',
        alpha2Code: 'GW',
        alpha3Code: 'GNB',
        numericCode: '624',
        callingCode: '+245'
    },
    {
        name: 'Гайана',
        alpha2Code: 'GY',
        alpha3Code: 'GUY',
        numericCode: '328',
        callingCode: '+592'
    },
    {
        name: 'Гаити',
        alpha2Code: 'HT',
        alpha3Code: 'HTI',
        numericCode: '332',
        callingCode: '+509'
    },
    {
        name: 'Херд и Макдональд',
        alpha2Code: 'HM',
        alpha3Code: 'HMD',
        numericCode: '334',
        callingCode: '+'
    },
    {
        name: 'Ватикан',
        alpha2Code: 'VA',
        alpha3Code: 'VAT',
        numericCode: '336',
        callingCode: '+379'
    },
    {
        name: 'Гондурас',
        alpha2Code: 'HN',
        alpha3Code: 'HND',
        numericCode: '340',
        callingCode: '+504'
    },
    {
        name: 'Гонконг',
        alpha2Code: 'HK',
        alpha3Code: 'HKG',
        numericCode: '344',
        callingCode: '+852'
    },
    {
        name: 'Венгрия',
        alpha2Code: 'HU',
        alpha3Code: 'HUN',
        numericCode: '348',
        callingCode: '+36'
    },
    {
        name: 'Исландия',
        alpha2Code: 'IS',
        alpha3Code: 'ISL',
        numericCode: '352',
        callingCode: '+354'
    },
    {
        name: 'Индия',
        alpha2Code: 'IN',
        alpha3Code: 'IND',
        numericCode: '356',
        callingCode: '+91'
    },
    {
        name: 'Индонезия',
        alpha2Code: 'ID',
        alpha3Code: 'IDN',
        numericCode: '360',
        callingCode: '+62'
    },
    {
        name: 'Кот-д\'Ивуар',
        alpha2Code: 'CI',
        alpha3Code: 'CIV',
        numericCode: '384',
        callingCode: '+225'
    },
    {
        name: 'Иран',
        alpha2Code: 'IR',
        alpha3Code: 'IRN',
        numericCode: '364',
        callingCode: '+98'
    },
    {
        name: 'Ирак',
        alpha2Code: 'IQ',
        alpha3Code: 'IRQ',
        numericCode: '368',
        callingCode: '+964'
    },
    {
        name: 'Ирландия',
        alpha2Code: 'IE',
        alpha3Code: 'IRL',
        numericCode: '372',
        callingCode: '+353'
    },
    {
        name: 'Остров Мэн',
        alpha2Code: 'IM',
        alpha3Code: 'IMN',
        numericCode: '833',
        callingCode: '+44'
    },
    {
        name: 'Израиль',
        alpha2Code: 'IL',
        alpha3Code: 'ISR',
        numericCode: '376',
        callingCode: '+972'
    },
    {
        name: 'Италия',
        alpha2Code: 'IT',
        alpha3Code: 'ITA',
        numericCode: '380',
        callingCode: '+39'
    },
    {
        name: 'Ямайка',
        alpha2Code: 'JM',
        alpha3Code: 'JAM',
        numericCode: '388',
        callingCode: '+1876'
    },
    {
        name: 'Япония',
        alpha2Code: 'JP',
        alpha3Code: 'JPN',
        numericCode: '392',
        callingCode: '+81'
    },
    {
        name: 'Джерси',
        alpha2Code: 'JE',
        alpha3Code: 'JEY',
        numericCode: '832',
        callingCode: '+44'
    },
    {
        name: 'Иордания',
        alpha2Code: 'JO',
        alpha3Code: 'JOR',
        numericCode: '400',
        callingCode: '+962'
    },
    {
        name: 'Казахстан',
        alpha2Code: 'KZ',
        alpha3Code: 'KAZ',
        numericCode: '398',
        callingCode: '+7'
    },
    {
        name: 'Кения',
        alpha2Code: 'KE',
        alpha3Code: 'KEN',
        numericCode: '404',
        callingCode: '+254'
    },
    {
        name: 'Кирибати',
        alpha2Code: 'KI',
        alpha3Code: 'KIR',
        numericCode: '296',
        callingCode: '+686'
    },
    {
        name: 'Кувейт',
        alpha2Code: 'KW',
        alpha3Code: 'KWT',
        numericCode: '414',
        callingCode: '+965'
    },
    {
        name: 'Киргизия',
        alpha2Code: 'KG',
        alpha3Code: 'KGZ',
        numericCode: '417',
        callingCode: '+996'
    },
    {
        name: 'Лаос',
        alpha2Code: 'LA',
        alpha3Code: 'LAO',
        numericCode: '418',
        callingCode: '+856'
    },
    {
        name: 'Латвия',
        alpha2Code: 'LV',
        alpha3Code: 'LVA',
        numericCode: '428',
        callingCode: '+371'
    },
    {
        name: 'Ливан',
        alpha2Code: 'LB',
        alpha3Code: 'LBN',
        numericCode: '422',
        callingCode: '+961'
    },
    {
        name: 'Лесото',
        alpha2Code: 'LS',
        alpha3Code: 'LSO',
        numericCode: '426',
        callingCode: '+266'
    },
    {
        name: 'Либерия',
        alpha2Code: 'LR',
        alpha3Code: 'LBR',
        numericCode: '430',
        callingCode: '+231'
    },
    {
        name: 'Ливия',
        alpha2Code: 'LY',
        alpha3Code: 'LBY',
        numericCode: '434',
        callingCode: '+218'
    },
    {
        name: 'Лихтенштейн',
        alpha2Code: 'LI',
        alpha3Code: 'LIE',
        numericCode: '438',
        callingCode: '+423'
    },
    {
        name: 'Литва',
        alpha2Code: 'LT',
        alpha3Code: 'LTU',
        numericCode: '440',
        callingCode: '+370'
    },
    {
        name: 'Люксембург',
        alpha2Code: 'LU',
        alpha3Code: 'LUX',
        numericCode: '442',
        callingCode: '+352'
    },
    {
        name: 'Макао',
        alpha2Code: 'MO',
        alpha3Code: 'MAC',
        numericCode: '446',
        callingCode: '+853'
    },
    {
        name: 'Северная Македония',
        alpha2Code: 'MK',
        alpha3Code: 'MKD',
        numericCode: '807',
        callingCode: '+389'
    },
    {
        name: 'Мадагаскар',
        alpha2Code: 'MG',
        alpha3Code: 'MDG',
        numericCode: '450',
        callingCode: '+261'
    },
    {
        name: 'Малави',
        alpha2Code: 'MW',
        alpha3Code: 'MWI',
        numericCode: '454',
        callingCode: '+265'
    },
    {
        name: 'Малайзия',
        alpha2Code: 'MY',
        alpha3Code: 'MYS',
        numericCode: '458',
        callingCode: '+60'
    },
    {
        name: 'Мальдивы',
        alpha2Code: 'MV',
        alpha3Code: 'MDV',
        numericCode: '462',
        callingCode: '+960'
    },
    {
        name: 'Мали',
        alpha2Code: 'ML',
        alpha3Code: 'MLI',
        numericCode: '466',
        callingCode: '+223'
    },
    {
        name: 'Мальта',
        alpha2Code: 'MT',
        alpha3Code: 'MLT',
        numericCode: '470',
        callingCode: '+356'
    },
    {
        name: 'Маршалловы Острова',
        alpha2Code: 'MH',
        alpha3Code: 'MHL',
        numericCode: '584',
        callingCode: '+692'
    },
    {
        name: 'Мартиника',
        alpha2Code: 'MQ',
        alpha3Code: 'MTQ',
        numericCode: '474',
        callingCode: '+596'
    },
    {
        name: 'Мавритания',
        alpha2Code: 'MR',
        alpha3Code: 'MRT',
        numericCode: '478',
        callingCode: '+222'
    },
    {
        name: 'Маврикий',
        alpha2Code: 'MU',
        alpha3Code: 'MUS',
        numericCode: '480',
        callingCode: '+230'
    },
    {
        name: 'Майотта',
        alpha2Code: 'YT',
        alpha3Code: 'MYT',
        numericCode: '175',
        callingCode: '+262'
    },
    {
        name: 'Мексика',
        alpha2Code: 'MX',
        alpha3Code: 'MEX',
        numericCode: '484',
        callingCode: '+52'
    },
    {
        name: 'Микронезия',
        alpha2Code: 'FM',
        alpha3Code: 'FSM',
        numericCode: '583',
        callingCode: '+691'
    },
    {
        name: 'Молдова',
        alpha2Code: 'MD',
        alpha3Code: 'MDA',
        numericCode: '498',
        callingCode: '+373'
    },
    {
        name: 'Монако',
        alpha2Code: 'MC',
        alpha3Code: 'MCO',
        numericCode: '492',
        callingCode: '+377'
    },
    {
        name: 'Монголия',
        alpha2Code: 'MN',
        alpha3Code: 'MNG',
        numericCode: '496',
        callingCode: '+976'
    },
    {
        name: 'Черногория',
        alpha2Code: 'ME',
        alpha3Code: 'MNE',
        numericCode: '499',
        callingCode: '+382'
    },
    {
        name: 'Монтсеррат',
        alpha2Code: 'MS',
        alpha3Code: 'MSR',
        numericCode: '500',
        callingCode: '+1664'
    },
    {
        name: 'Марокко',
        alpha2Code: 'MA',
        alpha3Code: 'MAR',
        numericCode: '504',
        callingCode: '+212'
    },
    {
        name: 'Мозамбик',
        alpha2Code: 'MZ',
        alpha3Code: 'MOZ',
        numericCode: '508',
        callingCode: '+258'
    },
    {
        name: 'Мьянма',
        alpha2Code: 'MM',
        alpha3Code: 'MMR',
        numericCode: '104',
        callingCode: '+95'
    },
    {
        name: 'Намибия',
        alpha2Code: 'NA',
        alpha3Code: 'NAM',
        numericCode: '516',
        callingCode: '+264'
    },
    {
        name: 'Науру',
        alpha2Code: 'NR',
        alpha3Code: 'NRU',
        numericCode: '520',
        callingCode: '+674'
    },
    {
        name: 'Непал',
        alpha2Code: 'NP',
        alpha3Code: 'NPL',
        numericCode: '524',
        callingCode: '+977'
    },
    {
        name: 'Нидерланды',
        alpha2Code: 'NL',
        alpha3Code: 'NLD',
        numericCode: '528',
        callingCode: '+31'
    },
    {
        name: 'Новая Каледония',
        alpha2Code: 'NC',
        alpha3Code: 'NCL',
        numericCode: '540',
        callingCode: '+687'
    },
    {
        name: 'Новая Зеландия',
        alpha2Code: 'NZ',
        alpha3Code: 'NZL',
        numericCode: '554',
        callingCode: '+64'
    },
    {
        name: 'Никарагуа',
        alpha2Code: 'NI',
        alpha3Code: 'NIC',
        numericCode: '558',
        callingCode: '+505'
    },
    {
        name: 'Нигер',
        alpha2Code: 'NE',
        alpha3Code: 'NER',
        numericCode: '562',
        callingCode: '+227'
    },
    {
        name: 'Нигерия',
        alpha2Code: 'NG',
        alpha3Code: 'NGA',
        numericCode: '566',
        callingCode: '+234'
    },
    {
        name: 'Ниуэ',
        alpha2Code: 'NU',
        alpha3Code: 'NIU',
        numericCode: '570',
        callingCode: '+683'
    },
    {
        name: 'Остров Норфолк',
        alpha2Code: 'NF',
        alpha3Code: 'NFK',
        numericCode: '574',
        callingCode: '+672'
    },
    {
        name: 'КНДР (Корейская Народно-Демократическая Республика)',
        alpha2Code: 'KP',
        alpha3Code: 'PRK',
        numericCode: '408',
        callingCode: '+850'
    },
    {
        name: 'Северные Марианские Острова',
        alpha2Code: 'MP',
        alpha3Code: 'MNP',
        numericCode: '580',
        callingCode: '+1670'
    },
    {
        name: 'Норвегия',
        alpha2Code: 'NO',
        alpha3Code: 'NOR',
        numericCode: '578',
        callingCode: '+47'
    },
    {
        name: 'Оман',
        alpha2Code: 'OM',
        alpha3Code: 'OMN',
        numericCode: '512',
        callingCode: '+968'
    },
    {
        name: 'Пакистан',
        alpha2Code: 'PK',
        alpha3Code: 'PAK',
        numericCode: '586',
        callingCode: '+92'
    },
    {
        name: 'Палау',
        alpha2Code: 'PW',
        alpha3Code: 'PLW',
        numericCode: '585',
        callingCode: '+680'
    },
    {
        name: 'Государство Палестина',
        alpha2Code: 'PS',
        alpha3Code: 'PSE',
        numericCode: '275',
        callingCode: '+970'
    },
    {
        name: 'Панама',
        alpha2Code: 'PA',
        alpha3Code: 'PAN',
        numericCode: '591',
        callingCode: '+507'
    },
    {
        name: 'Папуа — Новая Гвинея',
        alpha2Code: 'PG',
        alpha3Code: 'PNG',
        numericCode: '598',
        callingCode: '+675'
    },
    {
        name: 'Парагвай',
        alpha2Code: 'PY',
        alpha3Code: 'PRY',
        numericCode: '600',
        callingCode: '+595'
    },
    {
        name: 'Перу',
        alpha2Code: 'PE',
        alpha3Code: 'PER',
        numericCode: '604',
        callingCode: '+51'
    },
    {
        name: 'Филиппины',
        alpha2Code: 'PH',
        alpha3Code: 'PHL',
        numericCode: '608',
        callingCode: '+63'
    },
    {
        name: 'Острова Питкэрн',
        alpha2Code: 'PN',
        alpha3Code: 'PCN',
        numericCode: '612',
        callingCode: '+64'
    },
    {
        name: 'Польша',
        alpha2Code: 'PL',
        alpha3Code: 'POL',
        numericCode: '616',
        callingCode: '+48'
    },
    {
        name: 'Португалия',
        alpha2Code: 'PT',
        alpha3Code: 'PRT',
        numericCode: '620',
        callingCode: '+351'
    },
    {
        name: 'Пуэрто-Рико',
        alpha2Code: 'PR',
        alpha3Code: 'PRI',
        numericCode: '630',
        callingCode: '+1'
    },
    {
        name: 'Катар',
        alpha2Code: 'QA',
        alpha3Code: 'QAT',
        numericCode: '634',
        callingCode: '+974'
    },
    {
        name: 'Республика Косово',
        alpha2Code: 'XK',
        alpha3Code: 'KOS',
        numericCode: null,
        callingCode: '+383'
    },
    {
        name: 'Реюньон',
        alpha2Code: 'RE',
        alpha3Code: 'REU',
        numericCode: '638',
        callingCode: '+262'
    },
    {
        name: 'Румыния',
        alpha2Code: 'RO',
        alpha3Code: 'ROU',
        numericCode: '642',
        callingCode: '+40'
    },
    {
        name: 'Россия',
        alpha2Code: 'RU',
        alpha3Code: 'RUS',
        numericCode: '643',
        callingCode: '+7'
    },
    {
        name: 'Руанда',
        alpha2Code: 'RW',
        alpha3Code: 'RWA',
        numericCode: '646',
        callingCode: '+250'
    },
    {
        name: 'Сен-Бартелеми (Карибы)',
        alpha2Code: 'BL',
        alpha3Code: 'BLM',
        numericCode: '652',
        callingCode: '+590'
    },
    {
        name: 'Острова Святой Елены, Вознесения и Тристан-да-Кунья',
        alpha2Code: 'SH',
        alpha3Code: 'SHN',
        numericCode: '654',
        callingCode: '+290'
    },
    {
        name: 'Сент-Китс и Невис',
        alpha2Code: 'KN',
        alpha3Code: 'KNA',
        numericCode: '659',
        callingCode: '+1869'
    },
    {
        name: 'Сент-Люсия',
        alpha2Code: 'LC',
        alpha3Code: 'LCA',
        numericCode: '662',
        callingCode: '+1758'
    },
    {
        name: 'Сен-Мартен',
        alpha2Code: 'MF',
        alpha3Code: 'MAF',
        numericCode: '663',
        callingCode: '+590'
    },
    {
        name: 'Сен-Пьер и Микелон',
        alpha2Code: 'PM',
        alpha3Code: 'SPM',
        numericCode: '666',
        callingCode: '+508'
    },
    {
        name: 'Сент-Винсент и Гренадины',
        alpha2Code: 'VC',
        alpha3Code: 'VCT',
        numericCode: '670',
        callingCode: '+1784'
    },
    {
        name: 'Самоа',
        alpha2Code: 'WS',
        alpha3Code: 'WSM',
        numericCode: '882',
        callingCode: '+685'
    },
    {
        name: 'Сан-Марино',
        alpha2Code: 'SM',
        alpha3Code: 'SMR',
        numericCode: '674',
        callingCode: '+378'
    },
    {
        name: 'Сан-Томе и Принсипи',
        alpha2Code: 'ST',
        alpha3Code: 'STP',
        numericCode: '678',
        callingCode: '+239'
    },
    {
        name: 'Саудовская Аравия',
        alpha2Code: 'SA',
        alpha3Code: 'SAU',
        numericCode: '682',
        callingCode: '+966'
    },
    {
        name: 'Сенегал',
        alpha2Code: 'SN',
        alpha3Code: 'SEN',
        numericCode: '686',
        callingCode: '+221'
    },
    {
        name: 'Сербия',
        alpha2Code: 'RS',
        alpha3Code: 'SRB',
        numericCode: '688',
        callingCode: '+381'
    },
    {
        name: 'Сейшельские Острова',
        alpha2Code: 'SC',
        alpha3Code: 'SYC',
        numericCode: '690',
        callingCode: '+248'
    },
    {
        name: 'Сьерра-Леоне',
        alpha2Code: 'SL',
        alpha3Code: 'SLE',
        numericCode: '694',
        callingCode: '+232'
    },
    {
        name: 'Сингапур',
        alpha2Code: 'SG',
        alpha3Code: 'SGP',
        numericCode: '702',
        callingCode: '+65'
    },
    {
        name: 'Синт-Мартен',
        alpha2Code: 'SX',
        alpha3Code: 'SXM',
        numericCode: '534',
        callingCode: '+1721'
    },
    {
        name: 'Словакия',
        alpha2Code: 'SK',
        alpha3Code: 'SVK',
        numericCode: '703',
        callingCode: '+421'
    },
    {
        name: 'Словения',
        alpha2Code: 'SI',
        alpha3Code: 'SVN',
        numericCode: '705',
        callingCode: '+386'
    },
    {
        name: 'Соломоновы Острова',
        alpha2Code: 'SB',
        alpha3Code: 'SLB',
        numericCode: '090',
        callingCode: '+677'
    },
    {
        name: 'Сомали',
        alpha2Code: 'SO',
        alpha3Code: 'SOM',
        numericCode: '706',
        callingCode: '+252'
    },
    {
        name: 'ЮАР',
        alpha2Code: 'ZA',
        alpha3Code: 'ZAF',
        numericCode: '710',
        callingCode: '+27'
    },
    {
        name: 'Южная Георгия и Южные Сандвичевы Острова',
        alpha2Code: 'GS',
        alpha3Code: 'SGS',
        numericCode: '239',
        callingCode: '+500'
    },
    {
        name: 'Республика Корея',
        alpha2Code: 'KR',
        alpha3Code: 'KOR',
        numericCode: '410',
        callingCode: '+82'
    },
    {
        name: 'Южный Судан',
        alpha2Code: 'SS',
        alpha3Code: 'SSD',
        numericCode: '728',
        callingCode: '+211'
    },
    {
        name: 'Испания',
        alpha2Code: 'ES',
        alpha3Code: 'ESP',
        numericCode: '724',
        callingCode: '+34'
    },
    {
        name: 'Шри-Ланка',
        alpha2Code: 'LK',
        alpha3Code: 'LKA',
        numericCode: '144',
        callingCode: '+94'
    },
    {
        name: 'Судан',
        alpha2Code: 'SD',
        alpha3Code: 'SDN',
        numericCode: '729',
        callingCode: '+249'
    },
    {
        name: 'Суринам',
        alpha2Code: 'SR',
        alpha3Code: 'SUR',
        numericCode: '740',
        callingCode: '+597'
    },
    {
        name: 'Шпицберген и Ян-Майен',
        alpha2Code: 'SJ',
        alpha3Code: 'SJM',
        numericCode: '744',
        callingCode: '+4779'
    },
    {
        name: 'Эсватини',
        alpha2Code: 'SZ',
        alpha3Code: 'SWZ',
        numericCode: '748',
        callingCode: '+268'
    },
    {
        name: 'Швеция',
        alpha2Code: 'SE',
        alpha3Code: 'SWE',
        numericCode: '752',
        callingCode: '+46'
    },
    {
        name: 'Швейцария',
        alpha2Code: 'CH',
        alpha3Code: 'CHE',
        numericCode: '756',
        callingCode: '+41'
    },
    {
        name: 'Сирия',
        alpha2Code: 'SY',
        alpha3Code: 'SYR',
        numericCode: '760',
        callingCode: '+963'
    },
    {
        name: 'Китайская Республика',
        alpha2Code: 'TW',
        alpha3Code: 'TWN',
        numericCode: '158',
        callingCode: '+886'
    },
    {
        name: 'Таджикистан',
        alpha2Code: 'TJ',
        alpha3Code: 'TJK',
        numericCode: '762',
        callingCode: '+992'
    },
    {
        name: 'Танзания',
        alpha2Code: 'TZ',
        alpha3Code: 'TZA',
        numericCode: '834',
        callingCode: '+255'
    },
    {
        name: 'Таиланд',
        alpha2Code: 'TH',
        alpha3Code: 'THA',
        numericCode: '764',
        callingCode: '+66'
    },
    {
        name: 'Восточный Тимор',
        alpha2Code: 'TL',
        alpha3Code: 'TLS',
        numericCode: '626',
        callingCode: '+670'
    },
    {
        name: 'Того',
        alpha2Code: 'TG',
        alpha3Code: 'TGO',
        numericCode: '768',
        callingCode: '+228'
    },
    {
        name: 'Токелау',
        alpha2Code: 'TK',
        alpha3Code: 'TKL',
        numericCode: '772',
        callingCode: '+690'
    },
    {
        name: 'Тонга',
        alpha2Code: 'TO',
        alpha3Code: 'TON',
        numericCode: '776',
        callingCode: '+676'
    },
    {
        name: 'Тринидад и Тобаго',
        alpha2Code: 'TT',
        alpha3Code: 'TTO',
        numericCode: '780',
        callingCode: '+1868'
    },
    {
        name: 'Тунис',
        alpha2Code: 'TN',
        alpha3Code: 'TUN',
        numericCode: '788',
        callingCode: '+216'
    },
    {
        name: 'Турция',
        alpha2Code: 'TR',
        alpha3Code: 'TUR',
        numericCode: '792',
        callingCode: '+90'
    },
    {
        name: 'Туркмения',
        alpha2Code: 'TM',
        alpha3Code: 'TKM',
        numericCode: '795',
        callingCode: '+993'
    },
    {
        name: 'Теркс и Кайкос',
        alpha2Code: 'TC',
        alpha3Code: 'TCA',
        numericCode: '796',
        callingCode: '+1649'
    },
    {
        name: 'Тувалу',
        alpha2Code: 'TV',
        alpha3Code: 'TUV',
        numericCode: '798',
        callingCode: '+688'
    },
    {
        name: 'Уганда',
        alpha2Code: 'UG',
        alpha3Code: 'UGA',
        numericCode: '800',
        callingCode: '+256'
    },
    {
        name: 'Украина',
        alpha2Code: 'UA',
        alpha3Code: 'UKR',
        numericCode: '804',
        callingCode: '+380'
    },
    {
        name: 'ОАЭ',
        alpha2Code: 'AE',
        alpha3Code: 'ARE',
        numericCode: '784',
        callingCode: '+971'
    },
    {
        name: 'Великобритания',
        alpha2Code: 'GB',
        alpha3Code: 'GBR',
        numericCode: '826',
        callingCode: '+44'
    },
    {
        name: 'США',
        alpha2Code: 'US',
        alpha3Code: 'USA',
        numericCode: '840',
        callingCode: '+1'
    },
    {
        name: 'Уругвай',
        alpha2Code: 'UY',
        alpha3Code: 'URY',
        numericCode: '858',
        callingCode: '+598'
    },
    {
        name: 'Узбекистан',
        alpha2Code: 'UZ',
        alpha3Code: 'UZB',
        numericCode: '860',
        callingCode: '+998'
    },
    {
        name: 'Вануату',
        alpha2Code: 'VU',
        alpha3Code: 'VUT',
        numericCode: '548',
        callingCode: '+678'
    },
    {
        name: 'Венесуэла',
        alpha2Code: 'VE',
        alpha3Code: 'VEN',
        numericCode: '862',
        callingCode: '+58'
    },
    {
        name: 'Вьетнам',
        alpha2Code: 'VN',
        alpha3Code: 'VNM',
        numericCode: '704',
        callingCode: '+84'
    },
    {
        name: 'Уоллис и Футуна',
        alpha2Code: 'WF',
        alpha3Code: 'WLF',
        numericCode: '876',
        callingCode: '+681'
    },
    {
        name: 'САДР',
        alpha2Code: 'EH',
        alpha3Code: 'ESH',
        numericCode: '732',
        callingCode: '+212'
    },
    {
        name: 'Йемен',
        alpha2Code: 'YE',
        alpha3Code: 'YEM',
        numericCode: '887',
        callingCode: '+967'
    },
    {
        name: 'Замбия',
        alpha2Code: 'ZM',
        alpha3Code: 'ZMB',
        numericCode: '894',
        callingCode: '+260'
    },
    {
        name: 'Зимбабве',
        alpha2Code: 'ZW',
        alpha3Code: 'ZWE',
        numericCode: '716',
        callingCode: '+263'
    }
];

var ru = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB_RU: COUNTRIES_DB_RU
});

const COUNTRIES_DB_UA = [
    {
        name: 'Афганістан',
        alpha2Code: 'AF',
        alpha3Code: 'AFG',
        numericCode: '004',
        callingCode: '+93'
    },
    {
        name: 'Аландські острови',
        alpha2Code: 'AX',
        alpha3Code: 'ALA',
        numericCode: '248',
        callingCode: '+358'
    },
    {
        name: 'Албанія',
        alpha2Code: 'AL',
        alpha3Code: 'ALB',
        numericCode: '008',
        callingCode: '+355'
    },
    {
        name: 'Алжир',
        alpha2Code: 'DZ',
        alpha3Code: 'DZA',
        numericCode: '012',
        callingCode: '+213'
    },
    {
        name: 'Американське Самоа',
        alpha2Code: 'AS',
        alpha3Code: 'ASM',
        numericCode: '016',
        callingCode: '+1684'
    },
    {
        name: 'Андорра',
        alpha2Code: 'AD',
        alpha3Code: 'AND',
        numericCode: '020',
        callingCode: '+376'
    },
    {
        name: 'Ангола',
        alpha2Code: 'AO',
        alpha3Code: 'AGO',
        numericCode: '024',
        callingCode: '+244'
    },
    {
        name: 'Ангілья',
        alpha2Code: 'AI',
        alpha3Code: 'AIA',
        numericCode: '660',
        callingCode: '+1264'
    },
    {
        name: 'Антарктида',
        alpha2Code: 'AQ',
        alpha3Code: 'ATA',
        numericCode: '010',
        callingCode: '+672'
    },
    {
        name: 'Антигуа і Барбуда',
        alpha2Code: 'AG',
        alpha3Code: 'ATG',
        numericCode: '028',
        callingCode: '+1268'
    },
    {
        name: 'Аргентина',
        alpha2Code: 'AR',
        alpha3Code: 'ARG',
        numericCode: '032',
        callingCode: '+54'
    },
    {
        name: 'Вірменія',
        alpha2Code: 'AM',
        alpha3Code: 'ARM',
        numericCode: '051',
        callingCode: '+374'
    },
    {
        name: 'Аруба',
        alpha2Code: 'AW',
        alpha3Code: 'ABW',
        numericCode: '533',
        callingCode: '+297'
    },
    {
        name: 'Австралія',
        alpha2Code: 'AU',
        alpha3Code: 'AUS',
        numericCode: '036',
        callingCode: '+61'
    },
    {
        name: 'Австрія',
        alpha2Code: 'AT',
        alpha3Code: 'AUT',
        numericCode: '040',
        callingCode: '+43'
    },
    {
        name: 'Азербайджан',
        alpha2Code: 'AZ',
        alpha3Code: 'AZE',
        numericCode: '031',
        callingCode: '+994'
    },
    {
        name: 'Багамські Острови',
        alpha2Code: 'BS',
        alpha3Code: 'BHS',
        numericCode: '044',
        callingCode: '+1242'
    },
    {
        name: 'Бахрейн',
        alpha2Code: 'BH',
        alpha3Code: 'BHR',
        numericCode: '048',
        callingCode: '+973'
    },
    {
        name: 'Бангладеш',
        alpha2Code: 'BD',
        alpha3Code: 'BGD',
        numericCode: '050',
        callingCode: '+880'
    },
    {
        name: 'Барбадос',
        alpha2Code: 'BB',
        alpha3Code: 'BRB',
        numericCode: '052',
        callingCode: '+1246'
    },
    {
        name: 'Білорусь',
        alpha2Code: 'BY',
        alpha3Code: 'BLR',
        numericCode: '112',
        callingCode: '+375'
    },
    {
        name: 'Бельгія',
        alpha2Code: 'BE',
        alpha3Code: 'BEL',
        numericCode: '056',
        callingCode: '+32'
    },
    {
        name: 'Беліз',
        alpha2Code: 'BZ',
        alpha3Code: 'BLZ',
        numericCode: '084',
        callingCode: '+501'
    },
    {
        name: 'Бенін',
        alpha2Code: 'BJ',
        alpha3Code: 'BEN',
        numericCode: '204',
        callingCode: '+229'
    },
    {
        name: 'Бермудські Острови',
        alpha2Code: 'BM',
        alpha3Code: 'BMU',
        numericCode: '060',
        callingCode: '+1441'
    },
    {
        name: 'Бутан',
        alpha2Code: 'BT',
        alpha3Code: 'BTN',
        numericCode: '064',
        callingCode: '+975'
    },
    {
        name: 'Болівія',
        alpha2Code: 'BO',
        alpha3Code: 'BOL',
        numericCode: '068',
        callingCode: '+591'
    },
    {
        name: 'Карибські Нідерланди',
        alpha2Code: 'BQ',
        alpha3Code: 'BES',
        numericCode: '535',
        callingCode: '+5997'
    },
    {
        name: 'Боснія і Герцеговина',
        alpha2Code: 'BA',
        alpha3Code: 'BIH',
        numericCode: '070',
        callingCode: '+387'
    },
    {
        name: 'Ботсвана',
        alpha2Code: 'BW',
        alpha3Code: 'BWA',
        numericCode: '072',
        callingCode: '+267'
    },
    {
        name: 'Острів Буве',
        alpha2Code: 'BV',
        alpha3Code: 'BVT',
        numericCode: '074',
        callingCode: '+'
    },
    {
        name: 'Бразилія',
        alpha2Code: 'BR',
        alpha3Code: 'BRA',
        numericCode: '076',
        callingCode: '+55'
    },
    {
        name: 'Британська Територія в Індійському Океані',
        alpha2Code: 'IO',
        alpha3Code: 'IOT',
        numericCode: '086',
        callingCode: '+246'
    },
    {
        name: 'Зовнішні малі острови США',
        alpha2Code: 'UM',
        alpha3Code: 'UMI',
        numericCode: '581',
        callingCode: '+'
    },
    {
        name: 'Британські Віргінські Острови',
        alpha2Code: 'VG',
        alpha3Code: 'VGB',
        numericCode: '092',
        callingCode: '+1284'
    },
    {
        name: 'Американські Віргінські Острови',
        alpha2Code: 'VI',
        alpha3Code: 'VIR',
        numericCode: '850',
        callingCode: '+1 340'
    },
    {
        name: 'Бруней',
        alpha2Code: 'BN',
        alpha3Code: 'BRN',
        numericCode: '096',
        callingCode: '+673'
    },
    {
        name: 'Болгарія',
        alpha2Code: 'BG',
        alpha3Code: 'BGR',
        numericCode: '100',
        callingCode: '+359'
    },
    {
        name: 'Буркіна-Фасо',
        alpha2Code: 'BF',
        alpha3Code: 'BFA',
        numericCode: '854',
        callingCode: '+226'
    },
    {
        name: 'Бурунді',
        alpha2Code: 'BI',
        alpha3Code: 'BDI',
        numericCode: '108',
        callingCode: '+257'
    },
    {
        name: 'Камбоджа',
        alpha2Code: 'KH',
        alpha3Code: 'KHM',
        numericCode: '116',
        callingCode: '+855'
    },
    {
        name: 'Камерун',
        alpha2Code: 'CM',
        alpha3Code: 'CMR',
        numericCode: '120',
        callingCode: '+237'
    },
    {
        name: 'Канада',
        alpha2Code: 'CA',
        alpha3Code: 'CAN',
        numericCode: '124',
        callingCode: '+1'
    },
    {
        name: 'Кабо-Верде',
        alpha2Code: 'CV',
        alpha3Code: 'CPV',
        numericCode: '132',
        callingCode: '+238'
    },
    {
        name: 'Кайманові Острови',
        alpha2Code: 'KY',
        alpha3Code: 'CYM',
        numericCode: '136',
        callingCode: '+1345'
    },
    {
        name: 'Центральноафриканська Республіка',
        alpha2Code: 'CF',
        alpha3Code: 'CAF',
        numericCode: '140',
        callingCode: '+236'
    },
    {
        name: 'Чад',
        alpha2Code: 'TD',
        alpha3Code: 'TCD',
        numericCode: '148',
        callingCode: '+235'
    },
    {
        name: 'Чилі',
        alpha2Code: 'CL',
        alpha3Code: 'CHL',
        numericCode: '152',
        callingCode: '+56'
    },
    {
        name: 'Китайська Народна Республіка',
        alpha2Code: 'CN',
        alpha3Code: 'CHN',
        numericCode: '156',
        callingCode: '+86'
    },
    {
        name: 'Острів Різдва',
        alpha2Code: 'CX',
        alpha3Code: 'CXR',
        numericCode: '162',
        callingCode: '+61'
    },
    {
        name: 'Кокосові острови',
        alpha2Code: 'CC',
        alpha3Code: 'CCK',
        numericCode: '166',
        callingCode: '+61'
    },
    {
        name: 'Колумбія',
        alpha2Code: 'CO',
        alpha3Code: 'COL',
        numericCode: '170',
        callingCode: '+57'
    },
    {
        name: 'Коморські Острови',
        alpha2Code: 'KM',
        alpha3Code: 'COM',
        numericCode: '174',
        callingCode: '+269'
    },
    {
        name: 'Республіка Конго',
        alpha2Code: 'CG',
        alpha3Code: 'COG',
        numericCode: '178',
        callingCode: '+242'
    },
    {
        name: 'ДР Конго',
        alpha2Code: 'CD',
        alpha3Code: 'COD',
        numericCode: '180',
        callingCode: '+243'
    },
    {
        name: 'Острови Кука',
        alpha2Code: 'CK',
        alpha3Code: 'COK',
        numericCode: '184',
        callingCode: '+682'
    },
    {
        name: 'Коста-Рика',
        alpha2Code: 'CR',
        alpha3Code: 'CRI',
        numericCode: '188',
        callingCode: '+506'
    },
    {
        name: 'Хорватія',
        alpha2Code: 'HR',
        alpha3Code: 'HRV',
        numericCode: '191',
        callingCode: '+385'
    },
    {
        name: 'Куба',
        alpha2Code: 'CU',
        alpha3Code: 'CUB',
        numericCode: '192',
        callingCode: '+53'
    },
    {
        name: 'Кюрасао',
        alpha2Code: 'CW',
        alpha3Code: 'CUW',
        numericCode: '531',
        callingCode: '+599'
    },
    {
        name: 'Кіпр',
        alpha2Code: 'CY',
        alpha3Code: 'CYP',
        numericCode: '196',
        callingCode: '+357'
    },
    {
        name: 'Чехія',
        alpha2Code: 'CZ',
        alpha3Code: 'CZE',
        numericCode: '203',
        callingCode: '+420'
    },
    {
        name: 'Данія',
        alpha2Code: 'DK',
        alpha3Code: 'DNK',
        numericCode: '208',
        callingCode: '+45'
    },
    {
        name: 'Джибуті',
        alpha2Code: 'DJ',
        alpha3Code: 'DJI',
        numericCode: '262',
        callingCode: '+253'
    },
    {
        name: 'Домініка',
        alpha2Code: 'DM',
        alpha3Code: 'DMA',
        numericCode: '212',
        callingCode: '+1767'
    },
    {
        name: 'Домініканська Республіка',
        alpha2Code: 'DO',
        alpha3Code: 'DOM',
        numericCode: '214',
        callingCode: '+1809'
    },
    {
        name: 'Еквадор',
        alpha2Code: 'EC',
        alpha3Code: 'ECU',
        numericCode: '218',
        callingCode: '+593'
    },
    {
        name: 'Єгипет',
        alpha2Code: 'EG',
        alpha3Code: 'EGY',
        numericCode: '818',
        callingCode: '+20'
    },
    {
        name: 'Сальвадор',
        alpha2Code: 'SV',
        alpha3Code: 'SLV',
        numericCode: '222',
        callingCode: '+503'
    },
    {
        name: 'Екваторіальна Гвінея',
        alpha2Code: 'GQ',
        alpha3Code: 'GNQ',
        numericCode: '226',
        callingCode: '+240'
    },
    {
        name: 'Еритрея',
        alpha2Code: 'ER',
        alpha3Code: 'ERI',
        numericCode: '232',
        callingCode: '+291'
    },
    {
        name: 'Естонія',
        alpha2Code: 'EE',
        alpha3Code: 'EST',
        numericCode: '233',
        callingCode: '+372'
    },
    {
        name: 'Ефіопія',
        alpha2Code: 'ET',
        alpha3Code: 'ETH',
        numericCode: '231',
        callingCode: '+251'
    },
    {
        name: 'Фолклендські Острови',
        alpha2Code: 'FK',
        alpha3Code: 'FLK',
        numericCode: '238',
        callingCode: '+500'
    },
    {
        name: 'Фарерські острови',
        alpha2Code: 'FO',
        alpha3Code: 'FRO',
        numericCode: '234',
        callingCode: '+298'
    },
    {
        name: 'Фіджі',
        alpha2Code: 'FJ',
        alpha3Code: 'FJI',
        numericCode: '242',
        callingCode: '+679'
    },
    {
        name: 'Фінляндія',
        alpha2Code: 'FI',
        alpha3Code: 'FIN',
        numericCode: '246',
        callingCode: '+358'
    },
    {
        name: 'Франція',
        alpha2Code: 'FR',
        alpha3Code: 'FRA',
        numericCode: '250',
        callingCode: '+33'
    },
    {
        name: 'Французька Гвіана',
        alpha2Code: 'GF',
        alpha3Code: 'GUF',
        numericCode: '254',
        callingCode: '+594'
    },
    {
        name: 'Французька Полінезія',
        alpha2Code: 'PF',
        alpha3Code: 'PYF',
        numericCode: '258',
        callingCode: '+689'
    },
    {
        name: 'Французькі Південні і Антарктичні Території',
        alpha2Code: 'TF',
        alpha3Code: 'ATF',
        numericCode: '260',
        callingCode: '+'
    },
    {
        name: 'Габон',
        alpha2Code: 'GA',
        alpha3Code: 'GAB',
        numericCode: '266',
        callingCode: '+241'
    },
    {
        name: 'Гамбія',
        alpha2Code: 'GM',
        alpha3Code: 'GMB',
        numericCode: '270',
        callingCode: '+220'
    },
    {
        name: 'Грузія',
        alpha2Code: 'GE',
        alpha3Code: 'GEO',
        numericCode: '268',
        callingCode: '+995'
    },
    {
        name: 'Німеччина',
        alpha2Code: 'DE',
        alpha3Code: 'DEU',
        numericCode: '276',
        callingCode: '+49'
    },
    {
        name: 'Гана',
        alpha2Code: 'GH',
        alpha3Code: 'GHA',
        numericCode: '288',
        callingCode: '+233'
    },
    {
        name: 'Гібралтар',
        alpha2Code: 'GI',
        alpha3Code: 'GIB',
        numericCode: '292',
        callingCode: '+350'
    },
    {
        name: 'Греція',
        alpha2Code: 'GR',
        alpha3Code: 'GRC',
        numericCode: '300',
        callingCode: '+30'
    },
    {
        name: 'Гренландія',
        alpha2Code: 'GL',
        alpha3Code: 'GRL',
        numericCode: '304',
        callingCode: '+299'
    },
    {
        name: 'Гренада',
        alpha2Code: 'GD',
        alpha3Code: 'GRD',
        numericCode: '308',
        callingCode: '+1473'
    },
    {
        name: 'Гваделупа',
        alpha2Code: 'GP',
        alpha3Code: 'GLP',
        numericCode: '312',
        callingCode: '+590'
    },
    {
        name: 'Гуам',
        alpha2Code: 'GU',
        alpha3Code: 'GUM',
        numericCode: '316',
        callingCode: '+1671'
    },
    {
        name: 'Гватемала',
        alpha2Code: 'GT',
        alpha3Code: 'GTM',
        numericCode: '320',
        callingCode: '+502'
    },
    {
        name: 'Гернсі',
        alpha2Code: 'GG',
        alpha3Code: 'GGY',
        numericCode: '831',
        callingCode: '+44'
    },
    {
        name: 'Гвінея',
        alpha2Code: 'GN',
        alpha3Code: 'GIN',
        numericCode: '324',
        callingCode: '+224'
    },
    {
        name: 'Гвінея-Бісау',
        alpha2Code: 'GW',
        alpha3Code: 'GNB',
        numericCode: '624',
        callingCode: '+245'
    },
    {
        name: 'Гаяна',
        alpha2Code: 'GY',
        alpha3Code: 'GUY',
        numericCode: '328',
        callingCode: '+592'
    },
    {
        name: 'Гаїті',
        alpha2Code: 'HT',
        alpha3Code: 'HTI',
        numericCode: '332',
        callingCode: '+509'
    },
    {
        name: 'Острів Херд і острови Макдональд',
        alpha2Code: 'HM',
        alpha3Code: 'HMD',
        numericCode: '334',
        callingCode: '+'
    },
    {
        name: 'Ватикан',
        alpha2Code: 'VA',
        alpha3Code: 'VAT',
        numericCode: '336',
        callingCode: '+379'
    },
    {
        name: 'Гондурас',
        alpha2Code: 'HN',
        alpha3Code: 'HND',
        numericCode: '340',
        callingCode: '+504'
    },
    {
        name: 'Гонконг',
        alpha2Code: 'HK',
        alpha3Code: 'HKG',
        numericCode: '344',
        callingCode: '+852'
    },
    {
        name: 'Угорщина',
        alpha2Code: 'HU',
        alpha3Code: 'HUN',
        numericCode: '348',
        callingCode: '+36'
    },
    {
        name: 'Ісландія',
        alpha2Code: 'IS',
        alpha3Code: 'ISL',
        numericCode: '352',
        callingCode: '+354'
    },
    {
        name: 'Індія',
        alpha2Code: 'IN',
        alpha3Code: 'IND',
        numericCode: '356',
        callingCode: '+91'
    },
    {
        name: 'Індонезія',
        alpha2Code: 'ID',
        alpha3Code: 'IDN',
        numericCode: '360',
        callingCode: '+62'
    },
    {
        name: 'Кот-д\'Івуар',
        alpha2Code: 'CI',
        alpha3Code: 'CIV',
        numericCode: '384',
        callingCode: '+225'
    },
    {
        name: 'Іран',
        alpha2Code: 'IR',
        alpha3Code: 'IRN',
        numericCode: '364',
        callingCode: '+98'
    },
    {
        name: 'Ірак',
        alpha2Code: 'IQ',
        alpha3Code: 'IRQ',
        numericCode: '368',
        callingCode: '+964'
    },
    {
        name: 'Ірландія',
        alpha2Code: 'IE',
        alpha3Code: 'IRL',
        numericCode: '372',
        callingCode: '+353'
    },
    {
        name: 'Острів Мен',
        alpha2Code: 'IM',
        alpha3Code: 'IMN',
        numericCode: '833',
        callingCode: '+44'
    },
    {
        name: 'Ізраїль',
        alpha2Code: 'IL',
        alpha3Code: 'ISR',
        numericCode: '376',
        callingCode: '+972'
    },
    {
        name: 'Італія',
        alpha2Code: 'IT',
        alpha3Code: 'ITA',
        numericCode: '380',
        callingCode: '+39'
    },
    {
        name: 'Ямайка',
        alpha2Code: 'JM',
        alpha3Code: 'JAM',
        numericCode: '388',
        callingCode: '+1876'
    },
    {
        name: 'Японія',
        alpha2Code: 'JP',
        alpha3Code: 'JPN',
        numericCode: '392',
        callingCode: '+81'
    },
    {
        name: 'Джерсі',
        alpha2Code: 'JE',
        alpha3Code: 'JEY',
        numericCode: '832',
        callingCode: '+44'
    },
    {
        name: 'Йорданія',
        alpha2Code: 'JO',
        alpha3Code: 'JOR',
        numericCode: '400',
        callingCode: '+962'
    },
    {
        name: 'Казахстан',
        alpha2Code: 'KZ',
        alpha3Code: 'KAZ',
        numericCode: '398',
        callingCode: '+7'
    },
    {
        name: 'Кенія',
        alpha2Code: 'KE',
        alpha3Code: 'KEN',
        numericCode: '404',
        callingCode: '+254'
    },
    {
        name: 'Кірибаті',
        alpha2Code: 'KI',
        alpha3Code: 'KIR',
        numericCode: '296',
        callingCode: '+686'
    },
    {
        name: 'Кувейт',
        alpha2Code: 'KW',
        alpha3Code: 'KWT',
        numericCode: '414',
        callingCode: '+965'
    },
    {
        name: 'Киргизстан',
        alpha2Code: 'KG',
        alpha3Code: 'KGZ',
        numericCode: '417',
        callingCode: '+996'
    },
    {
        name: 'Лаос',
        alpha2Code: 'LA',
        alpha3Code: 'LAO',
        numericCode: '418',
        callingCode: '+856'
    },
    {
        name: 'Латвія',
        alpha2Code: 'LV',
        alpha3Code: 'LVA',
        numericCode: '428',
        callingCode: '+371'
    },
    {
        name: 'Ліван',
        alpha2Code: 'LB',
        alpha3Code: 'LBN',
        numericCode: '422',
        callingCode: '+961'
    },
    {
        name: 'Лесото',
        alpha2Code: 'LS',
        alpha3Code: 'LSO',
        numericCode: '426',
        callingCode: '+266'
    },
    {
        name: 'Ліберія',
        alpha2Code: 'LR',
        alpha3Code: 'LBR',
        numericCode: '430',
        callingCode: '+231'
    },
    {
        name: 'Лівія',
        alpha2Code: 'LY',
        alpha3Code: 'LBY',
        numericCode: '434',
        callingCode: '+218'
    },
    {
        name: 'Ліхтенштейн',
        alpha2Code: 'LI',
        alpha3Code: 'LIE',
        numericCode: '438',
        callingCode: '+423'
    },
    {
        name: 'Литва',
        alpha2Code: 'LT',
        alpha3Code: 'LTU',
        numericCode: '440',
        callingCode: '+370'
    },
    {
        name: 'Люксембург',
        alpha2Code: 'LU',
        alpha3Code: 'LUX',
        numericCode: '442',
        callingCode: '+352'
    },
    {
        name: 'Макао',
        alpha2Code: 'MO',
        alpha3Code: 'MAC',
        numericCode: '446',
        callingCode: '+853'
    },
    {
        name: 'Північна Македонія',
        alpha2Code: 'MK',
        alpha3Code: 'MKD',
        numericCode: '807',
        callingCode: '+389'
    },
    {
        name: 'Мадагаскар',
        alpha2Code: 'MG',
        alpha3Code: 'MDG',
        numericCode: '450',
        callingCode: '+261'
    },
    {
        name: 'Малаві',
        alpha2Code: 'MW',
        alpha3Code: 'MWI',
        numericCode: '454',
        callingCode: '+265'
    },
    {
        name: 'Малайзія',
        alpha2Code: 'MY',
        alpha3Code: 'MYS',
        numericCode: '458',
        callingCode: '+60'
    },
    {
        name: 'Мальдіви',
        alpha2Code: 'MV',
        alpha3Code: 'MDV',
        numericCode: '462',
        callingCode: '+960'
    },
    {
        name: 'Малі',
        alpha2Code: 'ML',
        alpha3Code: 'MLI',
        numericCode: '466',
        callingCode: '+223'
    },
    {
        name: 'Мальта',
        alpha2Code: 'MT',
        alpha3Code: 'MLT',
        numericCode: '470',
        callingCode: '+356'
    },
    {
        name: 'Маршаллові Острови',
        alpha2Code: 'MH',
        alpha3Code: 'MHL',
        numericCode: '584',
        callingCode: '+692'
    },
    {
        name: 'Мартиніка',
        alpha2Code: 'MQ',
        alpha3Code: 'MTQ',
        numericCode: '474',
        callingCode: '+596'
    },
    {
        name: 'Мавританія',
        alpha2Code: 'MR',
        alpha3Code: 'MRT',
        numericCode: '478',
        callingCode: '+222'
    },
    {
        name: 'Маврикій',
        alpha2Code: 'MU',
        alpha3Code: 'MUS',
        numericCode: '480',
        callingCode: '+230'
    },
    {
        name: 'Майотта',
        alpha2Code: 'YT',
        alpha3Code: 'MYT',
        numericCode: '175',
        callingCode: '+262'
    },
    {
        name: 'Мексика',
        alpha2Code: 'MX',
        alpha3Code: 'MEX',
        numericCode: '484',
        callingCode: '+52'
    },
    {
        name: 'Федеративні Штати Мікронезії',
        alpha2Code: 'FM',
        alpha3Code: 'FSM',
        numericCode: '583',
        callingCode: '+691'
    },
    {
        name: 'Молдова',
        alpha2Code: 'MD',
        alpha3Code: 'MDA',
        numericCode: '498',
        callingCode: '+373'
    },
    {
        name: 'Монако',
        alpha2Code: 'MC',
        alpha3Code: 'MCO',
        numericCode: '492',
        callingCode: '+377'
    },
    {
        name: 'Монголія',
        alpha2Code: 'MN',
        alpha3Code: 'MNG',
        numericCode: '496',
        callingCode: '+976'
    },
    {
        name: 'Чорногорія',
        alpha2Code: 'ME',
        alpha3Code: 'MNE',
        numericCode: '499',
        callingCode: '+382'
    },
    {
        name: 'Монтсеррат',
        alpha2Code: 'MS',
        alpha3Code: 'MSR',
        numericCode: '500',
        callingCode: '+1664'
    },
    {
        name: 'Марокко',
        alpha2Code: 'MA',
        alpha3Code: 'MAR',
        numericCode: '504',
        callingCode: '+212'
    },
    {
        name: 'Мозамбік',
        alpha2Code: 'MZ',
        alpha3Code: 'MOZ',
        numericCode: '508',
        callingCode: '+258'
    },
    {
        name: 'М\'янма',
        alpha2Code: 'MM',
        alpha3Code: 'MMR',
        numericCode: '104',
        callingCode: '+95'
    },
    {
        name: 'Намібія',
        alpha2Code: 'NA',
        alpha3Code: 'NAM',
        numericCode: '516',
        callingCode: '+264'
    },
    {
        name: 'Науру',
        alpha2Code: 'NR',
        alpha3Code: 'NRU',
        numericCode: '520',
        callingCode: '+674'
    },
    {
        name: 'Непал',
        alpha2Code: 'NP',
        alpha3Code: 'NPL',
        numericCode: '524',
        callingCode: '+977'
    },
    {
        name: 'Нідерланди',
        alpha2Code: 'NL',
        alpha3Code: 'NLD',
        numericCode: '528',
        callingCode: '+31'
    },
    {
        name: 'Нова Каледонія',
        alpha2Code: 'NC',
        alpha3Code: 'NCL',
        numericCode: '540',
        callingCode: '+687'
    },
    {
        name: 'Нова Зеландія',
        alpha2Code: 'NZ',
        alpha3Code: 'NZL',
        numericCode: '554',
        callingCode: '+64'
    },
    {
        name: 'Нікарагуа',
        alpha2Code: 'NI',
        alpha3Code: 'NIC',
        numericCode: '558',
        callingCode: '+505'
    },
    {
        name: 'Нігер',
        alpha2Code: 'NE',
        alpha3Code: 'NER',
        numericCode: '562',
        callingCode: '+227'
    },
    {
        name: 'Нігерія',
        alpha2Code: 'NG',
        alpha3Code: 'NGA',
        numericCode: '566',
        callingCode: '+234'
    },
    {
        name: 'Ніуе',
        alpha2Code: 'NU',
        alpha3Code: 'NIU',
        numericCode: '570',
        callingCode: '+683'
    },
    {
        name: 'Острів Норфолк',
        alpha2Code: 'NF',
        alpha3Code: 'NFK',
        numericCode: '574',
        callingCode: '+672'
    },
    {
        name: 'Північна Корея',
        alpha2Code: 'KP',
        alpha3Code: 'PRK',
        numericCode: '408',
        callingCode: '+850'
    },
    {
        name: 'Північні Маріанські Острови',
        alpha2Code: 'MP',
        alpha3Code: 'MNP',
        numericCode: '580',
        callingCode: '+1670'
    },
    {
        name: 'Норвегія',
        alpha2Code: 'NO',
        alpha3Code: 'NOR',
        numericCode: '578',
        callingCode: '+47'
    },
    {
        name: 'Оман',
        alpha2Code: 'OM',
        alpha3Code: 'OMN',
        numericCode: '512',
        callingCode: '+968'
    },
    {
        name: 'Пакистан',
        alpha2Code: 'PK',
        alpha3Code: 'PAK',
        numericCode: '586',
        callingCode: '+92'
    },
    {
        name: 'Палау',
        alpha2Code: 'PW',
        alpha3Code: 'PLW',
        numericCode: '585',
        callingCode: '+680'
    },
    {
        name: 'Палестина',
        alpha2Code: 'PS',
        alpha3Code: 'PSE',
        numericCode: '275',
        callingCode: '+970'
    },
    {
        name: 'Панама',
        alpha2Code: 'PA',
        alpha3Code: 'PAN',
        numericCode: '591',
        callingCode: '+507'
    },
    {
        name: 'Папуа Нова Гвінея',
        alpha2Code: 'PG',
        alpha3Code: 'PNG',
        numericCode: '598',
        callingCode: '+675'
    },
    {
        name: 'Парагвай',
        alpha2Code: 'PY',
        alpha3Code: 'PRY',
        numericCode: '600',
        callingCode: '+595'
    },
    {
        name: 'Перу',
        alpha2Code: 'PE',
        alpha3Code: 'PER',
        numericCode: '604',
        callingCode: '+51'
    },
    {
        name: 'Філіппіни',
        alpha2Code: 'PH',
        alpha3Code: 'PHL',
        numericCode: '608',
        callingCode: '+63'
    },
    {
        name: 'Піткерн',
        alpha2Code: 'PN',
        alpha3Code: 'PCN',
        numericCode: '612',
        callingCode: '+64'
    },
    {
        name: 'Польща',
        alpha2Code: 'PL',
        alpha3Code: 'POL',
        numericCode: '616',
        callingCode: '+48'
    },
    {
        name: 'Португалія',
        alpha2Code: 'PT',
        alpha3Code: 'PRT',
        numericCode: '620',
        callingCode: '+351'
    },
    {
        name: 'Пуерто-Рико',
        alpha2Code: 'PR',
        alpha3Code: 'PRI',
        numericCode: '630',
        callingCode: '+1'
    },
    {
        name: 'Катар',
        alpha2Code: 'QA',
        alpha3Code: 'QAT',
        numericCode: '634',
        callingCode: '+974'
    },
    {
        name: 'Республіка Косово',
        alpha2Code: 'XK',
        alpha3Code: 'KOS',
        numericCode: null,
        callingCode: '+383'
    },
    {
        name: 'Реюньйон',
        alpha2Code: 'RE',
        alpha3Code: 'REU',
        numericCode: '638',
        callingCode: '+262'
    },
    {
        name: 'Румунія',
        alpha2Code: 'RO',
        alpha3Code: 'ROU',
        numericCode: '642',
        callingCode: '+40'
    },
    {
        name: 'Росія',
        alpha2Code: 'RU',
        alpha3Code: 'RUS',
        numericCode: '643',
        callingCode: '+7'
    },
    {
        name: 'Руанда',
        alpha2Code: 'RW',
        alpha3Code: 'RWA',
        numericCode: '646',
        callingCode: '+250'
    },
    {
        name: 'Сен-Бартельмі',
        alpha2Code: 'BL',
        alpha3Code: 'BLM',
        numericCode: '652',
        callingCode: '+590'
    },
    {
        name: 'Острови Святої Єлени, Вознесіння і Тристан-да-Кунья',
        alpha2Code: 'SH',
        alpha3Code: 'SHN',
        numericCode: '654',
        callingCode: '+290'
    },
    {
        name: 'Сент-Кіттс і Невіс',
        alpha2Code: 'KN',
        alpha3Code: 'KNA',
        numericCode: '659',
        callingCode: '+1869'
    },
    {
        name: 'Сент-Люсія',
        alpha2Code: 'LC',
        alpha3Code: 'LCA',
        numericCode: '662',
        callingCode: '+1758'
    },
    {
        name: 'Сен-Мартен',
        alpha2Code: 'MF',
        alpha3Code: 'MAF',
        numericCode: '663',
        callingCode: '+590'
    },
    {
        name: 'Сен-П\'єр і Мікелон',
        alpha2Code: 'PM',
        alpha3Code: 'SPM',
        numericCode: '666',
        callingCode: '+508'
    },
    {
        name: 'Сент-Вінсент і Гренадини',
        alpha2Code: 'VC',
        alpha3Code: 'VCT',
        numericCode: '670',
        callingCode: '+1784'
    },
    {
        name: 'Самоа',
        alpha2Code: 'WS',
        alpha3Code: 'WSM',
        numericCode: '882',
        callingCode: '+685'
    },
    {
        name: 'Сан-Марино',
        alpha2Code: 'SM',
        alpha3Code: 'SMR',
        numericCode: '674',
        callingCode: '+378'
    },
    {
        name: 'Сан-Томе і Принсіпі',
        alpha2Code: 'ST',
        alpha3Code: 'STP',
        numericCode: '678',
        callingCode: '+239'
    },
    {
        name: 'Саудівська Аравія',
        alpha2Code: 'SA',
        alpha3Code: 'SAU',
        numericCode: '682',
        callingCode: '+966'
    },
    {
        name: 'Сенегал',
        alpha2Code: 'SN',
        alpha3Code: 'SEN',
        numericCode: '686',
        callingCode: '+221'
    },
    {
        name: 'Сербія',
        alpha2Code: 'RS',
        alpha3Code: 'SRB',
        numericCode: '688',
        callingCode: '+381'
    },
    {
        name: 'Сейшельські Острови',
        alpha2Code: 'SC',
        alpha3Code: 'SYC',
        numericCode: '690',
        callingCode: '+248'
    },
    {
        name: 'Сьєрра-Леоне',
        alpha2Code: 'SL',
        alpha3Code: 'SLE',
        numericCode: '694',
        callingCode: '+232'
    },
    {
        name: 'Сінгапур',
        alpha2Code: 'SG',
        alpha3Code: 'SGP',
        numericCode: '702',
        callingCode: '+65'
    },
    {
        name: 'Сінт-Мартен',
        alpha2Code: 'SX',
        alpha3Code: 'SXM',
        numericCode: '534',
        callingCode: '+1721'
    },
    {
        name: 'Словаччина',
        alpha2Code: 'SK',
        alpha3Code: 'SVK',
        numericCode: '703',
        callingCode: '+421'
    },
    {
        name: 'Словенія',
        alpha2Code: 'SI',
        alpha3Code: 'SVN',
        numericCode: '705',
        callingCode: '+386'
    },
    {
        name: 'Соломонові Острови',
        alpha2Code: 'SB',
        alpha3Code: 'SLB',
        numericCode: '090',
        callingCode: '+677'
    },
    {
        name: 'Сомалі',
        alpha2Code: 'SO',
        alpha3Code: 'SOM',
        numericCode: '706',
        callingCode: '+252'
    },
    {
        name: 'ПАР',
        alpha2Code: 'ZA',
        alpha3Code: 'ZAF',
        numericCode: '710',
        callingCode: '+27'
    },
    {
        name: 'Південна Джорджія та Південні Сандвічеві Острови',
        alpha2Code: 'GS',
        alpha3Code: 'SGS',
        numericCode: '239',
        callingCode: '+500'
    },
    {
        name: 'Південна Корея',
        alpha2Code: 'KR',
        alpha3Code: 'KOR',
        numericCode: '410',
        callingCode: '+82'
    },
    {
        name: 'Південний Судан',
        alpha2Code: 'SS',
        alpha3Code: 'SSD',
        numericCode: '728',
        callingCode: '+211'
    },
    {
        name: 'Іспанія',
        alpha2Code: 'ES',
        alpha3Code: 'ESP',
        numericCode: '724',
        callingCode: '+34'
    },
    {
        name: 'Шрі-Ланка',
        alpha2Code: 'LK',
        alpha3Code: 'LKA',
        numericCode: '144',
        callingCode: '+94'
    },
    {
        name: 'Судан',
        alpha2Code: 'SD',
        alpha3Code: 'SDN',
        numericCode: '729',
        callingCode: '+249'
    },
    {
        name: 'Суринам',
        alpha2Code: 'SR',
        alpha3Code: 'SUR',
        numericCode: '740',
        callingCode: '+597'
    },
    {
        name: 'Свальбард і Ян-Маєн',
        alpha2Code: 'SJ',
        alpha3Code: 'SJM',
        numericCode: '744',
        callingCode: '+4779'
    },
    {
        name: 'Есватіні',
        alpha2Code: 'SZ',
        alpha3Code: 'SWZ',
        numericCode: '748',
        callingCode: '+268'
    },
    {
        name: 'Швеція',
        alpha2Code: 'SE',
        alpha3Code: 'SWE',
        numericCode: '752',
        callingCode: '+46'
    },
    {
        name: 'Швейцарія',
        alpha2Code: 'CH',
        alpha3Code: 'CHE',
        numericCode: '756',
        callingCode: '+41'
    },
    {
        name: 'Сирія',
        alpha2Code: 'SY',
        alpha3Code: 'SYR',
        numericCode: '760',
        callingCode: '+963'
    },
    {
        name: 'Тайвань',
        alpha2Code: 'TW',
        alpha3Code: 'TWN',
        numericCode: '158',
        callingCode: '+886'
    },
    {
        name: 'Таджикистан',
        alpha2Code: 'TJ',
        alpha3Code: 'TJK',
        numericCode: '762',
        callingCode: '+992'
    },
    {
        name: 'Танзанія',
        alpha2Code: 'TZ',
        alpha3Code: 'TZA',
        numericCode: '834',
        callingCode: '+255'
    },
    {
        name: 'Таїланд',
        alpha2Code: 'TH',
        alpha3Code: 'THA',
        numericCode: '764',
        callingCode: '+66'
    },
    {
        name: 'Східний Тимор',
        alpha2Code: 'TL',
        alpha3Code: 'TLS',
        numericCode: '626',
        callingCode: '+670'
    },
    {
        name: 'Того',
        alpha2Code: 'TG',
        alpha3Code: 'TGO',
        numericCode: '768',
        callingCode: '+228'
    },
    {
        name: 'Токелау',
        alpha2Code: 'TK',
        alpha3Code: 'TKL',
        numericCode: '772',
        callingCode: '+690'
    },
    {
        name: 'Тонга',
        alpha2Code: 'TO',
        alpha3Code: 'TON',
        numericCode: '776',
        callingCode: '+676'
    },
    {
        name: 'Тринідад і Тобаго',
        alpha2Code: 'TT',
        alpha3Code: 'TTO',
        numericCode: '780',
        callingCode: '+1868'
    },
    {
        name: 'Туніс',
        alpha2Code: 'TN',
        alpha3Code: 'TUN',
        numericCode: '788',
        callingCode: '+216'
    },
    {
        name: 'Туреччина',
        alpha2Code: 'TR',
        alpha3Code: 'TUR',
        numericCode: '792',
        callingCode: '+90'
    },
    {
        name: 'Туркменістан',
        alpha2Code: 'TM',
        alpha3Code: 'TKM',
        numericCode: '795',
        callingCode: '+993'
    },
    {
        name: 'Острови Теркс і Кайкос',
        alpha2Code: 'TC',
        alpha3Code: 'TCA',
        numericCode: '796',
        callingCode: '+1649'
    },
    {
        name: 'Тувалу',
        alpha2Code: 'TV',
        alpha3Code: 'TUV',
        numericCode: '798',
        callingCode: '+688'
    },
    {
        name: 'Уганда',
        alpha2Code: 'UG',
        alpha3Code: 'UGA',
        numericCode: '800',
        callingCode: '+256'
    },
    {
        name: 'Україна',
        alpha2Code: 'UA',
        alpha3Code: 'UKR',
        numericCode: '804',
        callingCode: '+380'
    },
    {
        name: 'ОАЕ',
        alpha2Code: 'AE',
        alpha3Code: 'ARE',
        numericCode: '784',
        callingCode: '+971'
    },
    {
        name: 'Велика Британія',
        alpha2Code: 'GB',
        alpha3Code: 'GBR',
        numericCode: '826',
        callingCode: '+44'
    },
    {
        name: 'США',
        alpha2Code: 'US',
        alpha3Code: 'USA',
        numericCode: '840',
        callingCode: '+1'
    },
    {
        name: 'Уругвай',
        alpha2Code: 'UY',
        alpha3Code: 'URY',
        numericCode: '858',
        callingCode: '+598'
    },
    {
        name: 'Узбекистан',
        alpha2Code: 'UZ',
        alpha3Code: 'UZB',
        numericCode: '860',
        callingCode: '+998'
    },
    {
        name: 'Вануату',
        alpha2Code: 'VU',
        alpha3Code: 'VUT',
        numericCode: '548',
        callingCode: '+678'
    },
    {
        name: 'Венесуела',
        alpha2Code: 'VE',
        alpha3Code: 'VEN',
        numericCode: '862',
        callingCode: '+58'
    },
    {
        name: 'В\'єтнам',
        alpha2Code: 'VN',
        alpha3Code: 'VNM',
        numericCode: '704',
        callingCode: '+84'
    },
    {
        name: 'Волліс і Футуна',
        alpha2Code: 'WF',
        alpha3Code: 'WLF',
        numericCode: '876',
        callingCode: '+681'
    },
    {
        name: 'Західна Сахара',
        alpha2Code: 'EH',
        alpha3Code: 'ESH',
        numericCode: '732',
        callingCode: '+212'
    },
    {
        name: 'Ємен',
        alpha2Code: 'YE',
        alpha3Code: 'YEM',
        numericCode: '887',
        callingCode: '+967'
    },
    {
        name: 'Замбія',
        alpha2Code: 'ZM',
        alpha3Code: 'ZMB',
        numericCode: '894',
        callingCode: '+260'
    },
    {
        name: 'Зімбабве',
        alpha2Code: 'ZW',
        alpha3Code: 'ZWE',
        numericCode: '716',
        callingCode: '+263'
    }
];

var ua = /*#__PURE__*/Object.freeze({
    __proto__: null,
    COUNTRIES_DB_UA: COUNTRIES_DB_UA
});

/**
 * @author Anthony Nahas
 * @since 06.11.19
 */
// @dynamic
class MatSelectCountryModule {
    constructor(iconRegistry, sanitizer) {
        this.iconRegistry = iconRegistry;
        this.sanitizer = sanitizer;
        this.registerCountries();
    }
    static forRoot(i18n) {
        return {
            ngModule: MatSelectCountryModule,
            providers: [
                {
                    provide: MatSelectCountryLangToken,
                    useValue: i18n,
                },
            ],
        };
    }
    registerCountries() {
        for (const country of COUNTRIES_DB) {
            const countryAlpha2Code = country.alpha2Code.toLowerCase();
            try {
                this.iconRegistry.addSvgIcon(countryAlpha2Code, this.sanitizer.bypassSecurityTrustResourceUrl(`assets/svg-country-flags/svg/${countryAlpha2Code}.svg`));
            }
            catch (err) {
                console.error("Error: icon not found for " + countryAlpha2Code, err);
            }
        }
    }
}
MatSelectCountryModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.1", ngImport: i0, type: MatSelectCountryModule, deps: [{ token: i1$1.MatIconRegistry }, { token: i2$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.NgModule });
MatSelectCountryModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.1", ngImport: i0, type: MatSelectCountryModule, declarations: [MatSelectCountryComponent, JoinStringsPipe], imports: [CommonModule,
        // Forms
        FormsModule,
        ReactiveFormsModule,
        // Material
        MatButtonModule,
        MatMenuModule,
        MatInputModule,
        MatAutocompleteModule,
        MatIconModule,
        MatProgressBarModule,
        MatFormFieldModule], exports: [MatSelectCountryComponent] });
MatSelectCountryModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.1", ngImport: i0, type: MatSelectCountryModule, imports: [CommonModule,
        // Forms
        FormsModule,
        ReactiveFormsModule,
        // Material
        MatButtonModule,
        MatMenuModule,
        MatInputModule,
        MatAutocompleteModule,
        MatIconModule,
        MatProgressBarModule,
        MatFormFieldModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.1", ngImport: i0, type: MatSelectCountryModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MatSelectCountryComponent, JoinStringsPipe],
                    imports: [
                        CommonModule,
                        // Forms
                        FormsModule,
                        ReactiveFormsModule,
                        // Material
                        MatButtonModule,
                        MatMenuModule,
                        MatInputModule,
                        MatAutocompleteModule,
                        MatIconModule,
                        MatProgressBarModule,
                        MatFormFieldModule,
                    ],
                    exports: [MatSelectCountryComponent],
                }]
        }], ctorParameters: function () { return [{ type: i1$1.MatIconRegistry }, { type: i2$1.DomSanitizer }]; } });

/*
 * Public API Surface of select-country
 */

/**
 * Generated bundle index. Do not edit.
 */

export { COUNTRIES_DB, COUNTRIES_DB_BR, COUNTRIES_DB_BY, COUNTRIES_DB_CA, COUNTRIES_DB_DE, COUNTRIES_DB_ES, COUNTRIES_DB_EU, COUNTRIES_DB_FR, COUNTRIES_DB_GL, COUNTRIES_DB_HR, COUNTRIES_DB_HU, COUNTRIES_DB_IT, COUNTRIES_DB_NL, COUNTRIES_DB_PT, COUNTRIES_DB_RU, COUNTRIES_DB_UA, MatSelectCountryComponent, MatSelectCountryLangToken, MatSelectCountryModule };
//# sourceMappingURL=angular-material-extensions-select-country.mjs.map
